import { initializeApp } from 'firebase/app';
import React, { useState, useEffect, useMemo, createContext, useContext, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth, signInWithPopup, GoogleAuthProvider, onAuthStateChanged, signOut, createUserWithEmailAndPassword, signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { getFirestore, doc, getDoc, setDoc, collection, addDoc, getDocs, deleteDoc, onSnapshot, query, where, serverTimestamp, updateDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { CreditPreview, resolveCreditTypography, resolveMultiHeaderSpacing, getRenderedWeightKey } from './CreditPreview.js';
import LandingPage from './LandingPage.js';
import FeaturesPage from './FeaturesPage.js';
import CheckoutPage from './CheckoutPage.js';
import SignUpPage from './SignUpPage.js';
import OnboardingPage from './OnboardingPage.js';
import TermsOfService from './TermsOfService.js';
import PrivacyPolicy from './PrivacyPolicy.js';
import { loadStripe } from '@stripe/stripe-js';
import TutorialPage from './TutorialPage.js';
import { getAnalytics, logEvent } from 'firebase/analytics';
import AffiliateDashboard from './affiliatedashboard.js';
import AffiliateAdminPage from './AffiliateAdminPageA.js';
import ReviewPage from './ReviewPage.js';
import Papa from "papaparse";



import { Send, Play, Pause, CreditCard, Users, ArrowLeft, Mail, Lock, Loader2, HelpCircle, ArrowRight, RotateCcw, Frame, Plus, Trash2, Upload, Download, Save, FolderOpen, Copy, Undo2, Redo2, Eye, ChevronDown, ChevronUp, X, CloudUpload, Music, Languages, List, Check, AlertCircle, HardDrive, Video, Search, CloudDownload, ChevronLeft, GripVertical, Type, Palette, Info, LogIn, SquarePlus, ImagePlus, Grid3x3, ChevronsRight, ChevronsLeft, Settings2, SlidersHorizontal, Image, ToggleLeft, ToggleRight, Newspaper, Edit, AlignLeft, AlignCenter, AlignRight, AlignJustify, Archive, ArchiveRestore, Film, Star, FileVideo, FileAudio, Timer, Award, Tv, Camera, Images, CopyPlus, ClipboardPaste, Repeat, Brush, MoveHorizontal, MoveVertical, Layers, Sparkles, Rows4, ArrowUp, ArrowDown, MoreVertical, Ban, Zap, ArrowRightLeft, Wand2, ListPlus, ClipboardCheck } from 'lucide-react';

const firebaseConfig = {
  apiKey: "AIzaSyAvNdc5_0aMP_y2wmxPEMKui_oCiciddQ4",
  authDomain: "cuethecredits-69b39.firebaseapp.com",
  projectId: "cuethecredits-69b39",
  storageBucket: "cuethecredits-69b39.firebasestorage.app",
  messagingSenderId: "658242457611",
  appId: "1:658242457611:web:41b7392ed3a7ccef9e770e"
};

// --- OFFLINE MODE FLAG ---
const OFFLINE_MODE = false;
const __app_id = 'cuethecredits-69b39';

// ============================================================================
// SCROLL PACING / SMOOTHNESS — single source of truth
// ============================================================================
// Everything that suggests, previews, or displays a scroll duration/speed in
// this file (the "Suggest duration" sparkle button, the live in-editor
// preview animation, and the recommended-range display + warning in the
// Timing menu) reads from these constants. They used to be declared
// separately in two different components and had drifted apart — one still
// said "17m 12s" is a reasonable credits length while the other had already
// been tuned. Keeping exactly one copy is what prevents that from happening
// again.
//
// Two different jobs, two different constant pairs — don't merge them:
//   - MIN/MAX_SECONDS_PER_LINE = what WE consider a realistic pace, purely
//     for the suggestion algorithm and the UI's displayed range/warning.
//   - CORE_MIN/MAX_SECONDS_PER_LINE (inside resolveRenderDuration below) =
//     core-renderer.js's OWN actual ceiling, mirrored so we can predict what
//     it will really do. We deliberately keep our own window tighter than
//     core-renderer's (see numbers below) — that's safe and never causes a
//     mismatch, because core-renderer only clamps a duration DOWN if it's
//     over its own (looser) ceiling; it never raises one that's under ours.
//     Loosening our window past core-renderer's real ceiling would be the
//     unsafe direction, so if these ever need to move, core-renderer.js's
//     resolveDuration() is the one to check against first.
//
// core-renderer.js also used to round its scroll speed to a whole
// pixels-per-frame value before deriving the final render duration (an
// "integer lock"), and predictLockedRender() below mirrored that same
// rounding so the in-editor preview matched what would actually render. That
// rounding step is gone from both places now — it wasn't protecting against
// any real jitter (ffmpeg's overlay moves continuously against each frame's
// timestamp; it never stepped to a rounded pixel position), it was just
// quantizing the achievable duration into a coarse, unevenly-spaced ladder,
// which is what let rendered length drift several+ seconds from what a
// customer actually typed in — worse at slow scroll speeds, worse again once
// a letterbox shrinks the travel distance and lowers pixels-per-frame
// further. Both places are continuous math now; see the "CONTINUOUS SPEED"
// comment in core-renderer.js's renderScrollVideo() for the full writeup.
const MIN_SECONDS_PER_LINE = 1.0;      // fastest realistic pace per credit line
const MAX_SECONDS_PER_LINE = 3.0;      // slowest realistic pace per credit line — keeps e.g. 129 lines well under 5 min at the slow end

// The stock duration every new project starts with (mirrors DEFAULT_SETTINGS.targetDuration /
// the inline `targetDuration: 120` in EditorPage's initial settings state). Used only to detect
// whether a user has ever actually set a duration before they hit Export — see
// handleExportButtonClick / the "set your duration" reminder modal in EditorPage.
const DEFAULT_TARGET_DURATION_SECONDS = 120;
const RENDER_DEVICE_SCALE_FACTOR = 2;  // keep in sync with core-renderer.js renderScrollVideo()
const RENDER_TARGET_LOCKED_PPF = 4;    // ideal locked render px/frame for "smooth" motion — the number to retune if the baseline pace still feels off

// Mirrors core-renderer.js resolveDuration() exactly — its own numbers, not
// the pacing constants above. This is the real ceiling the render enforces;
// everything above is just how conservative we choose to be ahead of it.
function resolveRenderDuration(requestedDuration, lineCount) {
    const CORE_MIN_SECONDS_PER_LINE = 2.5; // core-renderer.js's actual floor input (only used for its fallback; not enforced there either)
    const CORE_MAX_SECONDS_PER_LINE = 8;   // core-renderer.js's actual ceiling
    const fallback = lineCount > 0 ? lineCount * CORE_MIN_SECONDS_PER_LINE : 120;
    let duration = (requestedDuration && requestedDuration > 0) ? requestedDuration : fallback;
    if (lineCount > 0) {
        const maxDuration = lineCount * CORE_MAX_SECONDS_PER_LINE;
        if (duration > maxDuration) duration = maxDuration;
    }
    return duration;
}

// Mirrors core-renderer.js's renderScrollVideo() speed/duration math exactly: continuous
// scrollSpeed = distance / resolvedTargetDuration, with no per-frame pixel rounding. There
// used to be an "integer lock" step here (round pixels-per-frame to a whole number, then
// recompute duration from that) mirroring the same step in core-renderer — that step has
// since been removed on the render side, because ffmpeg's overlay filter moves the image
// continuously against each frame's real timestamp rather than stepping to a rounded pixel
// position, so there was nothing the rounding actually protected there, only duration
// accuracy it cost (this is what was causing rendered length to drift from the requested
// duration — worse at slower scroll speeds, worse again with a letterbox shrinking travel
// distance). Keeping this function name/shape ("locked") for now to avoid touching every
// call site — but the math is continuous throughout, matching core-renderer.js.
// Travel distance still comes from the DOM preview, so this is a prediction, not a
// guarantee — the render measures the real screenshotted height — but with no rounding
// step left in either place, it now lands within a fraction of a second of the real render.
function predictLockedRender(totalTravelDistanceCssPx, requestedDuration, fps, lineCount) {
    const resolvedTargetDuration = resolveRenderDuration(requestedDuration, lineCount);
    const totalTravelDistanceRenderPx = totalTravelDistanceCssPx * RENDER_DEVICE_SCALE_FACTOR;

    const renderScrollSpeed = totalTravelDistanceRenderPx / resolvedTargetDuration;
    const lockedDuration = resolvedTargetDuration;
    const cssPixelsPerSecond = renderScrollSpeed / RENDER_DEVICE_SCALE_FACTOR;

    return { lockedDuration, cssPixelsPerSecond, resolvedTargetDuration };
}

// ============================================================================
// BUILT-IN FONT LOADER
// ============================================================================
// Mirrors FONT_MAPPING in core-renderer.js — filename, family, weight, style
// for every bundled font. This is a deliberate copy, not an import (importing
// the Node backend file directly into a browser bundle isn't safe/possible),
// so if a font is added/renamed in core-renderer.js, mirror the change here.
// Moving both into one shared JSON file both repos import is the real
// long-term fix; this keeps the two in sync manually until then.
const BUILT_IN_FONTS = [
    { filename: 'Arial.ttf', family: 'Arial', weight: '400', style: 'normal' },
    { filename: 'Arial Bold.ttf', family: 'Arial', weight: '700', style: 'normal' },
    { filename: 'Arial Italic.ttf', family: 'Arial', weight: '400', style: 'italic' },
    { filename: 'Arial Bold Italic.ttf', family: 'Arial', weight: '700', style: 'italic' },
    { filename: 'CourierNew.ttf', family: 'Courier New', weight: '400', style: 'normal' },
    { filename: 'CourierNew-Bold.ttf', family: 'Courier New', weight: '700', style: 'normal' },
    { filename: 'CourierNew-Italic.ttf', family: 'Courier New', weight: '400', style: 'italic' },
    { filename: 'CourierNew-BoldItalic.ttf', family: 'Courier New', weight: '700', style: 'italic' },
    { filename: 'EBGaramond-Regular.ttf', family: 'EB Garamond', weight: '400', style: 'normal' },
    { filename: 'EBGaramond-Italic.ttf', family: 'EB Garamond', weight: '400', style: 'italic' },
    { filename: 'EBGaramond-Bold.ttf', family: 'EB Garamond', weight: '700', style: 'normal' },
    { filename: 'EBGaramond-BoldItalic.ttf', family: 'EB Garamond', weight: '700', style: 'italic' },
    { filename: 'futura.ttf', family: 'Futura', weight: '400', style: 'normal' },
    { filename: 'futura-light.ttf', family: 'Futura', weight: '300', style: 'normal' },
    { filename: 'futura-bold.ttf', family: 'Futura', weight: '700', style: 'normal' },
    { filename: 'futura-italic.ttf', family: 'Futura', weight: '400', style: 'italic' },
    { filename: 'futura-light-italic.ttf', family: 'Futura', weight: '300', style: 'italic' },
    { filename: 'futura-bold-italic.ttf', family: 'Futura', weight: '700', style: 'italic' },
    { filename: 'garamond.ttf', family: 'Garamond', weight: '400', style: 'normal' },
    { filename: 'Garamond Light.ttf', family: 'Garamond', weight: '300', style: 'normal' },
    { filename: 'Garamond Bold.ttf', family: 'Garamond', weight: '700', style: 'normal' },
    { filename: 'Garamond Italic.ttf', family: 'Garamond', weight: '400', style: 'italic' },
    { filename: 'Garamond Light Italic.otf', family: 'Garamond', weight: '300', style: 'italic' },
    { filename: 'Garamond Bold Italic.ttf', family: 'Garamond', weight: '700', style: 'italic' },
    { filename: 'Georgia.ttf', family: 'Georgia', weight: '400', style: 'normal' },
    { filename: 'Georgia Bold.ttf', family: 'Georgia', weight: '700', style: 'normal' },
    { filename: 'Georgia Italic.ttf', family: 'Georgia', weight: '400', style: 'italic' },
    { filename: 'Georgia Bold Italic.ttf', family: 'Georgia', weight: '700', style: 'italic' },
    { filename: 'Helvetica.ttf', family: 'Helvetica', weight: '400', style: 'normal' },
    { filename: 'helvetica-light.ttf', family: 'Helvetica', weight: '300', style: 'normal' },
    { filename: 'Helvetica-Bold.ttf', family: 'Helvetica', weight: '700', style: 'normal' },
    { filename: 'Helvetica-Oblique.ttf', family: 'Helvetica', weight: '400', style: 'italic' },
    { filename: 'Helvetica-BoldOblique.ttf', family: 'Helvetica', weight: '700', style: 'italic' },
    { filename: 'helvetica-light-italic.ttf', family: 'Helvetica', weight: '300', style: 'italic' },
    { filename: 'helvetica-bold-italic.ttf', family: 'Helvetica', weight: '700', style: 'italic' },
    { filename: 'Helvetica Rounded Bold.otf', family: 'Helvetica Rounded', weight: '700', style: 'normal' },
    { filename: 'optima.TTF', family: 'Optima', weight: '400', style: 'normal' },
    { filename: 'OPTIMA_B.TTF', family: 'Optima', weight: '700', style: 'normal' },
    { filename: 'Optima_Italic.ttf', family: 'Optima', weight: '400', style: 'italic' },
    { filename: 'Optima Bold Italic.TTF', family: 'Optima', weight: '700', style: 'italic' },
    { filename: 'Palatino Roman.ttf', family: 'Palatino', weight: '400', style: 'normal' },
    { filename: 'Palatino-Light.ttf', family: 'Palatino', weight: '300', style: 'normal' },
    { filename: 'Palatino Bold.ttf', family: 'Palatino', weight: '700', style: 'normal' },
    { filename: 'palatino-italic.ttf', family: 'Palatino', weight: '400', style: 'italic' },
    { filename: 'Palatino Light Italic.ttf', family: 'Palatino', weight: '300', style: 'italic' },
    { filename: 'palatino_bold_italic.ttf', family: 'Palatino', weight: '700', style: 'italic' },
    { filename: 'Poppins-Thin.ttf', family: 'Poppins', weight: '100', style: 'normal' },
    { filename: 'Poppins-Light.ttf', family: 'Poppins', weight: '300', style: 'normal' },
    { filename: 'Poppins-Regular.ttf', family: 'Poppins', weight: '400', style: 'normal' },
    { filename: 'Poppins-Italic.ttf', family: 'Poppins', weight: '400', style: 'italic' },
    { filename: 'Poppins-LightItalic.ttf', family: 'Poppins', weight: '300', style: 'italic' },
    { filename: 'Poppins-Bold.ttf', family: 'Poppins', weight: '700', style: 'normal' },
    { filename: 'Poppins-BoldItalic.ttf', family: 'Poppins', weight: '700', style: 'italic' },
];

// TODO: confirm this matches wherever the fonts/ folder actually gets
// uploaded in Firebase Storage. Placeholder path — same bucket, new prefix,
// doesn't touch anything under artifacts/{appId}/users/.
const BUILT_IN_FONTS_STORAGE_PATH = `artifacts/${__app_id}/global/fonts`;

// Module-level cache (not component state): guarantees the ~55 files are
// fetched at most once per browser session — survives React StrictMode's
// dev double-invoke and repeated mounts, instead of re-requesting every
// download URL each time this runs.
let _builtInFontsLoadPromise = null;

// document.fonts.check() runs CSS font matching — it consults locally
// installed system fonts and matches the nearest weight in a family, so it
// returns true for "Futura 300" on any Mac purely because the OS ships
// Futura. Using it as a skip guard meant no bundled Futura/Arial/Helvetica/
// Georgia/Palatino face ever loaded, and every weight of those families
// collapsed onto one system face in the preview. Test the FontFaceSet for
// the exact family+weight+style instead — that's the only question we care
// about, and it never falls back.
function hasExactFace(family, weight, style) {
    const want = family.trim().toLowerCase();
    for (const face of document.fonts) {
        const have = face.family.replace(/^['"]|['"]$/g, '').trim().toLowerCase();
        if (have === want && String(face.weight) === String(weight) && face.style === style) {
            return true;
        }
    }
    return false;
}

function ensureBuiltInFontsLoaded(storage) {
    if (_builtInFontsLoadPromise) return _builtInFontsLoadPromise;

    let loaded = 0, skipped = 0, failed = 0;
    _builtInFontsLoadPromise = Promise.allSettled(
        BUILT_IN_FONTS.map(async ({ filename, family, weight, style }) => {
            if (hasExactFace(family, weight, style)) return 'skipped';

            const url = await getDownloadURL(ref(storage, `${BUILT_IN_FONTS_STORAGE_PATH}/${filename}`));
            const font = new FontFace(family, `url(${url})`, { weight, style });
            const loadedFont = await font.load();
            document.fonts.add(loadedFont);
            return 'loaded';
        })
    ).then((results) => {
        results.forEach((r, i) => {
            if (r.status === 'fulfilled') {
                if (r.value === 'skipped') skipped++; else loaded++;
            } else {
                failed++;
                const { filename, family, weight, style } = BUILT_IN_FONTS[i];
                console.warn(`⚠️ Built-in font failed to load: ${family} ${weight} ${style} (${filename})`, r.reason);
            }
        });
        console.log(`🔤 Built-in fonts: ${loaded} loaded, ${skipped} already present, ${failed} failed, ${BUILT_IN_FONTS.length} total.`);
    });

    return _builtInFontsLoadPromise;
}

// A simple debounce function to prevent too many database writes
const debounce = (func, delay) => {
    let timeout;
    return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), delay);
    };
};

const useHistoryState = (key, initialState) => {
    const [history, setHistory] = useState([initialState]);
    const [index, setIndex] = useState(0);

    const setState = (newState, overwrite = false) => {
        const newHistory = history.slice(0, index + 1);
        newHistory.push(newState);
        
        setHistory(newHistory);
        setIndex(newHistory.length - 1);
    };
    
    const undo = () => {
        if (index > 0) {
            setIndex(index - 1);
        }
    };

    const redo = () => {
        if (index < history.length - 1) {
            setIndex(index + 1);
        }
    };

    const state = history[index];

    // This part is a placeholder; you'd need to replace the regular undo/redo functions
    // with these if you want the hook to manage it fully. For now, this makes the app run.
    return [state, setState, undo, redo, history, index];
};

// --- Shared text-case options used by both the Inspector panel and the top toolbar ---
const CASE_OPTIONS = [
    { value: 'normal', label: 'Normal' },
    { value: 'uppercase', label: 'UPPERCASE' },
    { value: 'lowercase', label: 'lowercase' },
    { value: 'capitalize', label: 'Title Case' },
];

// --- START: Original CollapsibleSection for backward compatibility ---
const CollapsibleSection = ({ title, icon: Icon, children, defaultOpen = true, onToggle, count, headerActions }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    const handleToggle = () => {
        setIsOpen(!isOpen);
        if (onToggle) {
            onToggle();
        }
    };

    return (
        <div className="mb-5">
            <div className="flex justify-between items-center py-2.5 px-1 group">
                <button onClick={handleToggle} className="flex-grow flex items-center gap-2.5 text-left">
                    {Icon && <Icon size={14} className="text-zinc-500 group-hover:text-zinc-300 transition-colors"/>}
                    <span className="text-[11px] font-bold tracking-[0.18em] uppercase text-zinc-400 group-hover:text-zinc-200 transition-colors">
                        {title}
                    </span>
                    {count !== undefined && (
                        <span className="text-[10px] text-zinc-600 font-mono">({count})</span>
                    )}
                </button>
                <div className="flex items-center gap-2">
                    {headerActions}
                    <button onClick={handleToggle} className="text-zinc-600 hover:text-zinc-300 transition-colors">
                        {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </button>
                </div>
            </div>
            <div className="h-px bg-zinc-800/70 mb-3" />
            {isOpen && <div className="pt-1">{children}</div>}
        </div>
    );
};

const CSV_CREDITS_TEMPLATE = `DisplayType,Role,Name,Notes
Card,Directed By,Oscar Micheaux,
Card,Written By,Charlie Kaufman,
Card,Produced By,Emma Thomas,
Scroll,,CAST,
Scroll,Dr. Aris Thorne,Idris Elba,
Scroll,Jessa,Zendaya,
Scroll,Commander Valerius,Oscar Isaac,
Scroll,The Archivist,Tilda Swinton,
Scroll,,CREW,
Scroll,Director of Photography,Hoyte van Hoytema,
Scroll,Production Designer,Patrice Vermette,
Scroll,Editor,Joe Walker,
Scroll,Stunt Coordinator,Sam Hargrave,
Scroll,Stunts,,
Scroll,,Chris Brewster,
Scroll,,Heidi Moneymaker,
Scroll,,Daniel Hargrave,
Scroll,,Monique Ganderton,
Scroll,,Special Thanks,
Scroll,,The City of Ocoee Film Commission,
Scroll,,"The events, characters, and firms depicted in this motion picture are fictitious.",
`;

// Add this component definition in App.js
const handleDownloadTemplate = () => {
    // Create a Blob from the new template string
    const blob = new Blob([CSV_CREDITS_TEMPLATE], { type: 'text/csv;charset=utf-8;' });
    
    // Create a temporary URL for the browser
    const url = URL.createObjectURL(blob);
    
    // Create a hidden anchor tag to force the download
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'blackboxcredits_template.csv');
    
    // Append to body, trigger click, and cleanup
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url); // Free up memory
};

const EnhancedCollapsibleSection = ({ title, icon: Icon, children, defaultOpen = true, onToggle, count, headerActions, priority = 'normal', className = '' }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    const handleToggle = () => {
        setIsOpen(!isOpen);
        if (onToggle) {
            onToggle();
        }
    };

    const accentColor = {
        high: 'text-amber-400/90',
        normal: 'text-zinc-300',
        low: 'text-zinc-500'
    };

    const dividerColor = {
        high: 'bg-amber-500/30',
        normal: 'bg-zinc-700/60',
        low: 'bg-zinc-800/40'
    };

    return (
        <div className={`mb-4 ${className}`}>
            <div className="flex justify-between items-center py-2 px-0.5 group cursor-pointer" onClick={handleToggle}>
                <div className={`flex items-center gap-2 ${accentColor[priority]}`}>
                    {Icon && <Icon size={13} className="shrink-0"/>}
                    <span className="text-[10px] font-bold tracking-[0.2em] uppercase">
                        {title}
                    </span>
                    {count !== undefined && (
                        <span className="text-[9px] font-mono opacity-60">({count})</span>
                    )}
                </div>
                <div className="flex items-center gap-1.5">
                    {headerActions}
                    <span className="text-zinc-600 group-hover:text-zinc-400 transition-colors">
                        {isOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                    </span>
                </div>
            </div>
            <div className={`h-px ${dividerColor[priority]} mb-3`} />
            {isOpen && <div className="pb-1">{children}</div>}
        </div>
    );
};


// --- START: Quick Control Components ---
const QuickFontSize = ({ value, onChange, onReset, isOverridden }) => (
    <div className="flex items-center gap-2">
        <span className="text-xs text-zinc-400 w-8">Size</span>
        <input 
            type="range" 
            min="8" 
            max="120" 
            value={value} 
            onChange={(e) => onChange(parseFloat(e.target.value))}
            className="flex-1 h-2 bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
        />
        <span className="text-xs text-zinc-300 w-8 text-right">{value}</span>
        {isOverridden && (
            <button onClick={onReset} className="p-1 text-zinc-500 hover:text-white transition-colors rounded">
                <RotateCcw size={12} />
            </button>
        )}
    </div>
);

const QuickColorPicker = ({ label, value, onChange, onReset, isOverridden }) => (
    <div className="flex items-center justify-between py-1">
        <span className="text-[11px] text-zinc-500 font-medium">{label}</span>
        <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 bg-zinc-900 border border-zinc-800 rounded overflow-hidden px-2 py-1">
                <input 
                    type="color" 
                    value={value} 
                    onChange={(e) => onChange(e.target.value)}
                    className="w-5 h-4 rounded-sm border-0 cursor-pointer bg-transparent p-0"
                />
                <span className="text-[10px] font-mono text-zinc-400">{value?.toUpperCase()}</span>
            </div>
            {isOverridden && (
                <button onClick={onReset} className="p-1 text-zinc-700 hover:text-zinc-400 transition-colors rounded">
                    <RotateCcw size={11} />
                </button>
            )}
        </div>
    </div>
);

const codecDisplayNames = {
    'h264-mp4': 'H.264 (MP4)',
    'h264-mov': 'H.264 (MOV)',
    'h265-mp4': 'H.265 (MP4)',
    'h265-mov': 'H.265 (MOV)',
    'prores422': 'ProRes 422 (MOV)',
    'prores4444': 'ProRes 4444+Alpha (MOV)'
};

const QuickSelect = ({ label, value, options, onChange, onReset, isOverridden, className = "w-24" }) => (
    <div className="flex items-center gap-2">
        <span className="text-xs text-zinc-400 min-w-[3rem]">{label}</span>
        <select 
            value={value} 
            onChange={(e) => onChange(e.target.value)} 
            className={`${className} text-xs p-1.5 rounded border border-zinc-600 bg-zinc-800 text-gray-200 focus:ring-1 focus:ring-purple-500`}
        >
            {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </select>
        {isOverridden && (
            <button onClick={onReset} className="p-1 text-zinc-500 hover:text-white transition-colors rounded">
                <RotateCcw size={12} />
            </button>
        )}
    </div>
);

// --- Error Boundary: contains render/commit crashes to just the preview pane ---
// React error boundaries also catch commit-phase DOM errors (e.g. a
// "removeChild"/"insertBefore" NotFoundError thrown while React is
// reconciling the DOM) as long as they're thrown by React itself during
// that commit — which covers the class of crash where a child component
// mutates its own DOM subtree outside React's control and React's next
// re-render then tries to remove a node that's already gone.
class RenderErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    componentDidCatch(error, info) {
        console.error('RenderErrorBoundary caught:', error, info);
    }
    handleReset = () => {
        this.setState({ hasError: false });
    };
    render() {
        if (this.state.hasError) {
            return (
                <div className="w-full h-full min-h-[240px] flex flex-col items-center justify-center gap-3 bg-black/40 border border-red-900/30 rounded-xl p-6 text-center">
                    <AlertCircle size={22} className="text-red-500" />
                    <p className="text-xs text-zinc-400">
                        {this.props.label || 'This preview hit a rendering error.'} Your work isn't lost — just the preview needs a reset.
                    </p>
                    <button
                        onClick={this.handleReset}
                        className="text-[10px] font-black uppercase tracking-widest text-yellow-brand border border-yellow-brand/30 rounded-lg px-4 py-2 hover:bg-yellow-brand/10 transition-colors"
                    >
                        Reset Preview
                    </button>
                </div>
            );
        }
        return this.props.children;
    }
}

// --- AppContext for Firebase and App State ---
export const AppContext = createContext(null);

// --- AppProvider Component (Central state management and Firebase init) ---
const AppProvider = ({ children }) => {
  const [functions, setFunctions] = useState(null);
  const [appInstance, setAppInstance] = useState(null); // State to hold Firebase app instance
  const [db, setDb] = useState(null);
  const [auth, setAuth] = useState(null);
  const [storage, setStorage] = useState(null);
  const [userId, setUserId] = useState(null);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [firestoreLoading, setFirestoreLoading] = useState(false);
  const [currentProjectId, setCurrentProjectId] = useState(OFFLINE_MODE ? 'local-project' : null);
  const [messageBox, setMessageBox] = useState({ visible: false, title: '', message: '', type: 'info', onConfirm: null });
  const [isLoadingFirebase, setIsLoadingFirebase] = useState(!OFFLINE_MODE); 
  const [currentPage, setCurrentPage] = useState('landing'); 
  const [purchaseIntent, setPurchaseIntent] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [reviewToken, setReviewToken] = useState(null);

   
  const [exportNoticeStatus, setExportNoticeStatus] = useState('none'); // 'none', 'processing', 'ready', 'failed'

  // Add this BEFORE your existing auth useEffect (around line 314)
useEffect(() => {
    // Handle URL parameters for affiliate pages
    const urlParams = new URLSearchParams(window.location.search);
    const page = urlParams.get('page');
    
    if (page === 'affiliate') {
        setCurrentPage('affiliate');
    } else if (page === 'affiliate-admin') {
        setCurrentPage('affiliate-admin');
    } else if (page === 'review') {
        const token = urlParams.get('token');
        if (token) {
            setReviewToken(token);
            setCurrentPage('review');
        }
    }
}, []); // Empty dependency array - runs once on mount

useEffect(() => {
    const app = initializeApp(firebaseConfig);
    const firestoreDb = getFirestore(app);
    const firebaseAuth = getAuth(app);
    
    setAppInstance(app);
    setDb(firestoreDb);
    setAuth(firebaseAuth);
    const funcs = getFunctions(app);
    setFunctions(funcs);
    setStorage(getStorage(app));

    // ✅ ADD THIS LINE:
    const analyticsInstance = getAnalytics(app);
    setAnalytics(analyticsInstance);  // Add analytics to state

    const unsubscribe = onAuthStateChanged(firebaseAuth, async (user) => {
        // A review link is a standalone public route — if one is present in the URL,
        // honor it regardless of whether the visitor happens to be logged in
        // (e.g. the owner testing their own link) and skip all normal auth routing.
        const reviewUrlParams = new URLSearchParams(window.location.search);
        if (reviewUrlParams.get('page') === 'review' && reviewUrlParams.get('token')) {
            if (user) setUserId(user.uid);
            setReviewToken(reviewUrlParams.get('token'));
            setCurrentPage('review');
            setIsAuthReady(true);
            setIsLoadingFirebase(false);
            return;
        }

        if (user) {
            setUserId(user.uid);
            
            // Case 1: Is this a new DEMO user?
const isPendingDemo = localStorage.getItem('pendingDemoUser');
if (isPendingDemo) {
    localStorage.removeItem('pendingDemoUser');
    
    // ✅ IDEMPOTENCY CHECK: Verify they don't already have a trial project
    const projectsRef = collection(firestoreDb, `artifacts/${__app_id}/users/${user.uid}/projects`);
    const trialQuery = query(projectsRef, where('isTrial', '==', true));
    const existingTrials = await getDocs(trialQuery);
    
    if (existingTrials.empty) {
        try {
            const initializeDemo = httpsCallable(funcs, 'initializeDemoUser');
            const result = await initializeDemo();
            
            if (!result.data.existing) {
                showMessageBox("Welcome!", "Your 7-day free trial has started!", "success", true);
                if (analyticsInstance) {
                    logEvent(analyticsInstance, 'sign_up', {
                        method: 'trial',
                        timestamp: new Date().toISOString()
                    });
                }
            }
        } catch (error) {
            console.error("Fatal: Failed to initialize demo user:", error);
            showMessageBox("Trial Setup Failed", "Could not set up your free trial. Please contact support.", "error");
            await signOut(firebaseAuth);
            return;
        }
    }

    // ✅ ADD THESE TWO LINES TO PREVENT CRASHING:
    // This allows the app to bypass standard claims parsing until the cloud finishes initializing.
    setCurrentPage('projects');
    setIsAuthReady(true);
    setIsLoadingFirebase(false);
    return; // <-- CRITICAL: Prevents falling through to Case 3 check
}

            // Case 2: Is this a new PAID user heading to checkout?
            const isPendingPurchase = localStorage.getItem('pendingPurchaseUser');
            if (isPendingPurchase) {
                localStorage.removeItem('pendingPurchaseUser');
                console.log('New paid user detected, allowing them to proceed to checkout.');
                // By returning here, we DON'T check their (currently empty) claims,
                // which allows the app to proceed to the 'checkout' page as intended.
                setIsAuthReady(true);
                setIsLoadingFirebase(false);
                return;
            }

            // Case 3: This is an existing user logging in. Check their claims.
            const tokenResult = await user.getIdTokenResult(true); // Force refresh
            const claims = tokenResult.claims;
            console.log('User claims retrieved:', claims);

            // THE FIX: Check for 'hasPaidAccount' OR 'isDemo'
if (claims.hasPaidAccount || claims.hasPaid || claims.isDemo) {
            if (claims.planExpiry && new Date(claims.planExpiry) < new Date()) {
                    console.log('User plan has expired.');
                }
                setCurrentPage('projects');
            } else {
                // If no valid claims, send to signup/pricing.
                console.log('User has no access claims, redirecting to signup.');
                setCurrentPage('signup');
            }
        } else {
    // No user is logged in
    setUserId(null);
    
    // Only redirect to landing if not already on affiliate pages
    const urlParams = new URLSearchParams(window.location.search);
    const page = urlParams.get('page');
    
    if (page !== 'affiliate' && page !== 'affiliate-admin' && page !== 'review') {
        setCurrentPage('landing');
    }
}
        setIsAuthReady(true);
        setIsLoadingFirebase(false);
    });

    return () => unsubscribe();
}, []);

// Effect to listen for export jobs and update exportNoticeStatus
  useEffect(() => {
    if (!db || !userId) return;

    const currentAppId = __app_id;
    const exportsCollectionRef = collection(db, `artifacts/${currentAppId}/users/${userId}/exportJobs`);
    
    // Listen to all export jobs for the current user
    const unsubscribe = onSnapshot(exportsCollectionRef, (snapshot) => {
        let processing = false;
        let failed = false;
        let ready = false;
        const now = new Date();
        const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);

        snapshot.docs.forEach(doc => {
            const data = doc.data();
            const jobTimestamp = data.timestamp?.toDate ? data.timestamp.toDate() : now;
            
            // Only consider jobs from the last 24 hours for status indication
            if (jobTimestamp > twentyFourHoursAgo) {
                if (data.status === 'processing') {
                    processing = true;
                } else if (data.status === 'ready') {
                    ready = true;
                } else if (data.status === 'failed' || (data.status === 'processing' && (now.getTime() - jobTimestamp.getTime() > 180 * 60 * 1000))) { // 180 min timeout
                    failed = true;
                }
            }
        });

        if (processing) {
            setExportNoticeStatus('processing');
        } else if (failed) {
            setExportNoticeStatus('failed');
        } else if (ready) {
            setExportNoticeStatus('ready');
        } else {
            setExportNoticeStatus('none');
        }

    }, (error) => {
        console.error("Error listening to export jobs for status:", error);
    });

    return () => unsubscribe();
  }, [db, userId]);

 const showMessageBox = (title, message, type = 'info', autoDismiss = false, onConfirm = null, linkUrl = null, linkText = '') => {
    setMessageBox({ visible: true, title, message, type, onConfirm, linkUrl, linkText });
    
    // If autoDismiss is a number, use it as duration. If true, default to 3000ms
    if (autoDismiss) {
      const duration = typeof autoDismiss === 'number' ? autoDismiss : 3000;
      setTimeout(() => {
        setMessageBox(prev => ({ ...prev, visible: false }));
      }, duration);
    }
  };

  const closeMessageBox = () => {
    setMessageBox({ visible: false, title: '', message: '', type: 'info', onConfirm: null });
  };

  const confirmAction = () => {
    if (messageBox.onConfirm) {
      messageBox.onConfirm();
    }
    closeMessageBox();
  };

  const logout = async () => {
    if (auth && !OFFLINE_MODE) {
      try {
        await signOut(auth);
        setUserId(null);
        // CRITICAL FIX: Do NOT set isAuthReady to false here. 
        // Let the onAuthStateChanged listener handle it.
        // If we set it here, the LoginPage briefly sees !isAuthReady && !isLoadingFirebase 
        // which can lead to visual instability, but for sign-in page to be enabled, 
        // we must ensure loading is off and auth is ready.
        // The listener will be triggered, setting auth.currentUser to null,
        // and then it will correctly set isAuthReady and currentPage.
        
        // Ensure no loading flag is set that would gray out the form.
        setIsLoadingFirebase(false); 

        // Explicitly set the page right away for a smoother UI transition
        setCurrentPage('login'); 
        showMessageBox("Logged Out", "You have been logged out.", "info", true);
      } catch (error) {
        console.error("Error signing out:", error);
        showMessageBox("Error", `Failed to log out: ${error.message}`, "error");
      }
    }
  };

  const contextValue = {
    appInstance,
    db, auth, storage, userId, isAuthReady, functions, firestoreLoading, setFirestoreLoading,
    currentPage, setCurrentPage, currentProjectId, setCurrentProjectId, showMessageBox, closeMessageBox, 
    confirmAction, messageBox, logout, purchaseIntent,  setPurchaseIntent, 
    isLoadingFirebase, exportNoticeStatus, analytics, reviewToken
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

const UpgradeToProBanner = ({ onUpgrade, isUpgrading }) => {
    const [isOpen, setIsOpen] = React.useState(false);

    return (
        <div className="border border-zinc-800 bg-zinc-950 rounded-lg p-3 transition-all duration-200 hover:border-zinc-700/80 mb-1 w-full">
            {/* Minimal Trigger */}
            <button 
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between text-left group"
            >
                <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500/80 shadow-[0_0_8px_rgba(59,130,246,0.4)]"></div>
                    <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-400 group-hover:text-zinc-200 transition-colors">
                        Indie License
                    </span>
                </div>
                <span className="text-[10px] font-mono font-bold text-zinc-600 group-hover:text-purple-400 transition-colors uppercase tracking-wider">
                    {isOpen ? 'Close' : 'Upgrade Options'}
                </span>
            </button>

            {/* Elegant Drawer */}
            {isOpen && (
                <div className="mt-3 pt-3 border-t border-zinc-900 space-y-2 animate-in fade-in slide-in-from-top-1 duration-200">
                    <p className="text-[11px] leading-relaxed text-zinc-500 font-medium">
                        Need studio capabilities? Upgrade this project to Studio Pro for 4K mastering, ProRes 4444 + Alpha exports, and full NLE tracking data.
                    </p>
                    <div className="pt-1">
                        <button
                            onClick={onUpgrade}
                            disabled={isUpgrading}
                            className="w-full py-2 rounded bg-zinc-100 hover:bg-white text-black text-[11px] font-bold tracking-wider uppercase transition-all disabled:opacity-40"
                        >
                            {isUpgrading ? 'Redirecting to Checkout...' : 'Upgrade to Studio Pro (+$200)'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

// --- LoginPage Component ---
const LoginPage = ({ onLogin, onNavigate }) => { // Added onNavigate prop
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const auth = getAuth();

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (!email) { setLocalError('Please enter your email address above.'); return; }
    setLoading(true);
    setLocalError('');
    try {
      await sendPasswordResetEmail(auth, email);
      setResetSent(true);
    } catch (err) {
      let msg = err.message;
      if (msg.includes('auth/user-not-found') || msg.includes('auth/invalid-email')) msg = 'No account found with that email.';
      setLocalError(msg);
    } finally {
      setLoading(false);
    }
  };

  // Handle Login Only
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLocalError('');

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      if (onLogin) {
        onLogin(userCredential.user);
      }
    } catch (err) {
      console.error("Login Error:", err);
      let msg = err.message;
      if (msg.includes('auth/invalid-email')) msg = 'Please enter a valid email address.';
      if (msg.includes('auth/user-not-found') || msg.includes('auth/invalid-credential')) msg = 'Invalid email or password.';
      if (msg.includes('auth/wrong-password')) msg = 'Invalid email or password.';
      if (msg.includes('auth/too-many-requests')) msg = 'Too many failed attempts. Please try again later.';
      setLocalError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    setLocalError('');
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      // Google login is safe to use for both login/signup as it doesn't duplicate errors
      if (onLogin) {
        onLogin(result.user);
      }
    } catch (err) {
      console.error("Google Auth Error:", err);
      setLocalError('Unable to sign in with Google.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 font-sans flex flex-col items-center justify-center relative">
      
      <div className="w-full max-w-sm px-6 z-10">
        
        {/* Header */}
        <div className="mb-10">
          <div className="text-2xl font-bold tracking-tight text-white mb-1">
            BLACKBOX<span className="text-zinc-700">cr</span>
          </div>
          <p className="text-[10px] text-zinc-600 tracking-[0.25em] uppercase">
            Sign in to your workspace
          </p>
        </div>

        {/* Auth Card */}
        <div className="border border-zinc-800/80 rounded-lg p-7 bg-zinc-950">
          
          <form onSubmit={handleLogin} className="space-y-4">
            
            {/* Error Display */}
            {localError && (
              <div className="flex items-start gap-2.5 text-[11px] text-red-400 bg-red-950/30 border border-red-900/30 rounded p-3">
                <AlertCircle size={13} className="mt-0.5 shrink-0" />
                <span>{localError}</span>
              </div>
            )}

            {/* Email */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-bold tracking-[0.18em] uppercase text-zinc-500">Email</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail size={13} className="text-zinc-600" />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-zinc-900 border border-zinc-800 focus:border-zinc-600 text-zinc-100 text-sm rounded pl-9 pr-3 py-2.5 focus:outline-none transition-colors placeholder:text-zinc-700"
                  placeholder="name@studio.com"
                  required
                />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="text-[10px] font-bold tracking-[0.18em] uppercase text-zinc-500">Password</label>
                <button
                  type="button"
                  onClick={() => { setForgotMode(true); setLocalError(''); setResetSent(false); }}
                  className="text-[10px] text-zinc-600 hover:text-zinc-400 transition-colors"
                >
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock size={13} className="text-zinc-600" />
                </div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-zinc-900 border border-zinc-800 focus:border-zinc-600 text-zinc-100 text-sm rounded pl-9 pr-3 py-2.5 focus:outline-none transition-colors placeholder:text-zinc-700"
                  placeholder="••••••••"
                  required
                />
              </div>
            </div>

            {/* Sign In Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-white hover:bg-zinc-100 text-black font-semibold rounded py-2.5 transition-colors flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed mt-1 text-sm"
            >
              {loading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <>Sign In <ArrowRight size={14} /></>
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-zinc-800"></div></div>
            <div className="relative flex justify-center">
              <span className="bg-zinc-950 px-3 text-[10px] text-zinc-600 uppercase tracking-widest">or</span>
            </div>
          </div>

          {/* Google */}
          <button
            onClick={handleGoogleLogin}
            disabled={loading}
            className="w-full bg-zinc-900 border border-zinc-800 hover:border-zinc-600 hover:bg-zinc-800/50 text-zinc-400 hover:text-zinc-200 font-medium rounded py-2.5 transition-all flex items-center justify-center gap-2.5 text-sm"
          >
             <svg className="w-3.5 h-3.5" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.26.81-.58z" fill="#FBBC05" />
              <path d="M12 4.63c1.69 0 3.26.58 4.54 1.8l3.4-3.4C17.45.97 14.97 0 12 0 7.7 0 3.99 2.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
            </svg>
            Continue with Google
          </button>
        </div>

        {/* Footer */}
        <p className="text-center mt-6 text-[11px] text-zinc-600">
          No account?{' '}
          <button 
            onClick={() => onNavigate('signup')} 
            className="text-zinc-400 hover:text-white font-medium transition-colors"
          >
            Start free trial
          </button>
        </p>

      {/* Forgot Password Overlay */}
      {forgotMode && (
        <div className="fixed inset-0 flex items-center justify-center z-[100] px-4">
          <div className="absolute inset-0 bg-black/85 backdrop-blur-md" onClick={() => { setForgotMode(false); setResetSent(false); setLocalError(''); }} />
          <div className="relative z-10 w-full max-w-sm">
            <div className="h-px w-full bg-zinc-700/60 rounded-t-xl" />
            <div className="bg-zinc-950 border border-zinc-800/80 rounded-xl shadow-2xl shadow-black/60 p-7 flex flex-col gap-5">
              <div>
                <span className="text-[9px] font-bold tracking-[0.25em] uppercase text-zinc-600">Account Recovery</span>
                <h3 className="text-base font-semibold text-white mt-1 tracking-tight">Reset your password</h3>
                <p className="text-[11px] text-zinc-500 mt-1 leading-relaxed">Enter your account email and we'll send a reset link.</p>
              </div>
              {resetSent ? (
                <div className="flex items-start gap-2.5 text-[11px] text-emerald-400 bg-emerald-950/30 border border-emerald-900/30 rounded p-3">
                  <Check size={13} className="mt-0.5 shrink-0" />
                  <span>Reset link sent — check your inbox.</span>
                </div>
              ) : (
                <form onSubmit={handlePasswordReset} className="space-y-4">
                  {localError && (
                    <div className="flex items-start gap-2.5 text-[11px] text-red-400 bg-red-950/30 border border-red-900/30 rounded p-3">
                      <AlertCircle size={13} className="mt-0.5 shrink-0" />
                      <span>{localError}</span>
                    </div>
                  )}
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold tracking-[0.18em] uppercase text-zinc-500">Email</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail size={13} className="text-zinc-600" />
                      </div>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full bg-zinc-900 border border-zinc-800 focus:border-zinc-600 text-zinc-100 text-sm rounded pl-9 pr-3 py-2.5 focus:outline-none transition-colors placeholder:text-zinc-700"
                        placeholder="name@studio.com"
                        required
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-white hover:bg-zinc-100 text-black font-semibold rounded py-2.5 transition-colors flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed text-sm"
                  >
                    {loading ? <Loader2 size={16} className="animate-spin" /> : <>Send Reset Link <ArrowRight size={14} /></>}
                  </button>
                </form>
              )}
              <div className="flex justify-end pt-1 border-t border-zinc-800/50">
                <button
                  onClick={() => { setForgotMode(false); setResetSent(false); setLocalError(''); }}
                  className="px-5 py-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-zinc-300 hover:text-white text-sm font-medium rounded-lg transition-all tracking-wide"
                >
                  {resetSent ? 'Back to Sign In' : 'Cancel'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

        <div className="mt-10 text-center">
            <p className="text-[9px] text-zinc-800 uppercase tracking-widest">
                BLACKBOX<span className="text-zinc-700">cr</span> · Secure Access
            </p>
        </div>
      </div>
    </div>
  );
};

// Stripe Replinish Modal Component ---

const ReplenishModal = ({ isOpen, onClose, onChoose, isProcessing }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-black/85 backdrop-blur-md flex items-center justify-center z-50 p-4">
            <div className="bg-zinc-950 rounded-lg border border-zinc-800 max-w-sm w-full p-7">
                <div className="mb-1">
                    <span className="text-[9px] font-bold tracking-[0.25em] uppercase text-zinc-600">Render Limit</span>
                </div>
                <h3 className="text-base font-semibold text-white mb-1 tracking-tight">Export Limit Reached</h3>
                <p className="text-[11px] text-zinc-500 mb-6">Choose an option to continue exporting your project.</p>
                
                <div className="space-y-2.5">
                    <button
                        onClick={() => onChoose('replenish')}
                        disabled={isProcessing}
                        className="w-full text-left bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 hover:border-zinc-700 p-4 rounded-lg transition-all"
                    >
                        <div className="flex justify-between items-start">
                            <p className="text-sm font-semibold text-white">Replenish Renders</p>
                            <span className="text-xs font-bold text-zinc-300">$49</span>
                        </div>
                        <p className="text-[11px] text-zinc-500 mt-1">+15 exports · 90-day extension</p>
                    </button>
                    <button
                        onClick={() => onChoose('upgrade')}
                        disabled={isProcessing}
                        className="w-full text-left bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 hover:border-zinc-700 p-4 rounded-lg transition-all"
                    >
                        <div className="flex justify-between items-start">
                            <p className="text-sm font-semibold text-white">Upgrade to Pro</p>
                            <span className="text-xs font-bold text-zinc-300">$200</span>
                        </div>
                        <p className="text-[11px] text-zinc-500 mt-1">4K · ProRes · 25 export reset</p>
                    </button>
                </div>

                <button onClick={onClose} className="mt-6 w-full text-center text-[11px] text-zinc-600 hover:text-zinc-400 transition-colors tracking-wide">Cancel</button>
            </div>
        </div>
    );
};

// --- Reusable ExportNotices Component (Now a Modal) ---
// ============================================================================
// RENDER QUEUE — filtering & sorting
// ============================================================================
// Both export-queue lists (the Production Portal modal and the admin "All
// Export Notices" section) used to render every job the user had ever
// submitted in one long date-sorted list, so anyone running more than one
// production had to scroll past unrelated projects to find the render they
// were waiting on. These two helpers add a project filter plus a sort
// selector, and are shared by both lists so they can't drift apart.
//
// They also fix a quieter bug: the old code called `notices.sort(...)` during
// render, which sorts the state array in place. Everything here copies first.
const NOTICE_SORT_OPTIONS = [
    { value: 'newest', label: 'Newest first' },
    { value: 'oldest', label: 'Oldest first' },
    { value: 'project', label: 'Project A–Z' },
    { value: 'status', label: 'Status' },
];

// Sort-by-status puts what's still cooking (and what needs attention) on top.
const NOTICE_STATUS_RANK = { processing: 0, ready: 1, error: 2, failed: 2 };

const ALL_PROJECTS = '__all__';

const noticeTime = (notice) => notice?.timestamp?.toDate?.()?.getTime?.() ?? 0;
const noticeProject = (notice) => notice?.projectName || 'Untitled Production';

const useNoticeQueue = (notices) => {
    const [projectFilter, setProjectFilter] = useState(ALL_PROJECTS);
    const [sortMode, setSortMode] = useState('newest');

    const projectOptions = useMemo(() => {
        const counts = new Map();
        notices.forEach(n => {
            const name = noticeProject(n);
            counts.set(name, (counts.get(name) || 0) + 1);
        });
        return [
            { value: ALL_PROJECTS, label: `All projects (${notices.length})` },
            ...Array.from(counts.entries())
                .sort((a, b) => a[0].localeCompare(b[0]))
                .map(([name, count]) => ({ value: name, label: `${name} (${count})` })),
        ];
    }, [notices]);

    // If the last job for the filtered project gets dismissed (or the filter
    // was set before a live Firestore update removed it), fall back to All
    // rather than leaving the user staring at an empty list.
    useEffect(() => {
        if (projectFilter !== ALL_PROJECTS && !notices.some(n => noticeProject(n) === projectFilter)) {
            setProjectFilter(ALL_PROJECTS);
        }
    }, [notices, projectFilter]);

    const visibleNotices = useMemo(() => {
        const filtered = projectFilter === ALL_PROJECTS
            ? notices
            : notices.filter(n => noticeProject(n) === projectFilter);

        // Copy before sorting — `notices` is React state.
        return [...filtered].sort((a, b) => {
            switch (sortMode) {
                case 'oldest':
                    return noticeTime(a) - noticeTime(b);
                case 'project':
                    return noticeProject(a).localeCompare(noticeProject(b)) || noticeTime(b) - noticeTime(a);
                case 'status': {
                    const rank = (s) => NOTICE_STATUS_RANK[s] ?? 3;
                    return rank(a.status) - rank(b.status) || noticeTime(b) - noticeTime(a);
                }
                case 'newest':
                default:
                    return noticeTime(b) - noticeTime(a);
            }
        });
    }, [notices, projectFilter, sortMode]);

    return { projectFilter, setProjectFilter, sortMode, setSortMode, projectOptions, visibleNotices };
};

const NoticeQueueControls = ({
    projectFilter, setProjectFilter, sortMode, setSortMode,
    projectOptions, visibleCount, totalCount, className = ''
}) => {
    // Only one production in the queue? The filter would be a no-op, so it
    // stays out of the way and just the sort selector shows.
    const hasMultipleProjects = projectOptions.length > 2;
    const selectClass = "bg-zinc-900 border border-zinc-800 text-zinc-200 text-[11px] font-bold uppercase tracking-widest rounded-lg px-3 py-2 outline-none focus:border-yellow-brand/50 hover:border-zinc-700 transition-colors max-w-[240px] truncate";

    return (
        <div className={`flex flex-wrap items-center gap-2 ${className}`}>
            {hasMultipleProjects && (
                <label className="flex items-center gap-2">
                    <span className="text-[9px] font-black uppercase tracking-[0.15em] text-zinc-600">Project</span>
                    <select
                        value={projectFilter}
                        onChange={(e) => setProjectFilter(e.target.value)}
                        className={selectClass}
                    >
                        {projectOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                </label>
            )}
            <label className="flex items-center gap-2">
                <span className="text-[9px] font-black uppercase tracking-[0.15em] text-zinc-600">Sort</span>
                <select
                    value={sortMode}
                    onChange={(e) => setSortMode(e.target.value)}
                    className={selectClass}
                >
                    {NOTICE_SORT_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
            </label>
            {projectFilter !== ALL_PROJECTS && (
                <button
                    onClick={() => setProjectFilter(ALL_PROJECTS)}
                    className="text-[9px] font-black uppercase tracking-[0.15em] text-zinc-500 hover:text-yellow-brand transition-colors px-2 py-1.5 flex items-center gap-1.5"
                    title="Clear the project filter"
                >
                    <X size={11} /> Showing {visibleCount} of {totalCount}
                </button>
            )}
        </div>
    );
};

const ExportNoticesModal = ({ isOpen, onClose, db, userId, showMessageBox, functions }) => {
    const [notices, setNotices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedNoticeIds, setSelectedNoticeIds] = useState(new Set());
    const [isRefreshingLink, setIsRefreshingLink] = useState(null);
    const [isCopyingLink, setIsCopyingLink] = useState(null);
    const [isCreatingReview, setIsCreatingReview] = useState(null);
    const [isRevokingLink, setIsRevokingLink] = useState(null);
    const EXPORT_TIMEOUT_MS = 180 * 60 * 1000;
    const codecDisplayNames = { 'h264-mp4': 'H.264 MP4', 'prores_ks': 'Apple ProRes 422' };

    // Project filter + sort for the queue below (see useNoticeQueue).
    const {
        projectFilter, setProjectFilter, sortMode, setSortMode, projectOptions, visibleNotices
    } = useNoticeQueue(notices);

    const handleCreateReviewLink = async (notice) => {
        if (!functions) {
            showMessageBox("Error", "Connection error, please refresh.", "error");
            return;
        }
        setIsCreatingReview(notice.id);
        try {
            const createReviewLink = httpsCallable(functions, 'createReviewLink');
            const { data } = await createReviewLink({ jobId: notice.id, expiresInDays: 30 });
            if (data.url) {
                await navigator.clipboard.writeText(data.url);
                showMessageBox(
                    "Review Link Ready",
                    `A no-login screening page has been copied to your clipboard. It streams the credits for review — never the raw master — and stays live for 30 days.`,
                    "success",
                    false
                );
            } else throw new Error("Invalid response from server.");
        } catch (error) {
            showMessageBox("Couldn't Create Link", error.message, "error");
        } finally {
            setIsCreatingReview(null);
        }
    };


    // Bundles multiple finished exports (e.g. a Scroll render + a Cards render
    // for the same project) into ONE review link, instead of the client
    // getting separate links per file. Reuses the existing multi-select
    // checkboxes on the notice list — no new selection UI needed.
    const handleCreateReviewLinkFromSelection = async () => {
        if (!functions) {
            showMessageBox("Error", "Connection error, please refresh.", "error");
            return;
        }
        const selected = notices.filter(n => selectedNoticeIds.has(n.id));
        if (selected.length === 0) return;
        const notReady = selected.filter(n => n.status !== 'ready');
        if (notReady.length > 0) {
            showMessageBox(
                "Not Ready Yet",
                `${notReady.length} of the selected export${notReady.length > 1 ? 's are' : ' is'} still processing or didn't finish. Only completed exports can go into a review link — deselect ${notReady.length > 1 ? 'them' : 'it'} and try again.`,
                "error"
            );
            return;
        }
        setIsCreatingReview('__bulk__');
        try {
            const createReviewLink = httpsCallable(functions, 'createReviewLink');
            // jobIds (plural): the client picks which finished exports belong in
            // one review page. Backend orders/labels each deliverable — see the
            // getReviewData contract note in ReviewPage.js.
            const { data } = await createReviewLink({ jobIds: selected.map(n => n.id), expiresInDays: 30 });
            if (data.url) {
                await navigator.clipboard.writeText(data.url);
                showMessageBox(
                    "Review Link Ready",
                    `A no-login screening page for ${selected.length} file${selected.length > 1 ? 's' : ''} has been copied to your clipboard. It streams the credits for review — never the raw master — and stays live for 30 days.`,
                    "success",
                    false
                );
                setSelectedNoticeIds(new Set());
            } else throw new Error("Invalid response from server.");
        } catch (error) {
            showMessageBox("Couldn't Create Link", error.message, "error");
        } finally {
            setIsCreatingReview(null);
        }
    };

    const handleRevokeReviewLink = (notice) => {
        if (!functions || !notice.reviewToken) return;
        showMessageBox(
            "Revoke Review Link?",
            "The client's screening page will stop working immediately. This can't be undone — you'd need to create a new link to share again.",
            "warning",
            false,
            async () => {
                setIsRevokingLink(notice.id);
                try {
                    const revokeReviewLink = httpsCallable(functions, 'revokeReviewLink');
                    await revokeReviewLink({ token: notice.reviewToken });
                    await updateDoc(doc(db, `artifacts/cuethecredits-69b39/users/${userId}/exportJobs/${notice.id}`), {
                        reviewToken: null
                    }).catch(() => {});
                    showMessageBox("Link Revoked", "The review link has been shut off.", "success", true);
                } catch (error) {
                    showMessageBox("Couldn't Revoke Link", error.message, "error");
                } finally {
                    setIsRevokingLink(null);
                }
            }
        );
    };

    const handleCopyLink = async (notice) => {
        if (!functions || !notice.storagePath) {
            showMessageBox("Error", "Cannot generate link: Missing connection or file path.", "error");
            return;
        }
        setIsCopyingLink(notice.id);
        try {
            const getRefreshedUrl = httpsCallable(functions, 'getRefreshedDownloadUrl');
            const { data } = await getRefreshedUrl({ jobId: notice.id, storagePath: notice.storagePath });
            if (data.url) {
                await navigator.clipboard.writeText(data.url);
                showMessageBox("Link Copied!", "The secure download link has been copied to your clipboard.", "success", 3000);
            } else throw new Error("Invalid response from server.");
        } catch (error) {
            showMessageBox("Copy Failed", error.message, "error");
        } finally {
            setIsCopyingLink(null);
        }
    };

    const handleDownloadClick = async (notice) => {
        if (!functions) {
            showMessageBox("Error", "Connection error, please refresh.", "error");
            return;
        }
        if (!notice.storagePath) {
            showMessageBox("Error", "Missing file path.", "error");
            return;
        }
        setIsRefreshingLink(notice.id);
        try {
            const getRefreshedUrl = httpsCallable(functions, 'getRefreshedDownloadUrl');
            const { data } = await getRefreshedUrl({ jobId: notice.id, storagePath: notice.storagePath });
            if (data.url) window.open(data.url, '_blank');
            else throw new Error("Invalid response.");
        } catch (error) {
            showMessageBox("Download Failed", error.message, "error");
        } finally {
            setIsRefreshingLink(null);
        }
    };

    // --- NEW: BULK DOWNLOAD LOGIC ---
    const downloadAllCompleted = async () => {
        const readyNotices = notices.filter(n => n.status === 'ready');
        
        if (readyNotices.length === 0) {
            showMessageBox("Download All", "No completed masters found to download.", "info", true);
            return;
        }

        showMessageBox(
            "Batch Download", 
            `Preparing to download ${readyNotices.length} master files. Your browser may ask for permission to download multiple files.`,
            "info", 
            false,
            async () => {
                for (let i = 0; i < readyNotices.length; i++) {
                    const notice = readyNotices[i];
                    // Staggering prevent browser-level request clashing
                    setTimeout(() => {
                        handleDownloadClick(notice);
                    }, i * 800); 
                }
            }
        );
    };

    useEffect(() => {
        if (!db || !userId || !isOpen) return;
        setLoading(true);
        const currentAppId = 'cuethecredits-69b39';
        const exportsCollectionRef = collection(db, `artifacts/${currentAppId}/users/${userId}/exportJobs`);
        
        const unsubscribe = onSnapshot(exportsCollectionRef, (snapshot) => {
            const updatedNotices = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setNotices(updatedNotices);
            setLoading(false);
            setSelectedNoticeIds(prev => new Set([...prev].filter(id => updatedNotices.some(n => n.id === id))));
        });
        return () => unsubscribe();
    }, [db, userId, isOpen]);

    const clearAllFinishedNotices = () => {
        const finishedNotices = notices.filter(n => {
            const isProcessing = n.status === 'processing';
            const isTimedOut = isProcessing && (Date.now() - (n.timestamp?.toDate?.().getTime() || Date.now()) > EXPORT_TIMEOUT_MS);
            return n.status === 'ready' || n.status === 'failed' || isTimedOut;
        });

        if (finishedNotices.length === 0) {
            showMessageBox("Queue Clear", "No finished notices to dismiss.", "info", true);
            return;
        }

        showMessageBox("Clear History", `Dismiss all ${finishedNotices.length} completed or failed notices? Active renders will be preserved.`, "warning", false,
            async () => {
                setLoading(true);
                try {
                    const currentAppId = 'cuethecredits-69b39';
                    const batchPromises = finishedNotices.map(n => 
                        deleteDoc(doc(db, `artifacts/${currentAppId}/users/${userId}/exportJobs/${n.id}`))
                    );
                    await Promise.all(batchPromises);
                    setSelectedNoticeIds(new Set());
                } catch (error) {
                    showMessageBox("Error", "Failed to clear history.", "error");
                } finally {
                    setLoading(false);
                }
            }
        );
    };

    const deleteSelectedNotices = () => {
        if (selectedNoticeIds.size === 0) return;
        showMessageBox("Confirm Deletion", `Dismiss ${selectedNoticeIds.size} selected notices?`, "warning", false,
            async () => {
                setLoading(true);
                try {
                    const currentAppId = 'cuethecredits-69b39';
                    await Promise.all(Array.from(selectedNoticeIds).map(id =>
                        deleteDoc(doc(db, `artifacts/${currentAppId}/users/${userId}/exportJobs/${id}`))
                    ));
                    setSelectedNoticeIds(new Set());
                } catch (error) {
                    showMessageBox("Error", "Failed to delete selected.", "error");
                } finally {
                    setLoading(false);
                }
            }
        );
    };

    const toggleNoticeSelection = (id) => {
        setSelectedNoticeIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const deleteNotice = (notice) => {
        const isProcessing = notice.status === 'processing';
        const now = Date.now();
        const isTimedOut = isProcessing && (now - (notice.timestamp?.toDate?.().getTime() || now) > EXPORT_TIMEOUT_MS);
        
        const message = (isProcessing && !isTimedOut)
            ? "⚠️ ACTIVE RENDER: This export is currently rendering! Deleting this notice will hide it from your queue. Are you absolutely sure?"
            : "Are you sure you want to dismiss this export notice?";

        showMessageBox(isProcessing && !isTimedOut ? "Cancel Render?" : "Confirm Deletion", message, "warning", false,
            async () => {
                try {
                    await deleteDoc(doc(db, `artifacts/cuethecredits-69b39/users/${userId}/exportJobs/${notice.id}`));
                    setSelectedNoticeIds(prev => {
                        const next = new Set(prev);
                        next.delete(notice.id);
                        return next;
                    });
                } catch (error) {
                    showMessageBox("Error", "Failed to dismiss notice.", "error");
                }
            }
        );
    };

    if (!isOpen) return null;

    const readyCount = notices.filter(n => n.status === 'ready').length;

    return (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-[100] p-4 font-poppins">
            <div className="bg-zinc-900 rounded-2xl shadow-2xl max-w-2xl w-full text-gray-100 border border-zinc-800 relative flex flex-col max-h-[90vh] overflow-hidden">
                
                {/* Header with Batch Actions */}
                <div className="p-6 border-b border-zinc-800/50 flex items-center justify-between bg-zinc-900/50">
                    <div>
                        <h3 className="text-xl font-black text-white flex items-center gap-3 uppercase tracking-tight">
                            <CloudDownload size={24} className="text-yellow-brand" /> 
                            Production Portal
                        </h3>
                    </div>
                    <div className="flex items-center gap-4">
                        {readyCount > 1 && (
                            <button 
                                onClick={downloadAllCompleted}
                                className="text-[10px] font-black tracking-widest uppercase text-yellow-brand hover:text-yellow-300 transition-colors px-3 py-1.5 border border-yellow-brand/20 rounded-lg bg-yellow-brand/5 shadow-inner flex items-center gap-2"
                            >
                                <Download size={14} /> Download All ({readyCount})
                            </button>
                        )}
                        {notices.length > 0 && (
                            <button 
                                onClick={clearAllFinishedNotices}
                                className="text-[10px] font-black tracking-widest uppercase text-zinc-500 hover:text-red-400 transition-colors px-3 py-1.5 border border-zinc-800 rounded-lg hover:border-red-400/30"
                            >
                                Clear History
                            </button>
                        )}
                        <button onClick={onClose} className="p-2 rounded-full hover:bg-zinc-800 text-gray-400 transition-colors"><X size={20} /></button>
                    </div>
                </div>
                
                <div className="p-6 overflow-y-auto flex-grow custom-scrollbar bg-black/20">
                    {notices.length > 1 && (
                        <NoticeQueueControls
                            projectFilter={projectFilter}
                            setProjectFilter={setProjectFilter}
                            sortMode={sortMode}
                            setSortMode={setSortMode}
                            projectOptions={projectOptions}
                            visibleCount={visibleNotices.length}
                            totalCount={notices.length}
                            className="mb-6 pb-5 border-b border-zinc-800/50"
                        />
                    )}

                    {selectedNoticeIds.size > 0 && (
                        <div className="flex items-center gap-3 mb-6">
                            <button
                                onClick={handleCreateReviewLinkFromSelection}
                                disabled={isCreatingReview === '__bulk__'}
                                className="flex-1 px-4 py-3 bg-yellow-brand text-black border border-yellow-brand rounded-xl hover:bg-yellow-300 transition-all flex items-center justify-center text-xs font-black tracking-widest uppercase disabled:opacity-50"
                                title="Bundle the selected exports (e.g. Scroll + Cards) into one client review link"
                            >
                                {isCreatingReview === '__bulk__' ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Eye size={16} className="mr-2" />}
                                CREATE REVIEW LINK ({selectedNoticeIds.size})
                            </button>
                            <button onClick={deleteSelectedNotices} className="px-4 py-3 bg-red-900/20 text-red-400 border border-red-900/30 rounded-xl hover:bg-red-800 hover:text-white transition-all flex items-center justify-center text-xs font-black tracking-widest uppercase">
                                <Trash2 size={16} className="mr-2"/> DISMISS ({selectedNoticeIds.size})
                            </button>
                        </div>
                    )}

                    <div className="space-y-4">
                        {loading && notices.length === 0 && <p className="text-zinc-500 italic text-center py-10 animate-pulse font-thin tracking-widest uppercase text-xs">Accessing Production Data...</p>}
                        {!loading && notices.length === 0 && (
                            <div className="text-center py-20 border-2 border-dashed border-zinc-800 rounded-3xl opacity-30">
                                <CloudDownload size={48} className="mx-auto mb-4" />
                                <p className="uppercase text-[10px] tracking-widest font-black">Archive Empty</p>
                            </div>
                        )}

                        {visibleNotices.map(notice => {
                            const exportTime = notice.timestamp?.toDate ? new Date(notice.timestamp.toDate()).toLocaleString() : 'N/A';
                            const expirationDate = notice.expirationDate?.toDate ? new Date(notice.expirationDate.toDate()).toLocaleDateString() : 'N/A';
                            const isExpired = notice.expirationDate?.toDate ? new Date() > notice.expirationDate.toDate() : false;
                            const now = Date.now();
                            const isTimedOut = notice.status === 'processing' && (now - (notice.timestamp?.toDate?.().getTime() || now) > EXPORT_TIMEOUT_MS);
                            const finalStatus = isTimedOut ? 'TIMEOUT' : notice.status?.toUpperCase() ?? 'N/A';
                            const statusColor = finalStatus === 'PROCESSING' ? 'text-yellow-brand' : finalStatus === 'READY' ? 'text-green-400' : 'text-red-500';

                            return (
                                <div key={notice.id} className={`bg-zinc-900/60 p-5 rounded-2xl border transition-all duration-300 ${isExpired || isTimedOut ? 'border-red-900/30 grayscale opacity-50' : 'border-zinc-800 hover:border-zinc-700 shadow-xl'} ${selectedNoticeIds.has(notice.id) ? 'ring-2 ring-yellow-brand' : ''}`}>
                                    <div className="flex items-start gap-4">
                                        <input type="checkbox" checked={selectedNoticeIds.has(notice.id)} onChange={() => toggleNoticeSelection(notice.id)} className="mt-1.5 h-5 w-5 text-yellow-brand rounded border-zinc-700 bg-black focus:ring-yellow-brand cursor-pointer" />
                                        <div className="flex-grow">
                                            <div className="flex justify-between items-start mb-3">
                                                <div className="space-y-1">
                                                    <h3 className="text-sm font-black text-white tracking-tight uppercase truncate max-w-[350px]">"{notice.projectName || 'Untitled Production'}"</h3>
                                                    <div className="flex items-center gap-2 text-[10px] text-zinc-500 font-bold uppercase tracking-widest">
                                                        {notice.exportType && notice.exportType !== 'full' && (
                                                            <>
                                                                <span className="text-yellow-brand">{notice.exportType}</span>
                                                                <span className="w-1 h-1 bg-zinc-700 rounded-full"></span>
                                                            </>
                                                        )}
                                                        <span>{notice.quality}</span>
                                                        <span className="w-1 h-1 bg-zinc-700 rounded-full"></span>
                                                        <span className="text-zinc-300">{notice.projectFps || '23.98'} FPS</span>
                                                        <span className="w-1 h-1 bg-zinc-700 rounded-full"></span>
                                                        <span>{notice.resolution}</span>
                                                    </div>
                                                </div>
                                                <button onClick={() => deleteNotice(notice)} className="p-1 text-zinc-600 hover:text-red-500 transition-colors">
                                                    <X size={16} />
                                                </button>
                                            </div>

                                            {notice.status === 'processing' && !isTimedOut ? (
                                                <div className="my-5 space-y-3 bg-black/40 p-4 rounded-xl border border-zinc-800/50 shadow-inner">
                                                    <div className="flex justify-between items-end px-0.5 text-[10px] font-black">
                                                        <div className="font-mono text-zinc-500 tracking-tighter">
                                                            <span className="text-white">{notice.currentFrame?.toLocaleString() || 0}</span>
                                                            <span className="mx-1 text-zinc-600">/</span>
                                                            <span>{notice.totalFrames?.toLocaleString() || '---'} FRAMES</span>
                                                        </div>
                                                        <div className="text-yellow-brand italic">{notice.progress || 0}%</div>
                                                    </div>
                                                    <div className="relative w-full bg-zinc-800 h-1.5 rounded-full overflow-hidden">
                                                        <div className="absolute top-0 left-0 bg-yellow-brand h-full transition-all duration-700 ease-out shadow-[0_0_12px_rgba(253,224,71,0.6)]" style={{ width: `${notice.progress || 0}%` }} />
                                                    </div>
                                                    <div className="flex justify-between items-center pt-1 px-0.5 text-[9px] text-zinc-500 font-black uppercase tracking-[0.1em]">
                                                        <div>Velocity: <span className="text-zinc-300">{(notice.renderSpeed || notice.fps || 0)} FPS</span></div>
                                                        {(notice.renderSpeed || notice.fps) > 0 && notice.totalFrames > 0 && (
                                                            <div>Est: <span className="text-white">{Math.ceil(((notice.totalFrames || 0) - (notice.currentFrame || 0)) / (notice.renderSpeed || notice.fps || 1) / 60)} MIN REMAINING</span></div>
                                                        )}
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-2 mb-4 mt-2">
                                                    <div className={`w-2 h-2 rounded-full ${statusColor.replace('text', 'bg')} ${notice.status === 'processing' ? 'animate-pulse' : ''}`} />
                                                    <p className="text-[10px] text-zinc-400 font-black uppercase tracking-widest leading-none">Status: <span className={statusColor}>{finalStatus}</span></p>
                                                </div>
                                            )}

                                            <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-zinc-800/30">
                                                <div className="text-[10px] text-zinc-500 font-medium">Codec: <span className="text-zinc-300">{codecDisplayNames[notice.codecChoice] || notice.codecChoice}</span><br/>Requested: <span className="text-zinc-300">{exportTime}</span></div>
                                                {notice.status === 'ready' && (
                                                    <div className="text-right text-[10px] text-zinc-500 uppercase tracking-widest font-black">Retention<br/><span className={isExpired ? 'text-red-500' : 'text-zinc-400'}>{isExpired ? 'EXPIRED' : `EXPIRES ${expirationDate}`}</span></div>
                                                )}
                                            </div>
                                            
                                            {notice.status === 'ready' && !isExpired && (
                                                <div className="mt-6 flex gap-2 w-full">
                                                    <button onClick={() => handleDownloadClick(notice)} disabled={isRefreshingLink === notice.id || isCopyingLink === notice.id} className="flex-grow inline-flex items-center justify-center px-6 py-4 bg-yellow-brand text-black font-black text-xs tracking-[0.2em] uppercase rounded-xl hover:bg-yellow-300 transition-all transform active:scale-95 shadow-[0_8px_30px_rgba(253,224,71,0.2)] disabled:bg-zinc-800 disabled:text-zinc-600 disabled:shadow-none">
                                                        <Download size={20} className="mr-3"/> {isRefreshingLink === notice.id ? 'AUTHENTICATING...' : 'DOWNLOAD MASTER FILE'}
                                                    </button>
                                                    <button onClick={() => handleCopyLink(notice)} disabled={isCopyingLink === notice.id || isRefreshingLink === notice.id} className="inline-flex items-center justify-center px-5 py-4 bg-zinc-800 text-yellow-brand border border-zinc-700 font-black text-xs uppercase rounded-xl hover:bg-zinc-700 transition-all transform active:scale-95 disabled:opacity-50 shadow-inner" title="Copy Share Link">
                                                        {isCopyingLink === notice.id ? <Loader2 size={20} className="animate-spin" /> : <Copy size={20} />}
                                                    </button>
                                                    <button onClick={() => handleCreateReviewLink(notice)} disabled={isCreatingReview === notice.id} className="inline-flex items-center justify-center px-5 py-4 bg-zinc-800 text-zinc-200 border border-zinc-700 font-black text-xs uppercase rounded-xl hover:bg-zinc-700 transition-all transform active:scale-95 disabled:opacity-50 shadow-inner" title="Create a no-login client review link">
                                                        {isCreatingReview === notice.id ? <Loader2 size={20} className="animate-spin" /> : <Eye size={20} />}
                                                    </button>
                                                    {notice.reviewToken && (
                                                        <button onClick={() => handleRevokeReviewLink(notice)} disabled={isRevokingLink === notice.id} className="inline-flex items-center justify-center px-5 py-4 bg-zinc-800 text-red-400 border border-zinc-700 font-black text-xs uppercase rounded-xl hover:bg-red-950/40 hover:border-red-900/40 transition-all transform active:scale-95 disabled:opacity-50 shadow-inner" title="Revoke the client review link">
                                                            {isRevokingLink === notice.id ? <Loader2 size={20} className="animate-spin" /> : <Ban size={20} />}
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="p-6 border-t border-zinc-800 flex justify-end bg-zinc-900/50">
                    <button onClick={onClose} className="bg-zinc-800 text-zinc-300 px-8 py-3 rounded-xl hover:bg-zinc-700 font-bold uppercase text-[10px] tracking-widest transition-all border border-zinc-700 shadow-inner">Close Portal</button>
                </div>
            </div>
        </div>
    );
};

// --- Reusable ProjectExportNotices Component (For Projects Page) ---
const ProjectExportNotices = ({ db, userId, showMessageBox, functions }) => {
    const [notices, setNotices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedNoticeIds, setSelectedNoticeIds] = useState(new Set());
    const [isRefreshingLink, setIsRefreshingLink] = useState(null);
    const [isCopyingLink, setIsCopyingLink] = useState(null);
    const [isCreatingReview, setIsCreatingReview] = useState(null);
    const [isRevokingLink, setIsRevokingLink] = useState(null);
    const EXPORT_TIMEOUT_MS = 180 * 60 * 1000;

    // Project filter + sort for the queue below (see useNoticeQueue).
    const {
        projectFilter, setProjectFilter, sortMode, setSortMode, projectOptions, visibleNotices
    } = useNoticeQueue(notices);

    const handleCopyLink = async (notice) => {
        if (!functions || !notice.storagePath) {
            showMessageBox("Error", "Cannot generate link: Missing connection or file path.", "error");
            return;
        }
        setIsCopyingLink(notice.id);
        try {
            const getRefreshedUrl = httpsCallable(functions, 'getRefreshedDownloadUrl');
            const { data } = await getRefreshedUrl({ jobId: notice.id, storagePath: notice.storagePath });
            if (data.url) {
                await navigator.clipboard.writeText(data.url);
                showMessageBox("Link Copied!", "The secure download link has been copied to your clipboard.", "success", 3000);
            } else throw new Error("Invalid response from server.");
        } catch (error) {
            showMessageBox("Copy Failed", `Could not get a fresh link: ${error.message}`, "error");
        } finally {
            setIsCopyingLink(null);
        }
    };

    const handleCreateReviewLink = async (notice) => {
        if (!functions) {
            showMessageBox("Error", "Connection error, please refresh.", "error");
            return;
        }
        setIsCreatingReview(notice.id);
        try {
            const createReviewLink = httpsCallable(functions, 'createReviewLink');
            const { data } = await createReviewLink({ jobId: notice.id, expiresInDays: 30 });
            if (data.url) {
                await navigator.clipboard.writeText(data.url);
                showMessageBox(
                    "Review Link Ready",
                    `A no-login screening page has been copied to your clipboard. It streams the credits for review — never the raw master — and stays live for 30 days.`,
                    "success",
                    false
                );
            } else throw new Error("Invalid response from server.");
        } catch (error) {
            showMessageBox("Couldn't Create Link", error.message, "error");
        } finally {
            setIsCreatingReview(null);
        }
    };


    // Bundles multiple finished exports (e.g. a Scroll render + a Cards render
    // for the same project) into ONE review link, instead of the client
    // getting separate links per file. Reuses the existing multi-select
    // checkboxes on the notice list — no new selection UI needed.
    const handleCreateReviewLinkFromSelection = async () => {
        if (!functions) {
            showMessageBox("Error", "Connection error, please refresh.", "error");
            return;
        }
        const selected = notices.filter(n => selectedNoticeIds.has(n.id));
        if (selected.length === 0) return;
        const notReady = selected.filter(n => n.status !== 'ready');
        if (notReady.length > 0) {
            showMessageBox(
                "Not Ready Yet",
                `${notReady.length} of the selected export${notReady.length > 1 ? 's are' : ' is'} still processing or didn't finish. Only completed exports can go into a review link — deselect ${notReady.length > 1 ? 'them' : 'it'} and try again.`,
                "error"
            );
            return;
        }
        setIsCreatingReview('__bulk__');
        try {
            const createReviewLink = httpsCallable(functions, 'createReviewLink');
            // jobIds (plural): the client picks which finished exports belong in
            // one review page. Backend orders/labels each deliverable — see the
            // getReviewData contract note in ReviewPage.js.
            const { data } = await createReviewLink({ jobIds: selected.map(n => n.id), expiresInDays: 30 });
            if (data.url) {
                await navigator.clipboard.writeText(data.url);
                showMessageBox(
                    "Review Link Ready",
                    `A no-login screening page for ${selected.length} file${selected.length > 1 ? 's' : ''} has been copied to your clipboard. It streams the credits for review — never the raw master — and stays live for 30 days.`,
                    "success",
                    false
                );
                setSelectedNoticeIds(new Set());
            } else throw new Error("Invalid response from server.");
        } catch (error) {
            showMessageBox("Couldn't Create Link", error.message, "error");
        } finally {
            setIsCreatingReview(null);
        }
    };

    const handleRevokeReviewLink = (notice) => {
        if (!functions || !notice.reviewToken) return;
        showMessageBox(
            "Revoke Review Link?",
            "The client's screening page will stop working immediately. This can't be undone — you'd need to create a new link to share again.",
            "warning",
            false,
            async () => {
                setIsRevokingLink(notice.id);
                try {
                    const revokeReviewLink = httpsCallable(functions, 'revokeReviewLink');
                    await revokeReviewLink({ token: notice.reviewToken });
                    await updateDoc(doc(db, `artifacts/cuethecredits-69b39/users/${userId}/exportJobs/${notice.id}`), {
                        reviewToken: null
                    }).catch(() => {});
                    showMessageBox("Link Revoked", "The review link has been shut off.", "success", true);
                } catch (error) {
                    showMessageBox("Couldn't Revoke Link", error.message, "error");
                } finally {
                    setIsRevokingLink(null);
                }
            }
        );
    };

    const handleDownloadClick = async (notice) => {
        if (!functions) {
            showMessageBox("Error", "Connection error, please refresh.", "error");
            return;
        }
        if (!notice.storagePath) {
            showMessageBox("Error", "Cannot download: This export job is missing its file path.", "error");
            return;
        }

        setIsRefreshingLink(notice.id);
        try {
            const getRefreshedUrl = httpsCallable(functions, 'getRefreshedDownloadUrl');
            const { data } = await getRefreshedUrl({ 
                jobId: notice.id, 
                storagePath: notice.storagePath 
            });

            if (data.url) {
                window.open(data.url, '_blank');
            } else {
                throw new Error("Invalid response from server.");
            }
        } catch (error) {
            console.error("Error refreshing download link:", error);
            showMessageBox("Download Failed", `Could not get a fresh download link: ${error.message}`, "error");
        } finally {
            setIsRefreshingLink(null);
        }
    };

    useEffect(() => {
        if (!db || !userId) return;
        setLoading(true);
        const currentAppId = 'cuethecredits-69b39';
        const exportsCollectionRef = collection(db, `artifacts/${currentAppId}/users/${userId}/exportJobs`);
        
        const unsubscribe = onSnapshot(exportsCollectionRef, (snapshot) => {
            const updatedNotices = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setNotices(updatedNotices);
            setLoading(false);
            setSelectedNoticeIds(prev => new Set([...prev].filter(id => updatedNotices.some(n => n.id === id))));
        }, (error) => {
            console.error("Error listening to export jobs:", error);
            setLoading(false);
        });
        return () => unsubscribe();
    }, [db, userId]);

    const toggleNoticeSelection = (noticeId) => {
        setSelectedNoticeIds(prev => {
            const newSelected = new Set(prev);
            if (newSelected.has(noticeId)) newSelected.delete(noticeId);
            else newSelected.add(noticeId);
            return newSelected;
        });
    };

    const deleteNotice = (notice) => {
        const isProcessing = notice.status === 'processing';
        const now = Date.now();
        const isTimedOut = isProcessing && (now - (notice.timestamp?.toDate?.().getTime() || now) > EXPORT_TIMEOUT_MS);
        
        const message = (isProcessing && !isTimedOut)
            ? "⚠️ ACTIVE RENDER: This export is currently rendering! Deleting this notice will hide it from your queue. Are you absolutely sure?"
            : "Are you sure you want to dismiss this export notice?";

        showMessageBox(isProcessing && !isTimedOut ? "Cancel Render?" : "Confirm Deletion", message, "warning", false,
            async () => {
                if (!db || !userId) return;
                try {
                    const currentAppId = 'cuethecredits-69b39';
                    await deleteDoc(doc(db, `artifacts/${currentAppId}/users/${userId}/exportJobs/${notice.id}`));
                    showMessageBox("Success", "Export notice dismissed!", "success", true);
                } catch (error) {
                    showMessageBox("Error", `Failed to dismiss notice: ${error.message}`, "error");
                }
            }
        );
    };

    const deleteSelectedNotices = () => {
        if (selectedNoticeIds.size === 0) return;
        showMessageBox("Confirm Deletion", `Dismiss ${selectedNoticeIds.size} selected notices?`, "warning", false,
            async () => {
                if (!db || !userId) return;
                setLoading(true);
                try {
                    const currentAppId = 'cuethecredits-69b39';
                    const deletePromises = Array.from(selectedNoticeIds).map(noticeId =>
                        deleteDoc(doc(db, `artifacts/${currentAppId}/users/${userId}/exportJobs/${noticeId}`))
                    );
                    await Promise.all(deletePromises);
                    showMessageBox("Success", "Notices dismissed!", "success", true);
                    setSelectedNoticeIds(new Set());
                } catch (error) {
                    showMessageBox("Error", "Failed to dismiss selected notices.", "error");
                } finally {
                    setLoading(false);
                }
            }
        );
    };

    return (
        <CollapsibleSection title="All Export Notices" icon={CloudDownload} count={notices.length} defaultOpen={false}>
            <div className="bg-blue-900/30 border-l-4 border-blue-500 text-blue-200 p-4 mb-6 rounded-r-lg text-sm">
                <p className="font-bold mb-1 uppercase tracking-widest text-[10px]">Data Retention Policy</p>
                <p>Master files are available for 120 days. After this period, they are automatically purged from the production servers.</p>
            </div>

            {notices.length > 1 && (
                <NoticeQueueControls
                    projectFilter={projectFilter}
                    setProjectFilter={setProjectFilter}
                    sortMode={sortMode}
                    setSortMode={setSortMode}
                    projectOptions={projectOptions}
                    visibleCount={visibleNotices.length}
                    totalCount={notices.length}
                    className="mb-6 pb-5 border-b border-zinc-800/50"
                />
            )}

            {selectedNoticeIds.size > 0 && (
                <div className="flex items-center gap-3 mb-6">
                    <button
                        onClick={handleCreateReviewLinkFromSelection}
                        disabled={isCreatingReview === '__bulk__'}
                        className="flex-1 px-4 py-3 bg-yellow-brand text-black border border-yellow-brand rounded-xl hover:bg-yellow-300 transition-all flex items-center justify-center text-xs font-black tracking-widest uppercase disabled:opacity-50"
                        title="Bundle the selected exports (e.g. Scroll + Cards) into one client review link"
                    >
                        {isCreatingReview === '__bulk__' ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Eye size={16} className="mr-2" />}
                        CREATE REVIEW LINK ({selectedNoticeIds.size})
                    </button>
                    <button onClick={deleteSelectedNotices} className="px-4 py-3 bg-red-900/40 text-red-200 border border-red-800/50 rounded-xl hover:bg-red-800 hover:text-white transition-all flex items-center justify-center text-xs font-black tracking-widest uppercase">
                        <Trash2 size={16} className="mr-2"/> DISMISS ({selectedNoticeIds.size})
                    </button>
                </div>
            )}

            <div className="space-y-4">
                {loading && <p className="text-zinc-500 italic text-center py-8 animate-pulse">Synchronizing with Modal backend...</p>}
                {!loading && notices.length === 0 && (
                    <div className="text-center py-12 border-2 border-dashed border-zinc-800 rounded-2xl">
                        <CloudDownload size={48} className="mx-auto text-zinc-800 mb-4" />
                        <p className="text-zinc-500 font-medium tracking-tight">No active export history found.</p>
                    </div>
                )}
                {visibleNotices.map(notice => {
                    const exportTime = notice.timestamp?.toDate ? new Date(notice.timestamp.toDate()).toLocaleString() : 'N/A';
                    const expirationDate = notice.expirationDate?.toDate ? new Date(notice.expirationDate.toDate()).toLocaleDateString() : 'N/A';
                    const isExpired = notice.expirationDate?.toDate ? new Date() > notice.expirationDate.toDate() : false;
                    const now = Date.now();
                    const isTimedOut = notice.status === 'processing' && (now - (notice.timestamp?.toDate?.().getTime() || now) > EXPORT_TIMEOUT_MS);
                    
                    const finalStatus = isTimedOut ? 'TIMEOUT' : notice.status?.toUpperCase() ?? 'IDLE';
                    const statusColor = finalStatus === 'PROCESSING' ? 'text-yellow-brand' : finalStatus === 'READY' ? 'text-green-400' : 'text-red-500';

                    return (
                        <div key={notice.id} className={`bg-zinc-900/80 p-5 rounded-2xl border transition-all duration-300 ${isExpired || isTimedOut ? 'border-red-900/50 grayscale' : 'border-zinc-800 hover:border-zinc-700'} ${selectedNoticeIds.has(notice.id) ? 'ring-2 ring-yellow-brand' : ''}`}>
                            <div className="flex items-start gap-4">
                                <input type="checkbox" checked={selectedNoticeIds.has(notice.id)} onChange={() => toggleNoticeSelection(notice.id)} className="mt-1.5 h-5 w-5 text-yellow-brand rounded border-zinc-700 bg-black focus:ring-yellow-brand" />
                                
                                <div className="flex-grow">
                                    <div className="flex justify-between items-start mb-3">
                                        <div className="space-y-1">
                                            <h3 className="text-sm font-black text-white tracking-tight uppercase">
                                                {notice.projectName || 'Untitled Production'}
                                            </h3>
                                            <div className="flex items-center gap-2 text-[10px] text-zinc-500 font-bold uppercase tracking-widest">
                                                {notice.exportType && notice.exportType !== 'full' && (
                                                    <>
                                                        <span className="text-yellow-brand">{notice.exportType}</span>
                                                        <span className="w-1 h-1 bg-zinc-700 rounded-full"></span>
                                                    </>
                                                )}
                                                <span>{notice.quality}</span>
                                                <span className="w-1 h-1 bg-zinc-700 rounded-full"></span>
                                                <span className="text-zinc-400">{notice.projectFps || '23.98'} FPS</span>
                                                <span className="w-1 h-1 bg-zinc-700 rounded-full"></span>
                                                <span>{notice.resolution || '1080p'}</span>
                                            </div>
                                        </div>
                                        <button onClick={() => deleteNotice(notice)} className="p-1.5 bg-zinc-800 text-zinc-500 rounded-full hover:bg-red-600 hover:text-white transition-all shadow-inner">
                                            <X size={14} />
                                        </button>
                                    </div>

                                    {notice.status === 'processing' && !isTimedOut ? (
                                        <div className="my-5 space-y-3 bg-black/40 p-4 rounded-xl border border-zinc-800/50 shadow-inner">
                                            <div className="flex justify-between items-end">
                                                <div className="text-[10px] font-mono text-zinc-500 tracking-tighter">
                                                    <span className="text-white">{notice.currentFrame?.toLocaleString() || 0}</span>
                                                    <span className="mx-1 text-zinc-700">/</span>
                                                    <span>{notice.totalFrames?.toLocaleString() || '---'} FRAMES</span>
                                                </div>
                                                <div className="text-xs font-black text-yellow-brand italic tracking-tight">
                                                    {notice.progress || 0}%
                                                </div>
                                            </div>
                                            
                                            <div className="relative w-full bg-zinc-800 h-1.5 rounded-full overflow-hidden">
                                                <div 
                                                    className="absolute top-0 left-0 bg-yellow-brand h-full transition-all duration-700 ease-out shadow-[0_0_12px_rgba(253,224,71,0.6)]" 
                                                    style={{ width: `${notice.progress || 0}%` }}
                                                />
                                            </div>
                                            
                                            <div className="flex justify-between items-center pt-1">
                                                <div className="text-[9px] text-zinc-500 font-bold uppercase tracking-[0.2em]">
                                                    Velocity: <span className="text-zinc-300">{notice.renderSpeed || notice.fps || 0} FPS</span>
                                                </div>
                                                {(notice.renderSpeed || notice.fps) > 0 && notice.totalFrames > 0 && (
                                                    <div className="text-[9px] text-zinc-400 font-black uppercase tracking-[0.1em]">
                                                        Est: <span className="text-white">
                                                            {Math.ceil(((notice.totalFrames || 0) - (notice.currentFrame || 0)) / (notice.renderSpeed || notice.fps || 1) / 60)} MIN REMAINING
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-2 mb-4 mt-2">
                                            <div className={`w-2 h-2 rounded-full ${statusColor.replace('text', 'bg')} ${notice.status === 'processing' ? 'animate-pulse' : ''}`} />
                                            <p className="text-[10px] text-zinc-400 font-black uppercase tracking-widest leading-none">
                                                Status: <span className={statusColor}>{finalStatus}</span>
                                            </p>
                                        </div>
                                    )}

                                    <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-zinc-800/50">
                                        <div>
                                            <p className="text-[9px] text-zinc-600 uppercase tracking-widest font-black mb-1">Production Data</p>
                                            <p className="text-[10px] text-zinc-400 font-medium">Codec: <span className="text-zinc-300">{notice.codecChoice || 'H.264'}</span></p>
                                            <p className="text-[10px] text-zinc-400 font-medium">Requested: <span className="text-zinc-300">{exportTime}</span></p>
                                        </div>
                                        {notice.status === 'ready' && (
                                            <div className="text-right">
                                                <p className="text-[9px] text-zinc-600 uppercase tracking-widest font-black mb-1">Retention</p>
                                                <p className={`text-[10px] font-medium ${isExpired ? 'text-red-500' : 'text-zinc-400'}`}>
                                                    {isExpired ? 'EXPIRED' : `EXPIRES ${expirationDate}`}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                    
                                    {notice.status === 'ready' && !isExpired && (
                                        <div className="mt-6 flex gap-2 w-full">
                                            <button 
                                                onClick={() => handleDownloadClick(notice)}
                                                disabled={isRefreshingLink === notice.id || isCopyingLink === notice.id}
                                                className="flex-grow inline-flex items-center justify-center px-6 py-3 bg-yellow-brand text-black font-black text-xs tracking-[0.2em] uppercase rounded-xl hover:bg-yellow-300 transition-all transform active:scale-95 shadow-[0_8px_30px_rgba(253,224,71,0.15)] disabled:bg-zinc-800 disabled:text-zinc-600 disabled:shadow-none"
                                            >
                                                <CloudDownload size={18} className="mr-2"/> 
                                                {isRefreshingLink === notice.id ? 'AUTHENTICATING...' : 'DOWNLOAD MASTER FILE'}
                                            </button>
                                            <button 
                                                onClick={() => handleCopyLink(notice)} 
                                                disabled={isCopyingLink === notice.id || isRefreshingLink === notice.id} 
                                                className="inline-flex items-center justify-center px-4 py-3 bg-zinc-800 text-yellow-brand border border-zinc-700 font-black text-xs uppercase rounded-xl hover:bg-zinc-700 transition-all transform active:scale-95 disabled:opacity-50 shadow-inner" 
                                                title="Copy Share Link"
                                            >
                                                {isCopyingLink === notice.id ? <Loader2 size={18} className="animate-spin" /> : <Copy size={18} />}
                                            </button>
                                            <button 
                                                onClick={() => handleCreateReviewLink(notice)} 
                                                disabled={isCreatingReview === notice.id} 
                                                className="inline-flex items-center justify-center px-4 py-3 bg-zinc-800 text-zinc-200 border border-zinc-700 font-black text-xs uppercase rounded-xl hover:bg-zinc-700 transition-all transform active:scale-95 disabled:opacity-50 shadow-inner" 
                                                title="Create a no-login client review link"
                                            >
                                                {isCreatingReview === notice.id ? <Loader2 size={18} className="animate-spin" /> : <Eye size={18} />}
                                            </button>
                                            {notice.reviewToken && (
                                                <button 
                                                    onClick={() => handleRevokeReviewLink(notice)} 
                                                    disabled={isRevokingLink === notice.id} 
                                                    className="inline-flex items-center justify-center px-4 py-3 bg-zinc-800 text-red-400 border border-zinc-700 font-black text-xs uppercase rounded-xl hover:bg-red-950/40 hover:border-red-900/40 transition-all transform active:scale-95 disabled:opacity-50 shadow-inner" 
                                                    title="Revoke the client review link"
                                                >
                                                    {isRevokingLink === notice.id ? <Loader2 size={18} className="animate-spin" /> : <Ban size={18} />}
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </CollapsibleSection>
    );
};

// --- BrandedLoader ---
// Reusable full-app loading state. Used anywhere we're waiting on Firestore/Cloud
// Functions (initial projects fetch, trial project creation, editor project load, etc.)
// so the user always sees "this is BLACKBOXcr working on it" instead of a blank screen,
// a bare text string, or — worse — UI that looks actionable before it actually is.
// Ships its own tiny <style> block (rather than relying on the app shell's global
// one) because this loader is often shown BEFORE the main app tree — and its
// keyframes — have mounted.
const BrandedLoader = ({ message = 'Loading…', fullScreen = true }) => (
  <div className={`${fullScreen ? 'min-h-screen' : 'py-20'} w-full bg-zinc-950 flex flex-col items-center justify-center gap-4`}>
    <div className="flex items-center gap-4">
      <div className="text-3xl font-bold tracking-tight text-white whitespace-nowrap">
        BLACKBOX<span className="text-zinc-700">cr</span>
      </div>
      <div className="w-24 h-[3px] rounded-full bg-zinc-800 overflow-hidden relative">
        <div className="bcr-loader-sweep absolute inset-y-0 left-0 w-1/3 rounded-full bg-gradient-to-r from-transparent via-purple-400 to-transparent" />
      </div>
    </div>
    <span className="text-sm tracking-wide text-zinc-500">{message}</span>
    <style>{`
      @keyframes bcr-loader-sweep-kf {
        0%   { transform: translateX(-100%); }
        100% { transform: translateX(400%); }
      }
      .bcr-loader-sweep { animation: bcr-loader-sweep-kf 1.3s ease-in-out infinite; }
    `}</style>
  </div>
);

const UpgradePrompt = ({ setCurrentPage, setPurchaseIntent, activeProjects, expiredProjects }) => { 
  const hasExpired = expiredProjects.length > 0;

  const handleUpgradeClick = (plan) => {
    setPurchaseIntent(plan); // Set the intent before navigating
    setCurrentPage('checkout');
  };

  return (
      <div className="w-full">
          {/* Header Message */}
          <div className="flex items-center gap-4 mb-6">
              <div className="p-3 bg-zinc-900 border border-zinc-800 rounded-xl">
                  <Star size={20} className="text-purple-400" />
              </div>
              <div>
                  <h3 className="text-lg font-medium text-white">
                      {hasExpired ? 'Renew License Access' : 'Initialize New Production'}
                  </h3>
                  <p className="text-sm text-zinc-500 mt-1">
                      {hasExpired
                        ? `You have ${expiredProjects.length} expired project${expiredProjects.length !== 1 ? 's' : ''}. Select a capacity license to reactivate or start fresh.`
                        : 'Select a workspace capacity to begin your next project.'}
                  </p>
              </div>
          </div>

          {/* Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Indie Plan - Utility Look */}
              <button 
                  onClick={() => handleUpgradeClick('indie')} 
                  className="group relative flex flex-col p-6 bg-zinc-900/40 border border-white/5 rounded-xl hover:bg-zinc-900/80 hover:border-zinc-700 transition-all text-left"
              >
                  <div className="flex justify-between items-start w-full mb-6">
                      <div>
                          <span className="text-[10px] font-bold text-zinc-500 tracking-widest uppercase mb-1 block">Standard License</span>
                          <h4 className="text-xl font-semibold text-white group-hover:text-blue-300 transition-colors">Indie Project</h4>
                      </div>
                      <div className="text-right">
                          <span className="block text-2xl font-bold text-white">$199</span>
                          <span className="text-[10px] text-zinc-600 uppercase tracking-wider">Per Film</span>
                      </div>
                  </div>

                  <div className="space-y-3 flex-grow border-t border-white/5 pt-4">
                      <div className="flex items-center gap-3 text-sm text-zinc-400 group-hover:text-zinc-300 transition-colors">
                          <Check size={14} className="text-zinc-600 group-hover:text-blue-400 transition-colors" /> 
                          <span>1080p Resolution</span>
                      </div>
                      <div className="flex items-center gap-3 text-sm text-zinc-400 group-hover:text-zinc-300 transition-colors">
                          <Check size={14} className="text-zinc-600 group-hover:text-blue-400 transition-colors" /> 
                          <span>H.264 / .mp4 Export</span>
                      </div>
                      <div className="flex items-center gap-3 text-sm text-zinc-400 group-hover:text-zinc-300 transition-colors">
                          <Check size={14} className="text-zinc-600 group-hover:text-blue-400 transition-colors" /> 
                          <span>90 Days Storage</span>
                      </div>
                  </div>
              </button>
              
              {/* Pro Plan - Premium Look */}
              <button 
                  onClick={() => handleUpgradeClick('pro')} 
                  className="group relative flex flex-col p-6 bg-zinc-900/60 border border-purple-500/20 rounded-xl hover:bg-zinc-900 hover:border-purple-500/40 hover:shadow-[0_0_30px_-10px_rgba(168,85,247,0.15)] transition-all text-left"
              >
                  <div className="absolute top-4 right-4">
                      <div className="w-1.5 h-1.5 rounded-full bg-purple-500 shadow-[0_0_8px_rgba(168,85,247,0.8)]"></div>
                  </div>

                  <div className="flex justify-between items-start w-full mb-6">
                      <div>
                          <span className="text-[10px] font-bold text-purple-400/80 tracking-widest uppercase mb-1 block">Studio License</span>
                          <h4 className="text-xl font-semibold text-white group-hover:text-purple-300 transition-colors">Feature Film</h4>
                      </div>
                      <div className="text-right">
                          <span className="block text-2xl font-bold text-white">$399</span>
                          <span className="text-[10px] text-zinc-600 uppercase tracking-wider">Per Film</span>
                      </div>
                  </div>

                  <div className="space-y-3 flex-grow border-t border-white/5 pt-4">
                      <div className="flex items-center gap-3 text-sm text-zinc-300">
                          <Check size={14} className="text-purple-500" /> 
                          <span>4K Resolution</span>
                      </div>
                      <div className="flex items-center gap-3 text-sm text-zinc-300">
                          <Check size={14} className="text-purple-500" /> 
                          <span>ProRes 4444 + Alpha</span>
                      </div>
                      <div className="flex items-center gap-3 text-sm text-zinc-300">
                          <Check size={14} className="text-purple-500" /> 
                          <span>Priority Support</span>
                      </div>
                  </div>
              </button>
          </div>
      </div>
  );
};

// Sorts a project list by created date (newest first), name (A→Z), or expiry
// (soonest first — projects with no expiry date sort to the end).
const sortProjects = (list, sortBy) => {
    const arr = [...list];
    if (sortBy === 'name') {
        arr.sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
    } else if (sortBy === 'expiresAt') {
        const getExpiry = (p) => p.expiresAt?.toDate ? p.expiresAt.toDate().getTime() : Infinity;
        arr.sort((a, b) => getExpiry(a) - getExpiry(b));
    } else {
        // 'createdAt' (default) — newest first
        const getCreated = (p) => p.createdAt?.toDate ? p.createdAt.toDate().getTime() : 0;
        arr.sort((a, b) => getCreated(b) - getCreated(a));
    }
    return arr;
};

const SORT_OPTIONS = [
    { key: 'createdAt', label: 'Newest' },
    { key: 'name', label: 'Name' },
    { key: 'expiresAt', label: 'Expiring' },
];

const SortControl = ({ sortBy, setSortBy }) => (
    <div className="flex items-center gap-0.5 bg-zinc-900/70 border border-zinc-800 rounded-lg p-0.5">
        {SORT_OPTIONS.map(opt => (
            <button
                key={opt.key}
                onClick={(e) => { e.stopPropagation(); setSortBy(opt.key); }}
                className={`px-2.5 py-1 rounded-md text-[10px] font-bold tracking-wide uppercase transition-colors ${
                    sortBy === opt.key
                        ? 'bg-zinc-100 text-zinc-900'
                        : 'text-zinc-500 hover:text-zinc-300'
                }`}
            >
                {opt.label}
            </button>
        ))}
    </div>
);

// --- ProjectsPage Component ---
const ProjectsPage = () => {
  // FIXED: Added `currentProjectId` to the context destructuring
const { db, userId, isAuthReady, setCurrentPage, setCurrentProjectId, showMessageBox, setFirestoreLoading, firestoreLoading, logout, currentProjectId, setPurchaseIntent, auth, functions } = useContext(AppContext);
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [sortBy, setSortBy] = useState('createdAt'); // 'createdAt' | 'name' | 'expiresAt'
    // Add these state variables at the top of ProjectsPage component
const [showWelcomeTour, setShowWelcomeTour] = useState(false);
const [hasCheckedWelcome, setHasCheckedWelcome] = useState(false);
const [showPaymentHelp, setShowPaymentHelp] = useState(false);

  useEffect(() => {
    if (!db || !userId || !isAuthReady) {
      setLoadingProjects(false);
      return;
    }
    setLoadingProjects(true);
    const projectsCollectionRef = collection(db, `artifacts/${__app_id}/users/${userId}/projects`);
    const unsubscribe = onSnapshot(projectsCollectionRef, (snapshot) => {
      setProjects(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoadingProjects(false);
    }, (error) => {
      console.error("Error fetching projects:", error);
      showMessageBox("Error", `Failed to load projects: ${error.message}`, "error");
      setLoadingProjects(false);
    });
    return () => unsubscribe();
  }, [db, userId, isAuthReady, showMessageBox]);

  useEffect(() => {
    if (projects.length > 0 && !hasCheckedWelcome) {
        setHasCheckedWelcome(true);
        
        // Check if this is their first time
        const hasSeenWelcome = localStorage.getItem('hasSeenWelcome');
        const trialProject = projects.find(p => p.isTrial);
        
        if (!hasSeenWelcome && trialProject && projects.length === 1) {
            // Small delay to let the page render first
            setTimeout(() => {
                setShowWelcomeTour(true);
                localStorage.setItem('hasSeenWelcome', 'true');
            }, 500);
        }
    }
}, [projects, hasCheckedWelcome]);

  const loadProject = (projectId) => {
    setCurrentProjectId(projectId);
    setCurrentPage('editor');
  };

  const updateProjectName = async (projectId, newName) => {
    if (!newName || !newName.trim()) {
      showMessageBox("Error", "Project name cannot be empty.", "error", true);
      return;
    }
    if (!db || !userId) return;

    setFirestoreLoading(true);
    try {
      const projectRef = doc(db, `artifacts/${__app_id}/users/${userId}/projects/${projectId}`);
      await updateDoc(projectRef, { name: newName.trim() });
      showMessageBox("Success", "Project renamed successfully.", "success", true);
    } catch (error) {
      console.error("Error renaming project:", error);
      showMessageBox("Error", `Failed to rename project: ${error.message}`, "error");
    } finally {
      setFirestoreLoading(false);
    }
  };
  
const AdminButton = () => {
    const { auth, setCurrentPage } = useContext(AppContext);
    
    const ADMIN_UIDS = [
        "UKyFb2SjeFc02LxExv83cY7oo6v2",
    ];
    
    const isAdmin = auth?.currentUser && ADMIN_UIDS.includes(auth.currentUser.uid);
    
    if (!isAdmin) {
        return null;
    }
    

};

  const updateProjectStatus = async (projectId, isArchived) => {
    if (!db || !userId) return;
    setFirestoreLoading(true);
    try {
      const projectRef = doc(db, `artifacts/${__app_id}/users/${userId}/projects/${projectId}`);
      await updateDoc(projectRef, { isArchived: isArchived });
      showMessageBox("Success", `Project ${isArchived ? 'archived' : 'restored'}.`, "success", true);
    } catch (error) {
      console.error(`Error updating project status:`, error);
      showMessageBox("Error", `Failed to update project: ${error.message}`, "error");
    } finally {
      setFirestoreLoading(false);
    }
  };

  const deleteProject = (projectId, projectName) => {
    showMessageBox("Confirm Deletion", `READ THIS! Are you sure you want to permanently delete project "${projectName}"? This action cannot be undone and no refunds given. If you want to start over, use the clear all credits function inside your project.`, "warning", false,
      async () => {
        if (!db || !userId) return;
        setFirestoreLoading(true);
        try {
          await deleteDoc(doc(db, `artifacts/${__app_id}/users/${userId}/projects/${projectId}`));
          const q = query(collection(db, `artifacts/${__app_id}/users/${userId}/exportJobs`), where("projectId", "==", projectId));
          const querySnapshot = await getDocs(q);
          const deletePromises = querySnapshot.docs.map(d => deleteDoc(d.ref));
          await Promise.all(deletePromises);
          
          if (currentProjectId === projectId) {
            setCurrentProjectId(null);
          }
          showMessageBox("Success", `Project "${projectName}" and its exports have been deleted.`, "success", true);
        } catch (error) {
          console.error("Error deleting project:", error);
          showMessageBox("Error", `Failed to delete project: ${error.message}`, "error");
        } finally {
          setFirestoreLoading(false);
        }
      }
    );
  };

  // --- Duplicate Project ---
  // Free copy: credits, cardCredits, settings, and logos are cloned server-side (the
  // 'duplicateProject' Cloud Function does a real Storage copy, so the source project can
  // later be deleted/cleaned up without ever breaking the duplicate's logos). The clone comes
  // back with exportsLimit: 0 / needsLicense: true so it's fully editable but can't render
  // until the user runs it through checkout — same idea as upgrading a trial project.
  const [duplicatingProjectId, setDuplicatingProjectId] = useState(null);

  const duplicateProject = (projectId, projectName) => {
    if (!db || !userId || !functions) return;
    setDuplicatingProjectId(projectId);
    setFirestoreLoading(true);
    (async () => {
      try {
        const duplicateProjectFn = httpsCallable(functions, 'duplicateProject');
        const { data } = await duplicateProjectFn({ sourceProjectId: projectId });
        showMessageBox(
          "Success",
          `"${projectName}" was duplicated${data?.newProjectName ? ` as "${data.newProjectName}"` : ''}. It's ready to edit — you'll just need to assign a license before rendering.`,
          "success",
          true
        );
      } catch (error) {
        console.error("Error duplicating project:", error);
        showMessageBox("Error", `Failed to duplicate project: ${error.message}`, "error");
      } finally {
        setDuplicatingProjectId(null);
        setFirestoreLoading(false);
      }
    })();
  };
  
  // FIXED: The ProjectCard component is now defined within the scope of ProjectsPage
  // --- SUB-COMPONENT: Project Card (Studio Style) ---
const ProjectCard = ({ project, isArchived, loadProject, updateProjectStatus, deleteProject, setCurrentPage, updateProjectName, setPurchaseIntent, duplicateProject, isDuplicating }) => {
    const [isEditingName, setIsEditingName] = useState(false);
    const [currentName, setCurrentName] = useState(project.name);

    const now = new Date();
    const expiresAt = project.expiresAt?.toDate();
    const isExpired = expiresAt && now > expiresAt;
    const daysRemaining = expiresAt ? Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24)) : null;
    const isExpiringSoon = daysRemaining !== null && daysRemaining <= 7 && daysRemaining > 0;
    
    const exportsUsed = project.exportsUsed || 0;
    const exportsLimit = project.exportsLimit || 50;
    const exportsRemaining = exportsLimit - exportsUsed;
    const exportsLow = exportsRemaining <= 5 && exportsRemaining > 0;

    // Duplicated projects: fully editable, but can't render until a license is assigned.
    const needsLicense = project.needsLicense === true;
    
    const tier = project.tier || 'demo';
    const isTrial = project.isTrial || false;
    
    // Studio-style badges
    const tierConfig = {
        demo: { label: 'TRIAL', bg: 'bg-zinc-700', text: 'text-zinc-300' },
        indie: { label: 'INDIE', bg: 'bg-blue-900/50', text: 'text-blue-300' },
        pro: { label: 'PRO', bg: 'bg-purple-900/50', text: 'text-purple-300' }
    }[tier] || { label: 'STANDARD', bg: 'bg-zinc-700', text: 'text-zinc-300' };

    const handleNameChange = (e) => {
        e.stopPropagation();
        setIsEditingName(false);
        if (currentName !== project.name) {
            updateProjectName(project.id, currentName);
        }
    };

    return (
        <div 
            onClick={() => !isArchived && !isExpired && !isEditingName && loadProject(project.id)}
            className={`
                group relative flex flex-col justify-between
                bg-zinc-950 border rounded-lg p-5 transition-all duration-200
                ${isExpired ? 'border-red-900/30 opacity-50' : 'border-zinc-800/70 hover:border-zinc-600 hover:bg-zinc-900/60'}
                ${!isArchived && !isExpired && !isEditingName ? 'cursor-pointer' : ''}
            `}
        >
            {/* Tier Badge — top right corner, like a clapperboard label */}
            <div className="absolute top-4 right-4">
                <span className={`text-[9px] px-2 py-0.5 rounded font-bold tracking-[0.2em] uppercase border ${
                    isTrial ? 'border-zinc-700 text-zinc-500 bg-transparent' :
                    tier === 'pro' ? 'border-amber-600/40 text-amber-500/80 bg-transparent' :
                    'border-blue-800/40 text-blue-400/70 bg-transparent'
                }`}>
                    {isTrial ? 'Trial' : tierConfig.label}
                </span>
            </div>

            {/* Header */}
            <div className="mb-5 pr-16">
                {isEditingName ? (
                    <input
                        type="text"
                        value={currentName}
                        onChange={(e) => setCurrentName(e.target.value)}
                        onBlur={handleNameChange}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleNameChange(e); }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full bg-transparent border-b border-zinc-600 text-white font-medium text-sm focus:outline-none focus:border-zinc-400 py-0.5 transition-colors"
                        autoFocus
                    />
                ) : (
                    <div className="flex items-center gap-2 group/title">
                        <h3 className="text-sm font-semibold text-zinc-100 tracking-tight truncate leading-tight">{project.name}</h3>
                        {!isArchived && !isExpired && (
                            <button 
                                onClick={(e) => { e.stopPropagation(); setIsEditingName(true); }} 
                                className="opacity-0 group-hover/title:opacity-100 shrink-0 p-0.5 text-zinc-600 hover:text-zinc-300 transition-all"
                            >
                                <Edit size={11} />
                            </button>
                        )}
                    </div>
                )}
                <p className="text-[10px] text-zinc-600 mt-1 font-mono tracking-wider">
                    {project.createdAt?.toDate ? new Date(project.createdAt.toDate()).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—'}
                </p>
            </div>

            {/* Status block */}
            <div className="space-y-2.5 mb-5">
                {isExpired ? (
                    <div className="flex items-center gap-2 text-[11px]">
                        <span className="w-1 h-1 rounded-full bg-red-500 shrink-0" />
                        <span className="text-red-400 font-medium tracking-wide">License Expired</span>
                    </div>
                ) : needsLicense ? (
                    <div className="flex items-center gap-2 text-[11px]">
                        <span className="w-1 h-1 rounded-full bg-blue-500 shrink-0" />
                        <span className="text-blue-400 font-medium tracking-wide">Duplicate — needs a license to render</span>
                    </div>
                ) : (
                    <>
                        <div className="flex items-center gap-2 text-[11px]">
                            <span className={`w-1 h-1 rounded-full shrink-0 ${isExpiringSoon ? 'bg-amber-500' : 'bg-zinc-700'}`} />
                            <span className={isExpiringSoon ? 'text-amber-400 font-medium' : 'text-zinc-500'}>
                                {expiresAt 
                                    ? isExpiringSoon 
                                        ? `${daysRemaining}d remaining` 
                                        : `Active to ${expiresAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                                    : 'Active'
                                }
                            </span>
                        </div>
                        <div className="flex items-center gap-2 text-[11px]">
                            <span className={`w-1 h-1 rounded-full shrink-0 ${exportsLow ? 'bg-amber-500' : 'bg-zinc-700'}`} />
                            <span className={exportsLow ? 'text-amber-400 font-medium' : 'text-zinc-500'}>
                                {exportsRemaining} of {exportsLimit} renders remaining
                            </span>
                        </div>
                    </>
                )}
            </div>

            {/* Footer Actions */}
            <div className="pt-4 border-t border-zinc-800/50 flex gap-2">
                {isExpired ? (
                    <>
                        <button onClick={() => loadProject(project.id)} className="flex-1 py-1.5 text-zinc-500 hover:text-zinc-300 text-[11px] font-medium transition-colors">
                            View Read-Only
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); setPurchaseIntent(project.tier || 'indie'); localStorage.setItem('renewProjectId', project.id); setCurrentPage('checkout'); }} className="flex-1 py-1.5 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-zinc-300 text-[11px] font-medium rounded transition-colors">
                            Renew License
                        </button>
                        {duplicateProject && (
                            <button onClick={(e) => { e.stopPropagation(); duplicateProject(project.id, project.name); }} disabled={isDuplicating} className="px-3 py-1.5 text-zinc-600 hover:text-zinc-300 transition-colors disabled:opacity-40" title="Duplicate project">
                                {isDuplicating ? <Loader2 size={13} className="animate-spin" /> : <CopyPlus size={13} />}
                            </button>
                        )}
                    </>
                ) : isArchived ? (
                    <>
                        <button onClick={() => updateProjectStatus(project.id, false)} className="flex-1 py-1.5 text-emerald-500/80 hover:text-emerald-400 text-[11px] font-medium transition-colors">
                            Restore
                        </button>
                        {duplicateProject && (
                            <button onClick={(e) => { e.stopPropagation(); duplicateProject(project.id, project.name); }} disabled={isDuplicating} className="px-3 py-1.5 text-zinc-600 hover:text-zinc-300 transition-colors disabled:opacity-40" title="Duplicate project">
                                {isDuplicating ? <Loader2 size={13} className="animate-spin" /> : <CopyPlus size={13} />}
                            </button>
                        )}
                        <button onClick={(e) => { e.stopPropagation(); deleteProject(project.id, project.name); }} className="px-3 py-1.5 text-zinc-600 hover:text-red-400 transition-colors">
                            <Trash2 size={13} />
                        </button>
                    </>
                ) : needsLicense ? (
                    <>
                        <button onClick={() => loadProject(project.id)} className="flex-1 py-1.5 text-zinc-500 hover:text-zinc-300 text-[11px] font-medium transition-colors">
                            Open & Edit
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); setPurchaseIntent(project.tier || 'indie'); localStorage.setItem('renewProjectId', project.id); setCurrentPage('checkout'); }} className="flex-1 py-1.5 bg-blue-950/50 border border-blue-800/40 hover:border-blue-700 text-blue-400 text-[11px] font-medium rounded transition-all">
                            Assign License
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); deleteProject(project.id, project.name); }} className="px-3 py-1.5 text-zinc-600 hover:text-red-500 transition-colors" title="Delete">
                            <Trash2 size={13} />
                        </button>
                    </>
                ) : (
                    <>
                        {(isExpiringSoon || exportsLow) && (
                            <button onClick={(e) => { e.stopPropagation(); setPurchaseIntent(project.tier || 'indie'); localStorage.setItem('renewProjectId', project.id); setCurrentPage('checkout'); }} className="flex-1 py-1.5 bg-amber-950/50 border border-amber-800/40 hover:border-amber-700 text-amber-400 text-[11px] font-medium rounded transition-all">
                                {exportsLow ? 'Add Renders' : 'Extend'}
                            </button>
                        )}
                        {duplicateProject && (
                            <button onClick={(e) => { e.stopPropagation(); duplicateProject(project.id, project.name); }} disabled={isDuplicating} className="px-3 py-1.5 text-zinc-600 hover:text-zinc-400 transition-colors disabled:opacity-40" title="Duplicate project">
                                {isDuplicating ? <Loader2 size={13} className="animate-spin" /> : <CopyPlus size={13} />}
                            </button>
                        )}
                        <button onClick={(e) => { e.stopPropagation(); updateProjectStatus(project.id, true); }} className="px-3 py-1.5 text-zinc-600 hover:text-zinc-400 transition-colors" title="Archive">
                            <Archive size={13} />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); deleteProject(project.id, project.name); }} className="px-3 py-1.5 text-zinc-600 hover:text-red-500 transition-colors" title="Delete">
                            <Trash2 size={13} />
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

  const now = new Date();
  const isProjectExpired = (p) => {
    const exp = p.expiresAt?.toDate ? p.expiresAt.toDate() : null;
    return !!exp && now > exp;
  };
  const nonArchivedProjects = projects.filter(p => !p.isArchived);
  const archivedProjectsUnsorted = projects.filter(p => p.isArchived);
  const expiredProjectsUnsorted = nonArchivedProjects.filter(isProjectExpired);
  const activeProjectsUnsorted = nonArchivedProjects.filter(p => !isProjectExpired(p));

  const activeProjects = sortProjects(activeProjectsUnsorted, sortBy);
  const expiredProjects = sortProjects(expiredProjectsUnsorted, sortBy);
  const archivedProjects = sortProjects(archivedProjectsUnsorted, sortBy);
  
  const userDisplayName = auth?.currentUser?.email || `User: ${userId}`;

  const scrollToNewProject = () => {
    document.getElementById('new-project')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // While we're still fetching this user's projects (including, for a brand-new
  // signup, waiting on the trial project that was just created in the background),
  // show a branded full-page loader instead of the page below. Previously this state
  // rendered the full "buy $199/$399" upgrade cards with only a small "Loading
  // projects..." string beneath them — a new trial user would land here, see nothing
  // but purchase options, and have no idea their free trial was already being set up.
  if (loadingProjects) {
    return <BrandedLoader message="Setting up your workspace…" />;
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 font-sans relative flex flex-col items-center p-6 pt-8">
            
            <div className="w-full max-w-5xl relative z-10">
                
                {/* Header Bar */}
                <div className="flex justify-between items-center mb-14 pb-5 border-b border-zinc-800/60">
                    <div>
                        <div className="text-2xl font-bold tracking-tight text-white">
                            BLACKBOX<span className="text-zinc-700">cr</span>
                        </div>
                        <p className="text-[10px] text-zinc-600 tracking-[0.25em] uppercase mt-0.5">
                            Production Workspace
                        </p>
                    </div>

                    <div className="flex items-center gap-2">
                        <button 
                            onClick={() => setCurrentPage('tutorial')} 
                            className="flex items-center gap-1.5 px-3 py-1.5 text-zinc-500 hover:text-zinc-200 text-[11px] font-medium tracking-wide transition-colors"
                        >
                            <HelpCircle size={13} /> Tutorial
                        </button>

                        <button 
                            onClick={handleDownloadTemplate} 
                            className="flex items-center gap-1.5 px-3 py-1.5 text-zinc-500 hover:text-zinc-200 text-[11px] font-medium tracking-wide transition-colors"
                            title="Download CSV Template"
                        >
                            <Download size={13} /> Template
                        </button>

                        {userId === "UKyFb2SjeFc02LxExv83cY7oo6v2" && (
                            <button 
                                onClick={() => setCurrentPage('admin')} 
                                className="flex items-center gap-1.5 px-3 py-1.5 text-zinc-500 hover:text-zinc-200 text-[11px] font-medium tracking-wide transition-colors"
                            >
                                <Award size={13} /> Admin
                            </button>
                        )}
                        {userId === "UKyFb2SjeFc02LxExv83cY7oo6v2" && (
                            <button
                                onClick={() => setCurrentPage('affiliate-admin')}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-zinc-500 hover:text-zinc-200 text-[11px] font-medium tracking-wide transition-colors"
                            >
                                <Users size={13} /> Affiliates
                            </button>
                        )}

                        <div className="w-px h-4 bg-zinc-800 mx-1" />

                        <button 
                            onClick={logout} 
                            className="flex items-center gap-1.5 px-3 py-1.5 text-zinc-600 hover:text-red-400 text-[11px] font-medium tracking-wide transition-colors"
                        >
                            Sign Out <LogIn size={12} />
                        </button>
                    </div>
                </div>

        {/* Greeting */}
                <div className="mb-6">
                    <h1 className="text-xl font-semibold text-zinc-100 mb-1">
                        Welcome back, {userDisplayName.split('@')[0]}
                    </h1>
                    <p className="text-[11px] text-zinc-600 tracking-widest uppercase">
                        {userDisplayName}
                    </p>
                </div>

                {/* Start New Project — the primary action, front and center */}
                <div id="new-project" className="relative overflow-hidden bg-gradient-to-b from-zinc-900/70 to-zinc-950 border border-zinc-800/80 rounded-2xl p-6 md:p-8 mb-10 scroll-mt-6">
                    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple-500/50 to-transparent" />
                    <UpgradePrompt 
                        setCurrentPage={setCurrentPage} 
                        setPurchaseIntent={setPurchaseIntent}
                        activeProjects={activeProjects}
                        expiredProjects={expiredProjects}
                    />
                </div>

        <CollapsibleSection title="Active Productions" icon={HardDrive} count={activeProjects.length} defaultOpen={true} headerActions={activeProjects.length > 1 ? <SortControl sortBy={sortBy} setSortBy={setSortBy} /> : null}>
            <div className="mt-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {loadingProjects ? (
                    <div className="col-span-full text-center py-8 text-zinc-600 text-sm">Loading projects...</div>
                ) : activeProjects.length === 0 ? (
                    <button
                        onClick={scrollToNewProject}
                        className="col-span-full flex flex-col items-center justify-center gap-2 py-10 border border-dashed border-zinc-800 rounded-lg text-zinc-500 hover:text-zinc-300 hover:border-zinc-600 hover:bg-zinc-900/40 transition-all"
                    >
                        <Plus size={18} />
                        <span className="text-sm font-medium">No active projects yet — start one above</span>
                    </button>
                ) : (
                    <>
                        <button
                            onClick={scrollToNewProject}
                            title="Start a new project"
                            className="group flex flex-col items-center justify-center gap-2 min-h-[172px] border border-dashed border-zinc-800 rounded-lg text-zinc-600 hover:text-zinc-300 hover:border-zinc-600 hover:bg-zinc-900/40 transition-all"
                        >
                            <Plus size={18} className="transition-transform group-hover:scale-110" />
                            <span className="text-[11px] font-semibold tracking-wide">New Project</span>
                        </button>
                        {activeProjects.map(project => 
                            <ProjectCard 
                                key={project.id} 
                                project={project} 
                                isArchived={false}
                                loadProject={loadProject}
                                updateProjectStatus={updateProjectStatus}
                                deleteProject={deleteProject}
                                setCurrentPage={setCurrentPage}
                                updateProjectName={updateProjectName}
                                setPurchaseIntent={setPurchaseIntent}
                                duplicateProject={duplicateProject}
                                isDuplicating={duplicatingProjectId === project.id}
                            />
                        )}
                    </>
                )}
            </div>
        </CollapsibleSection>
        
    <ProjectExportNotices db={db} userId={userId} showMessageBox={showMessageBox} functions={functions} />

        {expiredProjects.length > 0 && (
            <CollapsibleSection title="Needs Renewal" icon={RotateCcw} count={expiredProjects.length} defaultOpen={true}>
                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {expiredProjects.map(project => 
                        <ProjectCard 
                            key={project.id} 
                            project={project} 
                            isArchived={false}
                            loadProject={loadProject}
                            updateProjectStatus={updateProjectStatus}
                            deleteProject={deleteProject}
                            setCurrentPage={setCurrentPage}
                            updateProjectName={updateProjectName}
                            setPurchaseIntent={setPurchaseIntent}
                            duplicateProject={duplicateProject}
                            isDuplicating={duplicatingProjectId === project.id}
                        />
                    )}
                </div>
            </CollapsibleSection>
        )}

        {archivedProjects.length > 0 && (
            <CollapsibleSection title="Archived" icon={Archive} count={archivedProjects.length} defaultOpen={false}>
                        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 opacity-75">
                            {archivedProjects.map(project => 
                                <ProjectCard 
                                    key={project.id} 
                                    project={project} 
                                    isArchived={true}
                                    loadProject={loadProject}
                                    updateProjectStatus={updateProjectStatus}
                                    deleteProject={deleteProject}
                                    setCurrentPage={setCurrentPage}
                                    updateProjectName={updateProjectName}
                                    setPurchaseIntent={setPurchaseIntent}
                                    duplicateProject={duplicateProject}
                                    isDuplicating={duplicatingProjectId === project.id}
                                />
                            )}
                        </div>
                    </CollapsibleSection>
        )}

        {/* ✅ ADD SUPPORT CONTACT HERE */}
        <div className="mt-8 pt-6 border-t border-zinc-800 text-center">
          <p className="text-sm text-gray-400 mb-2">
            Need help or have questions?
          </p>
          <a 
            href="mailto:support@blackbox-is.com" 
            className="text-purple-400 hover:text-purple-300 font-semibold transition-colors"
          >
            support@blackbox-is.com
          </a>
          <p className="text-xs text-gray-500 mt-2">
            We typically respond within 24 hours
          </p>
        </div>
        {/* ✅ END OF SUPPORT CONTACT */}
      </div>

        <AdminButton />

{/* Welcome Tour Modal (Studio Style) */}
            {showWelcomeTour && (
                <div className="fixed inset-0 bg-black/95 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-300">
                    <div className="bg-zinc-900 border border-white/10 p-8 rounded-2xl max-w-lg w-full shadow-2xl relative">
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-purple-500/20 blur-[50px] rounded-full pointer-events-none"></div>
                        
                        <div className="text-center mb-8 relative z-10">
                            <h2 className="text-2xl font-bold text-white mb-2">Workspace Initialized</h2>
                            <p className="text-zinc-400">Your 7-day trial environment is ready.</p>
                        </div>
                        
                        <div className="space-y-4 mb-8 relative z-10">
                            <div className="flex items-start gap-4 bg-zinc-950/50 p-4 rounded-xl border border-white/5">
                                <div className="mt-1"><Check className="text-green-500" size={16} /></div>
                                <div>
                                    <p className="font-medium text-white text-sm">Example Data Loaded</p>
                                    <p className="text-xs text-zinc-500">We've populated a sample credit roll for you.</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-4 bg-zinc-950/50 p-4 rounded-xl border border-white/5">
                                <div className="mt-1"><Check className="text-green-500" size={16} /></div>
                                <div>
                                    <p className="font-medium text-white text-sm">3 Standard Exports at 1080p</p>
                                    <p className="text-xs text-zinc-500">Full-quality render — see exactly what you'll deliver.</p>
                                </div>
                            </div>
                        </div>
                        
                        <div className="flex gap-3 relative z-10">
                            <button 
                                onClick={() => { setShowWelcomeTour(false); setCurrentPage('tutorial'); }}
                                className="flex-1 px-4 py-3 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors text-sm font-medium"
                            >
                                Watch Tutorial
                            </button>
                            <button 
                                onClick={() => { 
                                    setShowWelcomeTour(false); 
                                    const trial = projects.find(p => p.isTrial);
                                    if (trial) loadProject(trial.id);
                                }}
                                className="flex-1 px-4 py-3 bg-white hover:bg-zinc-200 text-black rounded-lg transition-colors text-sm font-bold"
                            >
                                Start Creating
                            </button>
                        </div>
                    </div>
                </div>
            )}
      {/* ✅ END OF WELCOME TOUR MODAL */}
    </div>
  );
};

// Admin Page

const AdminPage = () => {
  const { db, userId, showMessageBox, setCurrentPage } = useContext(AppContext);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalProjects: 0,
    activeTrials: 0,
    totalExports: 0
  });
  const [loading, setLoading] = useState(true);
  const [debugInfo, setDebugInfo] = useState([]);
  
  // Your admin UID
  const ADMIN_UID = "UKyFb2SjeFc02LxExv83cY7oo6v2";
  
  useEffect(() => {
    console.log("🔐 My UID:", userId);
    console.log("🔐 Admin UID:", ADMIN_UID);
    console.log("🔐 Match:", userId === ADMIN_UID);
    
    if (userId !== ADMIN_UID) {
      showMessageBox("Access Denied", "You don't have permission to view this page.", "error");
      setCurrentPage('projects');
      return;
    }
    
    fetchStats();
  }, [userId]);
  
  const fetchStats = async () => {
    setLoading(true);
    const debug = [];
    
    try {
      debug.push("Starting stats fetch...");
      
      // Try to get users collection
      const usersPath = `artifacts/${__app_id}/users`;
      debug.push(`Trying users path: ${usersPath}`);
      
      const usersSnapshot = await getDocs(collection(db, usersPath));
      debug.push(`Found ${usersSnapshot.size} users`);
      
      let projectCount = 0;
      let trialCount = 0;
      let exportCount = 0;
      
      // Log first user to see structure
      if (usersSnapshot.docs.length > 0) {
        const firstUser = usersSnapshot.docs[0];
        debug.push(`First user ID: ${firstUser.id}`);
        
        // Check projects for first user
        const projectsPath = `artifacts/${__app_id}/users/${firstUser.id}/projects`;
        debug.push(`Checking projects path: ${projectsPath}`);
        
        const testProjects = await getDocs(collection(db, projectsPath));
        debug.push(`First user has ${testProjects.size} projects`);
        
        if (testProjects.size > 0) {
          const firstProject = testProjects.docs[0];
          debug.push(`First project data:`, firstProject.data());
        }
      }
      
      // Now count everything
      for (const userDoc of usersSnapshot.docs) {
        const userId = userDoc.id;
        
        try {
          // Count projects
          const projectsSnapshot = await getDocs(
            collection(db, `artifacts/${__app_id}/users/${userId}/projects`)
          );
          projectCount += projectsSnapshot.size;
          
          // Count active trials
          projectsSnapshot.forEach(doc => {
            const data = doc.data();
            if (data.isTrial) {
              const expiresAt = data.expiresAt?.toDate();
              if (expiresAt && expiresAt > new Date()) {
                trialCount++;
              }
            }
          });
          
          // Count exports
          const exportsSnapshot = await getDocs(
            collection(db, `artifacts/${__app_id}/users/${userId}/exportJobs`)
          );
          exportCount += exportsSnapshot.size;
          
        } catch (error) {
          debug.push(`Error for user ${userId}: ${error.message}`);
        }
      }
      
      debug.push(`Final counts: Users=${usersSnapshot.size}, Projects=${projectCount}, Trials=${trialCount}, Exports=${exportCount}`);
      
      setStats({
        totalUsers: usersSnapshot.size,
        totalProjects: projectCount,
        activeTrials: trialCount,
        totalExports: exportCount
      });
      
      setDebugInfo(debug);
      console.log("🐛 Debug Info:", debug);
      
    } catch (error) {
      console.error('Error fetching stats:', error);
      debug.push(`ERROR: ${error.message}`);
      setDebugInfo(debug);
      showMessageBox("Error", `Failed to load stats: ${error.message}`, "error");
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-black">
        <div className="text-purple-400 text-xl">Loading analytics...</div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-black text-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-purple-400 mb-2">Admin Dashboard</h1>
            <p className="text-gray-400">Quick stats at a glance</p>
          </div>
          <button 
            onClick={() => setCurrentPage('projects')}
            className="bg-zinc-800 border border-zinc-700 text-gray-300 px-4 py-2 rounded-lg hover:bg-zinc-700"
          >
            Back to Projects
          </button>
        </div>
        
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <p className="text-sm text-gray-400 mb-2">Total Users</p>
            <h3 className="text-4xl font-bold text-blue-400">{stats.totalUsers}</h3>
            <p className="text-xs text-gray-500 mt-2">All registered users</p>
          </div>
          
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <p className="text-sm text-gray-400 mb-2">Total Projects</p>
            <h3 className="text-4xl font-bold text-purple-400">{stats.totalProjects}</h3>
            <p className="text-xs text-gray-500 mt-2">All projects created</p>
          </div>
          
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <p className="text-sm text-gray-400 mb-2">Active Trials</p>
            <h3 className="text-4xl font-bold text-green-400">{stats.activeTrials}</h3>
            <p className="text-xs text-gray-500 mt-2">Not yet expired</p>
          </div>
          
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <p className="text-sm text-gray-400 mb-2">Total Exports</p>
            <h3 className="text-4xl font-bold text-yellow-400">{stats.totalExports}</h3>
            <p className="text-xs text-gray-500 mt-2">All time exports</p>
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex gap-4">
          <button 
            onClick={fetchStats}
            className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 font-semibold"
          >
            🔄 Refresh Stats
          </button>
          
          <button 
            onClick={() => {
              console.log("📊 Current Stats:", stats);
              console.log("🐛 Debug Info:", debugInfo);
              showMessageBox("Stats Logged", "Check browser console for detailed debug info", "info", 3000);
            }}
            className="bg-zinc-800 border border-zinc-700 text-gray-300 px-6 py-3 rounded-lg hover:bg-zinc-700"
          >
            📋 Show Debug Info
          </button>
        </div>
        
        {/* Debug Info Panel */}
        {debugInfo.length > 0 && (
          <div className="mt-8 p-4 bg-zinc-900 border border-zinc-800 rounded-lg">
            <h3 className="text-lg font-semibold text-purple-400 mb-3">Debug Information</h3>
            <div className="space-y-1 text-sm text-gray-300 font-mono">
              {debugInfo.map((info, index) => (
                <div key={index} className="text-xs">
                  {typeof info === 'string' ? info : JSON.stringify(info)}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Quick Info */}
        <div className="mt-4 p-4 bg-zinc-900 border border-zinc-800 rounded-lg">
          <p className="text-sm text-gray-400">
            <strong>Note:</strong> This dashboard reads directly from Firestore. 
            Stats are calculated on-demand when you click Refresh.
          </p>
          <p className="text-xs text-gray-500 mt-2">
            __app_id: {__app_id}
          </p>
        </div>
      </div>
    </div>
  );
};

const FunnelStep = ({ label, value, percentage, color }) => (
  <div className="relative">
    <div className="flex items-center justify-between mb-2">
      <span className="text-gray-300 font-medium">{label}</span>
      <span className="text-gray-400">{value} ({percentage.toFixed(1)}%)</span>
    </div>
    <div className="w-full bg-zinc-800 rounded-full h-3">
      <div 
        className={`bg-${color}-500 h-3 rounded-full transition-all duration-500`}
        style={{ width: `${percentage}%` }}
      />
    </div>
  </div>
);

// --- START: LogoGridItem Component (For Inspector) ---
const LogoGridItem = ({ logo, index, onUpload, onRemove, onDragStart, onDragOver, onDragEnd, isDragOver, isDragging }) => {
    const fileInputRef = useRef(null);
    const handleUploadClick = () => fileInputRef.current.click();

    return (
        <div 
            className={`relative group aspect-square bg-zinc-800 rounded-md flex items-center justify-center border-2 border-dashed  transition-colors duration-200 ${isDragOver ? 'border-purple-500' : 'border-zinc-700'} ${isDragging ? 'opacity-30' : ''}`}
            draggable
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            onDragEnd={onDragEnd}
        >
            {logo.logoUrl ? (
                <img src={logo.logoUrl} alt="logo" className="max-w-full max-h-full object-contain pointer-events-none" />
            ) : (
                <button onClick={handleUploadClick} className="text-zinc-400 hover:text-white">
                    <Upload size={24} />
                </button>
            )}
            <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                onChange={onUpload}
                className="hidden"
            />
            {/* --- FIX: Added Numbering --- */}
            <span className="absolute top-1 left-1 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                {index + 1}
            </span>
            {logo.logoUrl && (
                 <button 
                    onClick={onRemove} 
                    className="absolute top-1 right-1 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Remove logo"
                 >
                    <X size={14} />
                </button>
            )}
        </div>
    );
};

// --- START: Inspector Panel Component ---

// --- START: Inspector Panel Helper Sub-components ---
const NumberStepper = ({ label, value, onCommit, onReset, isOverridden, min = 0, max = 100, step = 1, className = '' }) => {
    const [localValue, setLocalValue] = useState(value);

    useEffect(() => {
        setLocalValue(value);
    }, [value]);

    const handleBlur = () => {
        let finalValue = parseFloat(localValue);
        if (isNaN(finalValue)) {
            finalValue = value;
        } else {
            finalValue = Math.max(min, Math.min(max, finalValue));
        }
        setLocalValue(finalValue);
        if (finalValue !== value) {
            onCommit(finalValue);
        }
    };
    
    const handleKeyDown = (e) => {
        if (e.key === 'Enter') e.target.blur();
        else if (e.key === 'ArrowUp') { e.preventDefault(); handleStep(e, 1); }
        else if (e.key === 'ArrowDown') { e.preventDefault(); handleStep(e, -1); }
    }

    // HIGHLIGHT: Updated to accept the event `e`
    const handleStep = (e, direction) => {
        e.stopPropagation(); // This stops the click from closing the menu
        const newValue = Math.max(min, Math.min(max, value + (direction * step)));
        setLocalValue(newValue);
        onCommit(newValue); 
    };

    return (
        <div className={`flex items-center justify-between py-1 ${className}`}>
            <label className="text-[11px] text-zinc-500 font-medium">{label}</label>
            <div className="flex items-center gap-2">
                <div className="flex items-center bg-zinc-900 border border-zinc-800 rounded overflow-hidden">
                    <button onClick={(e) => handleStep(e, -1)} className="px-2.5 py-1.5 text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800 transition-colors text-sm leading-none">−</button>
                    <input
                        type="number"
                        value={localValue}
                        onChange={(e) => setLocalValue(e.target.value)}
                        onBlur={handleBlur}
                        onKeyDown={handleKeyDown}
                        className="w-12 text-center bg-transparent text-zinc-200 text-[11px] font-mono outline-none border-x border-zinc-800 py-1.5"
                        min={min}
                        max={max}
                        step={step}
                    />
                    <button onClick={(e) => handleStep(e, 1)} className="px-2.5 py-1.5 text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800 transition-colors text-sm leading-none">+</button>
                </div>
                {isOverridden && (
                    <button onClick={onReset} title="Reset to default" className="p-1 text-zinc-700 hover:text-zinc-400 transition-colors rounded">
                        <RotateCcw size={11} />
                    </button>
                )}
            </div>
        </div>
    );
};

// ============================================================================
// DURATION INPUT — seconds or minutes + seconds
// ============================================================================
// targetDuration is stored, previewed and rendered in whole seconds, but a
// credit roll is talked about in minutes ("make it about two and a half"), so
// users were doing the 150 ⇄ 2:30 conversion in their heads. This wraps the
// raw seconds value in a unit toggle: 'sec' is the original stepper, 'min'
// splits it into minutes and seconds fields. Only the DISPLAY changes — the
// committed value is always seconds, so nothing downstream (preview, render,
// export payload) needs to know this exists. Both readings are shown under the
// field either way, so there's never anything to work out.
//
// The chosen unit is remembered in localStorage and shared live between every
// duration field on screen (the inspector panel and the floating toolbar each
// mount their own), so flipping one flips them all.
const DURATION_UNIT_KEY = 'ctc.durationUnit';
const DURATION_UNIT_EVENT = 'ctc:duration-unit-change';

const readStoredDurationUnit = () => {
    try {
        return localStorage.getItem(DURATION_UNIT_KEY) === 'min' ? 'min' : 'sec';
    } catch {
        return 'sec'; // private mode / blocked storage — the toggle still works, it just won't persist
    }
};

const formatSecondsAsClock = (totalSeconds) => {
    const total = Math.max(0, Math.round(Number(totalSeconds) || 0));
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

const useDurationUnit = () => {
    const [unit, setUnit] = useState(readStoredDurationUnit);

    useEffect(() => {
        const sync = () => setUnit(readStoredDurationUnit());
        window.addEventListener(DURATION_UNIT_EVENT, sync);
        return () => window.removeEventListener(DURATION_UNIT_EVENT, sync);
    }, []);

    const changeUnit = (next) => {
        setUnit(next);
        try { localStorage.setItem(DURATION_UNIT_KEY, next); } catch { /* non-fatal */ }
        window.dispatchEvent(new Event(DURATION_UNIT_EVENT));
    };

    return [unit, changeUnit];
};

const DurationStepper = ({ value, onCommit, onReset, isOverridden, min = 0, max = 3600, step = 1, className = '' }) => {
    const [unit, setUnit] = useDurationUnit();
    const seconds = Math.max(0, Math.round(Number(value) || 0));

    const [minutesField, setMinutesField] = useState(String(Math.floor(seconds / 60)));
    const [secondsField, setSecondsField] = useState(String(seconds % 60));

    useEffect(() => {
        setMinutesField(String(Math.floor(seconds / 60)));
        setSecondsField(String(seconds % 60));
    }, [seconds]);

    // Commits minutes + seconds back as one clamped total in seconds. Fields
    // are always re-normalised from the clamped result, so typing "90" in the
    // seconds box rolls over to 1m 30s instead of sitting there invalid.
    const commitFields = (rawMinutes, rawSeconds) => {
        const m = parseInt(rawMinutes, 10);
        const s = parseFloat(rawSeconds);
        const total = (isNaN(m) ? 0 : m) * 60 + (isNaN(s) ? 0 : s);
        const clamped = Math.max(min, Math.min(max, Math.round(total)));
        setMinutesField(String(Math.floor(clamped / 60)));
        setSecondsField(String(clamped % 60));
        if (clamped !== seconds) onCommit(clamped);
    };

    const fieldClass = "w-14 text-center bg-zinc-900 border border-zinc-800 rounded text-zinc-200 text-[11px] font-mono outline-none focus:border-zinc-600 py-1.5";
    const unitButton = (target, label) => (
        <button
            key={target}
            onClick={(e) => { e.stopPropagation(); setUnit(target); }}
            title={target === 'sec' ? 'Enter the duration in seconds' : 'Enter the duration as minutes and seconds'}
            className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-[0.1em] transition-colors ${
                unit === target ? 'bg-zinc-100 text-zinc-900' : 'text-zinc-500 hover:text-zinc-300'
            }`}
        >
            {label}
        </button>
    );

    return (
        <div className={`space-y-1 ${className}`}>
            <div className="flex items-center justify-end gap-0.5 bg-zinc-900/70 border border-zinc-800 rounded-lg p-0.5 w-fit ml-auto">
                {unitButton('sec', 'Sec')}
                {unitButton('min', 'Min:Sec')}
            </div>

            {unit === 'sec' ? (
                <NumberStepper
                    label=""
                    value={seconds}
                    onCommit={onCommit}
                    onReset={onReset}
                    isOverridden={isOverridden}
                    min={min}
                    max={max}
                    step={step}
                />
            ) : (
                <div className="flex items-center justify-end gap-2 py-1">
                    <div className="flex items-center gap-1">
                        <input
                            type="number"
                            min={0}
                            max={Math.floor(max / 60)}
                            value={minutesField}
                            onChange={(e) => setMinutesField(e.target.value)}
                            onBlur={() => commitFields(minutesField, secondsField)}
                            onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                            className={fieldClass}
                            aria-label="Minutes"
                        />
                        <span className="text-[10px] text-zinc-500 font-bold">min</span>
                        <input
                            type="number"
                            min={0}
                            max={59}
                            value={secondsField}
                            onChange={(e) => setSecondsField(e.target.value)}
                            onBlur={() => commitFields(minutesField, secondsField)}
                            onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                            className={fieldClass}
                            aria-label="Seconds"
                        />
                        <span className="text-[10px] text-zinc-500 font-bold">sec</span>
                    </div>
                    {isOverridden && onReset && (
                        <button onClick={onReset} title="Reset to default" className="p-1 text-zinc-700 hover:text-zinc-400 transition-colors rounded">
                            <RotateCcw size={11} />
                        </button>
                    )}
                </div>
            )}

            <p className="text-[10px] text-zinc-500 font-mono text-right">
                {formatSecondsAsClock(seconds)} · {seconds}s
            </p>
        </div>
    );
};

const StyleControl = ({ label, children, onReset, isOverridden, className = '' }) => (
    <div className={`flex items-center justify-between py-1 ${className}`}>
        <label className="text-[11px] text-zinc-500 font-medium">{label}</label>
        <div className="flex items-center gap-2">
            {children}
            {isOverridden && (
                <button onClick={onReset} title="Reset to default style" className="p-1 text-zinc-700 hover:text-zinc-400 transition-colors rounded">
                    <RotateCcw size={11} />
                </button>
            )}
        </div>
    </div>
);


// This component exclusively handles the styling of a text layer.
// Replace your existing TextLayerEditor component with this updated version
const TextLayerEditor = ({ layer, index, totalLayers, updateLayer, removeLayer, reorderLayer }) => {
    const [isOpen, setIsOpen] = useState(true);
    const textareaRef = useRef(null);

    const handleUpdate = (field, value) => {
        updateLayer(index, { ...layer, [field]: value });
    };

    // Inject brackets around the highlighted text
    const handleReduceText = () => {
        const textarea = textareaRef.current;
        if (!textarea) return;
        
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        if (start === end) return; // Nothing selected
        
        const content = layer.content || '';
        const selectedText = content.substring(start, end);
        const newContent = content.substring(0, start) + '[' + selectedText + ']' + content.substring(end);
        
        handleUpdate('content', newContent);
        
        // Refocus and highlight the newly wrapped text
        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(start, end + 2); // +2 to account for the new brackets
        }, 0);
    };

    return (
        <div className="bg-zinc-950 p-3 rounded-lg border border-zinc-700 mb-3">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                     <div className="flex flex-col">
                        <button onClick={() => reorderLayer(index, 'up')} disabled={index === 0} className="disabled:opacity-20 disabled:cursor-not-allowed text-zinc-400 hover:text-white"><ChevronUp size={16} /></button>
                        <button onClick={() => reorderLayer(index, 'down')} disabled={index === totalLayers - 1} className="disabled:opacity-20 disabled:cursor-not-allowed text-zinc-400 hover:text-white"><ChevronDown size={16} /></button>
                    </div>
                    <span className="font-semibold text-zinc-300">Layer {index + 1}</span>
                </div>
                <div>
                    <button onClick={() => setIsOpen(!isOpen)} className="p-1 text-zinc-400 hover:text-white">
                        {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </button>
                    <button onClick={() => removeLayer(index)} className="p-1 text-red-500 hover:text-red-400">
                        <Trash2 size={18} />
                    </button>
                </div>
            </div>
            
            {isOpen && (
                <div className="mt-4 space-y-4">
                    <div>
                        {/* New Header Row with the Shrink Button */}
                        <div className="flex items-center justify-between mb-1.5">
                            <label className="block text-sm font-normal text-zinc-400">Text Content</label>
                            <button 
                                onClick={handleReduceText}
                                title="Wrap selected text to shrink its size (e.g. for P.G.A.)"
                                className="flex items-center gap-1.5 text-[9px] uppercase tracking-[0.2em] font-bold bg-zinc-900 text-purple-400 hover:bg-purple-900/30 hover:text-purple-300 px-2 py-1 rounded border border-zinc-800 transition-colors"
                            >
                                <span className="font-mono text-zinc-500">[ ]</span> Shrink
                            </button>
                        </div>
                        <textarea
                            ref={textareaRef}
                            value={layer.content || ''}
                            onChange={e => handleUpdate('content', e.target.value)}
                            rows="2"
                            className="w-full p-2 rounded-md border border-zinc-700 bg-zinc-800 text-gray-200 focus:ring-1 focus:ring-purple-500"
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

// 2. Add this new component right before the InspectorPanel component.
// This component exclusively handles the styling of a text layer.
const TextLayerStyleControl = ({ layer, index, updateLayer, settings, fontFamilyOptions, getWeightOptions, getStyleOptions }) => {
    
    const handleUpdate = (field, value) => {
        updateLayer(index, { ...layer, [field]: value });
    };

    const handleFontFamilyChange = (newFamilyKey) => {
        const FONT_DATA = { // A local copy for logic, assuming it's not passed down
            'arial': { name: 'Arial', variants: { '400': ['normal', 'italic'], '700': ['normal', 'italic'] } }, 'courier-new': { name: 'Courier New', variants: { '400': ['normal', 'italic'], '700': ['normal', 'italic'] } }, 'eb-garamond': { name: 'EB Garamond', variants: { '400': ['normal', 'italic'], '700': ['normal', 'italic'] } }, 'futura': { name: 'Futura', variants: { '300': ['normal', 'italic'], '400': ['normal', 'italic'], '700': ['normal', 'italic'] } }, 'garamond': { name: 'Garamond', variants: { '300': ['normal', 'italic'], '400': ['normal', 'italic'], '700': ['normal', 'italic'] } }, 'georgia': { name: 'Georgia', variants: { '400': ['normal', 'italic'], '700': ['normal', 'italic'] } }, 'helvetica': { name: 'Helvetica', variants: { '300': ['normal', 'italic'], '400': ['normal', 'italic'], '700': ['normal', 'italic'] } }, 'helvetica-rounded': { name: 'Helvetica Rounded', variants: { '700': ['normal'] } }, 'optima': { name: 'Optima', variants: { '400': ['normal', 'italic'], '700': ['normal', 'italic'] } }, 'palatino': { name: 'Palatino', variants: { '300': ['normal', 'italic'], '400': ['normal', 'italic'], '700': ['normal', 'italic'] } }, 'poppins': { name: 'Poppins', variants: { '100': ['normal'], '300': ['normal', 'italic'], '400': ['normal', 'italic'], '700': ['normal', 'italic'] } },
        };
        const familyData = FONT_DATA[newFamilyKey];
        let newLayer = { ...layer, fontFamily: newFamilyKey };
        
        if (familyData) {
            const availableWeights = Object.keys(familyData.variants);
            let newWeight = layer.fontWeight || '400';
            if (!availableWeights.includes(newWeight)) { 
                newWeight = availableWeights[0]; 
            }
            newLayer.fontWeight = newWeight;

            const availableStyles = familyData.variants[newWeight];
            let newStyle = layer.fontStyle || 'normal';
            if (!availableStyles.includes(newStyle)) { 
                newStyle = availableStyles[0]; 
            }
            newLayer.fontStyle = newStyle;
        }
        
        updateLayer(index, newLayer);
    };

    const handleFontWeightChange = (newWeight) => {
         const FONT_DATA = { // A local copy for logic
            'arial': { name: 'Arial', variants: { '400': ['normal', 'italic'], '700': ['normal', 'italic'] } }, 'courier-new': { name: 'Courier New', variants: { '400': ['normal', 'italic'], '700': ['normal', 'italic'] } }, 'eb-garamond': { name: 'EB Garamond', variants: { '400': ['normal', 'italic'], '700': ['normal', 'italic'] } }, 'futura': { name: 'Futura', variants: { '300': ['normal', 'italic'], '400': ['normal', 'italic'], '700': ['normal', 'italic'] } }, 'garamond': { name: 'Garamond', variants: { '300': ['normal', 'italic'], '400': ['normal', 'italic'], '700': ['normal', 'italic'] } }, 'georgia': { name: 'Georgia', variants: { '400': ['normal', 'italic'], '700': ['normal', 'italic'] } }, 'helvetica': { name: 'Helvetica', variants: { '300': ['normal', 'italic'], '400': ['normal', 'italic'], '700': ['normal', 'italic'] } }, 'helvetica-rounded': { name: 'Helvetica Rounded', variants: { '700': ['normal'] } }, 'optima': { name: 'Optima', variants: { '400': ['normal', 'italic'], '700': ['normal', 'italic'] } }, 'palatino': { name: 'Palatino', variants: { '300': ['normal', 'italic'], '400': ['normal', 'italic'], '700': ['normal', 'italic'] } }, 'poppins': { name: 'Poppins', variants: { '100': ['normal'], '300': ['normal', 'italic'], '400': ['normal', 'italic'], '700': ['normal', 'italic'] } },
        };
        const currentFontFamily = layer.fontFamily || settings.cardFontFamily || 'poppins';
        const familyData = FONT_DATA[currentFontFamily];
        let newLayer = { ...layer, fontWeight: newWeight };
        
        if (familyData && familyData.variants[newWeight]) {
            const availableStyles = familyData.variants[newWeight];
            const currentStyle = layer.fontStyle || 'normal';
            if (!availableStyles.includes(currentStyle)) { 
                newLayer.fontStyle = availableStyles[0]; 
            }
        }
        
        updateLayer(index, newLayer);
    };

    const currentFamilyKey = layer.fontFamily || settings.cardFontFamily || 'poppins';
    const currentWeight = layer.fontWeight || '400';

    return (
        <div className="bg-zinc-950 p-3 rounded-lg border border-zinc-700 mb-3">
             <h4 className="text-sm font-semibold text-zinc-300 border-b border-zinc-800 pb-2 mb-3">Layer {index + 1} Style</h4>
            <div className="space-y-3">
                <div>
                    <label className="block text-sm font-normal text-zinc-400 mb-1.5">Font Family</label>
                    <select
                        value={currentFamilyKey}
                        onChange={e => handleFontFamilyChange(e.target.value)}
                        className="w-full p-2 rounded-md border border-zinc-700 bg-zinc-800 text-gray-200 focus:ring-1 focus:ring-purple-500"
                    >
                       {fontFamilyOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-sm font-normal text-zinc-400 mb-1.5">Weight</label>
                        <select 
                            value={currentWeight} 
                            onChange={e => handleFontWeightChange(e.target.value)} 
                            className="w-full p-2 rounded-md border border-zinc-700 bg-zinc-800 text-gray-200 focus:ring-1 focus:ring-purple-500"
                        >
                            {getWeightOptions(currentFamilyKey).map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-normal text-zinc-400 mb-1.5">Style</label>
                        <select 
                            value={layer.fontStyle || 'normal'} 
                            onChange={e => handleUpdate('fontStyle', e.target.value)} 
                            className="w-full p-2 rounded-md border border-zinc-700 bg-zinc-800 text-gray-200 focus:ring-1 focus:ring-purple-500"
                        >
                            {getStyleOptions(currentFamilyKey, currentWeight).map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div>
                       <label className="block text-sm font-normal text-zinc-400 mb-1.5">Size (px)</label>
                       <input 
                            type="number" 
                            value={layer.fontSize || 48} 
                            onChange={e => handleUpdate('fontSize', parseFloat(e.target.value))}
                            className="w-full p-2 rounded-md border border-zinc-700 bg-zinc-800 text-gray-200 focus:ring-1 focus:ring-purple-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-normal text-zinc-400 mb-1.5">Transform</label>
                        <select 
                            value={layer.transform || 'normal'} 
                            onChange={e => handleUpdate('transform', e.target.value)} 
                            className="w-full p-2 rounded-md border border-zinc-700 bg-zinc-800 text-gray-200 focus:ring-1 focus:ring-purple-500"
                        >
                            <option value="normal">Normal</option>
                            <option value="uppercase">UPPERCASE</option>
                            <option value="lowercase">lowercase</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-normal text-zinc-400 mb-1.5">Color</label>
                        <input 
                            type="color" 
                            value={layer.color || '#ffffff'} 
                            onChange={e => handleUpdate('color', e.target.value)}
                            className="w-full h-10 p-1 bg-zinc-800 border border-zinc-700 rounded-md"
                         />
                    </div>
                </div>
            </div>
        </div>
    );
};


// REPLACE the entire InspectorPanel component with this corrected version.

// --- START: GlobalFontControl Component ---
// Provides a scoped font picker: Title Cards only, Scrolling only, or Both
const GlobalFontControl = ({ settings, fontFamilyOptions, handleDefaultFamilyChange, defaultFamily, compact = false }) => {
    const [fontScope, setFontScope] = React.useState('both');

    // Determine which font value to show in the selector based on current scope
    const getCurrentFontValue = () => {
        if (fontScope === 'cards') return settings.cardFontFamily || settings.fontFamily || defaultFamily;
        if (fontScope === 'scroll') return settings.scrollFontFamily || settings.fontFamily || defaultFamily;
        // 'both': show the shared font if they match, otherwise show fontFamily as the base
        return settings.fontFamily || defaultFamily;
    };

    const scopeOptions = [
        { value: 'both',   label: 'Both',         icon: '⬡' },
        { value: 'cards',  label: 'Title Cards',   icon: '◈' },
        { value: 'scroll', label: 'Scrolling',     icon: '≡' },
    ];

    // Check if fonts are diverged (different between cards and scroll)
    const cardFont = settings.cardFontFamily || settings.fontFamily;
    const scrollFont = settings.scrollFontFamily || settings.fontFamily;
    const areDiverged = cardFont !== scrollFont;

    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-zinc-400 font-medium">Font Family</span>
                {areDiverged && (
                    <span className="text-xs text-purple-400 bg-purple-900/30 px-1.5 py-0.5 rounded">Split</span>
                )}
            </div>

            {/* Scope Toggle */}
            <div className="flex bg-zinc-800 rounded-md p-0.5 gap-0.5">
                {scopeOptions.map(opt => (
                    <button
                        key={opt.value}
                        onClick={() => setFontScope(opt.value)}
                        title={opt.label}
                        className={`flex-1 text-xs py-1.5 px-1 rounded transition-all duration-150 truncate ${
                            fontScope === opt.value
                                ? 'bg-purple-600 text-white font-medium shadow'
                                : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700'
                        }`}
                    >
                        {compact ? opt.icon : opt.label}
                    </button>
                ))}
            </div>

            {/* Font Selector */}
            <div className="flex items-center gap-1.5">
                <select
                    value={getCurrentFontValue()}
                    onChange={(e) => handleDefaultFamilyChange(e.target.value, fontScope)}
                    className="flex-1 text-xs p-1.5 rounded border border-zinc-600 bg-zinc-800 text-gray-200 focus:ring-1 focus:ring-purple-500 min-w-0"
                >
                    {fontFamilyOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
                {/* Reset button */}
                {areDiverged && (
                    <button
                        onClick={() => handleDefaultFamilyChange(settings.fontFamily || defaultFamily, 'both')}
                        title="Reset both to same font"
                        className="p-1.5 text-zinc-500 hover:text-white hover:bg-zinc-700 rounded transition-colors flex-shrink-0"
                    >
                        <RotateCcw size={12} />
                    </button>
                )}
            </div>

            {/* Status hint */}
            {areDiverged && (
                <div className="text-xs text-zinc-500 space-y-0.5">
                    <div className="flex justify-between">
                        <span>Cards:</span>
                        <span className="text-zinc-300">{fontFamilyOptions.find(f => f.value === cardFont)?.label || cardFont}</span>
                    </div>
                    <div className="flex justify-between">
                        <span>Scrolling:</span>
                        <span className="text-zinc-300">{fontFamilyOptions.find(f => f.value === scrollFont)?.label || scrollFont}</span>
                    </div>
                </div>
            )}
        </div>
    );
};
// --- END: GlobalFontControl Component ---


const InspectorPanel = ({
    activeCredit, handleSuggestDuration, updateCredit, removeCredit, settings, setSettings,
    customFonts, addSpacerToGroup, addLogoToGroup, handleGroupedLogoUpload, removeLogoFromGrid,
    addTextItemToGroup, removeTextItemFromGroup, batchLogoUpload, handleLogoUpload,
    handleGridLogoUpload, handleFontUpload, copySettings, pasteSettings, copiedSettings,
    addTextLayer, updateTextLayer, removeTextLayer, reorderTextLayers,
    copyCardStyle, pasteCardStyle, copiedCardStyle, selectedCreditIds, bulkDeleteSelectedCredits,
    handleLogoDragStart, handleLogoDragOver, handleLogoDrop, handleLogoDragEnd, dragOverLogoInfo, draggedLogoInfo,
    activeLogoId, setActiveLogoId, handleDefaultFamilyChange, activeTab, setActiveTab, openLogoBoard, openSongBoard, resetAllCardDurations
}) => {
   
    const [openJustifyMenu, setOpenJustifyMenu] = useState(null);

    const FONT_DATA = {
        'arial': { name: 'Arial', variants: { '400': ['normal', 'italic'], '700': ['normal', 'italic'] } }, 'courier-new': { name: 'Courier New', variants: { '400': ['normal', 'italic'], '700': ['normal', 'italic'] } }, 'eb-garamond': { name: 'EB Garamond', variants: { '400': ['normal', 'italic'], '700': ['normal', 'italic'] } }, 'futura': { name: 'Futura', variants: { '300': ['normal', 'italic'], '400': ['normal', 'italic'], '700': ['normal', 'italic'] } }, 'garamond': { name: 'Garamond', variants: { '300': ['normal', 'italic'], '400': ['normal', 'italic'], '700': ['normal', 'italic'] } }, 'georgia': { name: 'Georgia', variants: { '400': ['normal', 'italic'], '700': ['normal', 'italic'] } }, 'helvetica': { name: 'Helvetica', variants: { '300': ['normal', 'italic'], '400': ['normal', 'italic'], '700': ['normal', 'italic'] } }, 'helvetica-rounded': { name: 'Helvetica Rounded', variants: { '700': ['normal'] } }, 'optima': { name: 'Optima', variants: { '400': ['normal', 'italic'], '700': ['normal', 'italic'] } }, 'palatino': { name: 'Palatino', variants: { '300': ['normal', 'italic'], '400': ['normal', 'italic'], '700': ['normal', 'italic'] } }, 'poppins': { name: 'Poppins', variants: { '100': ['normal'], '300': ['normal', 'italic'], '400': ['normal', 'italic'], '700': ['normal', 'italic'] } },
    };
    
    const DEFAULT_SETTINGS = {
        speed: 150, fontSize: 17, fontFamily: 'poppins', backgroundColor: '#000000', blockNameColor: '#ffffff', roleColor: '#ffffff', nameColor: '#ffffff', gutterWidth: 3, blockSpacing: 2, speedMode: 'auto', targetDuration: 120, cardAnimationType: 'fade', cardAnimationDuration: 1, fadeOutDuration: 1, scrollDirection: 'down', letterboxRatio: 'none', blockNameCaps: 'normal', roleCaps: 'normal', nameCaps: 'normal', textAlign: 'center', dualAlignment: 'classic', showAutoSaveNotice: false, cardsEnabled: true, cardSequence: 'before', cardFontFamily: 'poppins', scrollFontFamily: 'poppins', cardFontSize: 48, cardFontStyle: 'normal', cardDuration: 5, cardIntermissionDuration: 2, 
        headerFontSize: 24,
        headerFontWeight: '700', // Bold by default
        roleFontSize: 17,
        nameFontSize: 17,
        defaultFontWeight: '400', defaultFontStyle: 'normal', fps: '24',
    };

    const FONT_FAMILY_OPTIONS = Object.keys(FONT_DATA).map(key => ({
        value: key, label: FONT_DATA[key].name
    })).concat((customFonts || []).map(f => ({ value: f.name, label: f.name })));

    const getWeightOptions = (familyKey) => {
        const family = FONT_DATA[familyKey];
        if (!family) return [{ value: '400', label: 'Normal' }, { value: '700', label: 'Bold' }];
        const weightMap = { '100': 'Thin', '300': 'Light', '400': 'Normal', '700': 'Bold' };
        return Object.keys(family.variants).map(weight => ({ value: weight, label: weightMap[weight] || weight }));
    };

    const getStyleOptions = (familyKey, weight) => {
        const family = FONT_DATA[familyKey];
        if (!family || !family.variants || !family.variants[weight]) {
            return [{ value: 'normal', label: 'Normal' }, { value: 'italic', label: 'Italic' }];
        }
        return family.variants[weight].map(style => ({ 
            value: style, 
            label: style.charAt(0).toUpperCase() + style.slice(1) 
        }));
    };
    
    const resetGlobalSettings = (keysToReset) => {
        const newSettings = { ...settings };
        keysToReset.forEach(key => {
            newSettings[key] = DEFAULT_SETTINGS[key];
        });
        setSettings(newSettings);
    };

    if (selectedCreditIds.size > 1) {
        return (
            <div className="h-full w-full p-4 overflow-y-auto bg-zinc-950">
                <div className="bg-gradient-to-r from-blue-900/30 to-blue-800/20 border border-blue-700/50 rounded-lg p-4 mb-4">
                    <h2 className="text-lg font-medium text-blue-200 mb-2 flex items-center gap-2">
                        <Edit size={18}/> Multiple Items Selected
                    </h2>
                    <p className="text-sm text-blue-300/80 mb-4">You have {selectedCreditIds.size} items selected.</p>
                </div>
                
                <div className="space-y-3">
                    <button onClick={bulkDeleteSelectedCredits} className="w-full p-3 bg-red-700 text-white rounded-lg hover:bg-red-800 transition-colors flex items-center justify-center gap-2 text-sm font-medium">
                        <Trash2 size={18}/> Delete Selected ({selectedCreditIds.size})
                    </button>
                    <button 
                        onClick={copiedCardStyle ? pasteCardStyle : pasteSettings} 
                        disabled={!copiedSettings && !copiedCardStyle} 
                        className="w-full p-3 bg-zinc-100 text-black border border-transparent hover:bg-white transition-all flex items-center justify-center gap-2 text-xs font-semibold tracking-wide disabled:opacity-30 disabled:bg-zinc-900 disabled:text-zinc-600 disabled:border-zinc-800 disabled:cursor-not-allowed"
                    >
                        <ClipboardPaste size={18}/> Paste Style to All
                    </button>
                </div>
            </div>
        );
    }
    
    const isTitleCard = activeCredit?.creditDisplayType === 'card';
    const handleUpdate = (field, value) => { if (!activeCredit) return; updateCredit(activeCredit.id, field, value, null, isTitleCard); };
    
    // What is actually drawn for this credit right now (overrides + globals),
    // from the same resolver the renderer uses — every control below displays
    // these values, so the panel always matches the screen.
    const typo = activeCredit ? resolveCreditTypography(activeCredit, settings, isTitleCard) : null;

    // Only the field being changed (plus `override`) is written. It used to also
    // copy fontFamily/fontWeight/fontStyle onto the credit — which froze seeded
    // placeholders (e.g. fontFamily 'default') into real overrides.
    const handleStyleUpdate = (field, value) => {
        if (!activeCredit) return;
        const finalUpdates = { override: true, [field]: value };

        // Changing family: if the CURRENTLY OVERRIDDEN weight doesn't exist in the
        // new family, drop it so the credit follows the global weight again.
        if (field === 'fontFamily') {
            const familyData = FONT_DATA[value];
            const ownWeight = typo && typo.fontWeight.overridden ? String(typo.fontWeight.value) : null;
            if (familyData && ownWeight && !familyData.variants[ownWeight]) {
                finalUpdates.fontWeight = undefined;
            }
        }

        Object.keys(finalUpdates).forEach(key => {
            updateCredit(activeCredit.id, key, finalUpdates[key], null, isTitleCard);
        });
    };

    // [FIX] Was gated on `activeCredit.override`, a flag only ever set by handleStyleUpdate
    // (Style tab controls). Layout-tab fields like marginBottom go through plain handleUpdate,
    // which never sets that flag — so the reset arrow silently never appeared for them, even
    // though the per-credit value WAS overriding the global setting at render time. Checking
    // the field's own presence matches how gutterWidth/dualAlignment already do it correctly.
    const isOverridden = (field) => activeCredit?.[field] !== undefined;
    const Input = ({ label, children, className = '' }) => ( <div className={`mb-3 ${className}`}> <label className="block text-sm font-medium text-zinc-300 mb-2">{label}</label> {children} </div> );
    const Select = ({ value, onChange, options, ...props }) => ( <select value={value} onChange={onChange} {...props} className="w-full p-2.5 rounded-md border border-zinc-600 bg-zinc-800 text-gray-200 focus:ring-2 focus:ring-purple-500 shadow-sm transition"> {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)} </select> );

    const currentFamilyKey = typo ? typo.fontFamily.value : (settings.scrollFontFamily || settings.fontFamily);
    // Weight as actually drawn (snapped to the family's available weights), so
    // the Weight dropdown can never show a weight the font isn't rendering.
    const shownWeight = (w) => getRenderedWeightKey(currentFamilyKey, w, typo ? typo.fontStyle.value : 'normal');
    // Reset arrows only for real overrides (seeded placeholders don't count).
    const isStyleOverridden = (key) => !!(typo && typo[key] && typo[key].overridden);
    // "Default (Uppercase)" etc. — the default option names what default currently means.
    const CASE_LABELS = Object.fromEntries((CASE_OPTIONS || []).map(o => [o.value, o.label]));
    const defaultCaseLabel = (globalValue) => `Default (${CASE_LABELS[globalValue || 'normal'] || globalValue || 'Normal'})`;
    
   const TabButton = ({ tabName, label }) => (
        <button
            onMouseDown={() => setActiveTab(tabName)} // <-- CHANGED from onClick
            className={`flex-1 pb-3 pt-3 text-[11px] font-semibold tracking-widest uppercase transition-all duration-200 border-b-2 ${
                activeTab === tabName 
                ? 'text-zinc-100 border-zinc-300' 
                : 'text-zinc-600 border-transparent hover:text-zinc-400 hover:border-zinc-700'
            }`}
        >
            {label}
        </button>
    );

    const renderContentTab = () => (
        <div className="space-y-4">
            {['dual', 'multiple', 'single', 'title'].includes(activeCredit.type) && !isTitleCard && !(activeCredit.type === 'multiple' && activeCredit.multipleMode === 'song') && (
                <EnhancedCollapsibleSection title="Text Content" icon={Type} priority="high" defaultOpen={true}>
                    {(activeCredit.type === 'dual' || activeCredit.type === 'multiple') && ( 
                        <Input label="Role / Department"> 
                            <textarea 
                                key={`role-${activeCredit.id}`} 
                                rows="2" 
                                defaultValue={activeCredit.role || ''} 
                                onBlur={e => handleUpdate('role', e.target.value)} 
                                className="w-full p-2.5 rounded-md border border-zinc-600 bg-zinc-800 text-gray-200 focus:ring-2 focus:ring-purple-500"
                            />
                        </Input> 
                    )}
                    
                    <Input label={activeCredit.type === 'multiple' ? "Names (one per line)" : "Name / Content"}>
                        <textarea
                            key={`name-${activeCredit.id}`}
                            rows="4"
                            defaultValue={activeCredit.name || ''}
                            onBlur={e => handleUpdate('name', e.target.value)}
                            placeholder="Paste a list of names here..."
                            className="w-full p-2.5 rounded-md border border-zinc-600 bg-zinc-800 text-gray-200 focus:ring-2 focus:ring-purple-500"
                        />
                    </Input>
                </EnhancedCollapsibleSection>
            )}

            {activeCredit.type === 'multiple' && activeCredit.multipleMode === 'song' && (
                <EnhancedCollapsibleSection title="Song Credits" icon={Music} priority="high" defaultOpen={true}>
                    <div className="space-y-3">
                        <Input label="Section Header (optional)">
                            <input
                                type="text"
                                key={`song-role-${activeCredit.id}`}
                                defaultValue={activeCredit.role || ''}
                                onBlur={e => handleUpdate('role', e.target.value)}
                                placeholder="e.g. Original Songs"
                                className="w-full p-2.5 rounded-md border border-zinc-600 bg-zinc-800 text-gray-200 focus:ring-2 focus:ring-purple-500"
                            />
                        </Input>

                        <p className="text-xs text-zinc-400">
                            {(() => {
                                const count = activeCredit.name ? activeCredit.name.split('\n').filter(l => l.trim()).length : 0;
                                if (count === 0) return 'No songs yet — open the Song Board to add one.';
                                if (count > 3) return `${count} songs in this block — over the 3-song limit. Open the Song Board to trim it down, or split the extras into a new Song Credits block.`;
                                return `${count} of 3 songs in this block.`;
                            })()}
                        </p>
                        <button onClick={() => openSongBoard(activeCredit.id)} className="w-full flex items-center justify-center p-2.5 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors font-medium">
                            <Music size={16} className="mr-2"/> {(!activeCredit.name || !activeCredit.name.trim()) ? 'Open Song Board' : 'Edit in Song Board'}
                        </button>

                        <NumberStepper
                            label="Space Between Songs (rem)"
                            value={activeCredit.columnGap ?? 2}
                            onCommit={value => handleUpdate('columnGap', value)}
                            min={0.1} max={10} step={0.1}
                        />

                        <Input label="Bold">
                            <div className="flex items-center space-x-1 bg-zinc-800 p-1 rounded-md">
                                {[{ v: false, label: 'Song Title' }, { v: true, label: 'Credit Info' }].map(item => (
                                    <button
                                        key={String(item.v)}
                                        onClick={() => handleUpdate('songSwapBold', item.v)}
                                        className={`flex-1 p-2 rounded-md text-sm font-medium transition-colors ${(!!activeCredit.songSwapBold) === item.v ? 'bg-purple-600 text-white' : 'hover:bg-zinc-700'}`}
                                    >
                                        {item.label}
                                    </button>
                                ))}
                            </div>
                            <p className="text-[11px] text-zinc-500 mt-1">Only changes which line is bold — Song Title's color/caps/font always come from "Song Title Typography" and Credit Info's always come from "Credit Info Typography" in the Style tab, no matter what you pick here. (If both Typography sections have the same Font Weight, this toggle won't look like it's doing anything — give them different weights there first.)</p>
                        </Input>
                    </div>
                </EnhancedCollapsibleSection>
            )}

            {isTitleCard && (
                <>
                    <EnhancedCollapsibleSection title="Text Layers" icon={Layers} priority="high" defaultOpen={true}>
                        <div className="space-y-2">
                            {(activeCredit.textLayers || []).map((layer, index) => ( 
                                <TextLayerEditor 
                                    key={layer.id} 
                                    layer={layer} 
                                    index={index} 
                                    totalLayers={(activeCredit.textLayers || []).length} 
                                    updateLayer={(i, nl) => updateTextLayer(activeCredit.id, i, nl)} 
                                    removeLayer={(i) => removeTextLayer(activeCredit.id, i)} 
                                    reorderLayer={(i, d) => reorderTextLayers(activeCredit.id, i, d)} 
                                />
                            ))}
                        </div>
                        <button onClick={() => addTextLayer(activeCredit.id)} className="mt-3 w-full flex items-center justify-center p-2.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
                            <Plus size={16} className="mr-2"/> Add Text Layer
                        </button>
                    </EnhancedCollapsibleSection>
                    
                    <EnhancedCollapsibleSection title="Card Settings" icon={Timer} priority="normal" defaultOpen={true}>
                       <div className="space-y-3">
                            <NumberStepper 
                                label="Duration (s)"
                                value={activeCredit.duration || settings.cardDuration} 
                                onCommit={value => handleUpdate('duration', value)}
                                onReset={() => handleUpdate('duration', undefined)}
                                isOverridden={activeCredit.duration !== undefined && activeCredit.duration !== null}
                                min={1} max={15} step={0.5}
                            />
                            {activeCredit.duration !== undefined && activeCredit.duration !== null && (
                                <p className="text-[10px] text-zinc-600 -mt-2">Overriding the global Card Duration ({settings.cardDuration || 5}s). Click the reset icon to follow global again.</p>
                            )}
                        </div>
                    </EnhancedCollapsibleSection>
                </>
            )}

            {activeCredit.type === 'logo' && (
                <EnhancedCollapsibleSection title="Logo Configuration" icon={Image} priority="high" defaultOpen={true}>
                    <div className="space-y-3">
                        <Input label="Logo Layout">
                            <Select
                                value={activeCredit.layout || 'single'}
                                onChange={e => { handleUpdate('layout', e.target.value); setActiveLogoId(null); }}
                                options={[
                                    // Single & Grid are retired from new selection — Logo Board covers both
                                    // (one row/one logo = old Single, several per row = old Grid) with more
                                    // flexibility. Still shown here (not offered elsewhere) so a credit that's
                                    // already on one of these keeps rendering & editing exactly as before.
                                    ...(activeCredit.layout === 'single' ? [{ value: 'single', label: 'Single Logo (legacy)' }] : []),
                                    ...(activeCredit.layout === 'grid' ? [{ value: 'grid', label: 'Logo Grid (legacy)' }] : []),
                                    { value: 'rows', label: 'Logo Board (Rows)' },
                                    { value: 'full-width', label: 'Full Width Title' },
                                ]}
                            />
                        </Input>
                        
                        {(activeCredit.layout === 'single' || activeCredit.layout === 'full-width') && (
                            <>
                                <Input label="Alignment">
                                    <div className="flex items-center space-x-1 bg-zinc-800 p-1 rounded-md">
                                        {[{v:'flex-start', i: AlignLeft}, {v:'center', i: AlignCenter}, {v:'flex-end', i: AlignRight}].map(item => (
                                            <button key={item.v} onClick={() => handleUpdate('logoAlignment', item.v)} className={`flex-1 p-2 rounded-md flex justify-center items-center transition-colors ${((activeCredit.logoAlignment || 'center') === item.v) ? 'bg-purple-600 text-white' : 'hover:bg-zinc-700'}`}>
                                                <item.i size={18} />
                                            </button>
                                        ))}
                                    </div>
                                </Input>
                                <Input label={activeCredit.layout === 'full-width' ? "Upload Full-Width Logo" : "Upload Logo"}>
                                    <input type="file" accept="image/*" onChange={(e) => handleLogoUpload(activeCredit.id, e)} className="block w-full text-sm text-zinc-300 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-zinc-700 file:text-purple-300 hover:file:bg-zinc-600 cursor-pointer"/>
                                </Input>
                                {activeCredit.logoUrl && <img src={activeCredit.logoUrl} alt="logo preview" className="w-32 mx-auto rounded-md bg-zinc-700 p-2 my-2"/>}
                            </>
                        )}

                        {activeCredit.layout === 'rows' && (
                            <div className="space-y-3">
                                <p className="text-xs text-zinc-400">
                                    {(activeCredit.rows || []).length === 0
                                        ? "No rows yet — open the Logo Board to arrange logos into rows (up to 5 per row), each row independent of the others."
                                        : `${(activeCredit.rows || []).length} row${(activeCredit.rows || []).length === 1 ? '' : 's'}, ${(activeCredit.rows || []).reduce((sum, r) => sum + (r.logos || []).length, 0)} logo${(activeCredit.rows || []).reduce((sum, r) => sum + (r.logos || []).length, 0) === 1 ? '' : 's'} total.`}
                                </p>
                                <button onClick={() => openLogoBoard(activeCredit.id)} className="w-full flex items-center justify-center p-2.5 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors font-medium">
                                    <Grid3x3 size={16} className="mr-2"/> {(activeCredit.rows || []).length === 0 ? 'Open Logo Board' : 'Edit in Logo Board'}
                                </button>
                                <NumberStepper 
                                    label="Block Width (%)"
                                    value={activeCredit.blockWidth ?? 90} 
                                    onCommit={value => handleUpdate('blockWidth', value)}
                                    min={20} max={100} step={1}
                                />
                                <NumberStepper 
                                    label="Row/Logo Spacing (rem)"
                                    value={activeCredit.gridGap || 1} 
                                    onCommit={value => handleUpdate('gridGap', value)}
                                    min={0} max={5} step={0.1}
                                />
                                <NumberStepper 
                                    label="Overall Size (%)"
                                    value={activeCredit.logoScale ?? 100} 
                                    onCommit={value => handleUpdate('logoScale', value)}
                                    min={25} max={250} step={5}
                                />
                                <p className="text-[11px] text-zinc-500 -mt-2">Scales every logo in this board at once. Use the size options inside the Logo Board (X-Small–X-Large) for individual logos that need to stand out from the rest.</p>
                            </div>
                        )}

                        {activeCredit.layout === 'grid' && (
                            <EnhancedCollapsibleSection title="Grid Setup" icon={Grid3x3} defaultOpen={true}>
                                <div className="space-y-3">
                                    <div className="grid grid-cols-2 gap-3">
                                        <Input label="Columns">
                                            <Select value={activeCredit.gridColumns || 3} onChange={e => handleUpdate('gridColumns', parseInt(e.target.value))} options={[{ value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }, { value: 5, label: '5' }]}/>
                                        </Input>
                                        <NumberStepper 
                                            label="Block Width (%)"
                                            value={activeCredit.blockWidth ?? 90} 
                                            onCommit={value => handleUpdate('blockWidth', value)}
                                            min={20} max={100} step={1}
                                        />
                                    </div>
                                    <NumberStepper 
                                        label="Logo Spacing (rem)"
                                        value={activeCredit.gridGap || 1} 
                                        onCommit={value => handleUpdate('gridGap', value)}
                                        min={0} max={5} step={0.1}
                                    />
                                    
                                    <div className="space-y-2">
                                        <p className="text-xs text-zinc-400 mb-2">Click a logo in the preview to edit individually.</p>
                                        <Input label="Batch Upload & Add">
                                            <input type="file" accept="image/*" multiple onChange={(e) => batchLogoUpload(e, activeCredit.id)} className="block w-full text-sm text-zinc-300 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-zinc-700 file:text-purple-300 hover:file:bg-zinc-600 cursor-pointer"/>
                                        </Input>
                                        <button onClick={() => addLogoToGroup(activeCredit.id)} className="w-full flex items-center justify-center p-2.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
                                            <Plus size={16} className="mr-2"/> Add Logo Slot
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <button onClick={() => addLogoToGroup(activeCredit.id)} className="w-full flex items-center justify-center p-2.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm">
                                            <ImagePlus size={16} className="mr-2"/> Add Logo Slot
                                        </button>
                                        <button onClick={() => addSpacerToGroup(activeCredit.id)} className="w-full flex items-center justify-center p-2.5 bg-zinc-700 text-zinc-200 rounded-md hover:bg-zinc-600 transition-colors text-sm">
                                            <SquarePlus size={16} className="mr-2"/> Add Spacer
                                        </button>
                                    </div>
                                </div>
                            </EnhancedCollapsibleSection>
                        )}

                        {activeLogoId && activeCredit.layout === 'grid' ? (
                            (() => {
                                const selectedLogo = activeCredit.logos.find(l => l.id === activeLogoId);
                                if (!selectedLogo) return null;
                                return (
                                    <EnhancedCollapsibleSection title={`Logo #${activeCredit.logos.findIndex(l => l.id === activeLogoId) + 1} Settings`} icon={SlidersHorizontal} defaultOpen={true}>
                                    <div className="space-y-4 p-2">
                                        {/* --- Existing Controls --- */}
                                        <Input label="Individual Size">
                                            <Select value={selectedLogo.size || 'medium'} onChange={e => updateCredit(activeCredit.id, 'size', e.target.value, activeLogoId)} options={[{ value: 'small', label: 'Small' }, { value: 'medium', label: 'Medium (Default)' }, { value: 'large', label: 'Large' }]} />
                                        </Input>
                                        <NumberStepper 
                                            label="Individual Opacity (%)"
                                            value={selectedLogo.opacity ?? 100} 
                                            onCommit={value => updateCredit(activeCredit.id, 'opacity', value, activeLogoId)}
                                            min={0} max={100} step={1}
                                        />

                                        {/* --- THIS IS THE NEW SECTION --- */}
                                        <div>
                                            <label className="block text-sm font-normal text-zinc-400 mb-2">Position Adjustment</label>
                                            <div className="grid grid-cols-3 gap-2 items-center justify-items-center bg-zinc-800 p-2 rounded-lg">
                                                <div></div>
                                                <button onClick={() => updateCredit(activeCredit.id, 'offsetY', (selectedLogo.offsetY || 0) - 1, activeLogoId)} className="p-2 bg-zinc-700 rounded-md hover:bg-zinc-600"><ArrowUp size={16} /></button>
                                                <div></div>

                                                <button onClick={() => updateCredit(activeCredit.id, 'offsetX', (selectedLogo.offsetX || 0) - 1, activeLogoId)} className="p-2 bg-zinc-700 rounded-md hover:bg-zinc-600"><ArrowLeft size={16} /></button>
                                                <button onClick={() => { updateCredit(activeCredit.id, 'offsetY', 0, activeLogoId); updateCredit(activeCredit.id, 'offsetX', 0, activeLogoId); }} className="p-2 bg-zinc-700 rounded-md hover:bg-zinc-600"><RotateCcw size={14} /></button>
                                                <button onClick={() => updateCredit(activeCredit.id, 'offsetX', (selectedLogo.offsetX || 0) + 1, activeLogoId)} className="p-2 bg-zinc-700 rounded-md hover:bg-zinc-600"><ArrowRight size={16} /></button>

                                                <div></div>
                                                <button onClick={() => updateCredit(activeCredit.id, 'offsetY', (selectedLogo.offsetY || 0) + 1, activeLogoId)} className="p-2 bg-zinc-700 rounded-md hover:bg-zinc-600"><ArrowDown size={16} /></button>
                                                <div></div>
                                            </div>
                                        </div>
                                    </div>
                                </EnhancedCollapsibleSection>
                                )
                            })()
                        ) : (
                            <EnhancedCollapsibleSection title="Appearance (All Logos)" icon={Settings2} defaultOpen={true}>
                                <div className="space-y-3">
                                    {activeCredit.layout !== 'full-width' && ( 
                                        <Input label="Logo Size">
                                            <Select value={activeCredit.logoSize || 'medium'} onChange={e => handleUpdate('logoSize', e.target.value)} options={[{ value: 'small', label: 'Small' }, { value: 'medium', label: 'Medium' }, { value: 'large', label: 'Large' }]} />
                                        </Input> 
                                    )}
                                    <NumberStepper 
                                        label="Opacity (%)"
                                        value={activeCredit.logoOpacity || 100} 
                                        onCommit={value => handleUpdate('logoOpacity', value)}
                                        min={0} max={100} step={1}
                                    />
                                </div>
                            </EnhancedCollapsibleSection>
                        )}
                    </div>
                </EnhancedCollapsibleSection>
            )}
        </div>
    );

   const renderStyleTab = () => (
    <div className="space-y-4">
        {isTitleCard && (
            // This part for Title Cards remains the same
            <EnhancedCollapsibleSection title="Text Layer Styles" icon={Palette} priority="high" defaultOpen={true}>
                <div className="space-y-2">
                    {(activeCredit.textLayers || []).map((layer, index) => (
                        <TextLayerStyleControl
                            key={layer.id}
                            layer={layer}
                            index={index}
                            updateLayer={(i, nl) => updateTextLayer(activeCredit.id, i, nl)}
                            settings={settings}
                            fontFamilyOptions={FONT_FAMILY_OPTIONS}
                            getWeightOptions={getWeightOptions}
                            getStyleOptions={getStyleOptions}
                        />
                    ))}
                </div>
            </EnhancedCollapsibleSection>
        )}

        {/* --- THIS IS THE UPDATED SECTION FOR SCROLLING CREDITS --- */}
        {!isTitleCard && (activeCredit.type === 'dual' || activeCredit.type === 'multiple') && (() => {
            const isSong = activeCredit.type === 'multiple' && activeCredit.multipleMode === 'song';
            const roleTypographyTitle = isSong ? 'Credit Info Typography' : 'Role Typography';
            const nameTypographyTitle = isSong ? 'Song Title Typography' : 'Name Typography';
            return (
            <>
                <EnhancedCollapsibleSection title={roleTypographyTitle} icon={Type} priority="high" defaultOpen={true}>
                    <div className="space-y-3">
                        <NumberStepper 
                            label="Font Size"
                            value={typo.roleFontSize.value}
                            onCommit={(value) => handleStyleUpdate('roleFontSize', value)}
                            onReset={() => handleUpdate('roleFontSize', undefined)}
                            isOverridden={isStyleOverridden('roleFontSize')}
                            min={8} max={120} step={1}
                        />
                         <StyleControl
                            label="Font Weight"
                            onReset={() => handleUpdate('roleFontWeight', undefined)}
                            isOverridden={isStyleOverridden('roleFontWeight')}
                        >
                            <Select 
                                value={shownWeight(typo.roleFontWeight.value)}
                                options={getWeightOptions(currentFamilyKey)}
                                onChange={(e) => handleStyleUpdate('roleFontWeight', e.target.value)}
                                className="w-full"
                            />
                        </StyleControl>
                    </div>
                    {/* --- ADD THIS TRANSFORM CONTROL --- */}
                        <StyleControl
                            label="Transform"
                            onReset={() => handleUpdate('roleTransform', undefined)}
                            isOverridden={isStyleOverridden('roleTransform')}
                        >
                            <Select
                                value={typo.roleTransform.overridden ? typo.roleTransform.value : 'default'} // 'default' = following global
                                options={[{ value: 'default', label: defaultCaseLabel(settings.roleCaps) }, ...CASE_OPTIONS]}
                                onChange={(e) => handleStyleUpdate('roleTransform', e.target.value === 'default' ? undefined : e.target.value)}
                                className="w-full"
                            />
                        </StyleControl>
                        {/* --- END ADDITION --- */}
                </EnhancedCollapsibleSection>
                <EnhancedCollapsibleSection title={nameTypographyTitle} icon={Type} priority="normal" defaultOpen={true}>
                    <div className="space-y-3">
                        <NumberStepper 
                            label="Font Size"
                            value={typo.nameFontSize.value}
                            onCommit={(value) => handleStyleUpdate('nameFontSize', value)}
                            onReset={() => handleUpdate('nameFontSize', undefined)}
                            isOverridden={isStyleOverridden('nameFontSize')}
                            min={8} max={120} step={1}
                        />
                         <StyleControl
                            label="Font Weight"
                            onReset={() => handleUpdate('nameFontWeight', undefined)}
                            isOverridden={isStyleOverridden('nameFontWeight')}
                        >
                            <Select 
                                value={shownWeight(typo.nameFontWeight.value)}
                                options={getWeightOptions(currentFamilyKey)}
                                onChange={(e) => handleStyleUpdate('nameFontWeight', e.target.value)}
                                className="w-full"
                            />
                        </StyleControl>
                        {/* --- ADD THIS TRANSFORM CONTROL --- */}
                        <StyleControl
                            label="Transform"
                            onReset={() => handleUpdate('nameTransform', undefined)}
                            isOverridden={isStyleOverridden('nameTransform')}
                        >
                            <Select
                                value={typo.nameTransform.overridden ? typo.nameTransform.value : 'default'} // 'default' = following global
                                options={[{ value: 'default', label: defaultCaseLabel(settings.nameCaps) }, ...CASE_OPTIONS]}
                                onChange={(e) => handleStyleUpdate('nameTransform', e.target.value === 'default' ? undefined : e.target.value)}
                                className="w-full"
                            />
                        </StyleControl>
                        {/* --- END ADDITION --- */}
                    </div>
                </EnhancedCollapsibleSection>
            </>
            );
        })()}

        {!isTitleCard && (activeCredit.type === 'title' || activeCredit.type === 'single') && (
            <EnhancedCollapsibleSection title="Header/Line Typography" icon={Type} priority="high" defaultOpen={true}>
                <div className="space-y-3">
                    <NumberStepper 
                        label="Font Size"
                        // A header draws at the global Header size; a single line at the base size.
                        value={activeCredit.type === 'title' ? typo.headerFontSize.value : typo.fontSize.value} 
                        onCommit={(value) => handleStyleUpdate('fontSize', value)}
                        onReset={() => handleUpdate('fontSize', undefined)}
                        isOverridden={isStyleOverridden('fontSize')}
                        min={8} max={120} step={1}
                    />
                    <StyleControl
                        label="Font Weight"
                        onReset={() => handleUpdate('fontWeight', undefined)}
                        isOverridden={isStyleOverridden('fontWeight')}
                    >
                        <Select 
                            value={shownWeight(activeCredit.type === 'title' ? typo.headerFontWeight.value : typo.fontWeight.value)} 
                            options={getWeightOptions(currentFamilyKey)}
                            onChange={(e) => handleStyleUpdate('fontWeight', e.target.value)}
                            className="w-full"
                        />
                    </StyleControl>
                </div>
            </EnhancedCollapsibleSection>
        )}
        
        {!isTitleCard && (
             <EnhancedCollapsibleSection title="Shared Styles" icon={Palette} priority="low" defaultOpen={true}>
                <div className="space-y-3">
                    <StyleControl
                        label="Font Family"
                        onReset={() => handleUpdate('fontFamily', undefined)}
                        isOverridden={isStyleOverridden('fontFamily')}
                    >
                        <Select 
                            value={currentFamilyKey} 
                            options={FONT_FAMILY_OPTIONS}
                            onChange={(e) => handleStyleUpdate('fontFamily', e.target.value)}
                            className="w-48"
                        />
                    </StyleControl>
                    {/* Color controls map to the field each credit type actually
                        draws with: headers use the Header color, single lines the
                        Name color, two-column / multi-name blocks both. (This used
                        to always edit roleColor, which a header never uses.) */}
                    {(activeCredit.type === 'title'
                        ? [{ key: 'blockNameColor', label: 'Text Color' }]
                        : activeCredit.type === 'single'
                            ? [{ key: 'nameColor', label: 'Text Color' }]
                            : [{ key: 'roleColor', label: activeCredit.type === 'multiple' ? 'Header Color' : 'Role Color' }, { key: 'nameColor', label: 'Name Color' }]
                    ).map(({ key, label }) => (
                        <QuickColorPicker
                            key={key}
                            label={label}
                            value={typo[key].value}
                            onChange={(value) => handleStyleUpdate(key, value)}
                            onReset={() => handleUpdate(key, undefined)}
                            isOverridden={isStyleOverridden(key)}
                        />
                    ))}
                </div>
             </EnhancedCollapsibleSection>
        )}
    </div>
);

    const renderLayoutTab = () => (
        <div className="space-y-4">
            {isTitleCard && (
                 <EnhancedCollapsibleSection title="Card Position" icon={MoveHorizontal} defaultOpen={true}>
                   <div className="space-y-3">
                        <NumberStepper 
                            label="Horizontal Position (%)"
                            value={activeCredit.cardPositionX || 50} 
                            onCommit={value => handleUpdate('cardPositionX', value)}
                            min={0} max={100} step={1}
                        />
                        <NumberStepper 
                            label="Vertical Position (%)"
                            value={activeCredit.cardPositionY || 50} 
                            onCommit={value => handleUpdate('cardPositionY', value)}
                            min={0} max={100} step={1}
                        />
                        {/* Reset Layout button — snaps position back to dead center (50/50) */}
                        <button
                            onClick={() => {
                                handleUpdate('cardPositionX', 50);
                                handleUpdate('cardPositionY', 50);
                            }}
                            className="w-full py-1.5 px-3 rounded-md bg-zinc-700 hover:bg-zinc-600 text-zinc-300 hover:text-white text-xs font-medium transition-colors flex items-center justify-center gap-1.5"
                            title="Reset position to center"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
                            Reset to Center
                        </button>
                        <NumberStepper 
                            label="Duration (s)"
                            value={activeCredit.duration || settings.cardDuration} 
                            onCommit={value => handleUpdate('duration', value)}
                            onReset={() => handleUpdate('duration', undefined)}
                            isOverridden={activeCredit.duration !== undefined && activeCredit.duration !== null}
                            min={1} max={15} step={0.5}
                        />
                        <NumberStepper 
                            label="Layer Spacing (rem)"
                            value={activeCredit.cardVerticalSpacing || 0.5} 
                            onCommit={value => handleUpdate('cardVerticalSpacing', value)}
                            min={0} max={10} step={0.1}
                        />
                        {/* --- END OF FIX --- */}

                        {/* NAME GAP — how wide a run of 2+ typed spaces renders on this
                            card (e.g. "BRIAN ROME  HEATHER KLEINPETER"). Natural = the
                            typed spaces as-is; Custom = a fixed width in em of each
                            line's font size, so it scales with the text and matches
                            between the editor preview and every export resolution.
                            Stored as credit.cardNameGap (undefined = Natural). */}
                        {(() => {
                            const nameGap = activeCredit.cardNameGap;
                            const gapIsCustom = nameGap !== undefined && nameGap !== null && nameGap !== '';
                            const cardHasGap = (activeCredit.textLayers || []).some(l => / {2,}/.test(l.content || ''));
                            return (
                                <div className="pt-1">
                                    <Input label="Name Gap" className="mb-2">
                                        <div className="flex items-center space-x-1 bg-zinc-800 p-1 rounded-md">
                                            <button
                                                onClick={() => handleUpdate('cardNameGap', undefined)}
                                                title="Gap is exactly as wide as the spaces you typed"
                                                className={`flex-1 py-1.5 px-2 rounded-md text-xs font-medium transition-colors ${!gapIsCustom ? 'bg-purple-600 text-white' : 'text-zinc-400 hover:bg-zinc-700'}`}
                                            >
                                                Natural
                                            </button>
                                            <button
                                                onClick={() => { if (!gapIsCustom) handleUpdate('cardNameGap', 1.5); }}
                                                title="Set an exact gap width, regardless of how many spaces you typed"
                                                className={`flex-1 py-1.5 px-2 rounded-md text-xs font-medium transition-colors ${gapIsCustom ? 'bg-purple-600 text-white' : 'text-zinc-400 hover:bg-zinc-700'}`}
                                            >
                                                Custom
                                            </button>
                                        </div>
                                    </Input>
                                    {gapIsCustom && (
                                        <NumberStepper
                                            label="Gap Width (em)"
                                            value={Number(nameGap)}
                                            onCommit={value => handleUpdate('cardNameGap', value)}
                                            onReset={() => handleUpdate('cardNameGap', undefined)}
                                            isOverridden={true}
                                            min={0} max={20} step={0.25}
                                        />
                                    )}
                                    <p className={`text-[10px] leading-snug mt-1 ${cardHasGap ? 'text-zinc-500' : 'text-amber-400/80'}`}>
                                        {cardHasGap
                                            ? (gapIsCustom
                                                ? 'Every run of 2+ spaces on this card renders at this width. 1 em ≈ the width of a capital letter at that line’s size.'
                                                : 'Gaps render exactly as wide as the spaces you typed. Switch to Custom to set an exact width.')
                                            : 'No gap on this card yet. Type two spaces between names (e.g. BRIAN ROME  HEATHER KLEINPETER) to create one.'}
                                    </p>
                                </div>
                            );
                        })()}
                        
                        <Input label="Text Alignment"> 
                            <div className="flex items-center space-x-1 bg-zinc-800 p-1 rounded-md"> 
                                {['left', 'center', 'right'].map(align => ( 
                                    <button key={align} onClick={() => handleUpdate('cardTextAlign', align)} className={`flex-1 p-2 rounded-md flex justify-center items-center transition-colors ${((activeCredit.cardTextAlign || 'center') === align) ? 'bg-purple-600 text-white' : 'hover:bg-zinc-700'}`}> 
                                        {align === 'left' ? <AlignLeft size={18} /> : align === 'center' ? <AlignCenter size={18} /> : <AlignRight size={18} />} 
                                    </button> 
                                ))} 
                            </div> 
                        </Input>
                    </div>
                </EnhancedCollapsibleSection>
            )}

            {!isTitleCard && activeCredit.type !== 'logo' && (
                <EnhancedCollapsibleSection title="Alignment & Spacing" icon={Grid3x3} defaultOpen={true}>
                    <div className="space-y-3">
                        {['single', 'title'].includes(activeCredit.type) && ( 
                            <Input label="Block Alignment"> 
                                <div className="flex items-center space-x-1 bg-zinc-800 p-1 rounded-md"> 
                                    {[{v:'flex-start', i: AlignLeft}, {v:'center', i: AlignCenter}, {v:'flex-end', i: AlignRight}].map(item => ( 
                                        <button key={item.v} onClick={() => handleUpdate('blockAlignment', item.v)} className={`flex-1 p-2 rounded-md flex justify-center items-center transition-colors ${((activeCredit.blockAlignment || 'center') === item.v) ? 'bg-purple-600 text-white' : 'hover:bg-zinc-700'}`}> 
                                            <item.i size={18} /> 
                                        </button> 
                                    ))} 
                                </div> 
                            </Input> 
                        )}
                        
                        {activeCredit.type === 'multiple' && (
                            <>
                                <div className={activeCredit.multipleMode === 'song' ? '' : 'grid grid-cols-2 gap-3'}>
                                    {activeCredit.multipleMode !== 'song' && (
                                        <Input label="Columns"> 
                                            <Select 
                                                value={activeCredit.multipleNamesColumnCount || 1} 
                                                onChange={e => handleUpdate('multipleNamesColumnCount', parseInt(e.target.value, 10))} 
                                                options={[
                                                    { value: 1, label: '1' }, { value: 2, label: '2' }, 
                                                    { value: 3, label: '3' }, { value: 4, label: '4' }
                                                ]} 
                                            /> 
                                        </Input>
                                    )}
                                    <NumberStepper 
                                        label="Block Width (%)"
                                        value={activeCredit.blockWidth ?? 100} 
                                        onCommit={value => handleUpdate('blockWidth', value)}
                                        min={20} max={100} step={1}
                                    />
                                </div>
                                <NumberStepper 
                                    label="Column Gap (rem)"
                                    value={activeCredit.columnGap ?? 2} 
                                    onCommit={value => handleUpdate('columnGap', value)}
                                    min={0.1} max={10} step={0.1}
                                />
                                {/* HEADER SPACING — gap between this block's header and its
                                    names. Default matches every other header (global "Space
                                    Between Credits"); changing it affects only this block. */}
                                {(() => {
                                    const hs = resolveMultiHeaderSpacing(activeCredit, settings);
                                    const isCustom = hs.mode !== 'match';
                                    const shown = hs.mode === 'legacy'
                                        // old Role Spacing (x 0.5rem) expressed in header-spacing units
                                        ? Math.round(((hs.rem * 16) / ((typo.roleFontSize.value || 16) * 0.8)) * 100) / 100
                                        : hs.multiplier;
                                    return (
                                        <div>
                                            <NumberStepper
                                                label="Header Spacing"
                                                value={shown}
                                                onCommit={value => { handleUpdate('headerSpacing', value); if (activeCredit.roleSpacing !== undefined) handleUpdate('roleSpacing', undefined); }}
                                                onReset={() => { handleUpdate('headerSpacing', undefined); handleUpdate('roleSpacing', undefined); }}
                                                isOverridden={isCustom}
                                                min={0} max={10} step={0.1}
                                            />
                                            <p className="text-[10px] text-zinc-500 -mt-0.5">
                                                {isCustom
                                                    ? 'Custom for this block only. Reset to match your other headers.'
                                                    : 'Matching your other headers (Space Between Credits). Change it to adjust just this block.'}
                                            </p>
                                        </div>
                                    );
                                })()}
                                {activeCredit.multipleMode === 'song' && (
                                    <NumberStepper
                                        label="Song Spacing (rem)"
                                        value={activeCredit.songEntrySpacing ?? 1}
                                        onCommit={value => handleUpdate('songEntrySpacing', value)}
                                        min={0} max={10} step={0.1}
                                    />
                                )}
                                <Input label="Text Alignment">
                                    <div className="flex items-center space-x-1 bg-zinc-800 p-1 rounded-md">
                                        {['left', 'center', 'right'].map(align => (
                                            <button key={align} onClick={() => handleStyleUpdate('textAlign', align)} className={`flex-1 p-2 rounded-md flex justify-center items-center transition-colors ${typo.textAlign.value === align ? 'bg-purple-600 text-white' : 'hover:bg-zinc-700'}`}>
                                                {align === 'left' ? <AlignLeft size={18} /> : align === 'center' ? <AlignCenter size={18} /> : <AlignRight size={18} />}
                                            </button>
                                        ))}
                                    </div>
                                </Input>
                            </>
                        )}
                        
                        {activeCredit.type === 'dual' && (
                            <>
                                <Input label="Alignment">
                                    <div className="flex items-center space-x-1 bg-zinc-800 p-1 rounded-md">
                                        {[{v:'left', i: AlignLeft}, {v:'center', i: AlignCenter}, {v:'right', i: AlignRight}, {v:'classic', i: MoveHorizontal}, {v:'justified', i: AlignJustify}].map(item => (
                                            <button key={item.v} onClick={() => handleUpdate('dualAlignment', item.v)} className={`flex-1 p-2 rounded-md flex justify-center items-center transition-colors ${((activeCredit.dualAlignment || settings.dualAlignment || 'classic') === item.v) ? 'bg-purple-600 text-white' : 'hover:bg-zinc-700'}`} title={item.v === 'classic' ? 'Classic (Role & Name meet at the gutter)' : item.v}>
                                                <item.i size={18} />
                                            </button>
                                        ))}
                                    </div>
                                    {activeCredit.dualAlignment !== undefined && (
                                        <button onClick={() => handleUpdate('dualAlignment', undefined)} className="text-xs text-zinc-500 hover:text-zinc-300 mt-1">Reset to global</button>
                                    )}
                                </Input>
                                <NumberStepper
                                    label="Column Gap"
                                    value={activeCredit.gutterWidth ?? settings.gutterWidth ?? 3}
                                    onCommit={value => handleUpdate('gutterWidth', value)}
                                    onReset={() => handleUpdate('gutterWidth', undefined)}
                                    isOverridden={activeCredit.gutterWidth !== undefined}
                                    min={0.1} max={20} step={0.5}
                                />
                            </>
                        )}

                        <NumberStepper 
                            label="Bottom Spacing (rem)"
                            value={activeCredit.marginBottom ?? settings.blockSpacing} 
                            onCommit={value => handleUpdate('marginBottom', value)}
                            onReset={() => handleUpdate('marginBottom', undefined)}
                            isOverridden={isOverridden('marginBottom')}
                            min={0} max={10} step={0.1}
                        />
                    </div>
                </EnhancedCollapsibleSection>
            )}
        </div>
    );

    const renderProjectTab = () => (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <p className="text-xs text-zinc-500">These are the defaults for every credit. Any credit can still override them individually.</p>
                <button
                    onClick={() => resetGlobalSettings(Object.keys(DEFAULT_SETTINGS))}
                    className="text-xs text-zinc-400 hover:text-white bg-zinc-800 hover:bg-zinc-700 px-2 py-1 rounded-md transition-colors flex items-center gap-1 flex-shrink-0 ml-3"
                >
                    <RotateCcw size={12} /> Reset All
                </button>
            </div>
        <div className="space-y-4">
        {/* --- Shared Global Settings --- */}
        <div className="p-3 bg-zinc-950 rounded-lg border border-zinc-800">
            <h4 className="text-sm font-semibold text-zinc-300 border-b border-zinc-800 pb-2 mb-3">Global Styles</h4>
            <div className="space-y-3">
                <GlobalFontControl
                    settings={settings}
                    fontFamilyOptions={FONT_FAMILY_OPTIONS}
                    handleDefaultFamilyChange={handleDefaultFamilyChange}
                    defaultFamily={DEFAULT_SETTINGS.fontFamily}
                />
                <NumberStepper 
                    label="Line Height (wrapped text)"
                    value={settings.lineHeight || 1.5} 
                    onCommit={value => setSettings(s => ({...s, lineHeight: value}))}
                    min={1.0} max={3} step={0.1}
                />
                <NumberStepper 
                    label="Space Between Credits (rem)"
                    value={settings.blockSpacing ?? 2} 
                    onCommit={value => setSettings(s => ({...s, blockSpacing: value}))}
                    onReset={() => setSettings(s => ({...s, blockSpacing: DEFAULT_SETTINGS.blockSpacing}))}
                    isOverridden={settings.blockSpacing !== DEFAULT_SETTINGS.blockSpacing}
                    min={0.5} max={10} step={0.1}
                />
                <NumberStepper 
                    label="Letter Spacing (px)"
                    value={settings.letterSpacing || 0} 
                    onCommit={value => setSettings(s => ({...s, letterSpacing: value}))}
                    min={-5} max={10} step={0.5}
                />
                <div className="grid grid-cols-2 gap-3 pt-1">
                                        <StyleControl 
                                            label="Role Weight"
                                            isOverridden={settings.roleFontWeight !== undefined}
                                            onReset={() => setSettings(s => ({...s, roleFontWeight: undefined}))}
                                        >
                                            <Select 
                                                value={settings.roleFontWeight || settings.defaultFontWeight || '400'} 
                                                onChange={e => setSettings(s => ({...s, roleFontWeight: e.target.value}))} 
                                                options={getWeightOptions(settings.fontFamily)} 
                                            />
                                        </StyleControl>
                                        <StyleControl 
                                            label="Name Weight"
                                            isOverridden={settings.nameFontWeight !== undefined}
                                            onReset={() => setSettings(s => ({...s, nameFontWeight: undefined}))}
                                        >
                                            <Select 
                                                value={settings.nameFontWeight || settings.defaultFontWeight || '400'} 
                                                onChange={e => setSettings(s => ({...s, nameFontWeight: e.target.value}))} 
                                                options={getWeightOptions(settings.fontFamily)} 
                                            />
                                        </StyleControl>
                                    </div>
               <Input label="Upload Custom Font">
                    <label className="block w-full text-sm p-2 bg-zinc-700 text-purple-300 rounded-md hover:bg-zinc-600 cursor-pointer flex items-center gap-2 transition-colors mb-2">
                        <Upload size={14}/>
                        <span>Choose File (.ttf, .otf)</span>
                        <input type="file" accept=".ttf,.otf" onChange={handleFontUpload} className="hidden" />
                    </label>
                    {settings.customFonts && settings.customFonts.length > 0 && (
                        <div className="mt-2 space-y-1 bg-zinc-900 rounded-md p-2 border border-zinc-800">
                            <p className="text-[10px] text-zinc-500 font-medium uppercase tracking-wider mb-2">Uploaded Fonts</p>
                            {settings.customFonts.map(font => (
                                <div key={font.name} className="flex items-center justify-between bg-zinc-800/50 p-1.5 rounded border border-zinc-700/50 group hover:border-zinc-600 transition-colors">
                                    <span className="text-xs text-zinc-300 truncate pr-2" title={font.name}>{font.name}</span>
                                    <button 
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setSettings(s => ({ ...s, customFonts: s.customFonts.filter(f => f.name !== font.name) }));
                                        }}
                                        className="text-zinc-500 hover:text-red-400 p-1 rounded hover:bg-zinc-700 transition-colors opacity-50 group-hover:opacity-100"
                                        title="Remove Font"
                                    >
                                        <X size={12} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </Input>
            </div>
        </div>

        {/* --- NEW: Header Settings --- */}
        <div className="p-3 bg-zinc-950 rounded-lg border border-zinc-800">
            <h4 className="text-sm font-semibold text-zinc-300 border-b border-zinc-800 pb-2 mb-3">Headers / Titles</h4>
            <div className="space-y-3">
                 <QuickColorPicker 
                    label="Color" 
                    value={settings.blockNameColor} 
                    onChange={(value) => setSettings(s => ({...s, blockNameColor: value}))}
                    isOverridden={settings.blockNameColor !== DEFAULT_SETTINGS.blockNameColor}
                    onReset={() => setSettings(s => ({...s, blockNameColor: DEFAULT_SETTINGS.blockNameColor}))}
                />
                <div className="grid grid-cols-2 gap-3">
                    <NumberStepper 
                        label="Size"
                        value={settings.headerFontSize || 24} 
                        onCommit={value => setSettings(s => ({...s, headerFontSize: value}))}
                        min={10} max={72} step={1}
                    />
                    <StyleControl label="Weight">
                        <Select 
                            value={settings.headerFontWeight || '700'} 
                            onChange={e => setSettings(s => ({...s, headerFontWeight: e.target.value}))} 
                            options={getWeightOptions(settings.fontFamily)} 
                        />
                    </StyleControl>
                </div>
                <StyleControl label="Transform">
                     <Select 
                        value={settings.blockNameCaps} 
                        onChange={(e) => setSettings(s => ({...s, blockNameCaps: e.target.value}))} 
                        options={CASE_OPTIONS} 
                    />
                </StyleControl>
            </div>
        </div>

        {/* --- NEW: Role Settings --- */}
        <div className="p-3 bg-zinc-950 rounded-lg border border-zinc-800">
            <h4 className="text-sm font-semibold text-zinc-300 border-b border-zinc-800 pb-2 mb-3">Roles</h4>
            <div className="space-y-3">
                <QuickColorPicker 
                    label="Color" 
                    value={settings.roleColor} 
                    onChange={(value) => setSettings(s => ({...s, roleColor: value}))}
                    isOverridden={settings.roleColor !== DEFAULT_SETTINGS.roleColor}
                    onReset={() => setSettings(s => ({...s, roleColor: DEFAULT_SETTINGS.roleColor}))}
                />
                <div className="grid grid-cols-2 gap-3">
                    <NumberStepper 
                        label="Size"
                        value={settings.roleFontSize || 17} 
                        onCommit={value => setSettings(s => ({...s, roleFontSize: value}))}
                        min={10} max={72} step={1}
                    />
                    <StyleControl label="Weight">
                        <Select 
                            value={settings.roleFontWeight || settings.defaultFontWeight} 
                            onChange={e => setSettings(s => ({...s, roleFontWeight: e.target.value}))} 
                            options={getWeightOptions(settings.fontFamily)} 
                        />
                    </StyleControl>
                </div>
                 <StyleControl label="Transform">
                     <Select 
                        value={settings.roleCaps} 
                        onChange={(e) => setSettings(s => ({...s, roleCaps: e.target.value}))} 
                        options={CASE_OPTIONS} 
                    />
                </StyleControl>
            </div>
        </div>
        
        {/* --- NEW: Name Settings --- */}
        <div className="p-3 bg-zinc-950 rounded-lg border border-zinc-800">
            <h4 className="text-sm font-semibold text-zinc-300 border-b border-zinc-800 pb-2 mb-3">Names</h4>
            <div className="space-y-3">
                <QuickColorPicker 
                    label="Color" 
                    value={settings.nameColor} 
                    onChange={(value) => setSettings(s => ({...s, nameColor: value}))}
                    isOverridden={settings.nameColor !== DEFAULT_SETTINGS.nameColor}
                    onReset={() => setSettings(s => ({...s, nameColor: DEFAULT_SETTINGS.nameColor}))}
               />
                <div className="grid grid-cols-2 gap-3">
                    <NumberStepper 
                        label="Size"
                        value={settings.nameFontSize || 17} 
                        onCommit={value => setSettings(s => ({...s, nameFontSize: value}))}
                        min={10} max={72} step={1}
                    />
                    <StyleControl label="Weight">
                        <Select 
                            value={settings.nameFontWeight || settings.defaultFontWeight} 
                            onChange={e => setSettings(s => ({...s, nameFontWeight: e.target.value}))} 
                            options={getWeightOptions(settings.fontFamily)} 
                        />
                    </StyleControl>
                </div>
                 <StyleControl label="Transform">
                     <Select 
                        value={settings.nameCaps} 
                        onChange={(e) => setSettings(s => ({...s, nameCaps: e.target.value}))} 
                        options={CASE_OPTIONS} 
                    />
                </StyleControl>
            </div>
        </div>

        {/* --- Global Text Alignment: applies to Headers/Titles and the multi-column cast & crew block, unless a block overrides it --- */}
        <div className="p-3 bg-zinc-950 rounded-lg border border-zinc-800">
            <h4 className="text-sm font-semibold text-zinc-300 border-b border-zinc-800 pb-2 mb-3">Text Alignment (Headers &amp; Columns)</h4>
            <StyleControl label="Alignment">
                <div className="flex items-center space-x-1 bg-zinc-800 p-1 rounded-md">
                    {['left', 'center', 'right'].map(align => (
                        <button key={align} onClick={() => setSettings(s => ({...s, textAlign: align}))} className={`flex-1 p-2 rounded-md flex justify-center items-center transition-colors ${(settings.textAlign || 'center') === align ? 'bg-purple-600 text-white' : 'hover:bg-zinc-700'}`}>
                            {align === 'left' ? <AlignLeft size={18} /> : align === 'center' ? <AlignCenter size={18} /> : <AlignRight size={18} />}
                        </button>
                    ))}
                </div>
            </StyleControl>
        </div>

        {/* --- Role & Name Layout: default alignment + gutter for regular (Role/Name) credit rows. Grouped with Roles/Names above since they're the same two elements — one place to set how they're laid out, not just how they look. Individual credits can still override this in the Layout tab. --- */}
        <div className="p-3 bg-zinc-950 rounded-lg border border-zinc-800">
            <h4 className="text-sm font-semibold text-zinc-300 border-b border-zinc-800 pb-2 mb-3">Role &amp; Name Layout</h4>
            <div className="space-y-3">
                <StyleControl label="Alignment">
                    <div className="flex items-center space-x-1 bg-zinc-800 p-1 rounded-md">
                        {[{v:'left', i: AlignLeft}, {v:'center', i: AlignCenter}, {v:'right', i: AlignRight}, {v:'classic', i: MoveHorizontal}, {v:'justified', i: AlignJustify}].map(item => (
                            <button key={item.v} onClick={() => setSettings(s => ({...s, dualAlignment: item.v}))} className={`flex-1 p-2 rounded-md flex justify-center items-center transition-colors ${(settings.dualAlignment || 'classic') === item.v ? 'bg-purple-600 text-white' : 'hover:bg-zinc-700'}`} title={item.v === 'classic' ? 'Classic (Role & Name meet at the gutter)' : item.v}>
                                <item.i size={18} />
                            </button>
                        ))}
                    </div>
                </StyleControl>
                <NumberStepper
                    label="Gutter (rem)"
                    value={settings.gutterWidth || 3}
                    onCommit={value => setSettings(s => ({...s, gutterWidth: value}))}
                    onReset={() => setSettings(s => ({...s, gutterWidth: DEFAULT_SETTINGS.gutterWidth}))}
                    isOverridden={settings.gutterWidth !== DEFAULT_SETTINGS.gutterWidth}
                    min={1} max={10} step={0.5}
                />
            </div>
        </div>
    </div>
                        <div className="space-y-3 pt-2">
                            <EnhancedCollapsibleSection title="Animation & Timing" icon={Timer} defaultOpen={false}>
                                <div className="space-y-3">
                                    <StyleControl
                                        label="Enable Title Cards"
                                        isOverridden={settings.cardsEnabled !== DEFAULT_SETTINGS.cardsEnabled}
                                        onReset={() => setSettings(s => ({...s, cardsEnabled: DEFAULT_SETTINGS.cardsEnabled}))}
                                    >
                                        <input
                                            type="checkbox"
                                            className="h-5 w-5 rounded border-zinc-600 bg-zinc-700 text-purple-600 focus:ring-purple-500 cursor-pointer"
                                            checked={settings.cardsEnabled || false}
                                            onChange={(e) => setSettings(s => ({ ...s, cardsEnabled: e.target.checked }))}
                                        />
                                    </StyleControl>

                                    {settings.cardsEnabled && (
                                        <div className="pl-3 border-l border-zinc-800 space-y-2.5">
                                            <NumberStepper 
                                                label="Card Duration (s)"
                                                value={settings.cardDuration || 5} 
                                                onCommit={value => setSettings(s => ({...s, cardDuration: value}))}
                                                onReset={() => setSettings(s => ({...s, cardDuration: DEFAULT_SETTINGS.cardDuration}))}
                                                isOverridden={settings.cardDuration !== DEFAULT_SETTINGS.cardDuration}
                                                min={1} max={15} step={0.5}
                                            />
                                            {typeof resetAllCardDurations === 'function' && (
                                                <button
                                                    onClick={() => {
                                                        if (window.confirm('Clear the individual Duration override on every title card? They will all follow the global Card Duration above until edited again.')) {
                                                            resetAllCardDurations();
                                                        }
                                                    }}
                                                    className="w-full py-1.5 px-3 rounded-md bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-white text-[11px] font-medium transition-colors flex items-center justify-center gap-1.5"
                                                    title="Clear per-card duration overrides so every card follows the global Card Duration"
                                                >
                                                    <RotateCcw size={11} /> Sync All Cards to Global Duration
                                                </button>
                                            )}
                                            <StyleControl 
                                                label="Animation"
                                                isOverridden={settings.cardAnimationType !== DEFAULT_SETTINGS.cardAnimationType}
                                                onReset={() => setSettings(s => ({...s, cardAnimationType: DEFAULT_SETTINGS.cardAnimationType}))}
                                            >
                                                <Select 
                                                    value={settings.cardAnimationType} 
                                                    onChange={(e) => setSettings(s => ({...s, cardAnimationType: e.target.value}))} 
                                                    options={[
                                                        { value: 'fade', label: 'Fade' }, 
                                                        { value: 'slide-up', label: 'Slide Up' }, 
                                                        { value: 'slide-in', label: 'Slide In' },
                                                    ]} 
                                                />
                                            </StyleControl>
                                            <NumberStepper 
                                                label="Anim. In Duration (s)"
                                                value={settings.cardAnimationDuration || 1} 
                                                onCommit={value => setSettings(s => ({...s, cardAnimationDuration: value}))}
                                                onReset={() => setSettings(s => ({...s, cardAnimationDuration: DEFAULT_SETTINGS.cardAnimationDuration}))}
                                                isOverridden={settings.cardAnimationDuration !== DEFAULT_SETTINGS.cardAnimationDuration}
                                                min={0.1} max={5} step={0.1}
                                            />
                                            <NumberStepper 
                                                label="Anim. Out Durantion (s)"
                                                value={settings.fadeOutDuration ?? 1} 
                                                onCommit={value => setSettings(s => ({...s, fadeOutDuration: value}))}
                                                onReset={() => setSettings(s => ({...s, fadeOutDuration: DEFAULT_SETTINGS.fadeOutDuration}))}
                                                isOverridden={settings.fadeOutDuration !== DEFAULT_SETTINGS.fadeOutDuration}
                                                min={0} max={5} step={0.1}
                                            />
                                            <NumberStepper 
                                                label="Gap Between (s)"
                                                value={settings.cardIntermissionDuration ?? 2} 
                                                onCommit={value => setSettings(s => ({...s, cardIntermissionDuration: value}))}
                                                onReset={() => setSettings(s => ({...s, cardIntermissionDuration: DEFAULT_SETTINGS.cardIntermissionDuration}))}
                                                isOverridden={settings.cardIntermissionDuration !== DEFAULT_SETTINGS.cardIntermissionDuration}
                                                min={0} max={10} step={0.1}
                                            />
                                        </div>
                                    )}

                                    <StyleControl 
                                        label="FPS"
                                        isOverridden={settings.fps !== DEFAULT_SETTINGS.fps}
                                        onReset={() => setSettings(s => ({...s, fps: DEFAULT_SETTINGS.fps}))}
                                    >
                                        <Select 
                                            value={settings.fps || '24'} 
                                            onChange={(e) => setSettings(s => ({...s, fps: e.target.value}))} 
                                            options={[ { value: '23.98', label: '23.98' }, { value: '24', label: '24' }, { value: '25', label: '25' }, { value: '30', label: '30' }, { value: '60', label: '60' } ]} 
                                        />
                                    </StyleControl>
                                    <div className="py-1.5 space-y-1">
                                    <div className="flex items-center justify-between gap-2">
                                        <label className="block text-sm font-normal text-zinc-400">Target Duration</label>
                                        <button
                                            onClick={handleSuggestDuration}
                                            title="Suggest a duration for the smoothest scroll"
                                            className="p-2 bg-zinc-700 text-purple-300 rounded-md hover:bg-zinc-600 transition-colors"
                                        >
                                            <Sparkles size={16} />
                                        </button>
                                    </div>
                                    <DurationStepper
                                        value={settings.targetDuration}
                                        onCommit={value => setSettings(s => ({...s, targetDuration: value, speedMode: 'auto'}))}
                                        onReset={() => setSettings(s => ({...s, targetDuration: DEFAULT_SETTINGS.targetDuration, speedMode: 'auto'}))}
                                        isOverridden={settings.targetDuration !== DEFAULT_SETTINGS.targetDuration}
                                        min={30} max={600} step={1}
                                    />
                                </div>
                                  
                                </div>
                            </EnhancedCollapsibleSection>

                            <EnhancedCollapsibleSection title="Layout & Colors" icon={Palette} defaultOpen={false}>
                                <div className="space-y-2.5">
                                    <StyleControl 
                                        label="Letterbox"
                                        isOverridden={settings.letterboxRatio !== DEFAULT_SETTINGS.letterboxRatio}
                                        onReset={() => setSettings(s => ({...s, letterboxRatio: DEFAULT_SETTINGS.letterboxRatio}))}
                                    >
                                        <Select 
                                            value={settings.letterboxRatio || 'none'} 
                                            onChange={(e) => setSettings(s => ({...s, letterboxRatio: e.target.value}))} 
                                            options={[
                                                { value: 'none', label: 'None (Fill)' }, 
                                                { value: '16:9', label: '16:9' }, 
                                                { value: '1.85:1', label: '1.85:1' }, 
                                                { value: '2.35:1', label: '2.35:1' }, 
                                                { value: '2.39:1', label: '2.39:1' }, 
                                                { value: '4:3', label: '4:3' } 
                                            ]} 
                                        />
                                    </StyleControl>
                                    <NumberStepper 
                                        label="Gutter (rem)"
                                        value={settings.gutterWidth || 3} 
                                        onCommit={value => setSettings(s => ({...s, gutterWidth: value}))}
                                        onReset={() => setSettings(s => ({...s, gutterWidth: DEFAULT_SETTINGS.gutterWidth}))}
                                        isOverridden={settings.gutterWidth !== DEFAULT_SETTINGS.gutterWidth}
                                        min={1} max={10} step={0.5}
                                    />
                                    <QuickColorPicker 
                                        label="Background" 
                                        value={settings.backgroundColor} 
                                        onChange={(value) => setSettings(s => ({...s, backgroundColor: value}))}
                                        onReset={() => setSettings(s => ({...s, backgroundColor: DEFAULT_SETTINGS.backgroundColor}))}
                                        isOverridden={settings.backgroundColor !== DEFAULT_SETTINGS.backgroundColor}
                                    />

                                </div>
                            </EnhancedCollapsibleSection>
        </div>
        </div>
    );

    return (
        <div className="h-full w-full flex flex-col font-poppins inspector-panel bg-zinc-950">
            {/* Header */}
            <div className="px-4 py-3 border-b border-zinc-800/70 flex-shrink-0">
                {activeTab === 'project' ? (
                    <>
                        <div className="text-[9px] font-bold tracking-[0.22em] uppercase text-zinc-600 mb-0.5">Project</div>
                        <p className="text-[11px] text-zinc-400 truncate font-medium">Global defaults for every credit</p>
                    </>
                ) : activeCredit ? (
                    <>
                        <div className="text-[9px] font-bold tracking-[0.22em] uppercase text-zinc-600 mb-0.5">
                            {activeCredit.type.replace('-', ' ')}
                        </div>
                        <p className="text-[11px] text-zinc-400 truncate font-medium">
                            {activeCredit.name || activeCredit.role || (activeCredit.textLayers && activeCredit.textLayers[0]?.content) || `ID: ${activeCredit.id}`}
                        </p>
                    </>
                ) : (
                    <>
                        <div className="text-[9px] font-bold tracking-[0.22em] uppercase text-zinc-600 mb-0.5">Inspector</div>
                        <p className="text-[11px] text-zinc-400 truncate font-medium">No credit selected</p>
                    </>
                )}
            </div>

            {/* Tab Navigation */}
            <div className="flex items-center border-b border-zinc-800/70 px-2 flex-shrink-0">
                <TabButton tabName="content" label="Content" />
                <TabButton tabName="style" label="Style" />
                <TabButton tabName="layout" label="Layout" />
                <TabButton tabName="project" label="Project" />
            </div>

            {/* Main Content Area */}
            <div className="flex-grow overflow-y-auto">
                <div className="p-4">
                    {activeTab === 'project' ? (
                        renderProjectTab()
                    ) : !activeCredit ? (
                        <div className="text-zinc-500 text-center p-8 flex flex-col justify-center items-center h-full">
                            <Edit size={48} className="mx-auto mb-4 text-zinc-600" />
                            <h3 className="font-medium text-lg text-zinc-300 mb-2">Inspector</h3>
                            <p className="text-sm text-zinc-400">Select a credit to see its properties, or switch to the Project tab for global settings.</p>
                        </div>
                    ) : (
                        <>
                            {activeTab === 'content' && renderContentTab()}
                            {activeTab === 'style' && renderStyleTab()}
                            {activeTab === 'layout' && renderLayoutTab()}
                        </>
                    )}
                </div>

            </div>


            {/* Footer Actions */}
            {activeTab !== 'project' && activeCredit && (
                <div className="p-3 border-t border-zinc-800 grid grid-cols-2 gap-2 flex-shrink-0">
                    <button 
                        onClick={isTitleCard ? copyCardStyle : copySettings} 
                        className="p-2 bg-zinc-800/70 text-zinc-100 rounded-md hover:bg-zinc-700 flex items-center justify-center gap-2 text-xs transition-colors"
                    >
                        <CopyPlus size={14}/> Copy Style
                    </button>
                    <button 
                        onClick={isTitleCard ? pasteCardStyle : pasteSettings} 
                        disabled={isTitleCard ? !copiedCardStyle : !copiedSettings} 
                        className="p-2 bg-zinc-800/70 text-zinc-100 rounded-md hover:bg-zinc-700 flex items-center justify-center gap-2 text-xs disabled:opacity-50 transition-colors"
                    >
                        <ClipboardPaste size={14}/> Paste Style
                    </button>
                </div>
            )}
        </div>
    );
};


// --- END: Inspector Panel Component ---

// ============================================================================
// FIND & REPLACE — a regular, non-blocking find bar
// ============================================================================
// Works like find in any editor: matches update as you type, the first one is
// selected and scrolled into view, Enter / Shift+Enter (or the arrows) step
// through them, every match is highlighted in the preview with the current one
// emphasized, and Replace / Replace All work on the current match or all of
// them. Nothing covers the preview, so you can see each hit in place.
const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Search term -> RegExp. The term is literal (a "." or "(" in a name no longer
// breaks the search or matches everything). Whole Word uses Unicode-aware
// boundaries so names with accents/apostrophes behave.
const buildFindRegex = (term, caseSensitive, wholeWord) => {
    if (!term) return null;
    const body = escapeRegExp(term);
    const pattern = wholeWord ? `(?<![\\p{L}\\p{N}_])${body}(?![\\p{L}\\p{N}_])` : body;
    try {
        return new RegExp(pattern, 'gu' + (caseSensitive ? '' : 'i'));
    } catch (e) {
        return new RegExp(body, 'g' + (caseSensitive ? '' : 'i'));
    }
};

// Every match in project order: scrolling credits top-to-bottom (role, then
// names), then title cards (layers top-to-bottom). `occurrence` is the match's
// position within its credit, used to find the same hit in the preview DOM.
const collectFindMatches = (credits, cardCredits, regex) => {
    if (!regex) return [];
    const out = [];
    const scan = (text, base) => {
        if (typeof text !== 'string' || !text) return;
        regex.lastIndex = 0;
        let m;
        while ((m = regex.exec(text)) !== null) {
            if (m[0].length === 0) { regex.lastIndex++; continue; }
            out.push({ ...base(), index: m.index, length: m[0].length, matched: m[0], text });
        }
    };
    (credits || []).forEach(c => {
        if (c.type === 'logo') return;
        const startCount = out.length;
        const label = (c.type === 'multiple' && c.role) ? c.role : (c.role || c.name || 'Credit');
        ['role', 'name'].forEach(field => scan(c[field], () => ({ kind: 'scroll', creditId: c.id, field, label })));
        for (let i = startCount; i < out.length; i++) out[i].occurrence = i - startCount;
    });
    (cardCredits || []).forEach((card, cardIdx) => {
        const startCount = out.length;
        (card.textLayers || []).forEach((layer, layerIndex) =>
            scan(layer.content, () => ({ kind: 'card', creditId: card.id, field: 'textLayers', layerIndex, label: `Title Card ${cardIdx + 1}` })));
        for (let i = startCount; i < out.length; i++) out[i].occurrence = i - startCount;
    });
    return out;
};

// Highlight matches in the live preview with the CSS Custom Highlight API —
// no DOM changes, so it can't disturb the editable text. Silently skipped in
// browsers without it (the credit is still selected and scrolled to).
const FIND_HL_ALL = 'ctc-find';
const FIND_HL_CURRENT = 'ctc-find-current';
const ensureFindHighlightStyles = () => {
    if (document.getElementById('ctc-find-hl-style')) return;
    const style = document.createElement('style');
    style.id = 'ctc-find-hl-style';
    style.textContent = `::highlight(${FIND_HL_ALL}){background-color:rgba(250,204,21,.35);} ::highlight(${FIND_HL_CURRENT}){background-color:#f59e0b;color:#000;}`;
    document.head.appendChild(style);
};
const clearFindHighlights = () => {
    if (typeof CSS === 'undefined' || !CSS.highlights) return;
    CSS.highlights.delete(FIND_HL_ALL);
    CSS.highlights.delete(FIND_HL_CURRENT);
};
const rangesForRegexIn = (root, regex) => {
    const nodes = [];
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let text = '';
    let n;
    while ((n = walker.nextNode())) { nodes.push({ node: n, start: text.length }); text += n.nodeValue; }
    const locate = (offset, isEnd) => {
        for (let i = nodes.length - 1; i >= 0; i--) {
            if (offset > nodes[i].start || (!isEnd && offset === nodes[i].start)) return { node: nodes[i].node, offset: offset - nodes[i].start };
        }
        return nodes.length ? { node: nodes[0].node, offset: 0 } : null;
    };
    const ranges = [];
    regex.lastIndex = 0;
    let m;
    while ((m = regex.exec(text)) !== null) {
        if (m[0].length === 0) { regex.lastIndex++; continue; }
        const a = locate(m.index, false);
        const b = locate(m.index + m[0].length, true);
        if (!a || !b) continue;
        try {
            const r = document.createRange();
            r.setStart(a.node, a.offset);
            r.setEnd(b.node, b.offset);
            ranges.push(r);
        } catch (e) { /* node changed under us — skip */ }
    }
    return ranges;
};
const paintFindHighlights = (regex, current) => {
    if (typeof CSS === 'undefined' || !CSS.highlights || typeof window.Highlight === 'undefined') return;
    ensureFindHighlightStyles();
    clearFindHighlights();
    if (!regex) return;
    const all = [];
    let currentRange = null;
    document.querySelectorAll('[id^="preview-credit-"]').forEach(root => {
        const ranges = rangesForRegexIn(root, regex);
        all.push(...ranges);
        if (current && root.id === `preview-credit-${current.creditId}`) {
            currentRange = ranges[current.occurrence] || ranges[0] || null;
        }
    });
    if (all.length) CSS.highlights.set(FIND_HL_ALL, new window.Highlight(...all));
    if (currentRange) CSS.highlights.set(FIND_HL_CURRENT, new window.Highlight(currentRange));
};

const findSnippet = (m) => {
    const flat = (s) => s.replace(/[\n\u2028\t]+/g, ' · ');
    const before = m.text.slice(Math.max(0, m.index - 24), m.index);
    const after = m.text.slice(m.index + m.length, m.index + m.length + 24);
    return {
        before: (m.index > 24 ? '…' : '') + flat(before),
        hit: flat(m.text.substr(m.index, m.length)),
        after: flat(after) + (m.index + m.length + 24 < m.text.length ? '…' : ''),
    };
};

const FindAndReplaceModal = ({ isOpen, onClose, credits, cardCredits, onGoTo, onReplaceOne, onReplaceAll, focusSignal }) => {
    const [findTerm, setFindTerm] = useState('');
    const [replaceTerm, setReplaceTerm] = useState('');
    const [caseSensitive, setCaseSensitive] = useState(false);
    const [matchWholeWord, setMatchWholeWord] = useState(false);
    const [showReplace, setShowReplace] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [status, setStatus] = useState('');
    const findInputRef = useRef(null);

    const regex = useMemo(() => buildFindRegex(findTerm, caseSensitive, matchWholeWord), [findTerm, caseSensitive, matchWholeWord]);
    const matches = useMemo(() => collectFindMatches(credits, cardCredits, regex), [credits, cardCredits, regex]);
    const safeIndex = matches.length ? Math.min(currentIndex, matches.length - 1) : 0;
    const current = matches.length ? matches[safeIndex] : null;

    // Focus + select the Find field whenever the bar is opened (or Cmd/Ctrl+F is
    // pressed again while it's open).
    useEffect(() => {
        if (!isOpen) return;
        const t = setTimeout(() => { if (findInputRef.current) { findInputRef.current.focus(); findInputRef.current.select(); } }, 0);
        return () => clearTimeout(t);
    }, [isOpen, focusSignal]);

    // Typing a new search starts again from the first match.
    useEffect(() => { setCurrentIndex(0); setStatus(''); }, [findTerm, caseSensitive, matchWholeWord]);

    // Jump to the current match (debounced while typing).
    const currentKey = current ? `${current.kind}:${current.creditId}:${current.field}:${current.layerIndex ?? ''}:${current.index}` : '';
    useEffect(() => {
        if (!isOpen || !current) return;
        const t = setTimeout(() => { onGoTo(current); }, 200);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, currentKey]);

    // Keep the preview highlights in sync (after the jump has rendered).
    useEffect(() => {
        if (!isOpen) { clearFindHighlights(); return; }
        const t = setTimeout(() => paintFindHighlights(regex, current), 380);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, regex, currentKey, matches.length, credits, cardCredits]);
    useEffect(() => () => clearFindHighlights(), []);

    if (!isOpen) return null;

    const step = (dir) => {
        if (!matches.length) return;
        setCurrentIndex((safeIndex + dir + matches.length) % matches.length);
    };
    const handleFindKeyDown = (e) => {
        if (e.key === 'Enter') { e.preventDefault(); step(e.shiftKey ? -1 : 1); }
        else if (e.key === 'Escape') { e.preventDefault(); onClose(); }
    };
    const handleReplaceKeyDown = (e) => {
        if (e.key === 'Enter') { e.preventDefault(); if (e.metaKey || e.ctrlKey) doReplaceAll(); else doReplaceOne(); }
        else if (e.key === 'Escape') { e.preventDefault(); onClose(); }
    };
    const doReplaceOne = () => {
        if (!current) return;
        onReplaceOne(current, replaceTerm);
        // The next match slides into this index once the text updates.
        setStatus('Replaced 1');
    };
    const doReplaceAll = () => {
        if (!findTerm || !matches.length) return;
        const count = onReplaceAll({ findTerm, replaceTerm, caseSensitive, matchWholeWord });
        setStatus(`Replaced ${count}`);
    };

    const snippet = current ? findSnippet(current) : null;
    const toggleClass = (on) => `h-7 min-w-[28px] px-1.5 rounded text-[11px] font-semibold transition-colors ${on ? 'bg-purple-600 text-white' : 'text-zinc-400 hover:bg-zinc-700 hover:text-zinc-100'}`;
    const iconBtn = 'h-7 w-7 flex items-center justify-center rounded text-zinc-400 hover:bg-zinc-700 hover:text-zinc-100 disabled:opacity-30 disabled:hover:bg-transparent';

    return (
        <div
            className="fixed z-50 left-1/2 -translate-x-1/2 top-[84px] w-[min(34rem,calc(100vw-2rem))] bg-zinc-900/95 backdrop-blur border border-zinc-700 rounded-xl shadow-2xl text-gray-100 font-poppins"
            role="search"
        >
            <div className="flex items-center gap-1.5 p-2">
                <button onClick={() => setShowReplace(v => !v)} className={iconBtn} title={showReplace ? 'Hide replace' : 'Show replace'}>
                    {showReplace ? <ChevronDown size={14} /> : <ChevronUp size={14} className="rotate-90" />}
                </button>
                <div className="flex-1 flex items-center gap-2 bg-zinc-800 border border-zinc-700 focus-within:border-purple-500 rounded-md px-2">
                    <Search size={14} className="text-zinc-500 flex-shrink-0" />
                    <input
                        ref={findInputRef}
                        type="text"
                        value={findTerm}
                        onChange={(e) => setFindTerm(e.target.value)}
                        onKeyDown={handleFindKeyDown}
                        placeholder="Find a name or role"
                        className="flex-1 min-w-0 bg-transparent py-1.5 text-sm outline-none placeholder:text-zinc-500"
                        aria-label="Find"
                    />
                    <span className={`text-[11px] tabular-nums whitespace-nowrap ${findTerm && !matches.length ? 'text-red-400' : 'text-zinc-400'}`}>
                        {findTerm ? (matches.length ? `${safeIndex + 1} of ${matches.length}` : 'No results') : ''}
                    </span>
                </div>
                <button onClick={() => setCaseSensitive(v => !v)} className={toggleClass(caseSensitive)} title="Match case">Aa</button>
                <button onClick={() => setMatchWholeWord(v => !v)} className={toggleClass(matchWholeWord)} title="Whole word"><span className="underline underline-offset-2">ab</span></button>
                <button onClick={() => step(-1)} disabled={!matches.length} className={iconBtn} title="Previous (Shift+Enter)"><ArrowUp size={14} /></button>
                <button onClick={() => step(1)} disabled={!matches.length} className={iconBtn} title="Next (Enter)"><ArrowDown size={14} /></button>
                <button onClick={onClose} className={iconBtn} title="Close (Esc)"><X size={14} /></button>
            </div>

            {showReplace && (
                <div className="flex items-center gap-1.5 px-2 pb-2 pl-[2.6rem]">
                    <input
                        type="text"
                        value={replaceTerm}
                        onChange={(e) => setReplaceTerm(e.target.value)}
                        onKeyDown={handleReplaceKeyDown}
                        placeholder="Replace with"
                        className="flex-1 min-w-0 bg-zinc-800 border border-zinc-700 focus:border-purple-500 rounded-md px-2 py-1.5 text-sm outline-none placeholder:text-zinc-500"
                        aria-label="Replace with"
                    />
                    <button onClick={doReplaceOne} disabled={!current} className="h-8 px-3 rounded-md bg-zinc-700 hover:bg-zinc-600 text-xs font-medium disabled:opacity-30">Replace</button>
                    <button onClick={doReplaceAll} disabled={!matches.length} className="h-8 px-3 rounded-md bg-purple-600 hover:bg-purple-700 text-white text-xs font-medium disabled:opacity-30">Replace All</button>
                </div>
            )}

            {(snippet || status) && (
                <div className="flex items-center justify-between gap-3 px-3 py-1.5 border-t border-zinc-800 text-[11px] text-zinc-400">
                    {snippet ? (
                        <span className="truncate">
                            <span className="text-zinc-500">{current.label}: </span>
                            {snippet.before}<mark className="bg-amber-500 text-black rounded-sm px-0.5">{snippet.hit}</mark>{snippet.after}
                        </span>
                    ) : <span />}
                    {status && <span className="text-emerald-400 whitespace-nowrap">{status}</span>}
                </div>
            )}
        </div>
    );
};

// --- START: New ExportModal Component ---

const ExportModal = ({ isOpen, onClose, onExport, onNLEExport, projectName, isExporting, projectTier }) => {
    const [quality, setQuality] = useState('standard');
    const [codec, setCodec] = useState('h264-mp4');
    const [includeAlpha, setIncludeAlpha] = useState(false);
    const [exportType, setExportType] = useState('full');
    const [resolution, setResolution] = useState('1920x1080');
    const [fps, setFps] = useState('23.98');

    // This logic is now based on the `projectTier` prop
    const planPermissions = {
        // maxPixels: width * height
        demo:  { name: 'Demo',  maxPixels: 1920 * 1080, allowsProRes: false, allowsAlpha: false, allowsCinematic: false },
        indie: { name: 'Indie', maxPixels: 1920 * 1080, allowsProRes: false, allowsAlpha: false, allowsCinematic: true },
        pro:   { name: 'Pro',   maxPixels: 3840 * 2160, allowsProRes: true,  allowsAlpha: true,  allowsCinematic: true }
    };

    // Default to 'demo' if projectTier is undefined or invalid
    const permissions = planPermissions[projectTier] || planPermissions.demo;

    // Helper to check if the requested resolution is allowed by the current tier
    const isResolutionAllowed = (resValue) => {
        const [width, height] = resValue.split('x').map(Number);
        return (width * height) <= permissions.maxPixels;
    };
    
    // Helper to determine the required plan for a resolution/feature if not allowed
    const getRequiredPlan = (resValue) => {
        const [width, height] = resValue.split('x').map(Number);
        const requestedPixels = width * height;

        // Determine the minimum required plan based on pixel count
        if (requestedPixels > planPermissions.pro.maxPixels) {
            return 'Pro+'; // Assuming anything above Pro 4K requires Pro+
        } else if (requestedPixels > planPermissions.indie.maxPixels) {
            return 'Pro';
        } else if (requestedPixels > planPermissions.demo.maxPixels) {
            return 'Indie';
        }
        return '';
    };

    // Determine if ProRes 4444 is available based on quality selection (re-added from original logic)
    const isProRes4444AvailableByQuality = quality === 'professional' || quality === 'cinematic';
    
    // Automatically manage includeAlpha and codec settings based on each other
    useEffect(() => {
        // If alpha is enabled, force ProRes 4444
        if (includeAlpha) { 
            // Only set if allowed by plan, otherwise it should be disabled by the UI
            if (permissions.allowsAlpha) {
                setCodec('prores4444'); 
            } else {
                setIncludeAlpha(false);
            }
        }
        // If codec is set to ProRes 4444 but alpha is turned off, switch to 422
        else if (codec === 'prores4444') { 
            setCodec('prores422'); 
        }
    }, [includeAlpha, codec, permissions.allowsAlpha]);
    
    // Re-check selected codec and alpha status if quality or plan permissions change
    useEffect(() => {
        // If the current resolution is no longer allowed, default to the max allowed resolution
        if (!isResolutionAllowed(resolution)) {
            // Find the highest resolution that IS allowed
            const availableResolutions = [
                '3840x2160', 
                '2560x1440', 
                '1920x1080', 
                '1280x720'
            ];
            const maxAllowed = availableResolutions.find(res => isResolutionAllowed(res)) || '1280x720';
            setResolution(maxAllowed);
        }

        // If the current quality is no longer allowed (e.g., switched from Pro to Demo), downgrade
        if (quality === 'cinematic' && !permissions.allowsCinematic) {
             setQuality('standard');
        }

        // If a prores codec is selected but not allowed by the plan, default to h264
        if ((codec.startsWith('prores') && !permissions.allowsProRes) || (codec === 'prores4444' && !permissions.allowsAlpha)) {
             setCodec('h264-mp4');
             setIncludeAlpha(false);
        }

    }, [projectTier, resolution, quality]);

    const handleCodecChange = (e) => {
        const newCodec = e.target.value;
        // If ProRes 4444 is selected, automatically check the alpha box (if allowed)
        if (newCodec === 'prores4444') {
            if (permissions.allowsAlpha) {
                setIncludeAlpha(true);
            } else {
                // Should not happen if select is disabled correctly, but as a safeguard
                alert("ProRes 4444 requires the Pro Plan.");
            }
        } else if (newCodec.startsWith('prores')) {
            // If any other ProRes is selected, ensure alpha is off
            setIncludeAlpha(false);
        } else {
            // Default codecs, ensure alpha is off
            setIncludeAlpha(false);
        }
        setCodec(newCodec);
    };


    const handleExportClick = () => {
        // Safety check before exporting
        if (!isResolutionAllowed(resolution)) {
            console.error("Attempted to export to a forbidden resolution.");
            // Prevent export or show user an error message (using custom modal, not alert)
            return;
        }

        onExport({ 
            quality, 
            codecChoice: codec, 
            hasAlphaChannel: includeAlpha, 
            exportType, 
            resolution, 
            fps 
        });
    };
    
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 font-poppins">
            <div className="bg-zinc-900 rounded-xl shadow-2xl max-w-2xl w-full text-gray-100 border border-zinc-700 relative flex flex-col max-h-[90vh]">
                <div className="p-6 border-b border-zinc-800 flex items-center justify-between">
                    <h3 className="text-2xl font-thin text-purple-400 flex items-center gap-2">
                        <Download size={24} /> Export Options
                    </h3>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-zinc-700 text-gray-400">
                        <X size={20} />
                    </button>
                </div>
                
                <div className="p-6 space-y-6 overflow-y-auto flex-grow">
                    <div className="bg-blue-900/30 border border-blue-700/50 rounded-lg p-3 text-sm">
                        <p className="text-blue-200">
                            Project License: <span className="font-semibold text-white">{permissions.name}</span>
                            <span className="text-blue-300"> • Max Resolution: {(permissions.maxPixels === 3840 * 2160) ? '4K' : (permissions.maxPixels === 1920 * 1080) ? '1080p' : '720p'}</span>
                        </p>
                    </div>

                    <div>
                        <p className="font-medium mb-2 text-gray-300">Video Render</p>
                        <div className="p-4 bg-zinc-800 rounded-lg border border-zinc-700">
                            
                            <label className="block text-sm font-medium text-gray-300 mb-2">Export Type:</label>
                            <div className="flex space-x-2 rounded-lg bg-zinc-700 p-1 mb-4">
                                <button onClick={() => setExportType('full')} className={`flex-1 p-2 text-sm rounded-md transition-colors ${exportType === 'full' ? 'bg-purple-600 text-white' : 'hover:bg-zinc-600'}`}>Full Sequence</button>
                                <button onClick={() => setExportType('cards')} className={`flex-1 p-2 text-sm rounded-md transition-colors ${exportType === 'cards' ? 'bg-purple-600 text-white' : 'hover:bg-zinc-600'}`}>Cards Only</button>
                                <button onClick={() => setExportType('scroll')} className={`flex-1 p-2 text-sm rounded-md transition-colors ${exportType === 'scroll' ? 'bg-purple-600 text-white' : 'hover:bg-zinc-600'}`}>Scroll Only</button>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">Resolution:</label>
                                    <select 
                                        value={resolution} 
                                        onChange={(e) => setResolution(e.target.value)} 
                                        className="block w-full rounded-md border-zinc-700 shadow-sm focus:border-purple-500 focus:ring focus:ring-purple-500 focus:ring-opacity-50 bg-zinc-700 text-gray-200 p-2"
                                    >
                                        <option value="1280x720" disabled={!isResolutionAllowed('1280x720')}>
                                            1280x720 (720p) {getRequiredPlan('1280x720') && `- ${getRequiredPlan('1280x720')}+`}
                                        </option>
                                        <option value="1920x1080" disabled={!isResolutionAllowed('1920x1080')}>
                                            1920x1080 (1080p) {getRequiredPlan('1920x1080') && `- ${getRequiredPlan('1920x1080')}+`}
                                        </option>
                                        <option value="2560x1440" disabled={!isResolutionAllowed('2560x1440')}>
                                            2560x1440 (1440p) {getRequiredPlan('2560x1440') && `- ${getRequiredPlan('2560x1440')}+`}
                                        </option>
                                        <option value="3840x2160" disabled={!isResolutionAllowed('3840x2160')}>
                                            3840x2160 (4K) {getRequiredPlan('3840x2160') && `- ${getRequiredPlan('3840x2160')}+`}
                                        </option>
                                    </select>
                                </div>
                                <div>
                                    <label htmlFor="fps-select-modal" className="block text-sm font-medium text-gray-300 mb-1">FPS:</label>
                                    <select id="fps-select-modal" value={fps} onChange={(e) => setFps(e.target.value)} className="block w-full rounded-md border-zinc-700 shadow-sm focus:border-purple-500 focus:ring focus:ring-purple-500 focus:ring-opacity-50 bg-zinc-700 text-gray-200 p-2">
                                        <option value="23.98">23.98 (Film)</option>
                                        <option value="24">24</option>
                                        <option value="25">25</option>
                                        <option value="30">30</option>
                                        <option value="60">60</option>
                                    </select>
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div>
                                    <label htmlFor="quality-select-modal" className="block text-sm font-medium text-gray-300 mb-1">Quality Tier:</label>
                                   <select 
                                            id="quality-select-modal" 
                                            value={quality} 
                                            onChange={(e) => setQuality(e.target.value)} 
                                            className="block w-full rounded-md border-zinc-700 shadow-sm focus:border-purple-500 focus:ring focus:ring-purple-500 focus:ring-opacity-50 bg-zinc-700 text-gray-200 p-2"
                                        >
                                            <option value="draft">Draft</option>
                                            <option value="standard">Standard</option>
                                            <option value="cinematic" disabled={!permissions.allowsCinematic}>
                                                Cinematic {!permissions.allowsCinematic && '- Indie+'}
                                            </option>
                                        </select>
                                        
                                        {quality === 'draft' && (
                                            <div className="mt-2 p-3 bg-amber-950/60 border border-amber-600/70 rounded-lg text-sm text-amber-200 flex items-start gap-2">
                                                <AlertCircle size={15} className="shrink-0 mt-0.5 text-amber-400" />
                                                <span><span className="font-bold text-amber-300">Not for production use.</span> Draft mode disables motion blur, frame interpolation, and timing fidelity. Use Standard or higher for any deliverable, screener, or festival submission.</span>
                                            </div>
                                        )}
                                        {!permissions.allowsCinematic && (
                                            <div className="mt-2 p-2 bg-blue-900/30 border border-blue-700/50 rounded text-sm text-blue-200">
                                                Upgrade to Indie or Pro for Cinematic quality!
                                            </div>
                                        )}
                                </div>
                                <div>
                                    <label htmlFor="codec-select-modal" className="block text-sm font-medium text-gray-300 mb-1">Codec:</label>
                                    <select 
                                        id="codec-select-modal" 
                                        value={codec} 
                                        onChange={handleCodecChange} 
                                        className="block w-full rounded-md border-zinc-700 shadow-sm focus:border-purple-500 focus:ring focus:ring-purple-500 focus:ring-opacity-50 bg-zinc-700 text-gray-200 p-2"
                                    >
                                        <option value="h264-mp4">H.264 (mp4)</option>
                                        <option value="h264-mov">H.264 (mov)</option>
                                        <option value="h265-mp4">H.265 (mp4)</option>
                                        <option value="h265-mov">H.265 (mov)</option>
                                        <option value="prores422" disabled={!permissions.allowsProRes}>
                                            ProRes 422 (mov) {!permissions.allowsProRes && '- Pro Plan'}
                                        </option>
                                        <option value="prores4444" disabled={!permissions.allowsAlpha || !isProRes4444AvailableByQuality}>
                                            ProRes 4444 (mov) {(!permissions.allowsAlpha || !isProRes4444AvailableByQuality) && '- Pro Plan & Cinematic Quality'}
                                        </option>
                                    </select>
                                </div>
                            </div>
                            
                            <div className="mt-4 p-3 bg-zinc-950 rounded-lg border border-zinc-700">
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="alpha-channel-checkbox"
                                        checked={includeAlpha}
                                        onChange={(e) => setIncludeAlpha(e.target.checked)}
                                        disabled={!permissions.allowsAlpha || !isProRes4444AvailableByQuality}
                                        className="h-4 w-4 rounded border-zinc-600 bg-zinc-700 text-purple-600 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                    />
                                    <label htmlFor="alpha-channel-checkbox" className={`ml-2 text-sm font-medium ${permissions.allowsAlpha && isProRes4444AvailableByQuality ? 'text-gray-300' : 'text-gray-500'}`}>
                                        Include Alpha Channel (Transparency) {(!permissions.allowsAlpha || !isProRes4444AvailableByQuality) && '- Pro Plan & Cinematic Quality Required'}
                                    </label>
                                </div>
                                <p className="text-xs text-zinc-400 mt-2">
                                    Enabling Alpha Channel will automatically select ProRes 4444.
                                </p>
                            </div>

                            <div className="mt-4 p-3 bg-yellow-900/50 border border-yellow-700/50 rounded-lg text-sm text-yellow-200 flex items-start gap-2">
                                <AlertCircle size={24} className="shrink-0 mt-0.5" />
                                <span>NOTE: If your main sequence is set to 'Scroll Only' or 'Card Only' the full sequence export will not include both title cards and scroll. Set the sequence (cards first or scroll first) for the order you wish to export and then come back to export.</span>
                            </div>

                            <button 
                                onClick={handleExportClick} 
                                disabled={isExporting || !isResolutionAllowed(resolution)} 
                                className="w-full mt-4 bg-purple-600 text-white p-3 rounded-lg hover:bg-purple-700 font-poppins font-semibold shadow-md disabled:bg-zinc-600 disabled:cursor-not-allowed"
                            >
                                {isExporting ? 'Exporting...' : 'Start Render'}
                            </button>
                        </div>
                    </div>
                    
                    <div>
                        <p className="font-medium mb-2 text-zinc-500">NLE Data Export (BETA-Best for Title Cards)</p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <button onClick={() => onNLEExport('resolve')} className="p-3 bg-orange-600 text-white rounded-lg text-sm flex items-center justify-center gap-2 hover:bg-orange-700">
                                <FileVideo size={16}/> Resolve (.xml)
                            </button>
                            <button onClick={() => onNLEExport('fcpxml')} className="p-3 bg-blue-600 text-white rounded-lg text-sm flex items-center justify-center gap-2 hover:bg-blue-700">
                                <FileVideo size={16}/> FCPX (.fcpxml)
                            </button>
                            <button onClick={() => onNLEExport('premiere')} className="p-3 bg-blue-600 text-white rounded-lg text-sm flex items-center justify-center gap-2 hover:bg-blue-700">
                                <FileVideo size={16}/> Premiere (.xml)
                            </button>
                        </div>
                    </div>
                </div>
                
                <div className="p-6 border-t border-zinc-800 flex justify-end">
                    <button onClick={onClose} className="bg-zinc-700 text-gray-200 px-5 py-2 rounded-lg hover:bg-zinc-600">Close</button>
                </div>
            </div>
        </div>
    );
};


// --- END: New ExportModal Component ---


    // --- START: ScrollingCreditsDisplay Component ---
    const ScrollingCreditsDisplay = ({ credits, activeCreditId, selectedCreditIds, handleCreditSelect, handleCreditDragStart, handleCreditDragOver, handleCreditDrop, draggedCreditId, dragOverCreditId, removeCredit, updateCardCredit, setActiveCardId, setActiveCreditId, cardCredits, settings, addCredit, showAddCreditDropdown, setShowAddCreditDropdown, addCreditDropdownRef, setShowBatchAddModal, setShowImportModal, isProjectEmpty, askCopilot }) => {
        return (
            <div className="space-y-2">
                <div className="relative sticky top-0 z-20 bg-zinc-900 pb-1" ref={addCreditDropdownRef}>
                    {isProjectEmpty ? (
                        /* --- START HERE: an empty project's single most useful action is a ---
                           --- paste, not a one-at-a-time manual add. This card makes Batch  ---
                           --- Add the obvious first move instead of a buried menu item.     --- */
                        <div className="relative my-2 overflow-hidden rounded-xl border border-purple-500/25 bg-gradient-to-b from-purple-950/40 to-zinc-900 p-4">
                            <div className="pointer-events-none absolute -top-12 left-1/2 h-24 w-48 -translate-x-1/2 rounded-full bg-purple-600/20 blur-2xl" />
                            <div className="relative">
                                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-purple-400">Start here</p>
                                <h4 className="mt-1 text-[15px] font-semibold text-white">Paste your credit list</h4>
                                <p className="mt-1.5 text-[11.5px] leading-relaxed text-zinc-400">
                                    Straight from a spreadsheet, script, or notes. Sections, groups, and role/name pairs are detected for you — no template to download.
                                </p>
                                <button
                                    onClick={() => { setShowBatchAddModal(true); setShowAddCreditDropdown(false); }}
                                    className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-b from-purple-500 to-purple-700 px-3 py-2.5 text-[13px] font-semibold text-white shadow-lg shadow-purple-950/50 transition-all hover:from-purple-400 hover:to-purple-600"
                                >
                                    <Rows4 size={15} /> Batch Add Credits
                                </button>
                                <div className="mt-2.5 flex items-center justify-center gap-2 text-[11px]">
                                    <button onClick={() => { setShowImportModal(true); setShowAddCreditDropdown(false); }} className="font-medium text-zinc-400 transition-colors hover:text-green-300">Import CSV</button>
                                    <span className="text-zinc-700">·</span>
                                    <button onClick={() => setShowAddCreditDropdown(v => !v)} className="font-medium text-zinc-400 transition-colors hover:text-purple-300">Add one manually</button>
                                </div>
                                <button
                                    onClick={() => askCopilot && askCopilot("I'm starting a brand new credit sequence. Walk me through the fastest way to get my full cast and crew list into the project.")}
                                    className="mt-2.5 flex w-full items-center justify-center gap-1.5 text-[10.5px] font-medium text-zinc-500 transition-colors hover:text-purple-300"
                                >
                                    <Sparkles size={11} /> Not sure where to start? Ask the Copilot
                                </button>
                            </div>
                        </div>
                    ) : (
                        /* Batch Add stays visible and dominant for repeat users; the old
                           per-type menu collapses into the chevron beside it. */
                        <div className="my-2 flex items-stretch gap-1.5">
                            <button
                                onClick={() => { setShowBatchAddModal(true); setShowAddCreditDropdown(false); }}
                                title="Paste a credit list (Shift+Cmd/Ctrl+B)"
                                className="flex flex-1 items-center justify-center gap-2 rounded-md border border-purple-500/40 bg-gradient-to-b from-purple-600/90 to-purple-800/90 px-3 py-2 text-sm font-semibold text-white shadow-md shadow-purple-950/40 transition-all hover:from-purple-500 hover:to-purple-700"
                            >
                                <Rows4 size={15} /> Batch Add
                            </button>
                            <button
                                onClick={() => setShowAddCreditDropdown(v => !v)}
                                title="Add a single credit"
                                className="flex items-center justify-center gap-1 rounded-md border border-zinc-700 bg-zinc-800 px-2.5 text-sm font-semibold text-zinc-300 transition-colors hover:bg-zinc-700 hover:text-white"
                            >
                                <Plus size={15} />
                                <ChevronDown size={13} />
                            </button>
                        </div>
                    )}
                    {showAddCreditDropdown && (
                        <div className="absolute left-0 right-0 mt-1 z-20 bg-zinc-800 border border-zinc-700 rounded-lg shadow-2xl p-1.5 space-y-0.5">
                            <button onClick={() => { addCredit('dual'); setShowAddCreditDropdown(false); }} className="w-full text-left px-3 py-2 text-sm text-zinc-200 hover:bg-zinc-700 rounded-md flex items-center gap-2"><List size={16}/> Two Column (Role &amp; Name)</button>
                            <button onClick={() => { addCredit('single'); setShowAddCreditDropdown(false); }} className="w-full text-left px-3 py-2 text-sm text-zinc-200 hover:bg-zinc-700 rounded-md flex items-center gap-2"><Type size={16}/> Single Line</button>
                            <button onClick={() => { addCredit('multiple'); setShowAddCreditDropdown(false); }} className="w-full text-left px-3 py-2 text-sm text-zinc-200 hover:bg-zinc-700 rounded-md flex items-center gap-2"><Grid3x3 size={16}/> Multi-Name Block</button>
                            <button onClick={() => { addCredit('song'); setShowAddCreditDropdown(false); }} className="w-full text-left px-3 py-2 text-sm text-zinc-200 hover:bg-zinc-700 rounded-md flex items-center gap-2"><Music size={16}/> Song Credits</button>
                            <button onClick={() => { addCredit('title'); setShowAddCreditDropdown(false); }} className="w-full text-left px-3 py-2 text-sm text-zinc-200 hover:bg-zinc-700 rounded-md flex items-center gap-2"><Newspaper size={16}/> Header</button>
                            <button onClick={() => { addCredit('logo'); setShowAddCreditDropdown(false); }} className="w-full text-left px-3 py-2 text-sm text-zinc-200 hover:bg-zinc-700 rounded-md flex items-center gap-2"><Image size={16}/> Logo</button>
                            <button onClick={() => { addCredit('main-title'); setShowAddCreditDropdown(false); }} className="w-full text-left px-3 py-2 text-sm text-yellow-300 hover:bg-zinc-700 rounded-md flex items-center gap-2"><Film size={16}/> Title Card</button>
                            <div className="border-t border-zinc-700 my-1"></div>
                            <button onClick={() => { setShowBatchAddModal(true); setShowAddCreditDropdown(false); }} className="w-full text-left px-3 py-2 text-sm text-blue-300 hover:bg-zinc-700 rounded-md flex items-center gap-2"><Rows4 size={16}/> Batch Add</button>
                            <button onClick={() => { setShowImportModal(true); setShowAddCreditDropdown(false); }} className="w-full text-left px-3 py-2 text-sm text-green-300 hover:bg-zinc-700 rounded-md flex items-center gap-2"><Upload size={16}/> Import CSV</button>
                        </div>
                    )}
                </div>
                {credits.length === 0 && !isProjectEmpty && <p className="text-xs text-zinc-500 italic mb-2 text-center">No scrolling credits match your search.</p>}
                {credits.map((credit) => {
                    const isActive = credit.id === activeCreditId;
                    const isSelected = selectedCreditIds.has(credit.id);
                    const isDragging = selectedCreditIds.has(credit.id) && draggedCreditId != null;
                    const isDragOver = dragOverCreditId === credit.id;
                    const isCardInOtherList = cardCredits.some(c => c.originalId === credit.id);
    
                    return (
                        <div
                            key={credit.id}
                            id={`credit-item-${credit.id}`}
                            draggable
                            onDragStart={(e) => handleCreditDragStart(e, credit.id)}
                            onDragOver={(e) => handleCreditDragOver(e, credit.id)}
                            onDrop={(e) => handleCreditDrop(e, credit.id)}
                            onClick={(e) => handleCreditSelect(credit.id, e, 'scroll')}
                            className={`p-3 rounded-lg cursor-grab transition-all duration-200 flex items-center border
                                ${isDragging ? 'opacity-30 bg-zinc-700' : 'bg-zinc-800'}
                                ${isDragOver ? 'border-purple-500' : 'border-transparent'}
                                ${isActive ? 'bg-purple-900/50 ring-2 ring-purple-600' : ''}
                                ${isSelected && !isActive ? 'bg-blue-900/40 ring-2 ring-blue-600' : ''}
                                ${!isSelected && !isActive ? 'hover:bg-zinc-700' : ''}
                            `}
                        >
                            <GripVertical size={20} className="text-zinc-500 mr-3 shrink-0" />
                            <div className="flex-grow overflow-hidden pointer-events-none">
                                <p className="text-sm font-medium text-zinc-200 truncate flex items-center gap-2">
                                    {credit.type === 'main-title' ? `Title: ${credit.name}` : (credit.type === 'title' || credit.type === 'single' ? credit.name : credit.role || `Untitled ${credit.type}`)}
                                </p>
                                <p className="text-xs text-zinc-400 truncate">
                                    Type: {credit.type}
                                </p>
                            </div>
                            <button 
                                onClick={(e) => { 
                                    e.stopPropagation(); 
                                    if (isCardInOtherList) {
                                        const cardToRemove = cardCredits.find(c => c.originalId === credit.id);
                                        if (cardToRemove) removeCredit([cardToRemove.id], true);
                                    } else {
                                        const newCardId = Math.max(0, ...cardCredits.map(c => typeof c.id === 'number' ? c.id : 0), ...credits.map(c => typeof c.id === 'number' ? c.id : 0)) + 1;
                                        let newCard = {
                                            id: newCardId,
                                            originalId: credit.id,
                                            type: 'main-title',
                                            creditDisplayType: 'card',
                                            // duration intentionally omitted: falls back to settings.cardDuration
                                            // (global) until the user sets a per-card override in the Inspector.
                                            cardPositionX: 50,
                                            cardPositionY: 50,
                                            cardTextAlign: 'center',
                                            cardVerticalSpacing: 0.5,
                                            textLayers: [
                                                // --- FIX START: Use correct default font sizes ---
                                                { id: Math.random(), content: credit.role || "Subtitle", fontSize: 28, fontWeight: 'normal', fontStyle: 'italic', color: '#CCCCCC', fontFamily: settings.cardFontFamily || 'poppins' },
                                                { id: Math.random(), content: credit.name || "Main Title", fontSize: 46, fontWeight: 'bold', fontStyle: 'normal', color: '#FFFFFF', fontFamily: settings.cardFontFamily || 'poppins' }
                                                // --- FIX END ---
                                            ]
                                        };
                                        updateCardCredit(newCard);
                                        setActiveCardId(newCardId);
                                        setActiveCreditId(null);
                                    }
                                }}
                                className={`p-1.5 rounded-full transition-colors mx-2 ${isCardInOtherList ? 'bg-yellow-500 hover:bg-yellow-600 text-white' : 'bg-zinc-600 hover:bg-zinc-500 text-zinc-300'}`} 
                                title={isCardInOtherList ? 'Remove associated Title Card' : 'Create Title Card from this Credit'}>
                                <Film size={16} />
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); removeCredit([credit.id], false); }} className="p-1.5 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors" title="Remove credit">
                                <X size={16} />
                            </button>
                        </div>
                    );
                })}
            </div>
        );
    };

    // --- END: ScrollingCreditsDisplay Component ---
    
    // --- START: CardCreditsDisplay Component ---
    const CardCreditsDisplay = ({ cardCredits, activeCardId, selectedCreditIds, removeCredit, setActiveCardId, setActiveCreditId, handleCreditSelect, handleCardDragStart, handleCardDragOver, handleCardDrop, draggedCardItem, dragOverCardItem, addCredit }) => {
        return (
            <div className="space-y-2">
                <button onClick={() => addCredit('main-title')} className="w-full flex items-center justify-center p-2 my-2 bg-yellow-800/50 border border-yellow-700 text-yellow-300 rounded-md hover:bg-yellow-800/80 text-sm font-semibold">
                    <Plus size={16} className="mr-1"/> Add Title Card
                </button>
                {cardCredits.length === 0 && <p className="text-xs text-zinc-500 italic mb-2 text-center">No title cards match your search.</p>}
                {cardCredits.map((cardCredit, index) => {
                    const isActive = cardCredit.id === activeCardId;
                    const isSelected = selectedCreditIds.has(cardCredit.id);
                    // isDragging: this card is part of the currently moving selection
                    const isDragging = draggedCardItem !== null && selectedCreditIds.has(cardCredit.id);
                    const isDragOver = dragOverCardItem === cardCredit.id && !isDragging;
                    const displayName = (cardCredit.textLayers && cardCredit.textLayers.length > 0) ? cardCredit.textLayers[0].content : 'Untitled Card';
                    const selectedCardCount = Array.from(selectedCreditIds).filter(id => cardCredits.some(c => c.id === id)).length;
    
                    return (
                        <div
                            key={cardCredit.id}
                            id={`card-item-${cardCredit.id}`}
                            draggable
                            onDragStart={(e) => handleCardDragStart(e, cardCredit.id)}
                            onDragOver={(e) => handleCardDragOver(e, cardCredit.id)}
                            onDrop={(e) => handleCardDrop(e, cardCredit.id)}
                            onClick={(e) => handleCreditSelect(cardCredit.id, e, 'card')}
                            className={`p-3 rounded-lg cursor-grab transition-all duration-200 flex items-center border
                                ${isDragging ? 'opacity-30 bg-zinc-700' : 'bg-zinc-800'}
                                ${isDragOver ? 'border-yellow-400 border-2' : 'border-transparent'}
                                ${isActive ? 'bg-purple-900/50 ring-2 ring-purple-600' : ''}
                                ${isSelected && !isActive ? 'bg-blue-900/40 ring-2 ring-blue-600' : ''}
                                ${!isSelected && !isActive ? 'hover:bg-zinc-700' : ''}
                            `}
                        >
                            <GripVertical size={20} className="text-zinc-500 mr-3 shrink-0" />
                            <div className="flex-grow overflow-hidden pointer-events-none">
                                <p className="text-sm font-medium text-zinc-200 truncate flex items-center gap-2">
                                    <Star size={14} className="text-yellow-400 shrink-0" />
                                    {displayName}
                                </p>
                                <p className="text-xs text-zinc-400 truncate">
                                    {isSelected && selectedCardCount > 1 ? `${selectedCardCount} cards selected` : 'Title Card'}
                                </p>
                            </div>
                            <button onClick={(e) => { e.stopPropagation(); removeCredit([cardCredit.id], true); }} className="p-1 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors" title="Remove card">
                                <X size={16} />
                            </button>
                        </div>
                    );
                })}
            </div>
        );
    };
    // --- END: CardCreditsDisplay Component ---
    

const defaultTemplates = {
    'tv-opening': {
        credits: JSON.stringify([
            { id: 1, role: 'Executive Producer', name: '(Name)', type: 'single' },
            { id: 2, role: 'Executive Producer', name: '(Name)', type: 'single' },
            { id: 3, role: 'Co-Executive Producer', name: '(Name)', type: 'single' }
        ]),
        cardCredits: JSON.stringify([
            { id: 101, type: 'main-title', textLayers: [{ id: 1, content: 'SHOW TITLE', fontSize: 72 }, { id: 2, content: 'Created By: (Creator Name)', fontSize: 36 }] },
            { id: 102, type: 'main-title', textLayers: [{ id: 1, content: '(Star Name)', fontSize: 60 }] },
            { id: 103, type: 'main-title', textLayers: [{ id: 1, content: 'Also Starring', fontSize: 32 }, { id: 2, content: '(Star Name)', fontSize: 48 }] }
        ]),
        settings: JSON.stringify({ speed: 800, fontFamily: 'helvetica', cardFontFamily: 'helvetica', cardFontSize: 72 })
    },
    'indie-film': {
        cardCredits: JSON.stringify([
            { id: 201, type: 'main-title', textLayers: [{ id: 1, content: '(Your Film Title)', fontSize: 64 }]},
            { id: 202, type: 'main-title', textLayers: [{ id: 1, content: 'Directed By', fontSize: 28 }, {id: 2, content: '(Director Name)', fontSize: 42}]},
            { id: 203, type: 'main-title', textLayers: [{ id: 1, content: 'Written By', fontSize: 28 }, {id: 2, content: '(Writer Name)', fontSize: 42}]},
            { id: 204, type: 'main-title', textLayers: [{ id: 1, content: 'Produced By', fontSize: 28 }, {id: 2, content: '(Producer Name)', fontSize: 42}]},
            { id: 205, type: 'main-title', textLayers: [{ id: 1, content: 'Starring', fontSize: 28 }, {id: 2, content: '(Lead Actor Name)', fontSize: 42}]},
        ]),
        credits: JSON.stringify([
            { id: 1, type: 'title', name: 'Cast' },
            { id: 2, type: 'dual', name: '(Actor Name)', role: '(Character Name)' },
            { id: 3, type: 'dual', name: '(Actor Name)', role: '(Character Name)' },
            { id: 4, type: 'title', name: 'Unit Production Manager' },
            { id: 5, type: 'single', name: '(Name)' },
            { id: 6, type: 'title', name: 'First Assistant Director' },
            { id: 7, type: 'single', name: '(Name)' },
            { id: 8, type: 'title', name: 'Art Department' },
            { id: 9, type: 'dual', name: '(Name)', role: '(Role)' },
            { id: 10, type: 'title', name: 'Sound Department' },
            { id: 11, type: 'dual', name: '(Name)', role: '(Role)' },
            { id: 12, type: 'title', name: 'Special Thanks' },
            { id: 13, type: 'single', name: '(Name)' },
            { id: 14, type: 'single', name: '(Name)' },
        ]),
        settings: JSON.stringify({ speed: 150, fontFamily: 'eb-garamond', cardFontFamily: 'eb-garamond', cardFontSize: 64 })
    },
    'studio-film': {
        cardCredits: JSON.stringify([
            { id: 301, type: 'main-title', textLayers: [{ id: 1, content: 'Director', fontSize: 32 }, {id: 2, content: '(Director Name)', fontSize: 48}]},
            { id: 302, type: 'main-title', textLayers: [{ id: 1, content: 'Writers', fontSize: 32 }, {id: 2, content: '(Writer Name)', fontSize: 48}]},
            { id: 303, type: 'main-title', textLayers: [{ id: 1, content: 'Producer', fontSize: 32 }, {id: 2, content: '(Producer Name)', fontSize: 48}]},
            { id: 304, type: 'main-title', textLayers: [{ id: 1, content: 'Executive Producer', fontSize: 32 }, {id: 2, content: '(Producer Name)', fontSize: 48}]},
            { id: 305, type: 'main-title', textLayers: [{ id: 1, content: 'Lead Cast', fontSize: 32 }, {id: 2, content: '(Actor Name)', fontSize: 48}]},
            { id: 306, type: 'main-title', textLayers: [{ id: 1, content: 'Supporting Cast', fontSize: 32 }, {id: 2, content: '(Actor Name)', fontSize: 48}]},
            { id: 307, type: 'main-title', textLayers: [{ id: 1, content: 'Director of Photography', fontSize: 32 }, {id: 2, content: '(Name)', fontSize: 48}]},
            { id: 308, type: 'main-title', textLayers: [{ id: 1, content: 'Production Designer', fontSize: 32 }, {id: 2, content: '(Name)', fontSize: 48}]},
            { id: 309, type: 'main-title', textLayers: [{ id: 1, content: 'Editor', fontSize: 32 }, {id: 2, content: '(Name)', fontSize: 48}]},
            { id: 310, type: 'main-title', textLayers: [{ id: 1, content: 'Costume Designer', fontSize: 32 }, {id: 2, content: '(Name)', fontSize: 48}]},
            { id: 311, type: 'main-title', textLayers: [{ id: 1, content: 'Music Composer', fontSize: 32 }, {id: 2, content: '(Name)', fontSize: 48}]},
            { id: 312, type: 'main-title', textLayers: [{ id: 1, content: 'Casting Director', fontSize: 32 }, {id: 2, content: '(Name)', fontSize: 48}]},
        ]),
        credits: JSON.stringify([
            { id: 401, type: 'title', name: 'Unit Production Manager' },
            { id: 402, type: 'single', name: '(Name)' },
            { id: 403, type: 'title', name: 'First Assistant Director' },
            { id: 404, type: 'single', name: '(Name)' },
            { id: 405, type: 'title', name: 'Second Assistant Director' },
            { id: 406, type: 'single', name: '(Name)' },
            { id: 407, type: 'title', name: 'Cast' },
            { id: 408, type: 'dual', name: '(Actor Name)', role: 'Lead Character' },
            { id: 409, type: 'dual', name: '(Actor Name)', role: 'Supporting Character' },
            { id: 410, type: 'dual', name: '(Actor Name)', role: '(Character Name)' },
            { id: 411, type: 'dual', name: '(Actor Name)', role: '(Character Name)' },
            { id: 412, type: 'title', name: 'Stunt Department' },
            { id: 413, type: 'multiple', role: 'Stunt Performers', name: '(Name)\n(Name)\n(Name)' },
            { id: 414, type: 'title', name: 'Art Department' },
            { id: 415, type: 'multiple', role: 'Set Dressers', name: '(Name)\n(Name)\n(Name)\n(Name)' },
            { id: 416, type: 'title', name: 'Visual Effects' },
            { id: 417, type: 'multiple', role: 'VFX Artists', name: '(Name)\n(Name)\n(Name)' },
            { id: 418, type: 'title', name: 'Songs' },
            { id: 419, type: 'single', name: '"(Song Title)"\nWritten by (Writer)\nPerformed by (Artist)\nCourtesy of (Label)' },
            { id: 420, type: 'title', name: 'Special Thanks' },
            { id: 421, type: 'single', name: '(Name)' },
            { id: 422, type: 'single', name: '(Name)' },
            { id: 423, type: 'logo', layout: 'grid', gridColumns: 4, logos: [{id:1},{id:2},{id:3},{id:4}] },
            { id: 424, type: 'single', name: 'Copyright © 2025 Your Studio Name' },
        ]),
        settings: JSON.stringify({ speed: 200, fontFamily: 'helvetica', cardFontFamily: 'helvetica', cardFontSize: 48, defaultFontWeight: 'bold' })
    }
};


    // --- START: Floating Toolbar Component ---
const FloatingToolbar = ({ 
    selectedCreditIds, 
    credits, 
    cardCredits, 
    onDelete, 
    onConvertToCards, 
    onCopyStyle, 
    onPasteStyle, 
    onGroupTogether,
    onMoveToTop,
    onMoveToBottom,
    copiedCardStyle,
    copiedSettings,
    position 
}) => {
    const [showMoreMenu, setShowMoreMenu] = useState(false);
    const moreMenuRef = useRef(null);
    
    useClickOutside(moreMenuRef, () => setShowMoreMenu(false));
    
   if (selectedCreditIds.size < 2) return null;
    
    const selectedCount = selectedCreditIds.size;
    const hasScrollCredits = Array.from(selectedCreditIds).some(id => credits.some(c => c.id === id));
    const hasCardCredits = Array.from(selectedCreditIds).some(id => cardCredits.some(c => c.id === id));
    
    return (
        <div 
            className="fixed z-30 bg-zinc-900/95 backdrop-blur-sm border border-zinc-700 rounded-lg shadow-2xl p-2 flex items-center gap-1"
            style={{
                bottom: position?.bottom || '2rem',
                left: '50%',
                transform: 'translateX(-50%)',
                transition: 'all 0.2s ease-out'
            }}
        >
            {/* Selection indicator */}
            <div className="px-3 py-2 bg-blue-900/30 rounded-md flex items-center gap-2 mr-1">
                <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium text-blue-200">{selectedCount} selected</span>
            </div>
            
            {/* Quick Actions */}
            {selectedCount === 1 && (
                <>
                    <button
                        onClick={onCopyStyle}
                        className="p-2 text-zinc-300 hover:text-white hover:bg-zinc-800 rounded-md transition-colors"
                        title="Copy Style"
                    >
                        <Copy size={18} />
                    </button>
                </>
            )}
            
            {copiedSettings && (
                <button
                    onClick={onPasteStyle}
                    className="p-2 text-zinc-300 hover:text-white hover:bg-zinc-800 rounded-md transition-colors"
                    title="Paste Style"
                >
                    <ClipboardPaste size={18} />
                </button>
            )}
            
            {hasScrollCredits && (
                <button
                    onClick={onConvertToCards}
                    className="p-2 text-yellow-300 hover:text-yellow-200 hover:bg-zinc-800 rounded-md transition-colors"
                    title="Convert to Title Cards"
                >
                    <Film size={18} />
                </button>
            )}
            
            <div className="w-px h-8 bg-zinc-700 mx-1"></div>
            
            {/* Movement Actions */}
            <button
                onClick={onMoveToTop}
                className="p-2 text-zinc-300 hover:text-white hover:bg-zinc-800 rounded-md transition-colors"
                title="Move to Top"
            >
                <ArrowUp size={18} />
            </button>
            
            <button
                onClick={onMoveToBottom}
                className="p-2 text-zinc-300 hover:text-white hover:bg-zinc-800 rounded-md transition-colors"
                title="Move to Bottom"
            >
                <ArrowDown size={18} />
            </button>
            
            {selectedCount > 1 && (
                <button
                    onClick={onGroupTogether}
                    className="p-2 text-zinc-300 hover:text-white hover:bg-zinc-800 rounded-md transition-colors"
                    title="Group Together"
                >
                    <Grid3x3 size={18} />
                </button>
            )}
            
            <div className="w-px h-8 bg-zinc-700 mx-1"></div>
            
            {/* More Options */}
            <div className="relative" ref={moreMenuRef}>
                <button
                    onClick={() => setShowMoreMenu(!showMoreMenu)}
                    className="p-2 text-zinc-300 hover:text-white hover:bg-zinc-800 rounded-md transition-colors"
                    title="More Options"
                >
                    <MoreVertical size={18} />
                </button>
                
                {showMoreMenu && (
                    <div className="absolute bottom-full mb-2 right-0 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl p-2 min-w-[200px]">
                        <button
                            onClick={() => {
                                // Duplicate selected
                                const newCredits = [];
                                const newCards = [];
                                
                                selectedCreditIds.forEach(id => {
                                    const credit = credits.find(c => c.id === id);
                                    const card = cardCredits.find(c => c.id === id);
                                    
                                    if (credit) {
                                        newCredits.push({
                                            ...JSON.parse(JSON.stringify(credit)),
                                            id: Date.now() + Math.random()
                                        });
                                    }
                                    if (card) {
                                        newCards.push({
                                            ...JSON.parse(JSON.stringify(card)),
                                            id: Date.now() + Math.random()
                                        });
                                    }
                                });
                                
                                // Add them to state through parent
                                window.dispatchEvent(new CustomEvent('duplicateCredits', { 
                                    detail: { credits: newCredits, cards: newCards }
                                }));
                                setShowMoreMenu(false);
                            }}
                            className="w-full text-left px-3 py-2 text-sm text-zinc-200 hover:bg-zinc-700 rounded-md flex items-center gap-2"
                        >
                            <CopyPlus size={16} /> Duplicate All
                        </button>
                        
                        <button
                            onClick={() => {
                                // Apply film standard order
                                window.dispatchEvent(new CustomEvent('applyFilmOrder'));
                                setShowMoreMenu(false);
                            }}
                            className="w-full text-left px-3 py-2 text-sm text-zinc-200 hover:bg-zinc-700 rounded-md flex items-center gap-2"
                        >
                            <Award size={16} /> Film Standard Order
                        </button>
                        
                        <button
                            onClick={() => {
                                // Reset all styles
                                window.dispatchEvent(new CustomEvent('resetSelectedStyles'));
                                setShowMoreMenu(false);
                            }}
                            className="w-full text-left px-3 py-2 text-sm text-zinc-200 hover:bg-zinc-700 rounded-md flex items-center gap-2"
                        >
                            <RotateCcw size={16} /> Reset Styles
                        </button>
                        
                        <div className="border-t border-zinc-700 my-1"></div>
                        
                        <button
                            onClick={() => {
                                onDelete();
                                setShowMoreMenu(false);
                            }}
                            className="w-full text-left px-3 py-2 text-sm text-red-400 hover:bg-zinc-700 rounded-md flex items-center gap-2"
                        >
                            <Trash2 size={16} /> Delete Selected
                        </button>
                    </div>
                )}
            </div>
            
            <div className="w-px h-8 bg-zinc-700 mx-1"></div>
            
            {/* Delete Action */}
            <button
                onClick={onDelete}
                className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded-md transition-colors"
                title="Delete Selected"
            >
                <Trash2 size={18} />
            </button>
            
            {/* Clear Selection */}
            <button
                onClick={() => window.dispatchEvent(new CustomEvent('clearSelection'))}
                className="p-2 text-zinc-400 hover:text-zinc-300 hover:bg-zinc-800 rounded-md transition-colors ml-1"
                title="Clear Selection"
            >
                <X size={18} />
            </button>
        </div>
    );
};


    // --- START: New useClickOutside Hook ---
const useClickOutside = (ref, handler) => {
  useEffect(() => {
    const listener = (event) => {
      // Do nothing if clicking ref's element or descendent elements
      if (!ref.current || ref.current.contains(event.target)) {
        return;
      }
      handler(event);
    };
    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);
    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, handler]);
};

// 1. Replace your existing IconButton component with this corrected version.
const IconButton = ({ icon: Icon, onClick, isActive = false, title, children, isMenuButton = false, isOpen = false, menuName = '', noticeStatus = 'none', menuRef }) => (
    <div className="relative">
        <button
            onClick={isMenuButton ? (e) => onClick(menuName, e) : onClick}
            title={title}
            data-menu-trigger={menuName}
            className={`p-2 rounded-full transition-colors flex items-center justify-center gap-1 ${isActive || isOpen ? 'bg-purple-600 text-white' : 'text-zinc-300 hover:bg-zinc-700'}`}
        >
            {Icon && <Icon size={18} />}
        </button>
        {noticeStatus !== 'none' && (
             <div className={`absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-zinc-900 ${
                noticeStatus === 'processing' ? 'bg-yellow-400 animate-pulse' :
                noticeStatus === 'ready' ? 'bg-green-400' :
                'bg-red-500'
            }`}></div>
        )}
        {isOpen && (
             <div ref={menuRef} className="absolute top-full mt-2 left-0 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl p-2 min-w-[250px] z-20">
                {children}
            </div>
        )}
    </div>
);



// Replace the ENTIRE TopMenuBar component with this corrected version.

const TopMenuBar = forwardRef(({
    togglePlayPause,
    isPlaying,
    rewindAnimation,
    addCredit,
    setShowBatchAddModal,
    setShowImportModal,
    settings,
    setSettings,
    creditsCount,
    onExportClick,
    showSafeGuides,
    setShowSafeGuides,
    showMessageBox,
    fontFamilyOptions,
    handleDefaultFamilyChange,
    handleSuggestDuration,
    setShowSettingsModal,
    setShowFindReplaceModal,
    exportNoticeStatus,
    showAssistant,
    setShowAssistant
}, ref) => {
    const [openMenu, setOpenMenu] = useState(null);
    const menuRef = useRef(null);
    const handleMenuClick = (menuName, e) => {
        e.stopPropagation();
        setOpenMenu(openMenu === menuName ? null : menuName);
    };

    // Lets external callers (like the AI Copilot's "take me there" actions)
    // open one of these dropdown menus programmatically. Deferred with a
    // timeout so it fires after the triggering click has already finished
    // bubbling/closing other UI (e.g. the assistant panel), otherwise the
    // document-level click-outside listener below can immediately close it.
    useImperativeHandle(ref, () => ({
        openMenu: (menuName) => {
            setTimeout(() => setOpenMenu(menuName), 0);
        }
    }));

   useEffect(() => {
        const handleClickOutside = (event) => {
            if (event.target.closest('[data-menu-trigger]')) {
                return;
            }
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setOpenMenu(null);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, []);


    // --- THIS IS THE FIX ---
    // I've modified this internal IconButton component to handle the new logic.
    const IconButton = ({ icon: Icon, onClick, isActive = false, title, children, hasMenu = false, menuName = '', noticeStatus = 'none', isExportButton = false }) => {
        
        // This function now determines the correct button color class
        const getButtonClass = () => {
            // Active/open menus are always purple
            if (isActive || openMenu === menuName) {
                return 'bg-purple-600 text-white';
            }

            // Specific logic for the Export button
            if (isExportButton) {
                switch (noticeStatus) {
                    case 'processing':
                        return 'text-yellow-400 hover:bg-yellow-700 animate-pulse'; // Yellow for processing
                    case 'ready':
                        return 'text-green-400 hover:bg-green-700'; // Green for ready
                    case 'failed':
                        return 'text-red-400 hover:bg-red-700'; // Red for failed
                    default:
                        // IDLE state is now green, as requested
                        return 'text-green-400 hover:bg-green-700'; 
                }
            }
            
            // Default for all other buttons
            return 'text-zinc-300 hover:bg-zinc-700';
        };

        return (
            <div className="relative">
                <button
                    onMouseDown={hasMenu ? (e) => { e.preventDefault(); handleMenuClick(menuName, e); } : undefined}
                    onClick={!hasMenu ? onClick : undefined}
                    title={title}
                    data-menu-trigger={menuName}
                    // The className is now set by our new logic function
                    className={`p-2 rounded-full transition-colors flex items-center justify-center gap-1 ${getButtonClass()}`}
                >
                    {Icon && <Icon size={18} />}
                </button>
                
                {/* The status dot is still here and works as intended */}
                {noticeStatus !== 'none' && (
                     <div className={`absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-zinc-900 ${
                        noticeStatus === 'processing' ? 'bg-yellow-400 animate-pulse' :
                        noticeStatus === 'ready' ? 'bg-green-400' :
                        'bg-red-500'
                    }`}></div>
                )}
                {hasMenu && openMenu === menuName && (
                     <div ref={menuRef} className="absolute top-full mt-2 left-0 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl p-2 min-w-[280px] z-20">
                        {children}
                    </div>
                )}
            </div>
        );
    };
    
    const shortcutsContent = (
        <div className="text-left text-sm space-y-4">
            <div>
                <h4 className="font-bold text-purple-300 mb-2">PLAYBACK</h4>
                <div className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1 items-center">
                    <code className="bg-zinc-700 px-2 py-1 rounded-md font-mono">Space</code>
                    <span className="text-zinc-300">Play / Pause</span>
                </div>
            </div>
            <div>
                <h4 className="font-bold text-purple-300 mb-2">SELECTION & NAVIGATION</h4>
                <div className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1 items-center">
                    <code className="bg-zinc-700 px-2 py-1 rounded-md font-mono">Click</code>
                    <span className="text-zinc-300">Select Item</span>
                    <code className="bg-zinc-700 px-2 py-1 rounded-md font-mono">Cmd/Ctrl + Click</code>
                    <span className="text-zinc-300">Add to Selection</span>
                     <code className="bg-zinc-700 px-2 py-1 rounded-md font-mono">Shift + Click</code>
                    <span className="text-zinc-300">Select Range</span>
                    <code className="bg-zinc-700 px-2 py-1 rounded-md font-mono">Esc</code>
                    <span className="text-zinc-300">Deselect All</span>
                    <code className="bg-zinc-700 px-2 py-1 rounded-md font-mono">↑ / ↓</code>
                    <span className="text-zinc-300">Navigate Credits</span>
                </div>
            </div>
            <div>
                <h4 className="font-bold text-purple-300 mb-2">EDITING</h4>
                <div className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1 items-center">
                    <code className="bg-zinc-700 px-2 py-1 rounded-md font-mono">Cmd/Ctrl + C</code>
                    <span className="text-zinc-300">Copy Style / Credit</span>
                    <code className="bg-zinc-700 px-2 py-1 rounded-md font-mono">Cmd/Ctrl + V</code>
                    <span className="text-zinc-300">Paste Style / Credit</span>
                    <code className="bg-zinc-700 px-2 py-1 rounded-md font-mono">Cmd/Ctrl + D</code>
                    <span className="text-zinc-300">Duplicate</span>
                    <code className="bg-zinc-700 px-2 py-1 rounded-md font-mono">Cmd/Ctrl + Z</code>
                    <span className="text-zinc-300">Undo up to 10 moves</span>
                    <code className="bg-zinc-700 px-2 py-1 rounded-md font-mono">Delete / Bksp</code>
                    <span className="text-zinc-300">Delete Selected</span>
                    <code className="bg-zinc-700 px-2 py-1 rounded-md font-mono">Shift + Cmd/Ctrl + B</code>
                    <span className="text-zinc-300">Batch Add (paste credit list)</span>
                </div>
            </div>
        <div className="pt-3 border-t border-zinc-700">
            <h4 className="font-bold text-purple-300 mb-2">NEED HELP?</h4>
            <p className="text-zinc-400 text-xs mb-2">
                Questions or issues? We're here to help!
            </p>
            <a 
                href="mailto:support@blackbox-is.com" 
                className="inline-block w-full text-center bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 transition-colors text-sm font-semibold"
            >
                Contact Support
            </a>
            <p className="text-zinc-500 text-xs text-center mt-2">
                support@blackbox-is.com
            </p>
        </div>
        </div>
    );

    return (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 p-1.5 bg-zinc-900/80 backdrop-blur-sm border border-zinc-700 rounded-full shadow-2xl">
            {/* Left side: Playback and Add */}
            <div className="flex items-center gap-1">
                <IconButton icon={isPlaying ? Pause : Play} onClick={togglePlayPause} isActive={isPlaying} title={isPlaying ? "Pause" : "Play"} />
                <IconButton icon={RotateCcw} onClick={rewindAnimation} title="Rewind" />
                <div className="w-px h-6 bg-zinc-700 mx-1"></div>
                <IconButton icon={Plus} hasMenu={true} menuName="add" title="Add Credit">
                    <button onClick={() => { addCredit('main-title'); setOpenMenu(null); }} className="w-full text-left px-3 py-2 text-sm text-yellow-300 hover:bg-zinc-700 rounded-md flex items-center gap-2"><Film size={16}/> Title Card</button>
                    <button onClick={() => { addCredit('dual'); setOpenMenu(null); }} className="w-full text-left px-3 py-2 text-sm text-zinc-200 hover:bg-zinc-700 rounded-md flex items-center gap-2"><List size={16}/> Two Column</button>
                    <button onClick={() => { addCredit('single'); setOpenMenu(null); }} className="w-full text-left px-3 py-2 text-sm text-zinc-200 hover:bg-zinc-700 rounded-md flex items-center gap-2"><Type size={16}/> Single Line</button>
                    <button onClick={() => { addCredit('multiple'); setOpenMenu(null); }} className="w-full text-left px-3 py-2 text-sm text-zinc-200 hover:bg-zinc-700 rounded-md flex items-center gap-2"><Grid3x3 size={16}/> Multi-Name Block</button>
                    <button onClick={() => { addCredit('song'); setOpenMenu(null); }} className="w-full text-left px-3 py-2 text-sm text-zinc-200 hover:bg-zinc-700 rounded-md flex items-center gap-2"><Music size={16}/> Song Credits</button>
                    <button onClick={() => { addCredit('logo'); setOpenMenu(null); }} className="w-full text-left px-3 py-2 text-sm text-zinc-200 hover:bg-zinc-700 rounded-md flex items-center gap-2"><Image size={16}/> Logo</button>
                    <div className="border-t border-zinc-700 my-1"></div>
                    <button onClick={() => { setShowBatchAddModal(true); setOpenMenu(null); }} className="w-full text-left px-3 py-2 text-sm text-blue-300 hover:bg-zinc-700 rounded-md flex items-center gap-2"><Rows4 size={16}/> Batch Add</button>
                    <button onClick={() => { setShowImportModal(true); setOpenMenu(null); }} className="w-full text-left px-3 py-2 text-sm text-green-300 hover:bg-zinc-700 rounded-md flex items-center gap-2"><Upload size={16}/> Import CSV</button>
                </IconButton>
                {/* Batch Add is the primary ingestion path, so it gets its own
                    always-visible button rather than living one level down in the
                    Add menu. Tinted purple so it reads as the featured action. */}
                <button
                    onClick={() => setShowBatchAddModal(true)}
                    title="Paste Your Credit List — Batch Add (Shift+Cmd/Ctrl+B)"
                    className="flex items-center gap-1.5 rounded-full border border-purple-500/40 bg-purple-600/20 py-2 pl-2.5 pr-3 text-purple-200 transition-all hover:border-purple-400/60 hover:bg-purple-600/40 hover:text-white"
                >
                    <Rows4 size={18} />
                    <span className="hidden text-[11px] font-bold uppercase tracking-wider lg:inline">Paste List</span>
                </button>
            </div>

            <div className="w-px h-6 bg-zinc-700 mx-1"></div>

            {/* Center: Main Settings */}
            <div className="flex items-center gap-1">
                 <IconButton icon={Type} hasMenu={true} menuName="typography" title="Typography">
                    <div className="p-2 space-y-3">
                        <GlobalFontControl
                            settings={settings}
                            fontFamilyOptions={fontFamilyOptions}
                            handleDefaultFamilyChange={handleDefaultFamilyChange}
                            defaultFamily="poppins"
                            compact={true}
                        />
                        <NumberStepper label="Size" value={settings.fontSize} onCommit={value => setSettings(s => ({...s, fontSize: value}))} min={8} max={120} step={1} />
                        <NumberStepper label="Line Spacing" value={settings.lineHeight || 1.5} onCommit={value => setSettings(s => ({...s, lineHeight: value}))} min={1.0} max={3} step={0.1} />
                        <NumberStepper label="Space Between Credits" value={settings.blockSpacing ?? 2} onCommit={value => setSettings(s => ({...s, blockSpacing: value}))} min={0.5} max={10} step={0.1} />
                        <NumberStepper label="Letter Spacing" value={settings.letterSpacing || 0} onCommit={value => setSettings(s => ({...s, letterSpacing: value}))} min={-5} max={10} step={0.5} />
                        <NumberStepper label="Gutter (rem)" value={settings.gutterWidth || 3} onCommit={value => setSettings(s => ({...s, gutterWidth: value}))} min={1} max={10} step={0.5} />
                        <StyleControl label="Justification">
                            <div className="flex items-center space-x-1 bg-zinc-800 p-1 rounded-md">
                                {[{v:'left', i: AlignLeft}, {v:'center', i: AlignCenter}, {v:'right', i: AlignRight}, {v:'classic', i: MoveHorizontal}, {v:'justified', i: AlignJustify}].map(item => (
                                    <button key={item.v} onClick={() => setSettings(s => ({...s, dualAlignment: item.v}))} className={`flex-1 p-2 rounded-md flex justify-center items-center transition-colors ${(settings.dualAlignment || 'classic') === item.v ? 'bg-purple-600 text-white' : 'hover:bg-zinc-700'}`} title={item.v === 'classic' ? 'Classic (Role & Name meet at the gutter)' : item.v}>
                                        <item.i size={16} />
                                    </button>
                                ))}
                            </div>
                        </StyleControl>
                        <div className="pt-2 mt-1 border-t border-zinc-700 space-y-3">
                            <StyleControl label="Role Case">
                                <select
                                    value={settings.roleCaps}
                                    onChange={(e) => setSettings(s => ({...s, roleCaps: e.target.value}))}
                                    className="w-32 text-xs p-1.5 rounded border border-zinc-600 bg-zinc-800 text-gray-200 focus:ring-1 focus:ring-purple-500"
                                >
                                    {CASE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                </select>
                            </StyleControl>
                            <StyleControl label="Name Case">
                                <select
                                    value={settings.nameCaps}
                                    onChange={(e) => setSettings(s => ({...s, nameCaps: e.target.value}))}
                                    className="w-32 text-xs p-1.5 rounded border border-zinc-600 bg-zinc-800 text-gray-200 focus:ring-1 focus:ring-purple-500"
                                >
                                    {CASE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                </select>
                            </StyleControl>
                            <StyleControl label="Header Case">
                                <select
                                    value={settings.blockNameCaps}
                                    onChange={(e) => setSettings(s => ({...s, blockNameCaps: e.target.value}))}
                                    className="w-32 text-xs p-1.5 rounded border border-zinc-600 bg-zinc-800 text-gray-200 focus:ring-1 focus:ring-purple-500"
                                >
                                    {CASE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                </select>
                            </StyleControl>
                        </div>
                    </div>
                </IconButton>

                <IconButton icon={List} hasMenu={true} menuName="sequence" title="Scroll Type">
                    <button 
                        onClick={() => {setSettings(s => ({...s, cardSequence: 'before'})); setOpenMenu(null);}} 
                        className="w-full text-left px-3 py-2 text-sm text-zinc-200 hover:bg-zinc-700 rounded-md flex items-center justify-between gap-2"
                    >
                        <span className="flex items-center gap-2"><Film size={16}/><ChevronsRight size={16}/><List size={16}/> Cards then Scroll</span>
                        {settings.cardSequence === 'before' && <Check size={16} className="text-green-400" />}
                    </button>
                    <button 
                        onClick={() => {setSettings(s => ({...s, cardSequence: 'after'})); setOpenMenu(null);}} 
                        className="w-full text-left px-3 py-2 text-sm text-zinc-200 hover:bg-zinc-700 rounded-md flex items-center justify-between gap-2"
                    >
                         <span className="flex items-center gap-2"><List size={16}/><ChevronsRight size={16}/><Film size={16}/> Scroll then Cards</span>
                        {settings.cardSequence === 'after' && <Check size={16} className="text-green-400" />}
                    </button>
                    <button 
                        onClick={() => {setSettings(s => ({...s, cardSequence: 'cards-only'})); setOpenMenu(null);}} 
                        className="w-full text-left px-3 py-2 text-sm text-zinc-200 hover:bg-zinc-700 rounded-md flex items-center justify-between gap-2"
                    >
                        <span className="flex items-center gap-2"><Film size={16}/> Cards Only</span>
                        {settings.cardSequence === 'cards-only' && <Check size={16} className="text-green-400" />}
                    </button>
                    <button 
                        onClick={() => {setSettings(s => ({...s, cardSequence: 'scroll-only'})); setOpenMenu(null);}} 
                        className="w-full text-left px-3 py-2 text-sm text-zinc-200 hover:bg-zinc-700 rounded-md flex items-center justify-between gap-2"
                    >
                        <span className="flex items-center gap-2"><List size={16}/> Scroll Only</span>
                        {settings.cardSequence === 'scroll-only' && <Check size={16} className="text-green-400" />}
                    </button>
                </IconButton>

                <IconButton icon={Timer} hasMenu={true} menuName="timing" title="Timing">
                <div className="p-2 space-y-2 w-56">
    <div className="flex items-center justify-between py-1.5">
        <label className="block text-sm font-normal text-zinc-400">Duration</label>
    </div>
    {(() => {
        const lineCount = creditsCount;
        const minRec = lineCount > 0 ? lineCount * MIN_SECONDS_PER_LINE : 0;
        const maxRec = lineCount > 0 ? lineCount * MAX_SECONDS_PER_LINE : 9999;
        const current = Number(settings.targetDuration) || 0;
        const tooFast = lineCount > 0 && current > 0 && current < minRec;
        const tooSlow = lineCount > 0 && current > maxRec;

        const formatDuration = (totalSeconds) => {
            const s = Math.round(totalSeconds);
            const m = Math.floor(s / 60);
            const rem = s % 60;
            return m > 0 ? `${m}m ${rem}s` : `${rem}s`;
        };

        return (
            <>
                <div className="flex items-start gap-2">
                    <DurationStepper
                        className="flex-grow"
                        value={settings.targetDuration}
                        onCommit={value => setSettings(s => ({ ...s, targetDuration: Math.min(value, maxRec), speedMode: 'auto' }))}
                        min={1}
                        max={Math.round(maxRec)}
                        step={1}
                    />
                    <button onClick={handleSuggestDuration} title="Suggest a duration for the smoothest scroll" className="p-2 bg-zinc-700 text-purple-300 rounded-md hover:bg-zinc-600 transition-colors shrink-0">
                        <Sparkles size={16} />
                    </button>
                </div>
                <p className="text-xs text-zinc-500 pt-0.5">
                    Render length matches this duration exactly, unless it falls outside the recommended range below — then it's capped to stay readable.
                </p>
                {lineCount > 0 && (
                    <div className="space-y-1 pt-1">
                        <p className="text-sm text-zinc-400">
                            {lineCount} lines · recommended {formatDuration(minRec)}–{formatDuration(maxRec)}
                        </p>
                        {(tooFast || tooSlow) && (
                            <p className="text-sm font-medium text-amber-400 leading-snug">
                                {tooFast && `Below the recommended pace for smooth motion at this length.`}
                                {tooSlow && `Longer than needed for this many lines — will be capped at ${formatDuration(maxRec)} at render.`}
                            </p>
                        )}
                    </div>
                )}
            </>
        );
    })()}
</div>
            </IconButton>
            </div>

            <div className="w-px h-6 bg-zinc-700 mx-1"></div>

            {/* Right side: Tools (Reordered) */}
            <div className="flex items-center gap-1">
                <IconButton icon={Search} onClick={() => setShowFindReplaceModal(true)} title="Find and Replace" />
                <IconButton icon={Eye} onClick={() => setShowSafeGuides(!showSafeGuides)} isActive={showSafeGuides} title="Toggle Safe Guides" />
                <IconButton icon={Settings2} onClick={() => setShowSettingsModal(true)} title="Project Settings" />
                <IconButton icon={Info} onClick={() => showMessageBox("Keyboard Shortcuts", shortcutsContent, "info")} title="Information & Shortcuts" />
                <IconButton 
    icon={Sparkles} 
    onClick={() => setShowAssistant(!showAssistant)} 
    isActive={showAssistant} 
    title="AI Copilot" 
/>
                {/* --- This is the corrected Export button --- */}
                <IconButton 
                    icon={Download} 
                    onClick={onExportClick} 
                    title="Export & Render Queue" 
                    noticeStatus={exportNoticeStatus} 
                    isExportButton={true} // This flag enables the new status-color logic
                />
            </div>
        </div>
    );
});
TopMenuBar.displayName = 'TopMenuBar';

const SettingsModal = ({
    isOpen,
    onClose,
    // Music Sync Props
    audioInfo,
    handleAudioUpload,
    syncScrollToMusic,
    // Project Management Props
    saveProjectFile,
    saveAsTemplate,
    setShowTemplatesModal,
    // Language Props
    selectedDisplayLanguage,
    setSelectedDisplayLanguage
}) => {
    const [activeTab, setActiveTab] = useState('music');

    if (!isOpen) return null;

    const TabButton = ({ tabName, icon: Icon, label }) => (
        <button
            onClick={() => setActiveTab(tabName)}
            className={`flex-1 p-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors rounded-t-lg ${
                activeTab === tabName ? 'bg-zinc-800 text-purple-400 border-b-2 border-purple-400' : 'text-zinc-400 hover:bg-zinc-800/50'
            }`}
        >
            <Icon size={16} />
            {label}
        </button>
    );

    return (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 font-poppins">
            <div className="bg-zinc-900 rounded-xl shadow-2xl max-w-lg w-full text-gray-100 border border-zinc-700 flex flex-col max-h-[90vh]">
                <div className="p-6 border-b border-zinc-800 flex items-center justify-between">
                    <h3 className="text-2xl font-thin text-purple-400 flex items-center gap-2"><Settings2 size={24} /> Project Settings</h3>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-zinc-700 text-gray-400"><X size={20} /></button>
                </div>

                <div className="border-b border-zinc-800 px-4">
                    <div className="flex">
                        <TabButton tabName="music" icon={Music} label="Music Sync" />
                        <TabButton tabName="management" icon={Save} label="Management" />
                    </div>
                </div>

                <div className="p-6 overflow-y-auto flex-grow">
                    {activeTab === 'music' && (
                        <div className="space-y-4">
                            <h4 className="text-lg font-semibold text-zinc-200 mb-2">Sync to Audio</h4>
                            <p className="text-sm text-zinc-400">Upload an audio track to automatically sync your scroll duration to the music length.</p>
                            <label htmlFor="audio-upload-modal" className="w-full flex items-center justify-center p-3 bg-zinc-800/70 text-zinc-200 rounded-lg hover:bg-zinc-700 text-sm cursor-pointer transition-colors">
                                <FileAudio size={16} className="mr-2"/> Upload Audio Track
                            </label>
                            <input id="audio-upload-modal" type="file" accept="audio/*" onChange={handleAudioUpload} className="hidden" />
                            {audioInfo.name && (
                                <div className="text-sm text-zinc-400 p-3 bg-zinc-950 rounded-lg border border-zinc-700">
                                    <p className="font-semibold text-zinc-200 truncate">{audioInfo.name}</p>
                                    {audioInfo.duration > 0 ? (
                                        <p>Duration: {audioInfo.duration.toFixed(2)}s</p>
                                    ) : (
                                        <p>Loading metadata...</p>
                                    )}
                                </div>
                            )}
                            <button onClick={syncScrollToMusic} disabled={!audioInfo.duration} className="w-full flex items-center justify-center p-2.5 bg-green-700 text-green-100 rounded-lg text-sm font-medium hover:bg-green-600 disabled:bg-zinc-700 disabled:text-zinc-500 disabled:cursor-not-allowed transition-colors">
                                <Timer size={16} className="mr-2" /> Sync Scroll to Music
                            </button>
                        </div>
                    )}
                    {activeTab === 'management' && (
                         <div className="space-y-4">
                             <h4 className="text-lg font-semibold text-zinc-200 mb-2">Save & Load</h4>
                             <p className="text-sm text-zinc-400">Manage your project files and create reusable templates.</p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <button onClick={saveProjectFile} className="p-3 bg-zinc-800/70 text-zinc-100 rounded-lg hover:bg-zinc-700 flex items-center justify-center gap-2 text-sm transition-colors">
                                    <Save size={14}/> Save to File
                                </button>
                                <button onClick={saveAsTemplate} className="p-3 bg-zinc-800/70 text-zinc-100 rounded-lg hover:bg-zinc-700 flex items-center justify-center gap-2 text-sm transition-colors">
                                    <CopyPlus size={14}/> Save as Template
                                </button>
                            </div>
                            <button onClick={() => { onClose(); setShowTemplatesModal(true); }} className="w-full p-3 bg-zinc-800/70 text-zinc-100 rounded-lg hover:bg-zinc-700 flex items-center justify-center gap-2 text-sm transition-colors">
                                <Newspaper size={14}/> Load Template
                            </button>
                        </div>
                    )}
                    
                </div>

                <div className="p-6 border-t border-zinc-800 flex justify-end">
                    <button onClick={onClose} className="bg-zinc-700 text-gray-200 px-5 py-2 rounded-lg hover:bg-zinc-600">Close</button>
                </div>
            </div>
        </div>
    );
};

const UpgradeTrialBanner = ({ onUpgrade, isUpgrading }) => {
    const [isOpen, setIsOpen] = React.useState(false);

    return (
        <div className="border border-zinc-800 bg-zinc-950 rounded-lg p-3 transition-all duration-200 hover:border-zinc-700/80 mb-3 mx-1">
            {/* Minimal Trigger */}
            <button 
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between text-left group"
            >
                <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-amber-500/80 shadow-[0_0_8px_rgba(245,158,11,0.4)]"></div>
                    <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-400 group-hover:text-zinc-200 transition-colors">
                        Trial Account
                    </span>
                </div>
                <span className="text-[10px] font-mono font-bold text-zinc-600 group-hover:text-purple-400 transition-colors uppercase tracking-wider">
                    {isOpen ? 'Close' : 'Unlock 4K / ProRes'}
                </span>
            </button>

            {/* Elegant Drawer */}
            {isOpen && (
                <div className="mt-3 pt-3 border-t border-zinc-900 space-y-2 animate-in fade-in slide-in-from-top-1 duration-200">
                    <p className="text-[11px] leading-relaxed text-zinc-500 font-medium">
                        Ready for master deployment? Select a tier capacity license to remove watermarks, access 4K frames, and enable NLE xml configurations.
                    </p>
                    <div className="flex gap-2 pt-1">
                        <button
                            onClick={() => onUpgrade('indie')}
                            disabled={isUpgrading}
                            className="flex-1 py-2 rounded border border-zinc-800 bg-zinc-900 hover:bg-zinc-800 hover:border-zinc-700 text-[11px] font-bold tracking-wider uppercase text-zinc-200 transition-all disabled:opacity-40"
                        >
                            Indie ($199)
                        </button>
                        <button
                            onClick={() => onUpgrade('pro')}
                            disabled={isUpgrading}
                            className="flex-1 py-2 rounded bg-zinc-100 hover:bg-white text-black text-[11px] font-bold tracking-wider uppercase transition-all disabled:opacity-40"
                        >
                            {isUpgrading ? '...' : 'Studio Pro ($399)'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};


// --- EditorPage Component (Contains core playback and editing logic) ---
// Maps the navigation targets the assistant can suggest to the actual
// UI actions available in the Editor. Keep keys in sync with NAV_TARGETS
// in assistant.py.
const ASSISTANT_NAV_LABELS = {
  'projects': 'Go to Projects',
  'import': 'Open Import',
  'batch-add': 'Open Batch Add',
  'add-credit': 'Open Add Credit',
  'typography': 'Open Typography',
  'sequence': 'Open Scroll Type',
  'timing': 'Open Timing',
  'find-replace': 'Open Find & Replace',
  'settings': 'Open Project Settings',
  'export': 'Open Export',
  'render-queue': 'Open Render Queue',
  'shortcuts': 'Open Shortcuts',
};

const AssistantPanel = ({ isOpen, onClose, currentContext, onNavigateAction, seedPrompt, onSeedConsumed }) => {
  const { settings, activeCreditId, projectTier, creditCount, hasCardCredits, exampleCredit } = currentContext; // Destructure context
  const [messages, setMessages] = useState([
      { role: 'assistant', text: 'How can I help with your sequence today? I can walk you through anything from importing your credit list to exporting a final render.' }
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  // Tracks which seeded question we've already fired so a re-render (or the
  // same question asked twice in a row) can't double-send it.
  const consumedSeedRef = useRef(null);
  const sendMessageRef = useRef(null);

  // Lets other parts of the editor hand the Copilot a question on the user's
  // behalf — e.g. the "Ask the Copilot" link inside Batch Add. The panel opens
  // with an answer already on the way instead of an empty input box.
  useEffect(() => {
      if (!isOpen || !seedPrompt) return;
      if (consumedSeedRef.current === seedPrompt) return;
      consumedSeedRef.current = seedPrompt;
      if (sendMessageRef.current) sendMessageRef.current(seedPrompt);
      if (onSeedConsumed) onSeedConsumed();
  }, [isOpen, seedPrompt, onSeedConsumed]);

  const sendMessage = async (rawText) => {
      const trimmedInput = (rawText || '').trim();
      if (!trimmedInput) return;

      // 1. Add user message
      const userMessage = { role: 'user', text: trimmedInput };
      const historyForRequest = messages
          .filter(m => !m.isError)
          .map(m => ({ role: m.role, text: m.text }));
      setMessages(prev => [...prev, userMessage]);
      setInput('');
      setIsTyping(true);

      // 2. Front-end Guard: Handle special topics instantly
      const lowerInput = trimmedInput.toLowerCase();
      let instantReply = null;

      if (lowerInput.includes('refund')) {
          instantReply = "I am an AI assistant and cannot process refunds. Please contact our support team at support@blackbox-is.com, and they will assist you with your request.";
      }

      if (instantReply) {
          setMessages(prev => [...prev, { role: 'assistant', text: instantReply }]);
          setIsTyping(false);
          return;
      }

      // 3. API Call if no guard triggered
      try {
          const response = await fetch("https://wes-68745--blackboxcr-assistant-fastapi-app.modal.run", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
              message: trimmedInput,
              context: {
                  page: 'editor',
                  // If activeCreditId is undefined, send null explicitly
                  activeCreditId: activeCreditId || null,
                  settings: settings,
                  projectTier: projectTier || null,
                  creditCount: typeof creditCount === 'number' ? creditCount : null,
                  hasCardCredits: !!hasCardCredits,
                  exampleCredit: exampleCredit || null
              },
              history: historyForRequest
              }),
          });

          if (!response.ok) {
  const errorText = await response.text(); // Get the actual error from Modal
  console.error("Modal Server Error:", errorText);
  throw new Error(`Server responded with ${response.status}: ${errorText}`);
}
          const data = await response.json();
          setMessages(prev => [...prev, { role: 'assistant', text: data.reply, action: data.action || null }]);
      } catch (error) {
          console.error("Copilot Error:", error);
          setMessages(prev => [...prev, { role: 'assistant', text: 'I am having trouble connecting to the server. Please check your connection or email support@blackbox-is.com.', isError: true }]);
      } finally {
          setIsTyping(false);
      }
  };

  // Kept in a ref so the seed effect above can reach the latest closure
  // without re-running every render.
  sendMessageRef.current = sendMessage;

  const handleSend = (e) => {
      e.preventDefault();
      const trimmedInput = input.trim();
      if (!trimmedInput) return;
      setInput('');
      sendMessage(trimmedInput);
  };

  if (!isOpen) return null;

  return (
      /* z-[60] so the Copilot can float above full-screen dialogs like Batch
         Add — contextual help is useless if the thing you need help with has
         to be closed first. */
      <div data-assistant-panel="true" className="fixed right-4 top-20 bottom-4 w-96 bg-zinc-950 border border-zinc-800 rounded-xl shadow-2xl flex flex-col z-[60] overflow-hidden font-poppins">
          <div className="p-4 border-b border-zinc-800 flex items-center justify-between bg-zinc-900/50">
              <div className="flex items-center gap-2">
                  <Sparkles size={16} className="text-purple-400" />
                  <h3 className="text-sm font-bold text-white tracking-widest uppercase">Copilot</h3>
              </div>
              <button onClick={onClose} className="p-1.5 text-zinc-500 hover:text-white hover:bg-zinc-800 rounded-md transition-colors">
                  <X size={16} />
              </button>
          </div>

          <div className="flex-grow p-4 overflow-y-auto space-y-4 custom-scrollbar">
              {messages.map((msg, idx) => (
                  <div key={idx} className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
                      {/* FIX: onMouseDown prevents selection loss */}
                      <div 
                          onMouseDown={(e) => e.stopPropagation()}
                          className={`max-w-[85%] p-3 text-sm rounded-lg ${
                              msg.role === 'user' 
                                  ? 'bg-zinc-800 text-zinc-100 border border-zinc-700' 
                                  : 'bg-transparent text-zinc-300 border border-transparent'
                          }`}
                      >
                          {msg.text}
                      </div>
                      {msg.action && ASSISTANT_NAV_LABELS[msg.action] && (
                          <button
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={() => { onNavigateAction && onNavigateAction(msg.action); }}
                              className="mt-1 ml-1 flex items-center gap-1.5 text-xs font-medium text-purple-300 hover:text-purple-200 bg-purple-500/10 hover:bg-purple-500/20 border border-purple-500/30 rounded-md px-2.5 py-1.5 transition-colors"
                          >
                              {ASSISTANT_NAV_LABELS[msg.action]} <ArrowRight size={12} />
                          </button>
                      )}
                  </div>
              ))}
              {isTyping && (
                  <div className="flex justify-start">
                      <div className="p-3 text-zinc-500 flex gap-1">
                          <span className="animate-bounce">.</span>
                          <span className="animate-bounce" style={{ animationDelay: '0.2s' }}>.</span>
                          <span className="animate-bounce" style={{ animationDelay: '0.4s' }}>.</span>
                      </div>
                  </div>
              )}
          </div>

          <div className="p-4 border-t border-zinc-800 bg-zinc-900/50">
              <form onSubmit={handleSend} className="relative">
                  <input
                      type="text"
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      placeholder="Ask about codecs, formatting, or support..."
                      className="w-full bg-zinc-950 border border-zinc-700 text-zinc-200 text-sm rounded-lg pl-3 pr-10 py-3 focus:outline-none focus:border-purple-500 transition-colors placeholder:text-zinc-600"
                  />
                  <button type="submit" disabled={!input.trim() || isTyping} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-zinc-400 hover:text-purple-400 disabled:opacity-50 transition-colors">
                      <Send size={16} />
                  </button>
              </form>
          </div>
      </div>
  );
};

  const EditorPage = () => {
  const { appInstance, db, storage, userId, isAuthReady, currentProjectId, setCurrentPage, showMessageBox, setFirestoreLoading, firestoreLoading, logout, exportNoticeStatus, auth, setPurchaseIntent, analytics } = useContext(AppContext);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const functions = appInstance ? getFunctions(appInstance, 'us-central1') : null;
  const [activeLogoId, setActiveLogoId] = useState(null);
  const onSelectLogo = (logoId) => {
  setActiveLogoId(prevId => prevId === logoId ? null : logoId); // Toggle selection
  };  
  const [showLogoBoard, setShowLogoBoard] = useState(false);
  const [logoBoardCreditId, setLogoBoardCreditId] = useState(null);
  const [logoBoardRows, setLogoBoardRows] = useState([]);
  const [draggedLogoBoardItem, setDraggedLogoBoardItem] = useState(null); // { rowId, logoId } while dragging a logo card to reorder
  const [isBoardFileDragActive, setIsBoardFileDragActive] = useState(false); // true while dragging OS files over the board
  const [libraryPickerTarget, setLibraryPickerTarget] = useState(null); // { rowId, logoId } while the "choose from library" picker is open for a slot
  // --- Song Board: same draft-until-commit pattern as the Logo Board above,
  // but for Song Credits blocks (credit.multipleMode === 'song'). Each entry
  // is a { id, title, info } pair; on commit these serialize into
  // credit.name as tab-joined lines (see parseSongEntries in CreditPreview.js).
  const [showSongBoard, setShowSongBoard] = useState(false);
  const [songBoardCreditId, setSongBoardCreditId] = useState(null);
  const [songBoardEntries, setSongBoardEntries] = useState([]);
  const [draggedSongBoardEntryId, setDraggedSongBoardEntryId] = useState(null); // id of the song card currently being dragged to reorder
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackState, setPlaybackState] = useState('idle');
  const [isPreparing, setIsPreparing] = useState(false);
  const [currentCardIndex, setCurrentCardIndex] = useState(-1);
  const cardTimeoutRef = useRef(null);
  const [currentCardAnimationClass, setCurrentCardAnimationClass] = useState('');
  const lastCardFinalClass = useRef('');
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [projectData, setProjectData] = useState(null);

  const [showAssistant, setShowAssistant] = useState(false);
  // Question handed to the Copilot from elsewhere in the UI (Batch Add, empty
  // state, etc.). Cleared as soon as the panel consumes it.
  const [assistantSeedPrompt, setAssistantSeedPrompt] = useState(null);
  const askCopilot = (question) => { setAssistantSeedPrompt(question); setShowAssistant(true); };
  const topMenuRef = useRef(null);

  const [showImportModal, setShowImportModal] = useState(false);
  const [showBatchAddModal, setShowBatchAddModal] = useState(false);
  const [batchAddText, setBatchAddText] = useState('');
  const [batchPreviewRows, setBatchPreviewRows] = useState([]); // [{id, role, name}] — smart-parsed preview, editable before commit
  const [batchCardPreviewCards, setBatchCardPreviewCards] = useState([]); // [{id, layers: [{id, content, style}]}] — Title Card stacked preview
  const [batchInsertTargetId, setBatchInsertTargetId] = useState('end'); // 'end' or the id of an existing section/group credit to insert after
  const [batchInsertMode, setBatchInsertMode] = useState('new-section'); // 'new-section' | 'merge' — merge only applies when the target is a 'multiple' (grouped) credit
  
  const [showSettingsModal, setShowSettingsModal] = useState(false); // STATE FOR NEW MODAL

  const [batchAddType, setBatchAddType] = useState('dual');
  const [reverseBatchOrder, setReverseBatchOrder] = useState(false); // flips parsed order — for pasting an end-credits list to use at the front
  const [showExportModal, setShowExportModal] = useState(false);
  // Reminder shown the first time a user hits Export without ever having touched the
  // duration/speed controls, so they don't burn a render credit on the untouched 120s
  // default. "Don't show this again" is persisted so it only nags once per browser.
  const [showDurationReminderModal, setShowDurationReminderModal] = useState(false);
  const [hideDurationReminder, setHideDurationReminder] = useState(() => localStorage.getItem('hideDurationReminder') === 'true');
  const [exportIsProcessing, setExportIsProcessing] = useState(false);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [showImportOptionsModal, setShowImportOptionsModal] = useState(false);
  const [pendingCsvFile, setPendingCsvFile] = useState(null);
  const [convertImportedToCards, setConvertImportedToCards] = useState(false);
  const [showSafeGuides, setShowSafeGuides] = useState(false);
  const [isEditingContent, setIsEditingContent] = useState(false);
  const [pendingUploads, setPendingUploads] = useState(new Set());
  const [showExportNoticesModal, setShowExportNoticesModal] = useState(false); // State for new Export Notices Modal
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [showFindReplaceModal, setShowFindReplaceModal] = useState(false);  
  const [findFocusSignal, setFindFocusSignal] = useState(0);
  // Cmd/Ctrl+F opens the find bar (or re-focuses it), like any editor.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && (e.key === 'f' || e.key === 'F')) {
        e.preventDefault();
        setShowFindReplaceModal(true);
        setFindFocusSignal(n => n + 1);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // --- Asset Library: user-level (not project-level) logos & fonts, so they're reusable
  // across every project without re-uploading. Uploaded once to a user-scoped Storage path,
  // separate from this project's storage, so they never break if a project gets deleted.
  const [libraryAssets, setLibraryAssets] = useState([]);
  const [autoSaveToLibrary, setAutoSaveToLibrary] = useState(() => localStorage.getItem('autoSaveToLibrary') !== 'false');
  const [pendingLibraryUploads, setPendingLibraryUploads] = useState(new Set());
  const [isScanningAssets, setIsScanningAssets] = useState(false);

  const creditListRef = useRef(null);
  const [draggedCreditId, setDraggedCreditId] = useState(null);
  const [dragOverCreditId, setDragOverCreditId] = useState(null);
  const [draggedCardItem, setDraggedCardItem] = useState(null);
  const [dragOverCardItem, setDragOverCardItem] = useState(null);
  const [activeCreditId, setActiveCreditId] = useState(null);
  const [activeCardId, setActiveCardId] = useState(null);
  const [selectedCreditIds, setSelectedCreditIds] = useState(new Set());
  const [lastSelectedId, setLastSelectedId] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [copiedCredits, setCopiedCredits] = useState([]);
  const [copiedSettings, setCopiedSettings] = useState(null);
  const [copiedCardStyle, setCopiedCardStyle] = useState(null);
  const [showPasteOptionsModal, setShowPasteOptionsModal] = useState(false);
  const [pasteOptions, setPasteOptions] = useState({ transform: true, duration: true, layout: true, layerStyles: true });
  const [userTemplates, setUserTemplates] = useState([]);
  const [audioInfo, setAudioInfo] = useState({ url: null, name: '', duration: 0 });
  const audioRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDisplayLanguage, setSelectedDisplayLanguage] = useState('all');
  const previewRef = useRef(null);
  const [previewWidth, setPreviewWidth] = useState(0);
  const [previewHeight, setPreviewHeight] = useState(0);
  const contentAreaRef = useRef(null);
  const [draggedLogoInfo, setDraggedLogoInfo] = useState(null);
  const [dragOverLogoInfo, setDragOverLogoInfo] = useState(null);
  const [leftPanelVisible, setLeftPanelVisible] = useState(typeof window !== 'undefined' && window.innerWidth >= 768);
  const [rightPanelVisible, setRightPanelVisible] = useState(typeof window !== 'undefined' && window.innerWidth >= 1024);
  const [inspectorTab, setInspectorTab] = useState('content');
  const [showMobileSequenceMenu, setShowMobileSequenceMenu] = useState(false);
  const [credits, setCredits] = useState([]);
  const [cardCredits, setCardCredits] = useState([]);
  const [projectName, setProjectName] = useState('');
  const [settings, setSettings] = useState({
  speed: 150, 
  fontSize: 17, 
  fontFamily: 'poppins', 
  backgroundColor: '#000000', 
  textColor: '#ffffff', 
  blockNameColor: '#ffffff',
  roleColor: '#ffffff', 
  nameColor: '#ffffff', 
  gutterWidth: 3, 
  blockSpacing: 2, 
  showSettings: false, 
  speedMode: 'auto',
  targetDuration: 120,
  cardAnimationType: 'fade',
  cardAnimationDuration: 1,
  fadeOutDuration: 1,
  scrollDirection: 'down', 
  letterboxRatio: 'none',
  blockNameCaps: 'normal', 
  roleCaps: 'normal', 
  nameCaps: 'normal', 
  textAlign: 'center',
  dualAlignment: 'classic',
  showAutoSaveNotice: false,
  cardsEnabled: true, 
  cardSequence: 'before',
  cardFontFamily: 'poppins', 
  cardFontSize: 48, 
  cardFontStyle: 'normal', 
  cardDuration: 5,
  cardIntermissionDuration: 2,
  defaultFontWeight: '400', 
  defaultFontStyle: 'normal', 
  fps: '24',
lineHeight: 1.5,
  letterSpacing: 0,
});

const [showReplenishModal, setShowReplenishModal] = useState(false);
    const [isProcessingPurchase, setIsProcessingPurchase] = useState(false);

  const handlePurchase = async (type) => {
    setIsProcessingPurchase(true);
    const functionName = type === 'upgrade' ? 'createUpgradeCheckoutSession' : 'createReplenishCheckoutSession';
    try {
        // FIX: Replace the placeholder with the correct Stripe Public Key
        const stripe = await loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);
        
        const createSession = httpsCallable(functions, functionName);
        const { data } = await createSession({ projectId: currentProjectId });
        if (data.sessionId) {
            await stripe.redirectToCheckout({ sessionId: data.sessionId });
        }
    } catch (error) {
        showMessageBox("Error", `Could not start checkout: ${error.message}`, "error");
    }
    setIsProcessingPurchase(false);
};

const [userClaims, setUserClaims] = useState(null);

  // Add this useEffect to fetch claims (place it with other useEffects)
  useEffect(() => {
    const fetchClaims = async () => {
        if (auth?.currentUser) {
            const tokenResult = await auth.currentUser.getIdTokenResult();
            setUserClaims(tokenResult.claims);
        }
    };
    fetchClaims();
  }, [auth]);

  const defaultTemplates = {
    'tv-opening': {
        credits: JSON.stringify([
            { id: 1, role: 'Executive Producer', name: '(Name)', type: 'single' },
            { id: 2, role: 'Executive Producer', name: '(Name)', type: 'single' },
            { id: 3, role: 'Co-Executive Producer', name: '(Name)', type: 'single' }
        ]),
        cardCredits: JSON.stringify([
            { id: 101, type: 'main-title', textLayers: [{ id: 1, content: 'SHOW TITLE', fontSize: 72 }, { id: 2, content: 'Created By: (Creator Name)', fontSize: 36 }] },
            { id: 102, type: 'main-title', textLayers: [{ id: 1, content: '(Star Name)', fontSize: 60 }] },
            { id: 103, type: 'main-title', textLayers: [{ id: 1, content: 'Also Starring', fontSize: 32 }, { id: 2, content: '(Star Name)', fontSize: 48 }] }
        ]),
        settings: JSON.stringify({ speed: 800, fontFamily: 'helvetica', cardFontFamily: 'helvetica', cardFontSize: 72 })
    },
    'indie-film': {
        cardCredits: JSON.stringify([
            { id: 201, type: 'main-title', textLayers: [{ id: 1, content: '(Your Film Title)', fontSize: 64 }]},
            { id: 202, type: 'main-title', textLayers: [{ id: 1, content: 'Directed By', fontSize: 28 }, {id: 2, content: '(Director Name)', fontSize: 42}]},
            { id: 203, type: 'main-title', textLayers: [{ id: 1, content: 'Written By', fontSize: 28 }, {id: 2, content: '(Writer Name)', fontSize: 42}]},
            { id: 204, type: 'main-title', textLayers: [{ id: 1, content: 'Produced By', fontSize: 28 }, {id: 2, content: '(Producer Name)', fontSize: 42}]},
            { id: 205, type: 'main-title', textLayers: [{ id: 1, content: 'Starring', fontSize: 28 }, {id: 2, content: '(Lead Actor Name)', fontSize: 42}]},
        ]),
        credits: JSON.stringify([
            { id: 1, type: 'title', name: 'Cast' },
            { id: 2, type: 'dual', name: '(Actor Name)', role: '(Character Name)' },
            { id: 3, type: 'dual', name: '(Actor Name)', role: '(Character Name)' },
            { id: 4, type: 'title', name: 'Unit Production Manager' },
            { id: 5, type: 'single', name: '(Name)' },
            { id: 6, type: 'title', name: 'First Assistant Director' },
            { id: 7, type: 'single', name: '(Name)' },
            { id: 8, type: 'title', name: 'Art Department' },
            { id: 9, type: 'dual', name: '(Name)', role: '(Role)' },
            { id: 10, type: 'title', name: 'Sound Department' },
            { id: 11, type: 'dual', name: '(Name)', role: '(Role)' },
            { id: 12, type: 'title', name: 'Special Thanks' },
            { id: 13, type: 'single', name: '(Name)' },
            { id: 14, type: 'single', name: '(Name)' },
        ]),
        settings: JSON.stringify({ speed: 150, fontFamily: 'eb-garamond', cardFontFamily: 'eb-garamond', cardFontSize: 64 })
    },
    'studio-film': {
        cardCredits: JSON.stringify([
            { id: 301, type: 'main-title', textLayers: [{ id: 1, content: 'Director', fontSize: 32 }, {id: 2, content: '(Director Name)', fontSize: 48}]},
            { id: 302, type: 'main-title', textLayers: [{ id: 1, content: 'Writers', fontSize: 32 }, {id: 2, content: '(Writer Name)', fontSize: 48}]},
            { id: 303, type: 'main-title', textLayers: [{ id: 1, content: 'Producer', fontSize: 32 }, {id: 2, content: '(Producer Name)', fontSize: 48}]},
            { id: 304, type: 'main-title', textLayers: [{ id: 1, content: 'Executive Producer', fontSize: 32 }, {id: 2, content: '(Producer Name)', fontSize: 48}]},
            { id: 305, type: 'main-title', textLayers: [{ id: 1, content: 'Lead Cast', fontSize: 32 }, {id: 2, content: '(Actor Name)', fontSize: 48}]},
            { id: 306, type: 'main-title', textLayers: [{ id: 1, content: 'Supporting Cast', fontSize: 32 }, {id: 2, content: '(Actor Name)', fontSize: 48}]},
            { id: 307, type: 'main-title', textLayers: [{ id: 1, content: 'Director of Photography', fontSize: 32 }, {id: 2, content: '(Name)', fontSize: 48}]},
            { id: 308, type: 'main-title', textLayers: [{ id: 1, content: 'Production Designer', fontSize: 32 }, {id: 2, content: '(Name)', fontSize: 48}]},
            { id: 309, type: 'main-title', textLayers: [{ id: 1, content: 'Editor', fontSize: 32 }, {id: 2, content: '(Name)', fontSize: 48}]},
            { id: 310, type: 'main-title', textLayers: [{ id: 1, content: 'Costume Designer', fontSize: 32 }, {id: 2, content: '(Name)', fontSize: 48}]},
            { id: 311, type: 'main-title', textLayers: [{ id: 1, content: 'Music Composer', fontSize: 32 }, {id: 2, content: '(Name)', fontSize: 48}]},
            { id: 312, type: 'main-title', textLayers: [{ id: 1, content: 'Casting Director', fontSize: 32 }, {id: 2, content: '(Name)', fontSize: 48}]},
        ]),
        credits: JSON.stringify([
            { id: 401, type: 'title', name: 'Unit Production Manager' },
            { id: 402, type: 'single', name: '(Name)' },
            { id: 403, type: 'title', name: 'First Assistant Director' },
            { id: 404, type: 'single', name: '(Name)' },
            { id: 405, type: 'title', name: 'Second Assistant Director' },
            { id: 406, type: 'single', name: '(Name)' },
            { id: 407, type: 'title', name: 'Cast' },
            { id: 408, type: 'dual', name: '(Actor Name)', role: 'Lead Character' },
            { id: 409, type: 'dual', name: '(Actor Name)', role: 'Supporting Character' },
            { id: 410, type: 'dual', name: '(Actor Name)', role: '(Character Name)' },
            { id: 411, type: 'dual', name: '(Actor Name)', role: '(Character Name)' },
            { id: 412, type: 'title', name: 'Stunt Department' },
            { id: 413, type: 'multiple', role: 'Stunt Performers', name: '(Name)\n(Name)\n(Name)' },
            { id: 414, type: 'title', name: 'Art Department' },
            { id: 415, type: 'multiple', role: 'Set Dressers', name: '(Name)\n(Name)\n(Name)\n(Name)' },
            { id: 416, type: 'title', name: 'Visual Effects' },
            { id: 417, type: 'multiple', role: 'VFX Artists', name: '(Name)\n(Name)\n(Name)' },
            { id: 418, type: 'title', name: 'Songs' },
            { id: 419, type: 'single', name: '"(Song Title)"\nWritten by (Writer)\nPerformed by (Artist)\nCourtesy of (Label)' },
            { id: 420, type: 'title', name: 'Special Thanks' },
            { id: 421, type: 'single', name: '(Name)' },
            { id: 422, type: 'single', name: '(Name)' },
            { id: 423, type: 'logo', layout: 'grid', gridColumns: 4, logos: [{id:1},{id:2},{id:3},{id:4}] },
            { id: 424, type: 'single', name: 'Copyright © 2025 Your Studio Name' },
        ]),
        settings: JSON.stringify({ speed: 200, fontFamily: 'helvetica', cardFontFamily: 'helvetica', cardFontSize: 48, defaultFontWeight: 'bold' })
    }
  };

  const loadTemplate = (template) => {
      const applyTemplate = () => {
          try {
              const loadedSettings = JSON.parse(template.settings);
              const loadedCredits = JSON.parse(template.credits);
              const loadedCardCredits = JSON.parse(template.cardCredits);
              setSettings(s => ({ ...s, ...loadedSettings }));
              setCredits(loadedCredits);
              setCardCredits(loadedCardCredits);
              setShowTemplatesModal(false);
              showMessageBox("Success", "Template loaded!", "success", true);
          } catch (error) {
              showMessageBox("Error", `Failed to load template: ${error.message}`, "error");
          }
      };

      showMessageBox(
          "Load Template?",
          `This will replace your current credits and settings with the "${template.name}" template. Are you sure?`,
          "warning",
          false,
          applyTemplate 
      );
  };

  const textAlignMap = { 'flex-start': 'left', 'center': 'center', 'flex-end': 'right' };

  const inspectorActiveCredit = activeCardId ? cardCredits.find(c => c.id === activeCardId) : activeCreditId ? credits.find(c => c.id === activeCreditId) : null;

  const scrollRef = useRef(null);
  const animationRef = useRef(null);
  const lastTimestampRef = useRef(null);
  const isSavePending = useRef(false);
  const isLocalUpdate = useRef(false);
  const [playbackStatusText, setPlaybackStatusText] = useState('Ready');
  const [showAddCreditDropdown, setShowAddCreditDropdown] = useState(false);
  const [showTopAddCreditDropdown, setShowTopAddCreditDropdown] = useState(false);

   const [estimatedScrollHeight, setEstimatedScrollHeight] = useState(0);
   

  const FONT_DATA = {
        'arial': { name: 'Arial', variants: { '400': ['normal', 'italic'], '700': ['normal', 'italic'] } },
        'courier-new': { name: 'Courier New', variants: { '400': ['normal', 'italic'], '700': ['normal', 'italic'] } },
        'eb-garamond': { name: 'EB Garamond', variants: { '400': ['normal', 'italic'], '700': ['normal', 'italic'] } },
        'futura': { name: 'Futura', variants: { '300': ['normal', 'italic'], '400': ['normal', 'italic'], '700': ['normal', 'italic'] } },
        'garamond': { name: 'Garamond', variants: { '300': ['normal', 'italic'], '400': ['normal', 'italic'], '700': ['normal', 'italic'] } },
        'georgia': { name: 'Georgia', variants: { '400': ['normal', 'italic'], '700': ['normal', 'italic'] } },
        'helvetica': { name: 'Helvetica', variants: { '300': ['normal', 'italic'], '400': ['normal', 'italic'], '700': ['normal', 'italic'] } },
        'helvetica-rounded': { name: 'Helvetica Rounded', variants: { '700': ['normal'] } },
        'optima': { name: 'Optima', variants: { '400': ['normal', 'italic'], '700': ['normal', 'italic'] } },
        'palatino': { name: 'Palatino', variants: { '300': ['normal', 'italic'], '400': ['normal', 'italic'], '700': ['normal', 'italic'] } },
        'poppins': { name: 'Poppins', variants: { '100': ['normal'], '300': ['normal', 'italic'], '400': ['normal', 'italic'], '700': ['normal', 'italic'] } },
    };

    const handleUpgrade = async () => {
    if (!currentProjectId) {
      showMessageBox("Error", "No project selected to upgrade.", "error");
      return;
    }
    setIsUpgrading(true);
    if (analytics) {
        logEvent(analytics, 'begin_checkout', {
            plan: 'pro',
            value: 200,
            currency: 'USD'
        });
    }
    try {
      // Replace with your actual Stripe Publishable Key
      const stripe = await loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);
      
      const createUpgradeSession = httpsCallable(functions, 'createUpgradeCheckoutSession');
      const { data } = await createUpgradeSession({ projectId: currentProjectId });
      
     if (data.sessionId) {
    const { error } = await stripe.redirectToCheckout({ sessionId: data.sessionId });
    if (error) {
        console.error("Stripe redirect error:", error);
        
        // Show user-friendly error with retry option
        showMessageBox(
            "Checkout Error",
            <div>
                <p className="mb-3">{error.message}</p>
                <p className="text-sm text-gray-400">
                    This is usually a temporary issue. Please try again.
                </p>
                <button 
                    onClick={() => handleUpgrade()} 
                    className="mt-4 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700"
                >
                    Try Again
                </button>
            </div>,
            "error"
        );
        return; // Don't throw, let user retry
    }
} else {
    throw new Error("Could not retrieve a checkout session.");
}

    } catch (error) {
      console.error("Upgrade failed:", error);
      showMessageBox("Upgrade Failed", `Could not initiate upgrade: ${error.message}`, "error");
      setIsUpgrading(false);
    }
  };


  const handleDefaultFamilyChange = (newFamilyKey, scope = 'both') => {
        const familyData = FONT_DATA[newFamilyKey];
        const currentSettings = settings;
        let newDefaults = {};

        if (scope === 'both') {
            newDefaults.fontFamily = newFamilyKey;
            newDefaults.cardFontFamily = newFamilyKey;
            newDefaults.scrollFontFamily = newFamilyKey;
        } else if (scope === 'cards') {
            newDefaults.cardFontFamily = newFamilyKey;
        } else if (scope === 'scroll') {
            newDefaults.scrollFontFamily = newFamilyKey;
        }

        if (familyData && scope === 'both') {
            const availableWeights = Object.keys(familyData.variants);
            let newWeight = currentSettings.defaultFontWeight || '400';
            if (!availableWeights.includes(newWeight)) { newWeight = availableWeights[0]; }
            const weightToUse = availableWeights.includes(newWeight) ? newWeight : availableWeights[0];
            newDefaults.defaultFontWeight = weightToUse;
            const availableStyles = familyData.variants[weightToUse];
            let newStyle = currentSettings.defaultFontStyle || 'normal';
            if (!availableStyles.includes(newStyle)) { newStyle = availableStyles[0]; }
            newDefaults.defaultFontStyle = newStyle;
        }
        setSettings(s => ({ ...s, ...newDefaults }));

        // Propagate card font to all existing text layers (they store fontFamily directly)
        if (scope === 'cards' || scope === 'both') {
            setCardCredits(prev => prev.map(card => ({
                ...card,
                textLayers: (card.textLayers || []).map(layer => ({
                    ...layer,
                    fontFamily: newFamilyKey
                }))
            })));
        }
    };

  useEffect(() => {
    if (scrollRef.current) {
      setEstimatedScrollHeight(scrollRef.current.scrollHeight);
    }
  }, [credits]);

  const handleTrialUpgrade = async (targetPlan) => {
    if (!currentProjectId || !functions) {
      showMessageBox("Error", "No project selected or connection error.", "error");
      return;
    }
    if (analytics) {
        logEvent(analytics, 'trial_upgraded', {
            from_plan: 'demo',
            to_plan: targetPlan,
            value: targetPlan === 'indie' ? 99 : 200,
            currency: 'USD'
        });
    }
    setIsUpgrading(true);
    try {
      const stripe = await loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);
      
      const createUpgradeSession = httpsCallable(functions, 'createTrialUpgradeCheckoutSession');
      const { data } = await createUpgradeSession({ projectId: currentProjectId, targetPlan });
      
      if (data.sessionId) {
        const { error } = await stripe.redirectToCheckout({ sessionId: data.sessionId });
        if (error) throw new Error(error.message);
      } else {
        throw new Error("Could not retrieve a checkout session.");
      }
    } catch (error) {
      console.error("Trial Upgrade failed:", error);
      showMessageBox("Upgrade Failed", `Could not initiate upgrade: ${error.message}`, "error");
      setIsUpgrading(false);
    }
};

  // Scroll pacing/smoothness (MIN/MAX_SECONDS_PER_LINE, RENDER_TARGET_LOCKED_PPF,
  // resolveRenderDuration, predictLockedRender) are defined once, at module
  // scope near the top of this file, and used from there — see the
  // "SCROLL PACING / SMOOTHNESS" block for why they live in exactly one place.

  // Gate in front of opening the Export modal: if this project's duration is still sitting
  // at the untouched 120s default (in auto mode), most likely the user never opened the
  // Timing menu at all — remind them before they spend a render credit on it. Once they've
  // adjusted duration (via the Timing menu, the sparkle "suggest duration" button, or synced
  // it to music) targetDuration will no longer equal the default, so this stops firing on
  // its own — no separate "touched" flag to keep in sync.
  const handleExportButtonClick = () => {
    const durationLikelyUnset = settings.speedMode === 'auto'
        && Number(settings.targetDuration) === DEFAULT_TARGET_DURATION_SECONDS;

    if (durationLikelyUnset && !hideDurationReminder) {
        setShowDurationReminderModal(true);
    } else {
        setShowExportModal(true);
    }
  };

  const handleSuggestDuration = () => {
    if (!scrollRef.current) {
        showMessageBox("Error", "Could not calculate duration. Preview pane not available.", "error");
        return;
    }

    const viewportHeight = scrollRef.current.clientHeight;
    const totalTravelDistance = estimatedScrollHeight + viewportHeight;
    const projectFps = parseFloat(settings.fps) || 24;
    const lineCount = credits.length;
    const minReadableDuration = lineCount > 0 ? lineCount * MIN_SECONDS_PER_LINE : 0;
    const maxReadableDuration = lineCount > 0 ? lineCount * MAX_SECONDS_PER_LINE : Infinity;

    if (totalTravelDistance <= viewportHeight) {
        setSettings(s => ({ ...s, targetDuration: Math.max(10, Math.round(minReadableDuration)), speedMode: 'auto' }));
        return;
    }

    const totalTravelDistanceRenderPx = totalTravelDistance * RENDER_DEVICE_SCALE_FACTOR;

    // Aim for our ideal pace (RENDER_TARGET_LOCKED_PPF px/frame), then clamp into the
    // readable min/max window. core-renderer no longer rounds pixels-per-frame to a whole
    // number (see the "CONTINUOUS SPEED" fix in renderScrollVideo), so — unlike before —
    // there's no need to search in an integer-PPF domain just to dodge post-rounding drift;
    // whatever duration we land on here is what the render will actually produce.
    let idealDuration = totalTravelDistanceRenderPx / (RENDER_TARGET_LOCKED_PPF * projectFps);
    const slowedForReadability = minReadableDuration > 0 && idealDuration < minReadableDuration;
    const cappedForLength = Number.isFinite(maxReadableDuration) && idealDuration > maxReadableDuration;
    if (slowedForReadability) idealDuration = minReadableDuration;
    if (cappedForLength) idealDuration = maxReadableDuration;

    const finalDuration = Math.max(1, Math.round(idealDuration));

    setSettings(s => ({ ...s, targetDuration: finalDuration, speedMode: 'auto' }));
    showMessageBox(
        "Suggestion Applied",
        slowedForReadability
            ? `Set duration to ~${finalDuration}s — slowed below the smoothest-motion speed so all ${lineCount} lines stay readable.`
            : cappedForLength
            ? `Set duration to ~${finalDuration}s — capped so it doesn't run needlessly long for ${lineCount} lines.`
            : `Set duration to ~${finalDuration}s for the smoothest scroll at ${projectFps}fps.`,
        "info", true
    );
};

  const addCreditDropdownRef = useRef(null);
  const topAddCreditDropdownRef = useRef(null);
  const mobileSequenceMenuRef = useRef(null);

  useClickOutside(addCreditDropdownRef, () => setShowAddCreditDropdown(false));
  useClickOutside(topAddCreditDropdownRef, () => setShowTopAddCreditDropdown(false));
  useClickOutside(mobileSequenceMenuRef, () => setShowMobileSequenceMenu(false));

  const [previewOpacity, setPreviewOpacity] = useState(1);
  const prevCardSequence = useRef(settings.cardSequence);
  const rewindAnimation = useCallback(() => { 
    setIsPlaying(false); 
    setPlaybackState('idle'); 
    setCurrentCardIndex(-1); 
    
    if (cardTimeoutRef.current) clearTimeout(cardTimeoutRef.current); 
    if (animationRef.current) cancelAnimationFrame(animationRef.current); 
    
    if (scrollRef.current) { 
        const startPos = settings.scrollDirection === 'down' 
            ? 0
            : (scrollRef.current.scrollHeight - scrollRef.current.clientHeight);
        scrollRef.current.scrollTop = Math.max(0, startPos); 
    } 
    
    if (audioRef.current) { 
        audioRef.current.pause(); 
        audioRef.current.currentTime = 0; 
    } 
    
    setPlaybackStatusText('Ready'); 
    setCurrentCardAnimationClass(''); 
    lastCardFinalClass.current = ''; 
    
    const hasCards = cardCredits.length > 0;
    const hasScroll = credits.length > 0;
    
    if (settings.cardSequence === 'cards-only' && hasCards) {
        setActiveCardId(cardCredits[0].id);
        setActiveCreditId(null);
    } else if (settings.cardSequence === 'scroll-only' && hasScroll) {
        setActiveCreditId(credits[0].id);
        setActiveCardId(null);
    } else if (settings.cardSequence === 'before' && hasCards) {
        setActiveCardId(cardCredits[0].id);
        setActiveCreditId(null);
    } else if (settings.cardSequence === 'after' && hasScroll) {
        setActiveCreditId(credits[0].id);
        setActiveCardId(null);
    }
}, [settings.scrollDirection, settings.cardSequence, cardCredits, credits]);

  useEffect(() => {
    if (prevCardSequence.current !== settings.cardSequence) {
        setPreviewOpacity(0);
        const timer = setTimeout(() => {
            setPreviewOpacity(1);
        }, 200);
        prevCardSequence.current = settings.cardSequence;
        rewindAnimation();
        return () => clearTimeout(timer);
    }
  }, [settings.cardSequence]);

  // Add this useEffect to EditorPage or ProjectsPage
useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const upgradeSuccess = urlParams.get('upgrade') === 'success';
    const replenishSuccess = urlParams.get('replenish') === 'success';
    const paymentSuccess = urlParams.get('payment') === 'success';
    
    if ((upgradeSuccess || replenishSuccess || paymentSuccess) && db && userId) {
        console.log('🎉 Payment success detected');
        
        let messageTitle = "Payment Successful!";
        let messageBody = "Your purchase is being processed. This may take a few moments.";
        
        if (upgradeSuccess) {
            messageTitle = "Upgrade Complete!";
            messageBody = "Your project has been upgraded. Reloading...";
        } else if (replenishSuccess) {
            messageTitle = "Exports Added!";
            messageBody = "Your export limit has been increased. Reloading...";
        }
        
        showMessageBox(messageTitle, messageBody, "success", 5000);
        
        // Clean up URL after 1 second
        setTimeout(() => {
            const newUrl = window.location.pathname;
            window.history.replaceState({}, document.title, newUrl);
        }, 1000);
        
        // Refresh project data after 2 seconds to get latest from webhook
        setTimeout(() => {
            if (currentProjectId) {
                // Force refresh of project data
                const projectRef = doc(db, `artifacts/${__app_id}/users/${userId}/projects/${currentProjectId}`);
                getDoc(projectRef).then(snap => {
                    if (snap.exists()) {
                        setProjectData(snap.data());
                    }
                });
            }
        }, 2000);
    }
}, [db, userId, currentProjectId]);

 useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const upgradeSuccess = urlParams.get('upgrade') === 'success';
    
    if (upgradeSuccess && db && userId && currentProjectId) {
      console.log('🔄 Upgrade success detected, setting up listener...');
      if (analytics) {
            logEvent(analytics, 'purchase', {
                transaction_id: currentProjectId,
                value: 200,
                currency: 'USD',
                plan: 'pro'
            });
        }
      
      const projectRef = doc(db, `artifacts/${__app_id}/users/${userId}/projects/${currentProjectId}`);
      
      let hasShownMessage = false; // Prevent duplicate messages
      
      const unsubscribe = onSnapshot(projectRef, (docSnap) => {
        if (docSnap.exists() && !hasShownMessage) {
          const data = docSnap.data();
          console.log('📦 Project data received:', data.tier, data.exportsLimit);
          
          if (data.tier === 'pro') {
            setProjectData(data);
            hasShownMessage = true;
            
            // Show rollover details if available
            const upgradeDetails = data.upgradeDetails;
            if (upgradeDetails && upgradeDetails.rolledOverExports > 0) {
                console.log('✅ Showing rollover message:', upgradeDetails);
                showMessageBox(
                    "Upgrade Complete!", 
                    `Your project has been upgraded to Pro! We've added your ${upgradeDetails.rolledOverExports} remaining Indie exports to your new Pro limit. You now have ${upgradeDetails.totalNewLimit} total exports.`,
                    "success", 
                    10000 // 10 seconds as a number
                );
                } else {
                console.log('✅ Showing standard upgrade message');
                showMessageBox(
                    "Upgrade Complete!", 
                    `Your project has been upgraded to Pro with ${data.exportsLimit} exports.`,
                    "success", 
                    10000 // 10 seconds
                );
                }           
            unsubscribe(); // Stop listening once confirmed
          }
        }
      }, (error) => {
        console.error('❌ Error listening to project updates:', error);
      });

      // Clean up URL immediately
      window.history.replaceState({}, document.title, window.location.pathname);

      // Cleanup listener after 10 seconds if upgrade never confirmed
      const timeout = setTimeout(() => {
        if (!hasShownMessage) {
          console.warn('⏱️ Timeout: Upgrade not confirmed in 10 seconds');
          showMessageBox(
            "Upgrade Processing", 
            "Your upgrade is being processed. Please refresh the page in a moment.",
            "info",
            true
          );
        }
        unsubscribe();
      }, 10000);

      return () => {
        unsubscribe();
        clearTimeout(timeout);
      };
    }
  }, [currentProjectId, db, userId]); // Removed showMessageBox and setProjectData from deps

useEffect(() => {
    if (!isPlaying && prevCardSequence.current !== settings.cardSequence) {
        const hasCards = cardCredits.length > 0;
        const hasScroll = credits.length > 0;
        
        setActiveCreditId(null);
        setActiveCardId(null);
        
        if (settings.cardSequence === 'cards-only' && hasCards) {
            setActiveCardId(cardCredits[0].id);
        } else if (settings.cardSequence === 'scroll-only' && hasScroll) {
            setActiveCreditId(credits[0].id);
        } else if (settings.cardSequence === 'before' && hasCards) {
            setActiveCardId(cardCredits[0].id);
        } else if (settings.cardSequence === 'after' && hasScroll) {
            setActiveCreditId(credits[0].id);
        }
        
        prevCardSequence.current = settings.cardSequence;
    }
}, [settings.cardSequence, isPlaying, cardCredits, credits]);

useEffect(() => {
    const handleDuplicateCredits = (e) => {
        const { credits: newCredits, cards: newCards } = e.detail;
        if (newCredits.length > 0) setCredits(prev => [...prev, ...newCredits]);
        if (newCards.length > 0) setCardCredits(prev => [...prev, ...newCards]);
        showMessageBox("Success", `Duplicated ${newCredits.length + newCards.length} credits!`, "success", true);
    };
    
    const handleApplyFilmOrder = () => {
        const roleOrder = ['director', 'producer', 'executive producer', 'writer', 'cinematographer', 'editor', 'production designer', 'costume designer', 'makeup', 'music', 'sound'];
        const selectedScrollCredits = credits.filter(c => selectedCreditIds.has(c.id));
        const sortedCredits = [...selectedScrollCredits].sort((a, b) => {
            const aRole = (a.role || '').toLowerCase();
            const bRole = (b.role || '').toLowerCase();
            const aIndex = roleOrder.findIndex(role => aRole.includes(role));
            const bIndex = roleOrder.findIndex(role => bRole.includes(role));
            if (aIndex === -1 && bIndex === -1) return 0;
            if (aIndex === -1) return 1;
            if (bIndex === -1) return -1;
            return aIndex - bIndex;
        });
        
        const otherCredits = credits.filter(c => !selectedCreditIds.has(c.id));
        const firstSelectedIndex = credits.findIndex(c => selectedCreditIds.has(c.id));
        const newCredits = [...otherCredits];
        newCredits.splice(firstSelectedIndex, 0, ...sortedCredits);
        setCredits(newCredits);
        showMessageBox("Success", "Applied film industry standard order!", "success", true);
    };
    
    const handleResetSelectedStyles = () => {
        const fieldsToReset = ['fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'textDecoration', 'lineHeight', 'letterSpacing', 'roleColor', 'nameColor', 'blockNameColor', 'override'];
        
        setCredits(prev => prev.map(c => {
            if (selectedCreditIds.has(c.id)) {
                const resetCredit = { ...c };
                fieldsToReset.forEach(field => delete resetCredit[field]);
                return resetCredit;
            }
            return c;
        }));
        
        setCardCredits(prev => prev.map(c => {
            if (selectedCreditIds.has(c.id)) {
                const resetCredit = { ...c };
                fieldsToReset.forEach(field => delete resetCredit[field]);
                return resetCredit;
            }
            return c;
        }));
        
        showMessageBox("Success", `Reset styles for ${selectedCreditIds.size} credits!`, "success", true);
    };
    
    const handleClearSelection = () => {
        setSelectedCreditIds(new Set());
    };
    
    window.addEventListener('duplicateCredits', handleDuplicateCredits);
    window.addEventListener('applyFilmOrder', handleApplyFilmOrder);
    window.addEventListener('resetSelectedStyles', handleResetSelectedStyles);
    window.addEventListener('clearSelection', handleClearSelection);
    
    return () => {
        window.removeEventListener('duplicateCredits', handleDuplicateCredits);
        window.removeEventListener('applyFilmOrder', handleApplyFilmOrder);
        window.removeEventListener('resetSelectedStyles', handleResetSelectedStyles);
        window.removeEventListener('clearSelection', handleClearSelection);
    };
}, [credits, cardCredits, selectedCreditIds, showMessageBox]);

// Self-heal old data: batch-paste and CSV import used to compute the next id
// independently per list (credits vs cardCredits), so a scroll credit and a title
// card can already share the same numeric id in projects saved before that was
// fixed. Since selection is a single Set of raw ids with no "which list" tag,
// a collision like that makes an unrelated item in the other list light up as
// selected too. This runs once whenever the loaded lists actually contain a
// collision, quietly reassigns fresh ids to the card side only (never touches
// visible content), and leaves everything alone otherwise.
useEffect(() => {
    if (OFFLINE_MODE || isReadOnly || credits.length === 0 || cardCredits.length === 0) return;
    const creditIds = new Set(credits.map(c => c.id));
    const seen = new Set();
    let needsFix = false;
    for (const c of cardCredits) {
        if (creditIds.has(c.id) || seen.has(c.id)) { needsFix = true; break; }
        seen.add(c.id);
    }
    if (!needsFix) return;

    let nextId = Math.max(
        0,
        ...credits.map(c => typeof c.id === 'number' ? c.id : 0),
        ...cardCredits.map(c => typeof c.id === 'number' ? c.id : 0)
    );
    const usedIds = new Set(creditIds);
    setCardCredits(prev => prev.map(c => {
        if (usedIds.has(c.id)) {
            const freshId = ++nextId;
            usedIds.add(freshId);
            return { ...c, id: freshId };
        }
        usedIds.add(c.id);
        return c;
    }));
}, [credits, cardCredits, isReadOnly]);

// Replace All: same literal/whole-word matching as the find bar. Returns the
// count so the bar can show it inline (it stays open, like a regular editor).
const handleReplaceAll = ({ findTerm, replaceTerm, caseSensitive, matchWholeWord }) => {
    if (!findTerm) return 0;
    if (isReadOnly) { showMessageBox("Read-Only Mode", "Cannot edit expired projects.", "error", true); return 0; }
    const regex = buildFindRegex(findTerm, caseSensitive, matchWholeWord);
    let replacementCount = 0;

    const performReplace = (text) => {
        if (!text || typeof text !== 'string') return text;
        return text.replace(regex, () => { replacementCount++; return replaceTerm; });
    };

    const newCredits = credits.map(credit => credit.type === 'logo' ? credit : ({
        ...credit,
        role: performReplace(credit.role),
        name: performReplace(credit.name),
    }));
    const newCardCredits = cardCredits.map(card => ({
        ...card,
        textLayers: (card.textLayers || []).map(layer => ({ ...layer, content: performReplace(layer.content) })),
    }));

    setCredits(newCredits);
    setCardCredits(newCardCredits);
    return replacementCount;
};

// Replace just the current find match (by position, guarded against stale data).
const handleReplaceOneMatch = (m, replaceTerm) => {
    if (!m) return;
    if (isReadOnly) { showMessageBox("Read-Only Mode", "Cannot edit expired projects.", "error", true); return; }
    const splice = (str) => {
        if (typeof str !== 'string' || str.substr(m.index, m.length) !== m.matched) return str;
        return str.slice(0, m.index) + replaceTerm + str.slice(m.index + m.length);
    };
    if (m.kind === 'scroll') {
        setCredits(prev => prev.map(c => c.id === m.creditId ? { ...c, [m.field]: splice(c[m.field]) } : c));
    } else {
        setCardCredits(prev => prev.map(c => c.id === m.creditId
            ? { ...c, textLayers: (c.textLayers || []).map((l, i) => i === m.layerIndex ? { ...l, content: splice(l.content) } : l) }
            : c));
    }
};

// Find bar -> select the credit (opens it in the Inspector, shows the card)
// and center it in the preview, even if it was only partly on screen.
const goToFindMatch = (m) => {
    if (!m) return;
    const fakeEvent = { stopPropagation() {}, metaKey: false, ctrlKey: false, shiftKey: false };
    handleCreditSelect(m.creditId, fakeEvent, m.kind === 'card' ? 'card' : 'scroll');
    if (m.kind !== 'scroll') return;
    setTimeout(() => {
        const el = document.getElementById(`preview-credit-${m.creditId}`);
        if (!el || !scrollRef.current || !previewRef.current) return;
        const previewRect = previewRef.current.getBoundingClientRect();
        const r = el.getBoundingClientRect();
        const comfortablyVisible = r.top >= previewRect.top + previewRect.height * 0.15 && r.bottom <= previewRect.bottom - previewRect.height * 0.15;
        if (!comfortablyVisible) {
            const target = scrollRef.current.scrollTop + r.top - previewRect.top - (previewRect.height / 2) + Math.min(r.height, previewRect.height * 0.6) / 2;
            scrollRef.current.scrollTo({ top: target, behavior: 'smooth' });
        }
    }, 150);
};

const handleRenameProject = async (newName) => {
    if (!newName.trim() || newName === projectData?.name) {
      setProjectName(projectData?.name || 'Untitled Project');
      return;
    }
    if (isReadOnly) {
      showMessageBox("Read-Only Mode", "Cannot rename expired projects.", "error", true);
      setProjectName(projectData?.name);
      return;
    }
    if (!db || !userId || !currentProjectId) return;

    const projectRef = doc(db, `artifacts/${__app_id}/users/${userId}/projects/${currentProjectId}`);
    try {
      await updateDoc(projectRef, { name: newName });
      showMessageBox("Success", "Project renamed!", "success", true);
    } catch (error) {
      console.error("Error renaming project:", error);
      showMessageBox("Error", `Failed to rename project: ${error.message}`, "error");
      setProjectName(projectData?.name || 'Untitled Project');
    }
  };

  const debouncedSave = useCallback(debounce(async (currentCredits, currentSettings, currentCardCredits) => {
    if (pendingUploads.size > 0) {
        console.log("Auto-save skipped: pending uploads.");
        return;
    }
    if (OFFLINE_MODE || !currentProjectId || !db || !userId) {
      isSavePending.current = false;
      return;
    }
    try {
      isSavePending.current = true;
      isLocalUpdate.current = true;
      const currentAppId = __app_id;
      const docRef = doc(db, `artifacts/${currentAppId}/users/${userId}/projects/${currentProjectId}`);
      
      const sanitizeForJSON = (obj) => {
        return JSON.parse(JSON.stringify(obj, (key, value) => {
          if (key.startsWith('_') || value instanceof HTMLElement || value instanceof Node) {
            return undefined;
          }
          return value;
        }));
      };

      const sanitizedCredits = sanitizeForJSON(currentCredits);
      const sanitizedSettings = sanitizeForJSON(currentSettings);
      const sanitizedCardCredits = sanitizeForJSON(currentCardCredits);

      await setDoc(docRef, {
        credits: JSON.stringify(sanitizedCredits),
        settings: JSON.stringify(sanitizedSettings),
        cardCredits: JSON.stringify(sanitizedCardCredits),
        updatedAt: serverTimestamp()
      }, { merge: true });

      if (currentSettings.showAutoSaveNotice) {
        showMessageBox("Success", "Project auto-saved to cloud.", "success", true);
      }
    } catch (error) {
        console.error("Auto-save failed:", error);
      showMessageBox("Error", `Auto-save failed: ${error.message}`, "error");
    } finally {
      isSavePending.current = false;
      setTimeout(() => {
        isLocalUpdate.current = false;
      }, 2000);
    }
  }, 5000), [currentProjectId, db, userId, showMessageBox, pendingUploads]);

useEffect(() => {
    if (settings.customFonts && settings.customFonts.length > 0) {
        console.log(`Loading ${settings.customFonts.length} custom fonts from saved project...`);
        settings.customFonts.forEach(fontData => {
            // Removed document.fonts.check() guard to ensure reliable reloading across sessions/refreshes
            const font = new FontFace(fontData.name, `url(${fontData.url})`);
            font.load().then((loadedFont) => {
                document.fonts.add(loadedFont);
            }).catch(err => console.error(`Failed to load saved custom font ${fontData.name}:`, err));
        });
    }
}, [settings.customFonts]);

// Load the built-in font catalog (Poppins, Futura, Arial, etc.) from the same
// canonical fonts/ files the Modal export pipeline reads locally — see
// ensureBuiltInFontsLoaded() near the top of this file. Runs once; the
// module-level cache in that function makes repeated calls (StrictMode,
// remounts) safe/free.
useEffect(() => {
    if (!storage) return;
    ensureBuiltInFontsLoaded(storage);
}, [storage]);

  useEffect(() => {
    if (history.length === 0 && credits.length === 0 && cardCredits.length === 0) return;
    window.lastLocalChangeTime = Date.now();
    debouncedSave(credits, settings, cardCredits);
  }, [credits, settings, cardCredits, debouncedSave, history.length]);

  const HISTORY_LIMIT = 10; // You can adjust this number

  // This function takes a "snapshot" of the current state
  const addToHistory = useCallback((currentCredits, currentSettings, currentCardCredits) => {
    const snapshot = {
      credits: JSON.parse(JSON.stringify(currentCredits)),
      settings: JSON.parse(JSON.stringify(currentSettings)),
      cardCredits: JSON.parse(JSON.stringify(currentCardCredits || []))
    };
    
    // Prevent adding duplicate states to the history
    if (history.length > 0 && JSON.stringify(history[historyIndex]) === JSON.stringify(snapshot)) {
      return;
    }

    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(snapshot);

    // Enforce the 10-step limit
    if (newHistory.length > HISTORY_LIMIT) {
      newHistory.shift(); // Remove the oldest entry
    }
    
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, [history, historyIndex]);

// --- Asset Library listener: user-scoped, loads once and stays live across project switches ---
useEffect(() => {
  if (OFFLINE_MODE || !db || !userId) return;
  const libraryRef = collection(db, `artifacts/${__app_id}/users/${userId}/assetLibrary`);
  const unsubscribeLibrary = onSnapshot(libraryRef, (snapshot) => {
    const assets = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    assets.sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
    setLibraryAssets(assets);
  }, (error) => {
    console.error("Error loading asset library:", error);
  });
  return () => unsubscribeLibrary();
}, [db, userId]);

useEffect(() => {
  localStorage.setItem('autoSaveToLibrary', String(autoSaveToLibrary));
}, [autoSaveToLibrary]);

// Uploads a file to the user-scoped library path (independent of any project's storage)
// and records it in Firestore. Fire-and-forget from upload handlers — failures here
// shouldn't block the actual logo/font upload the user is waiting on.
const uploadAssetToLibrary = async (file, type) => {
  if (!storage || !db || !userId) return;
  const uploadId = Date.now() + Math.random();
  setPendingLibraryUploads(prev => new Set(prev).add(uploadId));
  try {
    const filePath = `artifacts/${__app_id}/users/${userId}/assetLibrary/${type}s/${Date.now()}-${file.name}`;
    const storageRef = ref(storage, filePath);
    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);
    const name = file.name.split('.').slice(0, -1).join('.') || file.name;
    await addDoc(collection(db, `artifacts/${__app_id}/users/${userId}/assetLibrary`), {
      type, name, url: downloadURL, storagePath: filePath, createdAt: serverTimestamp()
    });
  } catch (error) {
    console.error(`Failed to save ${type} to library:`, error);
    // Silent-ish failure: the logo/font itself already succeeded in the project, this is
    // just the "also remember it for next time" step, so we don't interrupt with a modal.
  } finally {
    setPendingLibraryUploads(prev => { const s = new Set(prev); s.delete(uploadId); return s; });
  }
};

const deleteFromLibrary = (asset) => {
  showMessageBox(
    "Remove from Library",
    `Remove "${asset.name}" from your Asset Library? This won't affect any project currently using it — it just stops showing up here.`,
    "warning",
    false,
    async () => {
      try {
        if (asset.storagePath) {
          await deleteObject(ref(storage, asset.storagePath)).catch(() => {}); // already-gone is fine
        }
        await deleteDoc(doc(db, `artifacts/${__app_id}/users/${userId}/assetLibrary/${asset.id}`));
      } catch (error) {
        console.error("Error removing library asset:", error);
        showMessageBox("Error", `Failed to remove asset: ${error.message}`, "error");
      }
    }
  );
};

// Apply a library logo to whatever logo slot is currently active, or create a new
// standalone Logo credit if nothing's selected. No re-upload — reuses the library URL.
const applyLibraryLogo = (asset) => {
  if (isReadOnly) return;
  if (inspectorActiveCredit && inspectorActiveCredit.type === 'logo') {
    if (inspectorActiveCredit.layout === 'grid') {
      setCredits(prev => prev.map(c => {
        if (c.id !== inspectorActiveCredit.id) return c;
        const logos = c.logos || [];
        if (activeLogoId && logos.some(l => l.id === activeLogoId)) {
          return { ...c, logos: logos.map(l => l.id === activeLogoId ? { ...l, logoUrl: asset.url, storagePath: asset.storagePath } : l) };
        }
        if (logos.length >= 9) return c; // grid cap
        return { ...c, logos: [...logos, { id: Date.now() + Math.random(), logoUrl: asset.url, storagePath: asset.storagePath, size: 'medium' }] };
      }));
    } else if (inspectorActiveCredit.layout === 'rows') {
      // Logo Board credit: append into the last row with room (5-logo cap per row,
      // matching the Logo Board's own placement rule), or start a new row.
      setCredits(prev => prev.map(c => {
        if (c.id !== inspectorActiveCredit.id) return c;
        const rows = c.rows && c.rows.length > 0 ? c.rows : [{ id: Date.now() + Math.random(), logos: [] }];
        const newLogo = { id: Date.now() + Math.random(), logoUrl: asset.url, storagePath: asset.storagePath, size: 'medium' };
        const lastRow = rows[rows.length - 1];
        let updatedRows;
        if ((lastRow.logos || []).length < 5) {
          updatedRows = rows.map((r, i) => i === rows.length - 1 ? { ...r, logos: [...(r.logos || []), newLogo] } : r);
        } else {
          updatedRows = [...rows, { id: Date.now() + Math.random(), logos: [newLogo] }];
        }
        return { ...c, rows: updatedRows };
      }));
    } else {
      setCredits(prev => prev.map(c => c.id === inspectorActiveCredit.id ? { ...c, logoUrl: asset.url, storagePath: asset.storagePath } : c));
    }
    showMessageBox("Success", `"${asset.name}" applied.`, "success", true);
    return;
  }

  // Nothing selected — create a new standalone logo credit pre-filled with this asset.
  const newId = Date.now() + Math.random();
  const newCredit = { id: newId, role: 'Role', name: 'Name', type: 'logo', layout: 'rows', rows: [{ id: Date.now() + Math.random(), logos: [{ id: Date.now() + Math.random(), logoUrl: asset.url, storagePath: asset.storagePath, size: 'medium' }] }], order: 'names-left', logoUrl: asset.url, storagePath: asset.storagePath, logoSize: 'medium', logos: [], textItems: [], language: selectedDisplayLanguage === 'all' ? 'en' : selectedDisplayLanguage, multipleNamesColumnCount: 1, blockAlignment: 'center', creditDisplayType: 'scroll', duration: 5 };
  setCredits(prev => [...prev, newCredit]);
  setActiveCreditId(newId);
  setActiveCardId(null);
  showMessageBox("Success", `"${asset.name}" added as a new Logo credit.`, "success", true);
};

// Apply a library font — loads it and registers it in this project's settings, same
// end state as handleFontUpload but with zero bytes uploaded.
const applyLibraryFont = async (asset) => {
  if (isReadOnly) return;
  try {
    const font = new FontFace(asset.name, `url(${asset.url})`);
    await font.load();
    document.fonts.add(font);
    setSettings(s => {
      const filteredFonts = (s.customFonts || []).filter(f => f.name !== asset.name);
      return { ...s, customFonts: [...filteredFonts, { name: asset.name, url: asset.url, storagePath: asset.storagePath }] };
    });
    showMessageBox("Success", `Font "${asset.name}" is ready to use in this project.`, "success", true);
  } catch (error) {
    console.error("Error applying library font:", error);
    showMessageBox("Error", `Failed to load font: ${error.message}`, "error");
  }
};

// Backfill: pulls logos/fonts already used in THIS project into the library, for assets
// uploaded before the library existed (or with auto-save off). Fetches each existing
// download URL as a blob and re-uploads it through the normal library path — no need to
// touch the user's disk again since we already have the file. Only covers the project
// that's currently open; to backfill older projects, open each once and run this there.
const scanProjectAssetsIntoLibrary = async () => {
  if (!storage || !db || !userId) return;

  setIsScanningAssets(true);
  try {
    // Query Firestore directly rather than trusting the local libraryAssets state — the
    // onSnapshot listener can lag a beat behind a just-finished scan, and using stale
    // local state for dedup was re-adding items that were already saved a moment ago.
    const librarySnap = await getDocs(collection(db, `artifacts/${__app_id}/users/${userId}/assetLibrary`));
    const currentLibrary = librarySnap.docs.map(d => d.data());
    const existingLogoUrls = new Set(currentLibrary.filter(a => a.type === 'logo').map(a => a.url));
    const existingFontNames = new Set(currentLibrary.filter(a => a.type === 'font').map(a => a.name));

    const logoTargets = [];
    const seenUrls = new Set();
    let logoCreditsScanned = 0;
    let skippedNoUrl = 0;
    const collectLogo = (url, name) => {
      if (!url) { skippedNoUrl++; console.warn('Asset Library scan: skipped a logo with no URL —', name); return; }
      if (existingLogoUrls.has(url) || seenUrls.has(url)) return;
      seenUrls.add(url);
      logoTargets.push({ url, name: name || 'Logo' });
    };
    const scanCreditList = (list) => {
      (list || []).forEach(c => {
        if (c.type !== 'logo') return;
        logoCreditsScanned++;
        if (c.layout === 'grid') {
          (c.logos || []).forEach(l => collectLogo(l.logoUrl, c.name));
        } else if (c.layout === 'rows') {
          (c.rows || []).forEach(row => (row.logos || []).forEach(l => collectLogo(l.logoUrl, c.name)));
        } else {
          collectLogo(c.logoUrl, c.name);
        }
      });
    };
    scanCreditList(credits);
    scanCreditList(cardCredits);
    console.log(`Asset Library scan: ${logoCreditsScanned} logo-type credit(s) found, ${logoTargets.length} new, ${skippedNoUrl} had no URL.`);

    const fontTargets = (settings.customFonts || []).filter(f => f.url && !existingFontNames.has(f.name));

    if (logoTargets.length === 0 && fontTargets.length === 0) {
      showMessageBox(
        "Nothing New",
        `Scanned ${logoCreditsScanned} logo credit(s) — everything's already in your library.${skippedNoUrl > 0 ? ` (${skippedNoUrl} had no logo file set.)` : ''}`,
        "info", true
      );
      return;
    }

    let failures = 0;
    for (const target of logoTargets) {
      try {
        const blob = await fetch(target.url).then(r => r.blob());
        await uploadAssetToLibrary(new File([blob], `${target.name}.png`, { type: blob.type || 'image/png' }), 'logo');
      } catch (e) { console.error('Failed to backfill logo', target.url, e); failures++; }
    }
    for (const target of fontTargets) {
      try {
        const blob = await fetch(target.url).then(r => r.blob());
        await uploadAssetToLibrary(new File([blob], `${target.name}.ttf`, { type: blob.type || 'font/ttf' }), 'font');
      } catch (e) { console.error('Failed to backfill font', target.url, e); failures++; }
    }
    const added = (logoTargets.length + fontTargets.length) - failures;
    showMessageBox(
      failures > 0 ? "Partially Added" : "Success",
      failures > 0
        ? `Added ${added} asset(s) to your library. ${failures} couldn't be fetched (this can happen if your Storage bucket needs CORS enabled for direct downloads — see gsutil cors set).`
        : `Added ${added} asset(s) to your library. Scanned ${logoCreditsScanned} logo credit(s) total.${skippedNoUrl > 0 ? ` ${skippedNoUrl} had no logo file set and were skipped.` : ''}`,
      failures > 0 ? "warning" : "success",
      true
    );
  } catch (error) {
    console.error("Error scanning project assets:", error);
    showMessageBox("Error", `Scan failed: ${error.message}`, "error");
  } finally {
    setIsScanningAssets(false);
  }
};


useEffect(() => {
  if (OFFLINE_MODE) {
    setCredits([
      { id: 1, role: 'Greg Sims', name: 'Leon', type: 'dual', fontFamily: 'serif', creditDisplayType: 'scroll', duration: 5 },
      { id: 2, role: 'Juror #2/Karla', name: 'Elise Neal', type: 'dual', fontFamily: 'serif', creditDisplayType: 'scroll', duration: 5 },
    ]);
    setCardCredits([]);
    setFirestoreLoading(false);
    return;
  }
  if (!db || !userId || !currentProjectId) {
    if (isAuthReady) setCurrentPage('projects');
    return;
  }

  setFirestoreLoading(true);
  const currentAppId = __app_id;
  const docRef = doc(db, `artifacts/${currentAppId}/users/${userId}/projects/${currentProjectId}`);

  const unsubscribe = onSnapshot(docRef, (docSnap) => {
    if (isSavePending.current || isLocalUpdate.current || docSnap.metadata.hasPendingWrites) {
      console.log("Firestore update skipped due to pending local changes.");
      return;
    }

    const now = Date.now();
    if (window.lastLocalChangeTime && (now - window.lastLocalChangeTime) < 6000) {
      console.log("Firestore update skipped: too close to recent local change.");
      return;
    }

    if (docSnap.exists()) {
      const data = docSnap.data();
      setProjectData(data); 

      const nowDate = new Date();
      const expiresAt = data.expiresAt?.toDate();
      const isExpired = expiresAt && nowDate > expiresAt;
      
      if (isExpired) {
        setIsReadOnly(true);
        
        // Show one-time notice when entering read-only mode
        const hasShownNotice = sessionStorage.getItem(`readonly-notice-${currentProjectId}`);
        if (!hasShownNotice) {
          const expiryMessage = (
            <div className="text-left space-y-3">
              <p className="font-semibold text-red-300">This project expired on {expiresAt.toLocaleDateString()}.</p>
              <p className="text-gray-300">You can view the project but cannot:</p>
              <ul className="list-disc list-inside text-gray-400 text-sm space-y-1">
                <li>Edit credits or settings</li>
                <li>Export videos</li>
                <li>Save changes</li>
              </ul>
              <p className="text-sm text-purple-300 font-medium pt-2">
                Purchase a new project license to continue creating!
              </p>
            </div>
          );
          
          showMessageBox(
            "📖 Read-Only Mode", 
            expiryMessage, 
            "warning",
            false,
            () => {
              setCurrentPage('projects');
            }
          );
          
          sessionStorage.setItem(`readonly-notice-${currentProjectId}`, 'shown');
        }
      } else {
        setIsReadOnly(false);
      }
      // ========================================
      
      setProjectName(data.name || 'Untitled Project');

      try {
        const loadedSettingsStr = typeof data.settings === 'string' ? data.settings : JSON.stringify(data.settings || {});
        if (loadedSettingsStr !== JSON.stringify(settings)) {
          const loadedSettings = JSON.parse(loadedSettingsStr);
          setSettings(s => ({ ...s, ...loadedSettings }));
        }
      } catch (e) { console.error("Error parsing settings:", e); }
      
      
      setProjectName(data.name || 'Untitled Project');

      try {
        const loadedSettingsStr = typeof data.settings === 'string' ? data.settings : JSON.stringify(data.settings || {});
        if (loadedSettingsStr !== JSON.stringify(settings)) {
          const loadedSettings = JSON.parse(loadedSettingsStr);
          setSettings(s => ({ ...s, ...loadedSettings }));
        }
      } catch (e) { console.error("Error parsing settings:", e); }
      
      try {
        const loadedCreditsStr = typeof data.credits === 'string' ? data.credits : JSON.stringify(data.credits || []);
        if (loadedCreditsStr !== JSON.stringify(credits)) {
            const loadedCredits = JSON.parse(loadedCreditsStr).map(c => ({ ...c, id: c.id || Math.random(), creditDisplayType: c.creditDisplayType || 'scroll' }));
            setCredits(loadedCredits);
        }
      } catch (e) { console.error("Error parsing credits:", e); }

      try {
        const loadedCardCreditsStr = typeof data.cardCredits === 'string' ? data.cardCredits : JSON.stringify(data.cardCredits || []);
        if (loadedCardCreditsStr !== JSON.stringify(cardCredits)) {
            const loadedSettings = JSON.parse(typeof data.settings === 'string' ? data.settings : JSON.stringify(data.settings || {}));
            const loadedCardCredits = JSON.parse(loadedCardCreditsStr).map(c => {
                const newCard = { ...c, id: c.id || Math.random(), creditDisplayType: 'card' };
                if (c.cardVerticalAlign && typeof c.cardPositionY === 'undefined') newCard.cardPositionY = { top: 5, center: 50, bottom: 95 }[c.cardVerticalAlign] || 50;
                else if (typeof c.cardPositionY === 'undefined') newCard.cardPositionY = 50;
                if (c.cardHorizontalAlign && typeof c.cardPositionX === 'undefined') newCard.cardPositionX = { left: 5, center: 50, right: 95 }[c.cardHorizontalAlign] || 50;
                else if (typeof c.cardPositionX === 'undefined') newCard.cardPositionX = 50;
                if (c.type === 'main-title' && !c.textLayers) {
                    newCard.textLayers = [];
                    if (c.name) newCard.textLayers.push({ id: Math.random(), content: c.name, fontSize: c.mainTitleFontSize || loadedSettings.cardFontSize || 48, fontWeight: 'bold' });
                    if (c.role) newCard.textLayers.push({ id: Math.random(), content: c.role, fontSize: c.subtitleFontSize || (loadedSettings.cardFontSize * 0.7) || 32, fontStyle: 'italic' });
                }
                return newCard;
            });
            setCardCredits(loadedCardCredits);
        }
      } catch (e) { console.error("Error parsing card credits:", e); }

      if (history.length === 0) {
        const initialSnapshot = {
            credits: JSON.parse(typeof data.credits === 'string' ? data.credits : JSON.stringify(data.credits || [])),
            settings: JSON.parse(typeof data.settings === 'string' ? data.settings : JSON.stringify(data.settings || {})),
            cardCredits: JSON.parse(typeof data.cardCredits === 'string' ? data.cardCredits : JSON.stringify(data.cardCredits || []))
        };
        setHistory([initialSnapshot]);
        setHistoryIndex(0);
      }
    } else {
      showMessageBox("Error", "Project not found or deleted.", "error");
      setCurrentPage('projects');
    }
    setFirestoreLoading(false);
  }, (error) => {
    console.error("Error listening to project data:", error);
    showMessageBox("Error", `Failed to load project: ${error.message}`, "error");
    setFirestoreLoading(false);
    setCurrentPage('projects');
  });

  const templatesCollectionRef = collection(db, `artifacts/${currentAppId}/users/${userId}/templates`);
  const unsubscribeTemplates = onSnapshot(templatesCollectionRef, (snapshot) => {
      const templates = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setUserTemplates(templates);
  }, (error) => {
      console.error("Error fetching user templates:", error);
  });

  return () => {
    unsubscribe();
    unsubscribeTemplates();
  };
}, [currentProjectId, db, userId, isAuthReady]);

  const hasCards = cardCredits && cardCredits.length > 0;
  const sequenceShowsScroll = settings.cardSequence !== 'cards-only';

  let cardToDisplay = null;
  if (playbackState === 'cards' && currentCardIndex > -1) {
    cardToDisplay = cardCredits[currentCardIndex];
  } else if (playbackState === 'idle') {
    if (activeCardId) {
      cardToDisplay = cardCredits.find(c => c.id === activeCardId);
    } else if (hasCards && (settings.cardSequence === 'cards-only' || settings.cardSequence === 'before')) {
      cardToDisplay = cardCredits[0];
    }
  }

  const isCardVisible = !!cardToDisplay;
  const isScrollVisible = !isCardVisible && sequenceShowsScroll;

  useEffect(() => {
    if (playbackState === 'idle' && isScrollVisible && scrollRef.current && previewHeight > 0) {
      const initialScrollPosition = settings.scrollDirection === 'down' 
          ? previewHeight 
          : scrollRef.current.scrollHeight - (2 * previewHeight);
          
      scrollRef.current.scrollTo({ top: initialScrollPosition, behavior: 'smooth' });

    } else if (playbackState === 'idle' && !isCardVisible && scrollRef.current) {
      const startPos = settings.scrollDirection === 'down' 
          ? 0 
          : (scrollRef.current.scrollHeight - scrollRef.current.clientHeight);
      scrollRef.current.scrollTop = Math.max(0, startPos);
    }
  }, [playbackState, isScrollVisible, isCardVisible, previewHeight, settings.scrollDirection]);

  const undo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      const prevState = history[newIndex];
      setCredits(JSON.parse(JSON.stringify(prevState.credits)));
      setSettings(JSON.parse(JSON.stringify(prevState.settings)));
      setCardCredits(JSON.parse(JSON.stringify(prevState.cardCredits || [])));
      rewindAnimation();
    }
  };

  const redo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      const nextState = history[newIndex];
      setCredits(JSON.parse(JSON.stringify(nextState.credits)));
      setSettings(JSON.parse(JSON.stringify(nextState.settings)));
      setCardCredits(JSON.parse(JSON.stringify(nextState.cardCredits || [])));
      rewindAnimation();
    }
  };

 const addCredit = (type = 'single') => {
    if (isReadOnly) {
        showMessageBox(
            "Read-Only Mode", 
            "This project has expired. Purchase a new project license to create credits.", 
            "error",
            false,
            () => setCurrentPage('projects')
        );
        return;
    }
    
    const newId = Date.now() + Math.random();
    let newCredit = { id: newId, role: 'Role', name: 'Name', type: type, layout: 'single', order: 'names-left', logoUrl: '', logoSize: 'medium', logos: [], textItems: [], language: selectedDisplayLanguage === 'all' ? 'en' : selectedDisplayLanguage, multipleNamesColumnCount: 1, blockAlignment: 'center', creditDisplayType: 'scroll', duration: 5 };

    // Single Logo & Logo Grid are retired from the picker (Logo Board covers both — one
    // row/one logo for what used to be Single, several logos per row for what used to be
    // Grid) — so new logo credits start on the Board instead of defaulting to 'single'.
    // Existing projects with layout 'single'/'grid' keep rendering exactly as before.
    if (type === 'logo') {
      newCredit.layout = 'rows';
      newCredit.rows = [{ id: Date.now() + Math.random(), logos: [] }];
    }

    // Song Credits is a mode of the 'multiple' block (same storage, same
    // duration/export pipeline), not a separate type — see parseSongEntries
    // in CreditPreview.js and the Song Board functions above for why.
    if (type === 'song') {
      newCredit.type = 'multiple';
      newCredit.multipleMode = 'song';
      newCredit.role = '';
      newCredit.name = 'Song Title\tWritten and Performed by Artist Name';
      newCredit.multipleNamesColumnCount = 2;
      newCredit.songSwapBold = false;
    }

    if (type === 'main-title') {
      newCredit = {
          id: newId,
          type: 'main-title',
          creditDisplayType: 'card',
          // duration intentionally omitted: falls back to settings.cardDuration (global).
          cardAnimationType: 'fade', 
          cardPositionX: 50,
          cardPositionY: 50,
          cardTextAlign: 'center',
          cardVerticalSpacing: 0.5,
          textLayers: [
              { id: Math.random(), content: 'Subtitle', fontSize: 28, fontWeight: '400', fontStyle: 'normal', color: '#FFFFFF', fontFamily: settings.cardFontFamily || 'poppins', transform: 'normal' },
              { id: Math.random(), content: 'Main Title', fontSize: 46, fontWeight: '400', fontStyle: 'normal', color: '#CCCCCC', fontFamily: settings.cardFontFamily || 'poppins', transform: 'normal' },
          ]
      };
      setCardCredits(prevCards => {
          if (activeCardId) {
              const activeIndex = prevCards.findIndex(c => c.id === activeCardId);
              if (activeIndex !== -1) {
                  const newCards = [...prevCards];
                  newCards.splice(activeIndex + 1, 0, newCredit);
                  return newCards;
              }
          }
          return [...prevCards, newCredit];
      });
      setActiveCardId(newId);
      setActiveCreditId(null);
      setSettings(s => ({ ...s, cardsEnabled: true }));
    } else {
      setCredits(prevCredits => {
          if (activeCreditId) {
              const activeIndex = prevCredits.findIndex(c => c.id === activeCreditId);
              if (activeIndex !== -1) {
                  const newCredits = [...prevCredits];
                  newCredits.splice(activeIndex + 1, 0, newCredit);
                  return newCredits;
              }
          }
          return [...prevCredits, newCredit];
      });
      setActiveCreditId(newId);
      setActiveCardId(null);
    }

    setTimeout(() => {
        const listElementId = type === 'main-title' ? `card-item-${newId}` : `credit-item-${newId}`;
        const listElement = document.getElementById(listElementId);
        if (listElement) listElement.scrollIntoView({ behavior: 'smooth', block: 'center' });

        if (type !== 'main-title') {
            const previewElement = document.getElementById(`preview-credit-${newId}`);
            if (previewElement) {
                const previewRect = previewRef.current.getBoundingClientRect();
                const elementRect = previewElement.getBoundingClientRect();
                const targetScrollTop = scrollRef.current.scrollTop + elementRect.top - previewRect.top - (previewRect.height / 2) + (elementRect.height / 2);
                scrollRef.current.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
            }
        }
        if (type === 'main-title') { handleCreditSelect(newId, { stopPropagation: () => {} }, 'card'); }
        else { handleCreditSelect(newId, { stopPropagation: () => {} }, 'scroll'); }
    }, 100);
  };

 const performDelete = (scrollIdsToDelete, cardIdsToDelete) => {
    if (isReadOnly) {
        showMessageBox("Read-Only Mode", "Cannot delete from expired projects.", "error", true);
        return;
    }

    if (scrollIdsToDelete.length > 0) {
        setCredits(prev => prev.filter(c => !scrollIdsToDelete.includes(c.id)));
        if (scrollIdsToDelete.some(id => selectedCreditIds.has(id))) {
            setActiveCreditId(null);
        }
    }
    if (cardIdsToDelete.length > 0) {
        setCardCredits(prev => prev.filter(c => !cardIdsToDelete.includes(c.id)));
        if (cardIdsToDelete.some(id => selectedCreditIds.has(id))) {
            setActiveCardId(null);
        }
    }
    setSelectedCreditIds(new Set());
  };

  const removeCredit = (idsToRemove, isCard) => {
    const type = isCard ? "Title Card" : "credit";
    const ids = Array.isArray(idsToRemove) ? idsToRemove : [idsToRemove];
    showMessageBox( "Confirm Deletion", `Are you sure you want to remove ${ids.length} ${type}(s)? This action cannot be undone.`, "warning", false,
      () => { if (isCard) { performDelete([], ids); } else { performDelete(ids, []); } }
    );
  };

  const bulkDeleteSelectedCredits = () => {
    if (selectedCreditIds.size === 0) { showMessageBox("Info", "No credits selected for deletion.", "info", true); return; }
    showMessageBox("Confirm Deletion", `Are you sure you want to delete ${selectedCreditIds.size} selected credits? This action cannot be undone.`, "warning", false, () => {
        const scrollIdsToDelete = []; const cardIdsToDelete = [];
        selectedCreditIds.forEach(id => {
            if (credits.some(c => c.id === id)) { scrollIdsToDelete.push(id); }
            else if (cardCredits.some(c => c.id === id)) { cardIdsToDelete.push(id); }
        });
        performDelete(scrollIdsToDelete, cardIdsToDelete);
    });
  };

  const addTextLayer = (creditId) => { setCardCredits(prev => prev.map(c => { if (c.id === creditId) { const newLayer = { id: Math.random(), content: 'New Layer', fontSize: 17, fontWeight: 'normal', fontStyle: 'normal', color: '#FFFFFF', fontFamily: settings.cardFontFamily || 'poppins' }; const newLayers = c.textLayers ? [...c.textLayers, newLayer] : [newLayer]; return { ...c, textLayers: newLayers }; } return c; })); };
  const removeTextLayer = (creditId, layerIndex) => { setCardCredits(prev => prev.map(c => { if (c.id === creditId) { const newLayers = c.textLayers.filter((_, index) => index !== layerIndex); return { ...c, textLayers: newLayers }; } return c; })); };
  const updateTextLayer = (creditId, layerIndex, updatedLayer) => { setCardCredits(prev => prev.map(c => { if (c.id === creditId) { const newLayers = [...c.textLayers]; newLayers[layerIndex] = updatedLayer; return { ...c, textLayers: newLayers }; } return c; })); };
  const reorderTextLayers = (creditId, layerIndex, direction) => { setCardCredits(prev => prev.map(c => { if (c.id === creditId) { const layers = [...c.textLayers]; const targetIndex = direction === 'up' ? layerIndex - 1 : layerIndex + 1; if (targetIndex >= 0 && targetIndex < layers.length) { const item = layers.splice(layerIndex, 1)[0]; layers.splice(targetIndex, 0, item); return { ...c, textLayers: layers }; } } return c; })); };
  const clearAllCredits = () => { showMessageBox( "Confirm Clear All", "Are you sure you want to clear all credits? This action cannot be undone.", "warning", false, () => { setCredits([]); setCardCredits([]); setActiveCreditId(null); setActiveCardId(null); setSelectedCreditIds(new Set()); rewindAnimation(); showMessageBox("Success", "All credits cleared!", "success", true); } ); };
  
const updateCredit = useCallback((id, field, value, itemIndex = null, isCardContext = false) => {
    if (isReadOnly) {
        showMessageBox("Read-Only Mode", "Cannot edit expired projects.", "error", true);
        return;
    }
    
    const setTargetCredits = isCardContext ? setCardCredits : setCredits;
    setTargetCredits(prevCredits => prevCredits.map(c => {
        if (c.id === id) {
            let updatedCredit = { ...c };

            // FIX: This block correctly handles individual logo updates within a grid.
if (c.type === 'logo' && c.layout === 'grid' && itemIndex !== null && ['logoUrl', 'size', 'opacity', 'offsetX', 'offsetY'].includes(field)) {
            const updatedLogos = c.logos.map(logo => {
                    if (logo.id === itemIndex) {
                        return { ...logo, [field]: value };
                    }
                    return logo;
                });
                updatedCredit.logos = updatedLogos;
            } 
            else if (field === 'textLayers' && c.type === 'main-title' && itemIndex !== null) {
                const updatedLayers = [...(c.textLayers || [])];
                if (updatedLayers[itemIndex]) {
                    updatedLayers[itemIndex] = { ...updatedLayers[itemIndex], content: value };
                    updatedCredit.textLayers = updatedLayers;
                }
            } else if (c.type === 'text-group' && itemIndex !== null && field === 'textItemContent') {
                const updatedTextItems = [...c.textItems];
                updatedTextItems[itemIndex] = { ...updatedTextItems[itemIndex], content: value };
                updatedCredit.textItems = updatedTextItems;
            } else if (field === 'name' && c.type === 'multiple') {
                updatedCredit.name = value;
            } else if (field === 'multipleNamesColumnCount' && c.type === 'multiple') {
                // Names live in credit.name (newline-delimited) and CreditPreview splits
                // them into individual grid items at render time, so CSS grid auto-flow
                // handles the redistribution — just update the column count itself.
                updatedCredit[field] = value;
            } else {
                updatedCredit[field] = value;
            }
            return updatedCredit;
        }
        return c;
    }));
}, [isReadOnly ]);

const updateCardCredit = useCallback((cardCreditData) => { setCardCredits(prevCardCredits => { const existingIndex = prevCardCredits.findIndex(c => c.id === cardCreditData.id); if (existingIndex > -1) { const newCards = [...prevCardCredits]; newCards[existingIndex] = { ...newCards[existingIndex], ...cardCreditData }; return newCards; } else { return [...prevCardCredits, cardCreditData]; } }); }, []);
// Clears any per-card Duration override on every title card, so they all fall back
// to settings.cardDuration (the global "Card Duration (s)" field) again. This exists
// because older cards were created with an explicit duration baked in, which — once
// set — permanently outranks the global value; this is the bulk escape hatch for that.
const resetAllCardDurations = useCallback(() => {
    setCardCredits(prevCardCredits => prevCardCredits.map(c => {
        if (c.duration === undefined || c.duration === null) return c;
        const { duration, ...rest } = c;
        return rest;
    }));
}, []);
const duplicateCredit = (id, isCard) => {
    if (!id) return;
    const sourceList = isCard ? cardCredits : credits; const setTargetList = isCard ? setCardCredits : setCredits;
    const creditToDuplicate = sourceList.find(c => c.id === id); if (!creditToDuplicate) return;
    const newCredit = { ...JSON.parse(JSON.stringify(creditToDuplicate)), id: Date.now() + Math.random() };
    setTargetList(prevList => { const index = prevList.findIndex(c => c.id === id); const newList = [...prevList]; newList.splice(index + 1, 0, newCredit); return newList; });
    if (isCard) setActiveCardId(newCredit.id); else setActiveCreditId(newCredit.id);
  };
  const copyCredit = (id, isCard) => {
    if (!id) return;
    const sourceList = isCard ? cardCredits : credits; const creditToCopy = sourceList.find(c => c.id === id);
    if (creditToCopy) {
        if(isCard) { copyCardStyle(); }
        else { setCopiedCredits([JSON.parse(JSON.stringify(creditToCopy))]); }
    }
  };
  const pasteCredit = () => {
    if (copiedCredits.length === 0) { showMessageBox("Clipboard Empty", "Nothing to paste.", "info", true); return; }
    const newCreditsToPaste = copiedCredits.map(c => ({ ...c, id: Date.now() + Math.random() }));
    const firstPastedIsCard = newCreditsToPaste[0].creditDisplayType === 'card';
    if (firstPastedIsCard) { setCardCredits(prev => [...prev, ...newCreditsToPaste]); }
    else { setCredits(prev => [...prev, ...newCreditsToPaste]); }
  };
  const copySettings = () => {
    if (!inspectorActiveCredit) { showMessageBox("No Selection", "Select a credit first, then use Copy Style.", "info", true); setCopiedSettings(null); return; }
    const c = inspectorActiveCredit;
    // Gather per-credit style fields — include any that are explicitly set on the credit,
    // falling back to nothing (undefined values are omitted). The paste step sets override: true.
    const settingsToCopy = { override: true };
    const styleFields = ['fontFamily', 'fontSize', 'marginBottom', 'fontWeight', 'fontStyle', 'textDecoration', 'lineHeight', 'letterSpacing', 'textAlign', 'roleColor', 'nameColor', 'roleFontSize', 'roleFontWeight', 'nameFontSize', 'nameFontWeight'];
    styleFields.forEach(field => { if (c[field] !== undefined) settingsToCopy[field] = c[field]; });
    const hasAnyStyle = Object.keys(settingsToCopy).length > 1; // more than just 'override'
    if (!hasAnyStyle) { showMessageBox("No Styles Set", "This credit has no custom styles yet. Make a style change in the Style tab first, then Copy Style.", "info", true); setCopiedSettings(null); return; }
    setCopiedSettings(settingsToCopy); showMessageBox("Copied!", "Credit styles copied. Select target credit(s) and use Paste Style.", "success", true);
  };
  const pasteSettings = () => {
    if (!copiedSettings) { showMessageBox("Clipboard Empty", "No formatting settings copied. Use Copy Style first.", "info", true); return; }
    if (selectedCreditIds.size === 0 && !inspectorActiveCredit) { showMessageBox("Error", "No credit selected to paste settings onto.", "error", true); return; }
    const targetIds = selectedCreditIds.size > 0 ? selectedCreditIds : (inspectorActiveCredit ? new Set([inspectorActiveCredit.id]) : new Set());
    if (targetIds.size === 0) { showMessageBox("Error", "No credit(s) selected or active to paste settings onto.", "error", true); return; }
    const applyPaste = (ids) => {
        let updatedCards = [...cardCredits]; let updatedScrolls = [...credits];
        ids.forEach(id => {
            const cardIndex = updatedCards.findIndex(c => c.id === id);
            if (cardIndex > -1) { updatedCards[cardIndex] = { ...updatedCards[cardIndex], ...copiedSettings, override: true }; }
            else { const scrollIndex = updatedScrolls.findIndex(c => c.id === id); if (scrollIndex > -1) { updatedScrolls[scrollIndex] = { ...updatedScrolls[scrollIndex], ...copiedSettings, override: true }; } }
        });
        setCardCredits(updatedCards); setCredits(updatedScrolls);
        showMessageBox("Pasted!", `Style applied to ${ids.size} credit(s).`, "success", true);
    };
    if (targetIds.size > 1) { showMessageBox( "Paste to Selection?", `Apply the copied style to all ${targetIds.size} selected credits?`, "warning", false, () => applyPaste(targetIds) ); }
    else { applyPaste(targetIds); }
  };
  const copyCardStyle = () => {
    if (!inspectorActiveCredit || inspectorActiveCredit.creditDisplayType !== 'card') return;
    const { cardPositionX, cardPositionY, duration, cardTextAlign, cardVerticalSpacing, cardNameGap, textLayers } = inspectorActiveCredit;
    const layerStyles = (textLayers || []).map(layer => { const { id, content, ...style } = layer; return style; });
    setCopiedCardStyle({ cardPositionX, cardPositionY, duration, cardTextAlign, cardVerticalSpacing, cardNameGap, layerStyles });
  };
  const pasteCardStyle = () => {
    if (!copiedCardStyle) { showMessageBox("Clipboard Empty", "No card style has been copied. Use 'Ctrl+C' on a card first.", "info", true); return; }
    const targetIds = selectedCreditIds;
    if (targetIds.size === 0) { showMessageBox("No Selection", "Select one or more title cards to paste the style onto.", "error", true); return; }
    setShowPasteOptionsModal(true);
  };
  const executePasteStyle = () => {
    const targetIds = selectedCreditIds;
    setCardCredits(prevCards =>
        prevCards.map(card => {
            if (targetIds.has(card.id) && card.creditDisplayType === 'card') {
                let newCard = { ...card };
                if (pasteOptions.transform) { newCard.cardPositionX = copiedCardStyle.cardPositionX; newCard.cardPositionY = copiedCardStyle.cardPositionY; }
                if (pasteOptions.duration) { newCard.duration = copiedCardStyle.duration; }
                if (pasteOptions.layout) { newCard.cardTextAlign = copiedCardStyle.cardTextAlign; newCard.cardVerticalSpacing = copiedCardStyle.cardVerticalSpacing; newCard.cardNameGap = copiedCardStyle.cardNameGap; }
                if (pasteOptions.layerStyles && copiedCardStyle.layerStyles) {
                    const newTextLayers = card.textLayers.map((originalLayer, index) => {
                        const styleToApply = copiedCardStyle.layerStyles[index];
                        if(styleToApply) { return { ...originalLayer, ...styleToApply }; }
                        return originalLayer;
                    });
                    newCard.textLayers = newTextLayers;
                }
                return newCard;
            }
            return card;
        })
    );
    setShowPasteOptionsModal(false); showMessageBox("Style Pasted!", `Selected attributes applied to ${targetIds.size} card(s).`, "success", true);
  };
  const saveAsTemplate = async () => {
      if (OFFLINE_MODE || !db || !userId) return;
      const templateName = prompt("Please enter a name for your template:");
      if (!templateName || !templateName.trim()) { showMessageBox("Cancelled", "Template saving was cancelled.", "info", true); return; }
      setFirestoreLoading(true);
      try {
          const currentAppId = __app_id;
          const templateData = { name: templateName, settings: JSON.stringify(settings), credits: JSON.stringify(credits), cardCredits: JSON.stringify(cardCredits), createdAt: serverTimestamp() };
          const templatesCollectionRef = collection(db, `artifacts/${currentAppId}/users/${userId}/templates`);
          await addDoc(templatesCollectionRef, templateData);
          showMessageBox("Success", `Template "${templateName}" saved!`, "success", true);
      } catch (error) { console.error("Error saving template:", error); showMessageBox("Error", `Failed to save template: ${error.message}`, "error"); }
      finally { setFirestoreLoading(false); }
  };
// Sample lists for the Batch Add modal's "Try a sample" affordance. The whole
// point of Batch Add is that a trial user shouldn't have to download a template
// and build a file before they can see the app do anything — one click loads a
// realistic mixed paste (sections, role/name pairs, and a multi-name group) so
// the smart detection is visible immediately.
const BATCH_SAMPLE_SCROLL = [
    'Directed by\tAVA GREEN',
    'Written by\tELIJAH CARTER',
    'Produced by\tSOFIA NADEEM',
    'CAST',
    'MARCUS\tJASON MITCHELL',
    'DELIA\tTAYE DIGGS',
    'THE WARDEN\tRON PERLMAN',
    'Director of Photography\tLEO YAMAMOTO',
    'Production Designer\tCHLOE MONROE',
    'Edited by\tISAAC BAUTISTA',
    'Stunts',
    '\tRAY OKONKWO',
    '\tMIA HALVORSEN',
    '\tDEVON PRICE',
    'Music by\tNOAH ROSSI',
].join('\n');

const BATCH_SAMPLE_CARDS = [
    'A Film by\tAVA GREEN',
    'Starring\tJASON MITCHELL',
    'Music by\tNOAH ROSSI',
    'Produced by\tSOFIA NADEEM',
].join('\n');

// --- Smart Batch Add: parses a raw paste into editable {role, name} rows, ---
// --- then classifies those rows into scroll credits using the same dual/  ---
// --- multiple/title/single rules as CSV import, so a mixed CAST/CREW/     ---
// --- Stunts-style paste doesn't need to be split into separate pastes.    ---
const parseBatchPasteToRows = (text) => {
    if (!text.trim()) return [];
    return text.split('\n')
        .filter(line => line.trim().length > 0)
        .map((line, idx) => {
            // Prefer a real tab (actual spreadsheet paste). Fall back to 2+ spaces
            // for lines typed by hand without a literal tab character.
            let cols = line.split('\t');
            if (cols.length < 2) cols = line.split(/\s{2,}/);
            return {
                id: Date.now() + Math.random() + idx,
                role: (cols[0] || '').trim(),
                name: (cols[1] || '').trim(),
            };
        });
};

// Returns a badge label per row for the preview table, using a lookahead so a
// role-only row followed by name-only rows reads as a group header rather than
// three unrelated rows.
// Computes the EFFECTIVE type of every row — auto-detected, unless the user
// has manually overridden it via the Type dropdown (row.manualType). This is
// the single source of truth: the preview badges below and the actual commit
// step both read from this, so what you see is exactly what you get.
//
// Manually setting a row to "Multiname Header" opens a cascade: every row
// after it with no manual override of its own, and no Role of its own, is
// swept in as a grouped member — until the next boundary row (anything with
// an explicit type, or a row that has its own Role filled) closes it. That's
// what "Special Thanks" followed by a run of names needs, since auto-detection
// only ever opens a group from a Role-only row, never a Name-only header.
const getBatchRowTypes = (rows) => {
    const types = new Array(rows.length).fill('empty');
    let cascadeOpen = false;
    for (let i = 0; i < rows.length; i++) {
        const role = (rows[i].role || '').trim();
        const name = (rows[i].name || '').trim();
        const manual = rows[i].manualType;

        if (!role && !name && !manual) { types[i] = 'empty'; cascadeOpen = false; continue; }

        if (manual === 'group-header') { types[i] = 'group-header'; cascadeOpen = true; continue; }
        if (manual === 'group-member') { types[i] = 'group-member'; continue; }
        if (manual === 'dual' || manual === 'single' || manual === 'title') {
            types[i] = manual;
            cascadeOpen = false;
            continue;
        }

        if (cascadeOpen) {
            if (role && name) { types[i] = 'dual'; cascadeOpen = false; continue; }
            if (role && !name) { cascadeOpen = false; /* falls through to normal detection below */ }
            else if (!role && name) { types[i] = 'group-member'; continue; }
        }

        if (role && name) { types[i] = 'dual'; continue; }
        if (role && !name) {
            let lookahead = i + 1;
            let sawName = false;
            while (lookahead < rows.length && !(rows[lookahead].role || '').trim() && (rows[lookahead].name || '').trim() && !rows[lookahead].manualType) {
                sawName = true;
                types[lookahead] = 'group-member';
                lookahead++;
            }
            types[i] = sawName ? 'group-header' : 'title';
            i = lookahead - 1;
            continue;
        }
        if (!role && name) { types[i] = 'single'; }
    }
    return types;
};

const BATCH_ROW_BADGE_META = {
    dual: { label: 'Role + Name', className: 'bg-blue-500/20 text-blue-300' },
    single: { label: 'Section Divider', className: 'bg-purple-500/20 text-purple-300' },
    title: { label: 'Header', className: 'bg-purple-500/20 text-purple-300' },
    'group-header': { label: 'Multiname Header', className: 'bg-amber-500/20 text-amber-300' },
    'group-member': { label: 'Grouped Below', className: 'bg-zinc-700 text-zinc-400' },
    empty: { label: 'Empty', className: 'bg-zinc-800 text-zinc-600' },
};

// Builds the actual credit objects from the preview rows using the same
// effective types getBatchRowTypes computed for the badges — no separate
// re-detection, so the preview and the commit can never disagree.
const classifyBatchRowsToScrollCredits = (rows, types, startId, blockNameColor) => {
    const result = [];
    let lastId = startId;
    const baseCredit = {
        // No per-credit style seeds (font, weight, line height, align...): new
        // credits follow the global defaults until the user overrides something.
        // Seeded placeholders like fontFamily 'default' used to kick in the moment
        // any Style-tab control was touched, dropping the block to Times.
        language: 'en', multipleNamesColumnCount: 1,
        blockAlignment: 'center'
    };
    for (let i = 0; i < rows.length; i++) {
        const role = (rows[i].role || '').trim();
        const name = (rows[i].name || '').trim();
        const type = types[i];
        if (type === 'empty' || type === 'group-member') continue;

        if (type === 'dual') {
            result.push({ ...baseCredit, id: ++lastId, type: 'dual', role, name, creditDisplayType: 'scroll' });
        } else if (type === 'group-header') {
            let names = [];
            let j = i + 1;
            while (j < rows.length && types[j] === 'group-member') {
                const memberName = (rows[j].name || '').trim() || (rows[j].role || '').trim();
                if (memberName) names.push(memberName);
                j++;
            }
            result.push({ ...baseCredit, id: ++lastId, type: 'multiple', role: role || name, name: names.join('\n'), creditDisplayType: 'scroll' });
            i = j - 1;
        } else if (type === 'title') {
            result.push({ ...baseCredit, id: ++lastId, type: 'title', name: role || name, role: '', creditDisplayType: 'scroll' });
        } else if (type === 'single') {
            result.push({ ...baseCredit, id: ++lastId, type: 'single', name: name || role, role: '', creditDisplayType: 'scroll' });
        }
    }
    return result;
};

// Layer visual defaults — deliberately matching the app's own "Add Credit >
// Title Card" defaults (see addCredit('main-title')) so a batch-created card
// looks identical to one built by hand: Role → small italic line, Name → big
// bold line.
const CARD_LAYER_STYLE_DEFAULTS = {
    subtitle: { fontSize: 28, fontWeight: 'normal', fontStyle: 'italic', color: '#CCCCCC' },
    main: { fontSize: 46, fontWeight: 'bold', fontStyle: 'normal', color: '#FFFFFF' },
};

// Builds Title Cards from the SAME row-pairing logic Scrolling Credits uses
// (parseBatchPasteToRows + getBatchRowTypes) — a role line followed by a name
// line (or several grouped name lines) is one logical credit either way; the
// only thing that differs between the two modes is what we build from it
// (a scroll block vs. a Title Card with layers). Previously this parser only
// combined role+name when they were tab-separated on the SAME line, so a
// spreadsheet paste with role and name on separate rows (the normal case —
// see CTC_IMPORT_TEMPLATE) landed as two orphaned one-layer cards instead of
// one two-layer card.
const parseBatchPasteToCards = (text) => {
    if (!text.trim()) return [];
    const rows = parseBatchPasteToRows(text);
    const types = getBatchRowTypes(rows);
    const cards = [];

    for (let i = 0; i < rows.length; i++) {
        const role = (rows[i].role || '').trim();
        const name = (rows[i].name || '').trim();
        const type = types[i];
        if (type === 'empty' || type === 'group-member') continue;

        const id = Date.now() + Math.random() + i;
        if (type === 'dual') {
            const layers = [];
            if (role) layers.push({ id: Date.now() + Math.random(), content: role, style: 'subtitle' });
            if (name) layers.push({ id: Date.now() + Math.random(), content: name, style: 'main' });
            cards.push({ id, layers });
        } else if (type === 'group-header') {
            const layers = [{ id: Date.now() + Math.random(), content: role || name, style: 'subtitle' }];
            let j = i + 1;
            while (j < rows.length && types[j] === 'group-member') {
                const memberName = (rows[j].name || '').trim() || (rows[j].role || '').trim();
                if (memberName) layers.push({ id: Date.now() + Math.random(), content: memberName, style: 'main' });
                j++;
            }
            cards.push({ id, layers });
            i = j - 1;
        } else if (type === 'title' || type === 'single') {
            cards.push({ id, layers: [{ id: Date.now() + Math.random(), content: role || name, style: 'main' }] });
        }
    }
    return cards;
};

// Mirrors classifyBatchRowsToScrollCredits, but for cards: builds real
// textLayers arrays from the (possibly hand-edited) preview cards. Blank
// layers are dropped; a card left fully blank is skipped entirely.
const classifyBatchCardsToTitleCredits = (cards, startId, cardFontFamily) => {
    const result = [];
    let lastId = startId;
    for (const card of cards) {
        const nonEmptyLayers = card.layers.filter(l => (l.content || '').trim());
        if (nonEmptyLayers.length === 0) continue;
        result.push({
            id: ++lastId,
            type: 'main-title',
            creditDisplayType: 'card',
            cardPositionX: 50,
            cardPositionY: 50,
            cardTextAlign: 'center',
            cardVerticalSpacing: 0.5,
            textLayers: nonEmptyLayers.map(l => ({
                id: Math.random(),
                content: l.content.trim(),
                fontFamily: cardFontFamily || 'poppins',
                transform: 'normal',
                ...(CARD_LAYER_STYLE_DEFAULTS[l.style] || CARD_LAYER_STYLE_DEFAULTS.main),
            })),
        });
    }
    return result;
};

useEffect(() => {
    if (batchAddType === 'dual') {
        const rows = parseBatchPasteToRows(batchAddText);
        setBatchPreviewRows(reverseBatchOrder ? [...rows].reverse() : rows);
    } else if (batchAddType === 'main-title') {
        const cards = parseBatchPasteToCards(batchAddText);
        setBatchCardPreviewCards(reverseBatchOrder ? [...cards].reverse() : cards);
    }
}, [batchAddText, batchAddType, reverseBatchOrder]);

const updateBatchPreviewCardLayer = (cardId, layerId, content) => {
    setBatchCardPreviewCards(prev => prev.map(c => c.id === cardId ? { ...c, layers: c.layers.map(l => l.id === layerId ? { ...l, content } : l) } : c));
};
const addLayerToBatchPreviewCard = (cardId, style) => {
    setBatchCardPreviewCards(prev => prev.map(c => c.id === cardId ? { ...c, layers: [...c.layers, { id: Date.now() + Math.random(), content: '', style }] } : c));
};
const removeLayerFromBatchPreviewCard = (cardId, layerId) => {
    setBatchCardPreviewCards(prev => prev.map(c => c.id === cardId ? { ...c, layers: c.layers.filter(l => l.id !== layerId) } : c));
};
const moveBatchPreviewCardLayer = (cardId, layerId, direction) => {
    setBatchCardPreviewCards(prev => prev.map(c => {
        if (c.id !== cardId) return c;
        const idx = c.layers.findIndex(l => l.id === layerId);
        const newIdx = idx + direction;
        if (idx === -1 || newIdx < 0 || newIdx >= c.layers.length) return c;
        const layers = [...c.layers];
        [layers[idx], layers[newIdx]] = [layers[newIdx], layers[idx]];
        return { ...c, layers };
    }));
};
const addBlankBatchPreviewCard = () => {
    setBatchCardPreviewCards(prev => [...prev, { id: Date.now() + Math.random(), layers: [{ id: Date.now() + Math.random(), content: '', style: 'subtitle' }, { id: Date.now() + Math.random(), content: '', style: 'main' }] }]);
};
const insertBlankBatchPreviewCardAfter = (afterId) => {
    setBatchCardPreviewCards(prev => {
        const idx = prev.findIndex(c => c.id === afterId);
        const newCard = { id: Date.now() + Math.random(), layers: [{ id: Date.now() + Math.random(), content: '', style: 'subtitle' }, { id: Date.now() + Math.random(), content: '', style: 'main' }] };
        if (idx === -1) return [...prev, newCard];
        const copy = [...prev];
        copy.splice(idx + 1, 0, newCard);
        return copy;
    });
};
const removeBatchPreviewCard = (cardId) => {
    setBatchCardPreviewCards(prev => prev.filter(c => c.id !== cardId));
};

const updateBatchPreviewRow = (id, field, value) => {
    setBatchPreviewRows(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));
};
const removeBatchPreviewRow = (id) => {
    setBatchPreviewRows(prev => prev.filter(r => r.id !== id));
};
const addBlankBatchPreviewRow = () => {
    setBatchPreviewRows(prev => [...prev, { id: Date.now() + Math.random(), role: '', name: '' }]);
};
const insertBlankBatchPreviewRowAfter = (afterId) => {
    setBatchPreviewRows(prev => {
        const idx = prev.findIndex(r => r.id === afterId);
        const newRow = { id: Date.now() + Math.random(), role: '', name: '' };
        if (idx === -1) return [...prev, newRow];
        const copy = [...prev];
        copy.splice(idx + 1, 0, newRow);
        return copy;
    });
};
const swapBatchColumns = () => {
    // Inverts Role/Name on the CURRENT rows — including anything already hand-
    // edited in the table — rather than re-parsing the raw paste. Re-parsing
    // would silently discard any corrections made in the preview.
    setBatchPreviewRows(prev => prev.map(r => ({ ...r, role: r.name, name: r.role })));
};

// Shared cleanup after a batch add commits (or after the user cancels a replace
// confirmation) — resets the modal back to its default state.
const resetBatchAddModal = () => {
    setShowBatchAddModal(false);
    setBatchAddText('');
    setBatchPreviewRows([]);
    setBatchCardPreviewCards([]);
    setBatchInsertTargetId('end');
    setBatchInsertMode('new-section');
    setReverseBatchOrder(false);
};

const handleBatchAddCommit = () => {
    if (batchAddType === 'main-title') {
        if (batchCardPreviewCards.length === 0) {
            setShowBatchAddModal(false);
            return;
        }
        // IDs must be unique across BOTH lists, not just within cardCredits — the shift-select
        // Set stores raw ids with no "which list" tag, so a scroll credit and a title card
        // sharing a numeric id would both light up as selected together. Taking the max across
        // credits + cardCredits combined (not just cardCredits) is what prevents that collision.
        const lastCardId = Math.max(
            0,
            ...cardCredits.map(c => typeof c.id === 'number' ? c.id : 0),
            ...credits.map(c => typeof c.id === 'number' ? c.id : 0)
        );
        const newCards = classifyBatchCardsToTitleCredits(batchCardPreviewCards, lastCardId, settings.cardFontFamily);

        if (batchInsertTargetId === 'replace' && cardCredits.length > 0) {
            // Destructive — this wipes every existing title card, so it needs an explicit
            // confirmation, same as Clear All. The modal stays open underneath until confirmed
            // (or the user backs out via Cancel) so nothing is lost by an accidental click.
            showMessageBox(
                "Replace All Title Cards?",
                `This will delete your current ${cardCredits.length} title card${cardCredits.length === 1 ? '' : 's'} and replace them with these ${newCards.length} new one${newCards.length === 1 ? '' : 's'}. This can't be undone.`,
                "warning",
                false,
                () => {
                    setCardCredits(newCards);
                    // Clear any selection pointing at a card that just got deleted — otherwise
                    // the live preview keeps trying to show the old activeCardId, finds nothing,
                    // and renders blank instead of falling back to the new cards.
                    setActiveCardId(null);
                    setActiveCreditId(null);
                    setSelectedCreditIds(new Set());
                    rewindAnimation();
                    resetBatchAddModal();
                }
            );
            return;
        }

        setCardCredits(prev => [...prev, ...newCards]);
        resetBatchAddModal();
        return;
    }
    if (batchPreviewRows.length === 0) {
        setShowBatchAddModal(false);
        return;
    }
    // Same reasoning as the card branch above — must consider cardCredits' ids too, not
    // just credits, or a new scroll credit can collide with an existing title card's id.
    const lastCreditId = Math.max(
        0,
        ...credits.map(c => typeof c.id === 'number' ? c.id : 0),
        ...cardCredits.map(c => typeof c.id === 'number' ? c.id : 0)
    );
    const rowTypes = getBatchRowTypes(batchPreviewRows);
    const newCredits = classifyBatchRowsToScrollCredits(batchPreviewRows, rowTypes, lastCreditId, settings.blockNameColor);

    if (batchInsertTargetId === 'replace' && credits.length > 0) {
        // Same destructive-action guard as the title card branch above.
        showMessageBox(
            "Replace All Credits?",
            `This will delete your current ${credits.length} credit${credits.length === 1 ? '' : 's'} and replace them with these ${newCredits.length} new one${newCredits.length === 1 ? '' : 's'}. This can't be undone.`,
            "warning",
            false,
            () => {
                setCredits(newCredits);
                // Same reasoning as the title-card branch — clear any selection left
                // pointing at a scroll credit that no longer exists post-replace.
                setActiveCreditId(null);
                setActiveCardId(null);
                setSelectedCreditIds(new Set());
                rewindAnimation();
                resetBatchAddModal();
            }
        );
        return;
    }

    if (batchInsertTargetId === 'end' || batchInsertTargetId === 'replace' || newCredits.length === 0) {
        setCredits(prev => [...prev, ...newCredits]);
    } else {
        setCredits(prev => {
            const targetIdx = prev.findIndex(c => String(c.id) === String(batchInsertTargetId));
            if (targetIdx === -1) return [...prev, ...newCredits]; // target vanished somehow — fall back to append rather than lose the batch
            const targetCredit = prev[targetIdx];

            if (batchInsertMode === 'merge' && targetCredit.type === 'multiple') {
                // Fold the new batch's names into the existing group credit instead of
                // creating a second, adjacent group — this is what actually reunites a
                // "Special Thanks" list that got split across two separate pastes.
                const mergedNames = newCredits
                    .map(c => c.name || c.role)
                    .filter(Boolean);
                const updatedTarget = { ...targetCredit, name: [targetCredit.name, ...mergedNames].filter(Boolean).join('\n') };
                const copy = [...prev];
                copy[targetIdx] = updatedTarget;
                return copy;
            }

            const copy = [...prev];
            copy.splice(targetIdx + 1, 0, ...newCredits);
            return copy;
        });
    }

    resetBatchAddModal();
};

const handleBatchAdd = () => {
    if (!batchAddText.trim()) { 
        setShowBatchAddModal(false); 
        return; 
    }
    
    const lines = batchAddText.trim().split('\n');
    const newCredits = lines.map(line => {
        const cols = line.split('\t'); 
        const newId = Date.now() + Math.random();
        
        if (batchAddType === 'main-title') {
            return {
                id: newId,
                type: 'main-title',
                creditDisplayType: 'card',
                // duration intentionally omitted: falls back to settings.cardDuration (global).
                cardPositionX: 50,
                cardPositionY: 50,
                cardTextAlign: 'center',
                cardVerticalSpacing: 0.5,
                textLayers: [
                    { 
                        id: Math.random(), 
                        content: cols[0] || 'Title', 
                        fontSize: 46, 
                        fontWeight: 'bold', 
                        fontStyle: 'normal', 
                        color: '#FFFFFF', 
                        fontFamily: settings.cardFontFamily || 'poppins',
                        transform: 'normal'
                    },
                    ...(cols[1] ? [{
                        id: Math.random(), 
                        content: cols[1], 
                        fontSize: 28, 
                        fontWeight: 'normal', 
                        fontStyle: 'italic', 
                        color: '#CCCCCC', 
                        fontFamily: settings.cardFontFamily || 'poppins',
                        transform: 'normal'
                    }] : [])
                ]
            };
        }
        
        let credit = { 
            id: newId, 
            type: batchAddType, 
            creditDisplayType: 'scroll', 
            duration: 5,
            blockAlignment: 'center',
            textAlign: 'center'
        };
        
        switch(batchAddType) {
            case 'dual': 
                credit.role = cols[0] || ''; 
                credit.name = cols[1] || ''; 
                break;
            case 'single': 
                credit.name = line; 
                break;
            case 'title': 
                credit.name = line; 
                break;
            default: 
                credit.name = line;
        }
        return credit;
    });
    
    const cardCreditsToAdd = newCredits.filter(c => c.creditDisplayType === 'card');
    const scrollCreditsToAdd = newCredits.filter(c => c.creditDisplayType === 'scroll');
    
    if (cardCreditsToAdd.length > 0) {
        setCardCredits(prev => [...prev, ...cardCreditsToAdd]);
    }
    if (scrollCreditsToAdd.length > 0) {
        setCredits(prev => [...prev, ...scrollCreditsToAdd]);
    }
    
    setShowBatchAddModal(false); 
    setBatchAddText('');
    
    const totalAdded = cardCreditsToAdd.length + scrollCreditsToAdd.length;
    showMessageBox("Success", `${totalAdded} credits were added.`, "success", true);
};
  
  const importFromCSV = (file, mode = 'add') => {
    Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
            if (results.errors.length > 0) {
                console.warn("CSV Parsing warnings:", results.errors);
            }
            
            const rawRows = results.data;
            if (rawRows.length === 0) {
                showMessageBox("Error", "CSV file has no data rows.", "error");
                return;
            }

            // Bulletproofing: make sure the header row actually contains a column we
            // recognize before processing. Without this, a CSV with the wrong header
            // names (or a garbled/binary file that slipped past the extension check)
            // silently produces 0 imported credits, and previously we'd still show a
            // "Success! ... 0 scrolling credits ..." message — which reads as if nothing
            // was wrong. Now we catch it here with an explicit, actionable error.
            const headerFields = (results.meta && results.meta.fields) || [];
            const normalizedHeaders = headerFields.map(h => (h || '').trim().toLowerCase());
            const hasRoleColumn = normalizedHeaders.some(h => ['role', 'character'].includes(h));
            const hasNameColumn = normalizedHeaders.some(h => ['name', 'actor'].includes(h));
            if (!hasRoleColumn && !hasNameColumn) {
                showMessageBox(
                    "No Recognized Columns",
                    `This file doesn't have a "Role" or "Name" column we recognize (found: ${headerFields.filter(Boolean).join(', ') || 'none'}). Make sure the first row has headers like Role/Character and Name/Actor, then try again.`,
                    "error",
                    true
                );
                return;
            }

            const importedScrollCredits = [];
            const importedCardCredits = [];
            // Same collision risk as the batch-paste commit: ids must be unique across BOTH
            // lists, since selection is a single Set of raw ids shared between them. A single
            // shared counter (not two independent ones, even from the same starting number)
            // is required here specifically because each row below can become EITHER a card
            // OR a scroll credit within the same pass — two separate counters would still
            // collide with each other on the very first row of each kind.
            let lastId = Math.max(
                0,
                ...credits.map(c => typeof c.id === 'number' ? c.id : 0),
                ...cardCredits.map(c => typeof c.id === 'number' ? c.id : 0)
            );

            const baseCredit = {
                // No per-credit style seeds — see classifyBatchRowsToScrollCredits.
                language: 'en', multipleNamesColumnCount: 1,
                blockAlignment: 'center'
                // duration intentionally omitted: falls back to settings.cardDuration (global) for cards.
            };

            for (let i = 0; i < rawRows.length; i++) {
    const row = rawRows[i];
    const role = (row['Role'] || row['role'] || row['Character'] || '').trim();
    const name = (row['Name'] || row['name'] || row['Actor'] || '').trim();
    const displayType = (row['DisplayType'] || row['displaytype'] || row['Type'] || '').trim().toLowerCase();
    
    if (!role && !name) continue;

    const shouldBeCard = displayType === 'card' || convertImportedToCards;

        if (shouldBeCard) {
            let cardName = name || role; 
            let cardRole = role && name ? role : 'Subtitle';

            importedCardCredits.push({
                ...baseCredit,
                id: ++lastId,
                type: 'main-title',
                textLayers: [
                    // Layer 1: The Role (on top), smaller and italicized
                    { 
                        id: Math.random(), 
                        content: cardRole, 
                        fontSize: (settings.cardFontSize || 48) * 0.7, 
                        fontStyle: 'italic', 
                        color: '#CCCCCC' 
                    },
                    // Layer 2: The Name (on bottom), larger and bold
                    { 
                        id: Math.random(), 
                        content: cardName, 
                        fontSize: settings.cardFontSize || 48, 
                        fontWeight: 'bold', 
                        color: '#FFFFFF' 
                    }
                ],
                creditDisplayType: 'card',
                originalId: null
            });
        } else {
                    // Standard Scroll Logic
                    if (role && name) {
                        importedScrollCredits.push({ ...baseCredit, id: ++lastId, type: 'dual', role: role, name: name, creditDisplayType: 'scroll' });
                    } else if (role && !name) {
                        // Lookahead for multiple names under one role (e.g., Stunts)
                        let names = [];
                        let lookahead = i + 1;
                        while (lookahead < rawRows.length) {
                            const nextRow = rawRows[lookahead];
                            const nextRole = (nextRow['Role'] || nextRow['role'] || nextRow['Character'] || '').trim();
                            const nextName = (nextRow['Name'] || nextRow['name'] || nextRow['Actor'] || '').trim();
                            
                            if (!nextRole && nextName) {
                                names.push(nextName);
                                lookahead++;
                            } else {
                                break;
                            }
                        }

                        if (names.length > 0) {
                            importedScrollCredits.push({ ...baseCredit, id: ++lastId, type: 'multiple', role: role, name: names.join('\n'), creditDisplayType: 'scroll' });
                            i = lookahead - 1; // Skip the rows we just consumed into the block
                        } else {
                            importedScrollCredits.push({ ...baseCredit, id: ++lastId, type: 'title', name: role, role: '', creditDisplayType: 'scroll' });
                        }
                    } else if (name && !role) {
                        importedScrollCredits.push({ ...baseCredit, id: ++lastId, type: 'single', name: name, role: '', creditDisplayType: 'scroll' });
                    }
                }
            }

            // Bulletproofing: "Add to Existing Credits" is meant to merge — bring in the
            // new people from an updated sheet without duplicating everyone who was
            // already there. Previously this just appended every row unconditionally, so
            // re-importing a sheet that mostly overlaps with the current project would
            // double up existing names instead of only adding the new ones.
            let scrollCreditsToCommit = importedScrollCredits;
            let cardCreditsToCommit = importedCardCredits;
            let duplicateScrollCount = 0;
            let duplicateCardCount = 0;

            const normalizeForCompare = (s) => (s || '').trim().toLowerCase();
            if (mode === 'add') {
                const existingScrollKeys = new Set(
                    credits.map(c => `${normalizeForCompare(c.role)}|${normalizeForCompare(c.name)}`)
                );
                scrollCreditsToCommit = importedScrollCredits.filter(c => {
                    const key = `${normalizeForCompare(c.role)}|${normalizeForCompare(c.name)}`;
                    if (existingScrollKeys.has(key)) { duplicateScrollCount++; return false; }
                    existingScrollKeys.add(key); // guard against duplicates within the same import too
                    return true;
                });

                const existingCardKeys = new Set(
                    cardCredits.map(c => (c.textLayers || []).map(l => normalizeForCompare(l.content)).join('|'))
                );
                cardCreditsToCommit = importedCardCredits.filter(c => {
                    const key = (c.textLayers || []).map(l => normalizeForCompare(l.content)).join('|');
                    if (existingCardKeys.has(key)) { duplicateCardCount++; return false; }
                    existingCardKeys.add(key);
                    return true;
                });
            }

            // Guard the single-type replace modes: if the file didn't actually produce
            // any rows of the type you're trying to replace, don't wipe out what's
            // there — that would silently blank your cards (or scroll credits) for no
            // reason, the same footgun we're fixing elsewhere in this flow.
            if (mode === 'replace-scroll' && importedScrollCredits.length === 0) {
                showMessageBox(
                    "Nothing to Replace With",
                    "This file didn't produce any scrolling credits, so your existing scrolling credits were left as-is (they were not cleared).",
                    "error",
                    true
                );
                setShowImportOptionsModal(false);
                setShowImportModal(false);
                setPendingCsvFile(null);
                return;
            }
            if (mode === 'replace-cards' && importedCardCredits.length === 0) {
                showMessageBox(
                    "Nothing to Replace With",
                    "This file didn't produce any title cards (check \"Convert imported credits to Title Cards\" if that's what you meant), so your existing title cards were left as-is (they were not cleared).",
                    "error",
                    true
                );
                setShowImportOptionsModal(false);
                setShowImportModal(false);
                setPendingCsvFile(null);
                return;
            }

            if (mode === 'replace-all') {
                setCredits(importedScrollCredits);
                setCardCredits(importedCardCredits);
                setActiveCreditId(null);
                setActiveCardId(null);
                setSelectedCreditIds(new Set());
            } else if (mode === 'replace-scroll') {
                setCredits(importedScrollCredits);
                setActiveCreditId(null);
                setSelectedCreditIds(new Set());
                // Cards are intentionally left untouched.
            } else if (mode === 'replace-cards') {
                setCardCredits(importedCardCredits);
                setActiveCardId(null);
                setSelectedCreditIds(new Set());
                // Scrolling credits are intentionally left untouched.
            } else {
                setCredits(prev => [...prev, ...scrollCreditsToCommit]);
                setCardCredits(prevCards => [...prevCards, ...cardCreditsToCommit]);
            }

            const isReplaceMode = mode === 'replace-all' || mode === 'replace-scroll' || mode === 'replace-cards';

            setShowImportOptionsModal(false);
            setShowImportModal(false);
            setPendingCsvFile(null);

            const committedScrollCount = isReplaceMode ? importedScrollCredits.length : scrollCreditsToCommit.length;
            const committedCardCount = isReplaceMode ? importedCardCredits.length : cardCreditsToCommit.length;
            const totalCommitted = committedScrollCount + committedCardCount;
            const totalDuplicates = duplicateScrollCount + duplicateCardCount;

            if (totalCommitted === 0 && importedScrollCredits.length + importedCardCredits.length === 0) {
                showMessageBox(
                    "Nothing Imported",
                    "The file had recognizable headers, but no rows contained a Role or Name value, so nothing was imported. Double-check the rows below the header actually have data in those columns.",
                    "error",
                    true
                );
                return;
            }
            if (totalCommitted === 0 && totalDuplicates > 0) {
                showMessageBox(
                    "No New Credits",
                    `Everyone in this file (${totalDuplicates}) already matches an existing credit by Role + Name, so nothing new was added. If someone's info changed, edit them directly, or use "Replace All" to overwrite everything.`,
                    "info",
                    true
                );
                return;
            }

            const duplicateNote = totalDuplicates > 0 ? ` (${totalDuplicates} already existed and were skipped)` : '';
            showMessageBox("Success", `Successfully imported ${committedScrollCount} scrolling credits and ${committedCardCount} title cards!${duplicateNote}`, "success");
        },
        error: (err) => {
            console.error('Error parsing CSV:', err);
            showMessageBox("Error", `Error parsing CSV file: ${err.message}.`, "error");
            setPendingCsvFile(null);
        }
    });
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    const inputEl = event.target;
    if (!file) return;
    if (!(file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv'))) {
        showMessageBox("Error", "Please select a CSV file (.csv extension).", "error");
        inputEl.value = '';
        return;
    }
    // Bulletproofing: a .csv extension doesn't guarantee the file is actually CSV text.
    // Apps like Numbers/Excel will happily save a spreadsheet as "something.csv" while
    // the file itself is still a zip-based binary package (Numbers docs, .xlsx, etc.),
    // which starts with the "PK" zip signature. Papa.parse will "succeed" on that binary
    // data (finding no real rows) and the import silently reports 0 credits added instead
    // of telling the user their file isn't real CSV. Catch that here, before we even open
    // the import modal.
    const reader = new FileReader();
    reader.onload = (e) => {
        const bytes = new Uint8Array(e.target.result);
        const isZipSignature = bytes.length >= 4 && bytes[0] === 0x50 && bytes[1] === 0x4B && (bytes[2] === 0x03 || bytes[2] === 0x05 || bytes[2] === 0x07);
        if (isZipSignature) {
            showMessageBox(
                "Not a CSV File",
                "This file is named .csv but its contents are actually a spreadsheet package (like a Numbers or Excel document), not plain CSV text. Open it in Numbers/Excel/Google Sheets and use File > Export/Download As > CSV, then try importing that file.",
                "error",
                true
            );
            inputEl.value = '';
            return;
        }
        setPendingCsvFile(file);
        setShowImportOptionsModal(true);
        inputEl.value = '';
    };
    reader.onerror = () => {
        showMessageBox("Error", "Couldn't read the selected file. Please try again.", "error");
        inputEl.value = '';
    };
    reader.readAsArrayBuffer(file.slice(0, 8));
  };
  
const prepareScrollAnimation = () => {
    if (!scrollRef.current) return;
    
    setIsPreparing(true);
    setPlaybackStatusText('Preparing...');

    const startPos = settings.scrollDirection === 'down' 
        ? 0 
        : (scrollRef.current.scrollHeight - scrollRef.current.clientHeight);
    
    // Smoothly scroll to the starting position
    scrollRef.current.scrollTo({ top: startPos, behavior: 'smooth' });

    // After the pre-roll scroll animation, start the main playback.
    setTimeout(() => {
        setIsPreparing(false);
        setPlaybackState('scroll');
        setIsPlaying(true);
    }, 700); // 700ms provides a comfortable pre-roll duration
};

const startCardSequence = (isResuming = false) => {
    setPlaybackState('cards');
    setIsPlaying(true);
    // If we are starting fresh, begin at card 0. If resuming, it will use the currentCardIndex.
    if (!isResuming) {
        setCurrentCardIndex(0);
    }
};

const togglePlayPause = useCallback(() => {
    if (isPlaying) {
        setIsPlaying(false);
        setPlaybackStatusText('Paused');
        return;
    }

    // --- START: UNIFIED RESUME LOGIC ---
    // Check if we are resuming from a paused state (for either cards or scroll).
    if (playbackState === 'scroll' || playbackState === 'cards') {
        // For both animation types, resuming is as simple as flipping the isPlaying flag.
        // The main animation useEffect will handle picking up from the correct state.
        setIsPlaying(true);
        return;
    }
    // --- END: UNIFIED RESUME LOGIC ---
    
    // If starting fresh (playbackState is 'idle'), determine the starting point.
    const hasCards = settings.cardsEnabled && cardCredits.length > 0;
    const hasScroll = credits.length > 0;
    const sequence = settings.cardSequence;

    if ((sequence === 'before' || sequence === 'cards-only') && hasCards) {
        startCardSequence(false);
    } else if ((sequence === 'after' || sequence === 'scroll-only' || (sequence === 'before' && !hasCards)) && hasScroll) {
        prepareScrollAnimation();
    }
}, [isPlaying, settings, credits, cardCredits, playbackState]);

  useEffect(() => { 
  const handleKeyDown = (e) => { 
    const isEditing = ['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName) || e.target.isContentEditable; 
    if (isEditing) return; 
    // Don't hijack keystrokes (copy, select-all, delete, arrows, etc.) while
    // the user is interacting with the AI Copilot panel — those should
    // behave like normal text selection/copy, not trigger editor shortcuts.
    if (e.target.closest && e.target.closest('[data-assistant-panel]')) return;
    
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0; 
    const ctrlCmd = isMac ? e.metaKey : e.ctrlKey; 

    // --- UNDO ---
      if (ctrlCmd && !e.shiftKey && e.code === 'KeyZ') {
        e.preventDefault();
        undo();
      }
      
      // --- REDO ---
      else if ((ctrlCmd && e.shiftKey && e.code === 'KeyZ') || (ctrlCmd && e.code === 'KeyY')) {
        e.preventDefault();
        redo();
      }
    
    // --- BATCH ADD (paste credit list) ---
    if (ctrlCmd && e.shiftKey && e.code === 'KeyB') {
      e.preventDefault();
      setShowBatchAddModal(true);
      return;
    }

    if (e.code === 'Space') { 
      e.preventDefault(); 
      togglePlayPause(); 
    } 
    
    else if (ctrlCmd && e.code === 'KeyD') {
      e.preventDefault();
      if (inspectorActiveCredit) {
        duplicateCredit(inspectorActiveCredit.id, inspectorActiveCredit.creditDisplayType === 'card');
      } else if (selectedCreditIds.size === 1) {
        const selectedId = Array.from(selectedCreditIds)[0];
        const isCard = cardCredits.some(c => c.id === selectedId);
        duplicateCredit(selectedId, isCard);
      }
    }
    
    else if (ctrlCmd && e.code === 'KeyC') { 
      e.preventDefault(); 
      if (inspectorActiveCredit) { 
        copyCredit(inspectorActiveCredit.id, inspectorActiveCredit.creditDisplayType === 'card');
      } else if (selectedCreditIds.size === 1) {
        const selectedId = Array.from(selectedCreditIds)[0];
        const isCard = cardCredits.some(c => c.id === selectedId);
        copyCredit(selectedId, isCard);
      }
    } 
    
    else if (ctrlCmd && e.shiftKey && e.code === 'KeyV') { 
      e.preventDefault(); 
      pasteCardStyle(); 
    } 
    
    else if (ctrlCmd && e.code === 'KeyV') { 
      e.preventDefault(); 
      if (copiedSettings && selectedCreditIds.size > 0) {
        pasteSettings();
      } else {
        pasteCredit();
      }
    }
    
    else if (ctrlCmd && e.code === 'KeyA') {
      e.preventDefault();
      const allIds = new Set([
        ...credits.map(c => c.id),
        ...cardCredits.map(c => c.id)
      ]);
      setSelectedCreditIds(allIds);
      if (allIds.size > 0) {
        showMessageBox("Selected All", `${allIds.size} credits selected.`, "info", true);
      }
    }
    
    else if (ctrlCmd && !e.shiftKey && e.code === 'KeyZ') {
      e.preventDefault();
      undo();
    }
    
    else if ((ctrlCmd && e.shiftKey && e.code === 'KeyZ') || (ctrlCmd && e.code === 'KeyY')) {
      e.preventDefault();
      redo();
    }
    
    else if (e.code === 'Escape') {
      e.preventDefault();
      setSelectedCreditIds(new Set());
      setActiveCreditId(null);
      setActiveCardId(null);
      setIsPlaying(false);
    }
    
    else if (e.code === 'Delete' || e.code === 'Backspace') {
    if (selectedCreditIds.size > 0) { 
        e.preventDefault(); 
        bulkDeleteSelectedCredits(); 
      } 
    }
    
    else if (['ArrowUp', 'ArrowDown'].includes(e.code)) {
      e.preventDefault();
      const allCredits = [...credits, ...cardCredits];
      if (allCredits.length === 0) return;
      
      let currentIndex = -1;
      if (inspectorActiveCredit) {
        currentIndex = allCredits.findIndex(c => c.id === inspectorActiveCredit.id);
      }
      
      const direction = e.code === 'ArrowUp' ? -1 : 1;
      const newIndex = currentIndex + direction;
      
      if (newIndex >= 0 && newIndex < allCredits.length) {
        const newCredit = allCredits[newIndex];
        const isCard = newCredit.creditDisplayType === 'card';
        
        if (isCard) {
          setActiveCardId(newCredit.id);
          setActiveCreditId(null);
        } else {
          setActiveCreditId(newCredit.id);
          setActiveCardId(null);
        }
        setSelectedCreditIds(new Set([newCredit.id]));
        
        setTimeout(() => {
          const listElementId = isCard ? `card-item-${newCredit.id}` : `credit-item-${newCredit.id}`;
          const listElement = document.getElementById(listElementId);
          if (listElement) {
            listElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }
    }
    
    else if (e.code === 'Enter') {
      if (inspectorActiveCredit && rightPanelVisible) {
        e.preventDefault();
        setTimeout(() => {
          const inspector = document.querySelector('.inspector-panel');
          const firstInput = inspector?.querySelector('textarea, input[type="text"]');
          if (firstInput) {
            firstInput.focus();
            firstInput.select();
          }
        }, 100);
      }
    }
  }; 
  
  window.addEventListener('keydown', handleKeyDown); 
  return () => window.removeEventListener('keydown', handleKeyDown); 
}, [
  togglePlayPause, inspectorActiveCredit, copyCredit, pasteCredit, pasteCardStyle, pasteSettings,
  selectedCreditIds, bulkDeleteSelectedCredits, duplicateCredit, undo, redo, credits, cardCredits,
  setSelectedCreditIds, setActiveCreditId, setActiveCardId, setIsPlaying, showMessageBox,
  rightPanelVisible, copiedSettings
]);
 
const runCardSequence = useCallback((index) => {
    if (!isPlaying || isEditingContent || index >= cardCredits.length) {
        return;
    }

    const card = cardCredits[index];
    const cardDisplayDuration = (card.duration || settings.cardDuration || 5) * 1000;
    const animationSpeed = (settings.cardAnimationDuration || 1) * 1000;
    const fadeOutSpeed = (settings.fadeOutDuration || 1) * 1000;
    const intermission = (settings.cardIntermissionDuration || 0) * 1000;

    let inClass = 'animate-base ';
    let outClass = 'animate-base ';

    switch (settings.cardAnimationType) {
        case 'slide-in':
            inClass += 'animate-slide-in';
            outClass += 'animate-slide-out';
            break;
        case 'slide-up':
            inClass += 'animate-slide-up';
            outClass += 'animate-slide-up-out';
            break;
        case 'scale-in':
            inClass += 'animate-scale-in';
            outClass += 'animate-scale-out';
            break;
        case 'fade':
        default:
            inClass += 'animate-fade-in';
            outClass += 'animate-fade-out';
            break;
    }

    setCurrentCardAnimationClass(inClass);

    const holdDuration = Math.max(0, cardDisplayDuration - animationSpeed);
    if (cardTimeoutRef.current) clearTimeout(cardTimeoutRef.current);

    cardTimeoutRef.current = setTimeout(() => {
        if (!isPlaying) return;
        
        setCurrentCardAnimationClass(outClass);
        
        cardTimeoutRef.current = setTimeout(() => {
            if (!isPlaying) return;
            // FIX: Reset the animation class first to prepare for the next card
            setCurrentCardAnimationClass(''); 
            // Now, advance to the next card.
            setCurrentCardIndex(prevIndex => prevIndex + 1);
        }, fadeOutSpeed + intermission);

    }, animationSpeed + holdDuration);
}, [isPlaying, isEditingContent, cardCredits, settings]);

  const calculateAutoSpeed = useCallback(() => {
      if (scrollRef.current && settings.speedMode === 'auto') {
          const scrollHeight = scrollRef.current.scrollHeight;
          const containerHeight = scrollRef.current.clientHeight;
          const scrollableDistance = scrollHeight - containerHeight;
          if (scrollableDistance <= 0) return 0;
          const totalTravelDistance = scrollableDistance + containerHeight;
          const projectFps = parseFloat(settings.fps) || 24;
          // Preview at the same (continuous) speed core-renderer will actually use for
          // this targetDuration/lineCount, so the in-editor preview and the export
          // agree on both duration and pace.
          const { cssPixelsPerSecond } = predictLockedRender(totalTravelDistance, settings.targetDuration, projectFps, credits.length);
          return cssPixelsPerSecond;
      }
      return settings.speed;
  }, [settings.speedMode, settings.targetDuration, settings.speed, settings.fps, credits.length]);
  const getCurrentSpeed = useCallback(() => { return settings.speedMode === 'auto' ? calculateAutoSpeed() : settings.speed; }, [settings.speedMode, settings.speed, calculateAutoSpeed]);
  const saveProjectFile = () => { const projectExport = { credits, settings, cardCredits, version: '1.3', created: new Date().toISOString() }; const blob = new Blob([JSON.stringify(projectExport, null, 2)], { type: 'application/json' }); const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = 'movie-credits-project.json'; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url); showMessageBox("Success", "Project saved to your device!", "success", true); };
  const loadProjectFile = (event) => { const file = event.target.files[0]; if (file) { const reader = new FileReader(); reader.onload = (e) => { try { const projectData = JSON.parse(e.target.result); const loadedCredits = (projectData.credits || []).map(c => ({ ...c, fontFamily: c.fontFamily || 'default', blockNameColor: c.blockNameColor || '#ffffff', creditDisplayType: c.creditDisplayType || 'scroll' })); const loadedCardCredits = (projectData.cardCredits || []).map(c => ({ ...c, id: c.id || Math.random(), creditDisplayType: 'card', fontFamily: c.fontFamily || 'default', blockNameColor: c.blockNameColor || '#ffffff' })); setCredits(loadedCredits); setCardCredits(loadedCardCredits); setSettings(s => ({...s, ...(projectData.settings || {})})); showMessageBox("Success", "Project loaded successfully!", "success"); } catch (error) { showMessageBox("Error", `Error loading project file: ${error.message}.`, "error"); } }; reader.readAsText(file); } event.target.value = ''; };
  const handleCreditDragStart = (e, creditId) => { setDraggedCreditId(creditId); if (!selectedCreditIds.has(creditId)) { setSelectedCreditIds(new Set([creditId])); setLastSelectedId(creditId); } if (e.dataTransfer) { e.dataTransfer.effectAllowed = 'move'; } };
  const handleCreditDragOver = (e, creditId) => { e.preventDefault(); if (creditId !== dragOverCreditId) { setDragOverCreditId(creditId); } };
  const handleCreditDrop = (e, dropTargetId) => {
    e.preventDefault(); if (!draggedCreditId) return;
    const movingIds = selectedCreditIds;
    if (movingIds.size === 0 || movingIds.has(dropTargetId)) { setDraggedCreditId(null); setDragOverCreditId(null); return; }
    let reordered = [...credits]; const movingItems = reordered.filter(c => movingIds.has(c.id));
    reordered = reordered.filter(c => !movingIds.has(c.id)); const dropIndex = reordered.findIndex(c => c.id === dropTargetId);
    if (dropIndex === -1) { reordered.push(...movingItems); }
    else { reordered.splice(dropIndex, 0, ...movingItems); }
    setCredits(reordered); setDraggedCreditId(null); setDragOverCreditId(null);
  };
  const handleCardDragStart = (e, cardId) => {
    setDraggedCardItem(cardId);
    // If dragging a card that isn't selected, select just it
    if (!selectedCreditIds.has(cardId)) {
      setSelectedCreditIds(new Set([cardId]));
      setLastSelectedId(cardId);
    }
    if (e.dataTransfer) { e.dataTransfer.effectAllowed = 'move'; }
  };
  const handleCardDragOver = (e, cardId) => { e.preventDefault(); if (cardId !== dragOverCardItem) setDragOverCardItem(cardId); };
  const handleCardDrop = (e, dropTargetId) => {
    e.preventDefault();
    if (!draggedCardItem) return;
    const movingIds = selectedCreditIds.size > 0 ? selectedCreditIds : new Set([draggedCardItem]);
    if (movingIds.has(dropTargetId)) { setDraggedCardItem(null); setDragOverCardItem(null); return; }
    const movingItems = cardCredits.filter(c => movingIds.has(c.id));
    let reordered = cardCredits.filter(c => !movingIds.has(c.id));
    const dropIndex = reordered.findIndex(c => c.id === dropTargetId);
    if (dropIndex === -1) { reordered.push(...movingItems); }
    else { reordered.splice(dropIndex, 0, ...movingItems); }
    setCardCredits(reordered);
    setDraggedCardItem(null);
    setDragOverCardItem(null);
  };
  const uploadFileToStorage = async (file) => { if (!storage || !userId || !currentProjectId) { showMessageBox("Error", "Storage not ready. Please try again.", "error"); throw new Error("Storage not configured"); } const currentAppId = __app_id; const filePath = `artifacts/${currentAppId}/users/${userId}/projects/${currentProjectId}/logos/${Date.now()}-${file.name}`; const storageRef = ref(storage, filePath); const snapshot = await uploadBytes(storageRef, file); const downloadURL = await getDownloadURL(snapshot.ref); return { downloadURL, filePath }; };
  const processLogoUpload = (file, updateFunction) => {
    const uploadId = Date.now() + file.name; setPendingUploads(prev => new Set(prev).add(uploadId));
    const reader = new FileReader();
    reader.onload = (e) => {
        updateFunction(e.target.result, null);
        uploadFileToStorage(file).then(({ downloadURL, filePath }) => { updateFunction(downloadURL, filePath); }).catch(error => { console.error("Logo upload error:", error); showMessageBox("Error", `Logo upload failed: ${error.message}`, "error"); updateFunction('', null); }).finally(() => { setPendingUploads(prev => { const newSet = new Set(prev); newSet.delete(uploadId); return newSet; }); });
    };
    reader.readAsDataURL(file);
  };
  const handleLogoUpload = (creditId, event) => {
    const file = event.target.files[0]; if (!file || !file.type.startsWith('image/')) return;
    const updateFunction = (url, path) => { setCredits(prev => prev.map(c => { if (c.id === creditId) { const updated = { ...c, logoUrl: url }; if (path !== null) updated.storagePath = path; return updated; } return c; })); };
    processLogoUpload(file, updateFunction);
    if (autoSaveToLibrary) uploadAssetToLibrary(file, 'logo');
  };
  const handleGridLogoUpload = (creditId, logoId, event) => {
    const file = event.target.files[0]; if (!file || !file.type.startsWith('image/')) return;
    const updateFunction = (url, path) => { setCredits(prev => prev.map(c => { if (c.id === creditId) { const updatedLogos = c.logos.map(logo => { if (logo.id === logoId) { const updatedLogo = { ...logo, logoUrl: url }; if (path !== null) updatedLogo.storagePath = path; return updatedLogo; } return logo; }); return { ...c, logos: updatedLogos }; } return c; })); };
    processLogoUpload(file, updateFunction);
    if (autoSaveToLibrary) uploadAssetToLibrary(file, 'logo');
  };
  const batchLogoUpload = (event, creditId) => {
    const files = Array.from(event.target.files).filter(f => f.type.startsWith('image/')); if (files.length === 0) return;
    files.forEach(file => {
        const newLogoId = Date.now() + Math.random(); const uploadId = newLogoId + file.name;
        setPendingUploads(prev => new Set(prev).add(uploadId));
        const reader = new FileReader();
        reader.onload = (e) => {
            const newLogo = { id: newLogoId, logoUrl: e.target.result, storagePath: null };
            setCredits(prev => prev.map(c => c.id === creditId ? { ...c, logos: [...(c.logos || []), newLogo] } : c ));
            uploadFileToStorage(file).then(({ downloadURL, filePath }) => {
                setCredits(prev => prev.map(c => { if (c.id === creditId) { const updatedLogos = c.logos.map(logo => logo.id === newLogoId ? { ...logo, logoUrl: downloadURL, storagePath: filePath } : logo ); return { ...c, logos: updatedLogos }; } return c; }));
            }).catch(error => { console.error("Batch logo upload error:", error); showMessageBox("Error", `Upload failed for ${file.name}.`, "error"); setCredits(prev => prev.map(c => c.id === creditId ? { ...c, logos: c.logos.filter(l => l.id !== newLogoId) } : c ));
            }).finally(() => { setPendingUploads(prev => { const newSet = new Set(prev); newSet.delete(uploadId); return newSet; }); });
        };
        reader.readAsDataURL(file);
        if (autoSaveToLibrary) uploadAssetToLibrary(file, 'logo');
    });
  };
  const addLogoToGroup = (creditId) => { setCredits(prevCredits => prevCredits.map(c => { if (c.id === creditId && c.type === 'logo' && c.layout === 'grid' && c.logos.length < 9) { const newLogo = { id: Date.now() + Math.random(), logoUrl: '', logoSize: 'medium' }; return { ...c, logos: [...(c.logos || []), newLogo] }; } return c; })); };

  // --- Logo Board: a draft workspace for arranging logos into rows (up to 5 per row)
  // before committing them to the actual credit. Nothing here touches the live credit
  // until commitLogoBoard() is called, so mid-arrangement changes never show up
  // half-finished in the timeline.
  const openLogoBoard = (creditId) => {
    const credit = credits.find(c => c.id === creditId);
    if (!credit) return;
    let initialRows;
    if (Array.isArray(credit.rows) && credit.rows.length > 0) {
        initialRows = JSON.parse(JSON.stringify(credit.rows));
    } else if (credit.layout === 'grid' && Array.isArray(credit.logos) && credit.logos.length > 0) {
        // Migrate a legacy grid (single column count, flat list) into rows of that width.
        const cols = credit.gridColumns || 3;
        const flat = credit.logos.filter(l => !l.isEmpty);
        initialRows = [];
        for (let i = 0; i < flat.length; i += cols) {
            initialRows.push({ id: Date.now() + Math.random(), logos: flat.slice(i, i + cols).map(l => ({ id: l.id || (Date.now() + Math.random()), logoUrl: l.logoUrl || '', size: l.size || 'medium', storagePath: l.storagePath || null })) });
        }
    } else if ((credit.layout === 'single' || !credit.layout) && credit.logoUrl) {
        initialRows = [{ id: Date.now() + Math.random(), logos: [{ id: Date.now() + Math.random(), logoUrl: credit.logoUrl, size: credit.logoSize || 'medium', storagePath: credit.storagePath || null }] }];
    } else {
        initialRows = [{ id: Date.now() + Math.random(), logos: [] }];
    }
    setLogoBoardCreditId(creditId);
    setLogoBoardRows(initialRows);
    setShowLogoBoard(true);
  };

  const closeLogoBoard = () => {
    setShowLogoBoard(false);
    setLogoBoardCreditId(null);
    setLogoBoardRows([]);
    setLibraryPickerTarget(null);
  };

  const addLogoBoardRow = () => {
    setLogoBoardRows(prev => [...prev, { id: Date.now() + Math.random(), logos: [] }]);
  };

  const removeLogoBoardRow = (rowId) => {
    setLogoBoardRows(prev => prev.filter(r => r.id !== rowId));
  };

  const moveLogoBoardRow = (rowId, direction) => {
    setLogoBoardRows(prev => {
        const idx = prev.findIndex(r => r.id === rowId);
        const swapWith = idx + direction;
        if (idx === -1 || swapWith < 0 || swapWith >= prev.length) return prev;
        const next = [...prev];
        [next[idx], next[swapWith]] = [next[swapWith], next[idx]];
        return next;
    });
  };

  const addLogoBoardSlot = (rowId) => {
    setLogoBoardRows(prev => prev.map(r => {
        if (r.id !== rowId) return r;
        if ((r.logos || []).length >= 5) return r; // keep the existing 5-per-row cap
        return { ...r, logos: [...(r.logos || []), { id: Date.now() + Math.random(), logoUrl: '', size: 'medium' }] };
    }));
  };

  // Same as addLogoBoardSlot, but immediately opens the library picker targeted at the
  // new slot — this is what "Add Logo" opens now, since it covers both upload and library.
  const addLogoBoardSlotFromLibrary = (rowId) => {
    const newId = Date.now() + Math.random();
    setLogoBoardRows(prev => prev.map(r => {
        if (r.id !== rowId) return r;
        if ((r.logos || []).length >= 5) return r;
        return { ...r, logos: [...(r.logos || []), { id: newId, logoUrl: '', size: 'medium' }] };
    }));
    setLibraryPickerTarget({ rowId, logoId: newId });
  };

  // Fills whichever slot the library picker is currently targeting with a library asset.
  // Draft-only (writes to logoBoardRows, not the live credit) — same as every other Logo
  // Board edit, nothing is saved until Commit.
  const applyLibraryLogoToBoardSlot = (asset) => {
    if (!libraryPickerTarget) return;
    const { rowId, logoId } = libraryPickerTarget;
    setLogoBoardRows(prev => prev.map(r => r.id === rowId ? { ...r, logos: r.logos.map(l => l.id === logoId ? { ...l, logoUrl: asset.url, storagePath: asset.storagePath } : l) } : r));
    setLibraryPickerTarget(null);
  };

  const removeLogoBoardLogo = (rowId, logoId) => {
    setLogoBoardRows(prev => prev.map(r => r.id === rowId ? { ...r, logos: r.logos.filter(l => l.id !== logoId) } : r));
  };

  const moveLogoBoardLogo = (rowId, logoId, direction) => {
    setLogoBoardRows(prev => prev.map(r => {
        if (r.id !== rowId) return r;
        const idx = r.logos.findIndex(l => l.id === logoId);
        const swapWith = idx + direction;
        if (idx === -1 || swapWith < 0 || swapWith >= r.logos.length) return r;
        const next = [...r.logos];
        [next[idx], next[swapWith]] = [next[swapWith], next[idx]];
        return { ...r, logos: next };
    }));
  };

  const setLogoBoardLogoSize = (rowId, logoId, size) => {
    setLogoBoardRows(prev => prev.map(r => r.id === rowId ? { ...r, logos: r.logos.map(l => l.id === logoId ? { ...l, size } : l) } : r));
  };

  const setLogoBoardRowGap = (rowId, gap) => {
    setLogoBoardRows(prev => prev.map(r => r.id === rowId ? { ...r, gap } : r));
  };

  const uploadLogoBoardFileToSlot = (rowId, logoId, file) => {
    const uploadId = Date.now() + Math.random() + file.name;
    setPendingUploads(prev => new Set(prev).add(uploadId));
    const reader = new FileReader();
    reader.onload = (e) => {
        const previewUrl = e.target.result;
        setLogoBoardRows(prev => prev.map(r => r.id === rowId ? { ...r, logos: r.logos.map(l => l.id === logoId ? { ...l, logoUrl: previewUrl } : l) } : r));
        uploadFileToStorage(file).then(({ downloadURL, filePath }) => {
            setLogoBoardRows(prev => prev.map(r => r.id === rowId ? { ...r, logos: r.logos.map(l => l.id === logoId ? { ...l, logoUrl: downloadURL, storagePath: filePath } : l) } : r));
        }).catch(error => {
            console.error("Logo Board upload error:", error);
            showMessageBox("Error", `Logo upload failed: ${error.message}`, "error");
        }).finally(() => {
            setPendingUploads(prev => { const newSet = new Set(prev); newSet.delete(uploadId); return newSet; });
        });
    };
    reader.readAsDataURL(file);
    if (autoSaveToLibrary) uploadAssetToLibrary(file, 'logo');
  };

  // Some drag sources (notably dragging straight from Finder/Explorer on certain
  // formats) don't reliably set file.type, so a strict `type.startsWith('image/')`
  // check silently drops those files with zero feedback — which is exactly what
  // makes a multi-file drag look like it "lost" logos. Fall back to the extension.
  const isLikelyImageFile = (file) => {
    if (file.type && file.type.startsWith('image/')) return true;
    return /\.(png|jpe?g|gif|webp|svg|bmp|tiff?|heic|heif|avif)$/i.test(file.name || '');
  };

  const uploadLogoBoardImage = (rowId, logoId, event) => {
    const file = event.target.files[0];
    if (!file || !isLikelyImageFile(file)) return;
    uploadLogoBoardFileToSlot(rowId, logoId, file);
  };

  // Drop a batch of files anywhere on the board: fills empty slots first, then the
  // last row up to its 5-logo cap, then keeps creating new rows for whatever's left —
  // so you can just drag a folder of logos in and watch rows build themselves.
  // Plans where each dropped file lands — reusing empty placeholders first, then a
  // preferred row (if one was targeted directly) up to its remaining capacity, then
  // the last row, then new rows as needed. Pure function: takes one snapshot of rows
  // and returns the fully-updated result in one shot, so a single drop never needs
  // more than one read + one write of state (which is what was causing the earlier
  // bug — two separate reads/writes of the same stale snapshot clobbering each other).
  const planLogoPlacements = (baseRows, files, options = {}) => {
    const { preferRowId } = options;
    const rows = baseRows.map(r => ({ ...r, logos: [...r.logos] }));
    const slotAssignments = [];
    const assignedLogoIds = new Set();

    files.forEach(file => {
        let targetRow = null;
        let targetLogo = null;

        // A row that was dropped onto directly gets first claim on its own remaining
        // capacity, before falling back to the general placement rules below.
        if (preferRowId) {
            const preferred = rows.find(r => r.id === preferRowId);
            if (preferred && preferred.logos.length < 5) {
                targetRow = preferred;
                targetLogo = { id: Date.now() + Math.random(), logoUrl: '', size: 'medium' };
                targetRow.logos.push(targetLogo);
            }
        }

        if (!targetLogo) {
            // Reuse an existing empty placeholder not already claimed earlier in this batch.
            targetRow = rows.find(r => r.logos.some(l => !l.logoUrl && !assignedLogoIds.has(l.id)));
            targetLogo = targetRow ? targetRow.logos.find(l => !l.logoUrl && !assignedLogoIds.has(l.id)) : null;
        }

        if (!targetLogo) {
            targetRow = rows.length > 0 ? rows[rows.length - 1] : null;
            if (!targetRow || targetRow.logos.length >= 5) {
                targetRow = { id: Date.now() + Math.random(), logos: [] };
                rows.push(targetRow);
            }
            targetLogo = { id: Date.now() + Math.random(), logoUrl: '', size: 'medium' };
            targetRow.logos.push(targetLogo);
        }

        assignedLogoIds.add(targetLogo.id);
        slotAssignments.push({ rowId: targetRow.id, logoId: targetLogo.id, file });
    });

    return { rows, slotAssignments };
  };

  const addFilesToLogoBoard = (fileList) => {
    const allFiles = Array.from(fileList || []);
    const files = allFiles.filter(isLikelyImageFile);
    if (allFiles.length > files.length) {
        showMessageBox("Some Files Skipped", `${allFiles.length - files.length} of ${allFiles.length} file(s) didn't look like images and were skipped.`, "error", true);
    }
    if (files.length === 0) return;
    const { rows, slotAssignments } = planLogoPlacements(logoBoardRows, files);
    setLogoBoardRows(rows);
    slotAssignments.forEach(({ rowId, logoId, file }) => uploadLogoBoardFileToSlot(rowId, logoId, file));
  };

  // Same idea, but targeted at one specific row: fills that row up to its remaining
  // capacity first, then spills any leftover files into other/new rows — computed in
  // the same single pass as the direct fill, not as a separate follow-up call.
  const addFilesToLogoBoardRow = (targetRowId, fileList) => {
    const allFiles = Array.from(fileList || []);
    const files = allFiles.filter(isLikelyImageFile);
    if (allFiles.length > files.length) {
        showMessageBox("Some Files Skipped", `${allFiles.length - files.length} of ${allFiles.length} file(s) didn't look like images and were skipped.`, "error", true);
    }
    if (files.length === 0) return;
    const { rows, slotAssignments } = planLogoPlacements(logoBoardRows, files, { preferRowId: targetRowId });
    setLogoBoardRows(rows);
    slotAssignments.forEach(({ rowId, logoId, file }) => uploadLogoBoardFileToSlot(rowId, logoId, file));
  };


  // Internal drag-to-reorder: move a logo from wherever it is to a specific row/index,
  // respecting each row's 5-logo cap. No-ops (returns state unchanged) if the target
  // row is already full and isn't the logo's current row.
  const moveLogoBoardLogoTo = (fromRowId, logoId, toRowId, toIndex) => {
    setLogoBoardRows(prev => {
        const rows = prev.map(r => ({ ...r, logos: [...r.logos] }));
        const fromRow = rows.find(r => r.id === fromRowId);
        if (!fromRow) return prev;
        const logoIdx = fromRow.logos.findIndex(l => l.id === logoId);
        if (logoIdx === -1) return prev;
        const toRow = rows.find(r => r.id === toRowId);
        if (!toRow) return prev;
        if (fromRowId !== toRowId && toRow.logos.length >= 5) return prev; // row is full, no-op
        const [logo] = fromRow.logos.splice(logoIdx, 1);
        const insertAt = Math.min(toIndex, toRow.logos.length);
        toRow.logos.splice(insertAt, 0, logo);
        return rows;
    });
  };

  const commitLogoBoard = () => {
    if (!logoBoardCreditId) return;
    // Drop fully-empty rows so a stray "+ Add Row" click doesn't leave blank gaps.
    const cleanedRows = logoBoardRows.filter(r => (r.logos || []).length > 0);
    setCredits(prev => prev.map(c => c.id === logoBoardCreditId ? { ...c, layout: 'rows', rows: cleanedRows } : c));
    closeLogoBoard();
  };

  // --- Song Board -------------------------------------------------------
  // Opens with a draft entries list parsed out of the live credit's `name`
  // field (Title<TAB>Info per line — see parseSongEntries in
  // CreditPreview.js). Nothing writes back to the real credit until
  // commitSongBoard, same contract as the Logo Board above.
  //
  // SONG_SOFT_BREAK must be the exact same character as SOFT_BREAK in
  // CreditPreview.js (U+2028). It lets a song's Info field hold multiple
  // lines — "Written by X", "Performed by Y", "Published by Z" — without
  // those internal line breaks being mistaken for the real '\n' that
  // separates one song from the next in credit.name. EditableCreditText
  // already splits display text on this character and renders <br/>
  // between the pieces, so multi-line Info renders correctly with no
  // changes needed on the rendering side.
  const SONG_SOFT_BREAK = '\u2028';

  const openSongBoard = (creditId) => {
    const credit = credits.find(c => c.id === creditId);
    if (!credit) return;
    const parsed = (credit.name || '').split('\n').filter(line => line !== '').map(line => {
        const idx = line.indexOf('\t');
        const rawTitle = idx === -1 ? line : line.slice(0, idx);
        const rawInfo = idx === -1 ? '' : line.slice(idx + 1);
        return {
            id: Date.now() + Math.random(),
            title: rawTitle,
            // Decode soft breaks back to real newlines so the Info textarea
            // (a normal multi-line field) displays them as separate lines.
            info: rawInfo.split(SONG_SOFT_BREAK).join('\n'),
        };
    });
    setSongBoardCreditId(creditId);
    setSongBoardEntries(parsed.length > 0 ? parsed : [{ id: Date.now() + Math.random(), title: '', info: '' }]);
    setShowSongBoard(true);
  };

  const closeSongBoard = () => {
    setShowSongBoard(false);
    setSongBoardCreditId(null);
    setSongBoardEntries([]);
    setDraggedSongBoardEntryId(null);
  };

  const SONG_BOARD_MAX_ENTRIES = 3;

  const addSongBoardEntry = () => {
    setSongBoardEntries(prev => prev.length >= SONG_BOARD_MAX_ENTRIES ? prev : [...prev, { id: Date.now() + Math.random(), title: '', info: '' }]);
  };

  const removeSongBoardEntry = (entryId) => {
    setSongBoardEntries(prev => prev.filter(e => e.id !== entryId));
  };

  const updateSongBoardEntryField = (entryId, field, value) => {
    setSongBoardEntries(prev => prev.map(e => e.id === entryId ? { ...e, [field]: value } : e));
  };

  // Drag-to-reorder: move the dragged entry to sit at `toIndex` in the list.
  const moveSongBoardEntryTo = (entryId, toIndex) => {
    setSongBoardEntries(prev => {
        const fromIndex = prev.findIndex(e => e.id === entryId);
        if (fromIndex === -1) return prev;
        const next = [...prev];
        const [item] = next.splice(fromIndex, 1);
        next.splice(Math.min(toIndex, next.length), 0, item);
        return next;
    });
  };

  const commitSongBoard = () => {
    if (!songBoardCreditId) return;
    // Title stays single-line (it's a title): strip tabs and collapse any
    // newlines to a space. Info keeps real newlines the user typed, just
    // re-encoded as SONG_SOFT_BREAK so they survive storage as part of
    // THIS song's entry instead of being read as "start a new song".
    const sanitizeTitle = (s) => (s || '').replace(/\t/g, ' ').replace(/\r?\n/g, ' ').trim();
    const sanitizeInfo = (s) => (s || '')
        .replace(/\t/g, ' ')
        .replace(/\r/g, '')
        .trim()
        .split('\n')
        .join(SONG_SOFT_BREAK);
    const cleaned = songBoardEntries
        .map(e => ({ title: sanitizeTitle(e.title), info: sanitizeInfo(e.info) }))
        .filter(e => e.title || e.info);
    const serialized = cleaned.map(e => `${e.title}\t${e.info}`).join('\n');
    setCredits(prev => prev.map(c => c.id === songBoardCreditId ? { ...c, multipleMode: 'song', name: serialized } : c));
    closeSongBoard();
  };

  const addSpacerToGroup = (creditId) => {
    setCredits(prevCredits =>
      prevCredits.map(c => {
        if (c.id === creditId && c.type === 'logo' && c.layout === 'grid') {
          const newSpacer = { id: Date.now() + Math.random(), isEmpty: true };
          return { ...c, logos: [...(c.logos || []), newSpacer] };
        }
        return c;
      })
    );
  };
  
  const removeLogoFromGrid = (creditId, logoIdToRemove) => { setCredits(prevCredits => prevCredits.map(c => { if (c.id === creditId && c.type === 'logo' && c.layout === 'grid') { const updatedLogos = c.logos.filter(logo => logo.id !== logoIdToRemove); return { ...c, logos: updatedLogos }; } return c; }) ); };
  const addTextItemToGroup = (creditId) => { setCredits(prevCredits => prevCredits.map(c => { if (c.id === creditId && c.type === 'text-group') { const newTextItem = { id: Date.now(), content: 'New Text Item', language: c.language || 'en' }; const updatedTextItems = c.textItems ? [...c.textItems, newTextItem] : [newTextItem]; return { ...c, textItems: updatedTextItems }; } return c; })); };
  const removeTextItemFromGroup = (creditId, itemIndex) => { setCredits(prevCredits => prevCredits.map(c => { if (c.id === creditId && c.type === 'text-group') { const updatedTextItems = c.textItems.filter((_, idx) => idx !== itemIndex); return { ...c, textItems: updatedTextItems }; } return c; })); };

// Replace the scroll animation logic with this optimized Safari version:

useEffect(() => {
    if (!isPlaying || isPreparing || isEditingContent) {
        if (audioRef.current && !audioRef.current.paused) audioRef.current.pause();
        if (cardTimeoutRef.current) clearTimeout(cardTimeoutRef.current);
        if (animationRef.current) cancelAnimationFrame(animationRef.current);
        lastTimestampRef.current = null;
        return;
    }

    if (audioRef.current && audioRef.current.paused) audioRef.current.play().catch(e => console.log("Audio play failed", e));

    // --- Card Playback Logic (unchanged) ---
    if (playbackState === 'cards') {
        if (currentCardIndex >= 0 && currentCardIndex < cardCredits.length) {
            setPlaybackStatusText(`Playing Card ${currentCardIndex + 1} of ${cardCredits.length}`);
            runCardSequence(currentCardIndex);
        } 
        else if (currentCardIndex >= cardCredits.length) {
            const hasScroll = credits.length > 0;
            if (settings.cardSequence === 'before' && hasScroll) {
                prepareScrollAnimation();
            } else {
                rewindAnimation();
            }
        }
    }

    // --- OPTIMIZED: Smooth Safari Scroll Animation ---
    else if (playbackState === 'scroll') {
    const hasCards = settings.cardsEnabled && cardCredits.length > 0;
    const runScrollSequence = () => {
        setPlaybackStatusText('Scrolling Credits');
        
        const scrollContainer = scrollRef.current;
        if (!scrollContainer) return;
        
        const scrollableDistance = scrollContainer.scrollHeight - scrollContainer.clientHeight;
        const currentSpeed = getCurrentSpeed();
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        
        let startTime = null;
        let accumulatedScroll = 0; // Only used for Safari
        
        const animationLoop = (timestamp) => {
            if (!scrollContainer || !isPlaying || isEditingContent) {
                animationRef.current = null;
                return;
            }
            
            if (startTime === null) {
                startTime = timestamp;
                lastTimestampRef.current = timestamp;
            }
            
            const deltaTime = (timestamp - (lastTimestampRef.current || timestamp)) / 1000;
            lastTimestampRef.current = timestamp;
            
            // Check for end conditions
            let hasEnded = false;
            
            if (scrollableDistance <= 0) {
                if (settings.speedMode === 'manual' || (settings.speedMode === 'auto' && (timestamp - startTime) / 1000 >= settings.targetDuration)) {
                    hasEnded = true;
                }
            } else {
                const currentScrollTop = scrollContainer.scrollTop;
                const tolerance = isSafari ? 1 : 0.5;
                
                const scrollEnded = settings.scrollDirection === 'down' 
                    ? currentScrollTop >= (scrollableDistance - tolerance)
                    : currentScrollTop <= tolerance;
                
                if (scrollEnded) hasEnded = true;
            }

            if (hasEnded) {
                if (settings.cardSequence === 'after' && hasCards) {
                    startCardSequence(false);
                } else {
                    rewindAnimation();
                }
                animationRef.current = null;
                return;
            }

            // Browser-specific scroll handling
            const pixelsPerSecond = currentSpeed;
            const rawPixelsThisFrame = pixelsPerSecond * deltaTime;
            const scrollDirection = settings.scrollDirection === 'down' ? 1 : -1;
            const currentScrollTop = scrollContainer.scrollTop;
            
            if (isSafari) {
                // Safari: Use frame throttling and pixel accumulation for smoothness
                if (deltaTime < 0.008) { // ~120fps cap for Safari
                    animationRef.current = requestAnimationFrame(animationLoop);
                    return;
                }
                
                accumulatedScroll += rawPixelsThisFrame;
                const pixelsToMove = Math.floor(accumulatedScroll);
                accumulatedScroll -= pixelsToMove;
                
                if (pixelsToMove > 0) {
                    const targetScrollTop = currentScrollTop + (scrollDirection * pixelsToMove);
                    scrollContainer.style.scrollBehavior = 'auto';
                    scrollContainer.scrollTop = Math.max(0, Math.min(scrollableDistance, targetScrollTop));
                }
            } else {
                // Chrome & Others: Use original smooth method (no throttling, no accumulation)
                const targetScrollTop = currentScrollTop + (scrollDirection * rawPixelsThisFrame);
                scrollContainer.scrollTop = Math.max(0, Math.min(scrollableDistance, targetScrollTop));
            }
            
            animationRef.current = requestAnimationFrame(animationLoop);
        };
        
        animationRef.current = requestAnimationFrame(animationLoop);
    };
    
    runScrollSequence();
}

    return () => {
        if (cardTimeoutRef.current) clearTimeout(cardTimeoutRef.current);
        if (animationRef.current) cancelAnimationFrame(animationRef.current);
        lastTimestampRef.current = null;
    };
}, [isPlaying, isPreparing, isEditingContent, playbackState, currentCardIndex]);

useEffect(() => {
    // We use a debounce to prevent saving too many snapshots in rapid succession (e.g., while typing)
    const handler = setTimeout(() => {
        // Don't add to history if the app is still loading from Firestore
        if (history.length === 0 && (credits.length > 0 || cardCredits.length > 0)) {
            // This is the initial load, set the first history state
            addToHistory(credits, settings, cardCredits);
        } else if (history.length > 0) {
            addToHistory(credits, settings, cardCredits);
        }
    }, 500); // Wait 500ms after the last change before saving

    return () => {
        clearTimeout(handler);
    };
  }, [credits, settings, cardCredits]);

  useEffect(() => { const observer = new ResizeObserver(entries => { if (entries[0]) { setPreviewWidth(entries[0].contentRect.width); setPreviewHeight(entries[0].contentRect.height); } }); if (previewRef.current) observer.observe(previewRef.current); return () => { if (previewRef.current) observer.unobserve(previewRef.current); }; }, []);
  const calculateContentDimensions = () => { if (settings.letterboxRatio === 'none' || previewWidth === 0 || previewHeight === 0) return { contentWidth: previewWidth, contentHeight: previewHeight }; const aspectRatios = { '16:9': 16/9, '1.85:1': 1.85/1, '2.35:1': 2.35/1, '4:3': 4/3 }; const targetRatio = aspectRatios[settings.letterboxRatio]; let contentWidth = previewWidth, contentHeight = previewHeight; const currentRatio = previewWidth / previewHeight; if (currentRatio > targetRatio) contentWidth = previewHeight * targetRatio; else contentHeight = previewWidth / targetRatio; return { contentWidth: Math.round(contentWidth), contentHeight: Math.round(contentHeight) }; };
  const { contentWidth, contentHeight } = calculateContentDimensions();
const handleCreditSelect = useCallback((creditId, e, type = 'scroll') => { 
    e.stopPropagation(); 
    setIsPlaying(false); 
    setIsEditingContent(false); 

    const targetCredits = type === 'scroll' ? credits : cardCredits; 
    let newSelectedIds = new Set(selectedCreditIds); 

    if (e.metaKey || e.ctrlKey) { 
        if (newSelectedIds.has(creditId)) { 
            newSelectedIds.delete(creditId); 
        } else { 
            newSelectedIds.add(creditId); 
        } 
    } else if (e.shiftKey && lastSelectedId) { 
        const lastIndex = targetCredits.findIndex(c => c.id === lastSelectedId); 
        const currentIndex = targetCredits.findIndex(c => c.id === creditId); 
        const start = Math.min(lastIndex, currentIndex); 
        const end = Math.max(lastIndex, currentIndex); 
        newSelectedIds = new Set(); 
        if (start > -1 && end > -1) { 
            for (let i = start; i <= end; i++) { 
                newSelectedIds.add(targetCredits[i].id); 
            } 
        } 
    } else { 
        newSelectedIds = new Set([creditId]); 
    } 

    setSelectedCreditIds(newSelectedIds); 
    if (!(e.metaKey || e.ctrlKey)) setLastSelectedId(creditId); 

    if (newSelectedIds.size === 1) { 
        if (type === 'card') { 
            setActiveCardId(creditId); 
            setActiveCreditId(null);

            // Whatever's on screen is what Play resumes from — always. Once a
            // sequence has actually started (cards or scroll, playing or
            // paused), selecting a card moves the real playhead to it, even
            // if a scroll pause was showing a moment ago. We skip this before
            // anything has ever played (playbackState still 'idle') so the
            // very first Play keeps its normal fresh-start behavior — the
            // pre-roll scroll, or whichever sequence settings.cardSequence
            // says to start with — instead of being hijacked by whatever was
            // last clicked in the editor before playback ever began.
            if (playbackState !== 'idle') {
                const clickedIndex = cardCredits.findIndex(c => c.id === creditId);
                if (clickedIndex > -1) {
                    setPlaybackState('cards');
                    setCurrentCardIndex(clickedIndex);
                    setCurrentCardAnimationClass('');
                }
            }
        } else { 
            setActiveCreditId(creditId); 
            setActiveCardId(null);

            // Mirror image of the card case: handing the playhead back to the
            // scroll view once a sequence has started, so a paused card deck
            // doesn't keep covering the scroll credit you just selected.
            if (playbackState !== 'idle') {
                setPlaybackState('scroll');
            }

            setTimeout(() => {
                const previewElement = document.getElementById(`preview-credit-${creditId}`);
                if (previewElement && scrollRef.current) {
                    const previewRect = previewRef.current.getBoundingClientRect();
                    const elementRect = previewElement.getBoundingClientRect();
                    // Only recenter if the element is ENTIRELY out of view. The previous
                    // check required the whole block (top AND bottom) to fit inside the
                    // viewport — which a block taller than the viewport (an 8-row "Special
                    // Thanks" grid, say) can never satisfy, so it recentered on every single
                    // click no matter where in the block you clicked. This checks for any
                    // overlap at all with the visible area instead, and only recenters when
                    // there's none — i.e. the block is genuinely off-screen.
                    const isMostlyVisible = elementRect.bottom > previewRect.top && elementRect.top < previewRect.bottom;
                    if (!isMostlyVisible) {
                        const targetScrollTop = scrollRef.current.scrollTop + elementRect.top - previewRect.top - (previewRect.height / 2) + (elementRect.height / 2);
                        scrollRef.current.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
                    }
                }
            }, 100);
        } 
        setRightPanelVisible(true); 
    } else if (newSelectedIds.size > 1) { 
        setActiveCardId(null); 
        setActiveCreditId(null); 
        setRightPanelVisible(true); 
    } else { 
        setActiveCardId(null); 
        setActiveCreditId(null); 
        setRightPanelVisible(false); 
    } 

    setTimeout(() => { 
        const listElement = document.getElementById(type === 'card' ? `card-item-${creditId}` : `credit-item-${creditId}`); 
        if (listElement) listElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); 
    }, 100); 
}, [credits, cardCredits, selectedCreditIds, lastSelectedId, previewRef, scrollRef, playbackState]);

  const handlePreviewClick = (creditId) => { const creditInScroll = credits.find(c => c.id === creditId); const creditInCards = cardCredits.find(c => c.id === creditId); if (creditInCards) { handleCreditSelect(creditId, { stopPropagation: () => {} }, 'card'); } else if (creditInScroll) { handleCreditSelect(creditId, { stopPropagation: () => {} }, 'scroll'); } };

const handleFontUpload = async (event) => {
    const file = event.target.files[0];
    if (!file || !['.ttf', '.otf', '.woff', '.woff2'].some(ext => file.name.endsWith(ext))) {
        showMessageBox("Error", "Please select a valid font file (.ttf, .otf, .woff, .woff2).", "error");
        return;
    }

    const fontName = file.name.split('.').slice(0, -1).join('.');

    setFirestoreLoading(true);
    showMessageBox("Uploading Font...", `Uploading "${file.name}" to project storage.`, "info", true);

    try {
        if (!storage || !userId || !currentProjectId) {
            throw new Error("Storage not configured or user not logged in.");
        }
        const currentAppId = __app_id;
        const filePath = `artifacts/${currentAppId}/users/${userId}/projects/${currentProjectId}/fonts/${Date.now()}-${file.name}`;
        const storageRef = ref(storage, filePath);
        const snapshot = await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(snapshot.ref);

        const font = new FontFace(fontName, `url(${downloadURL})`);
        await font.load();
        document.fonts.add(font);

        const newFont = { name: fontName, url: downloadURL, storagePath: filePath };
        setSettings(s => {
            // Filter out any existing font with the same name so the new one replaces it seamlessly
            const filteredFonts = (s.customFonts || []).filter(f => f.name !== fontName);
            return {
                ...s,
                customFonts: [...filteredFonts, newFont]
            };
        });

        showMessageBox("Success", `Font "${fontName}" was successfully added and saved to the project.`, "success", true);
        if (autoSaveToLibrary) uploadAssetToLibrary(file, 'font');

    } catch (error) {
        console.error("Font upload and save error:", error);
        showMessageBox("Error", `Failed to upload and save font: ${error.message}`, "error");
    } finally {
        setFirestoreLoading(false);
    }
};

  const applyTextTransform = (text, transform) => { if (!text) return ''; switch (transform) { case 'uppercase': return text.toUpperCase(); case 'lowercase': return text.toLowerCase(); case 'capitalize': return text.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.substring(1).toLowerCase()); default: return text; } };
  
  const handleLogoDragStart = useCallback((e, creditId, logoId) => {
    setDraggedLogoInfo({ creditId, logoId });
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleLogoDragOver = useCallback((e, creditId, logoId) => {
      e.preventDefault();
      if (draggedLogoInfo && draggedLogoInfo.creditId === creditId) {
          if (dragOverLogoInfo?.logoId !== logoId) {
              setDragOverLogoInfo({ creditId, logoId });
          }
      }
  }, [draggedLogoInfo, dragOverLogoInfo]);

  const handleLogoDragEnd = useCallback(() => {
      setDraggedLogoInfo(null);
      setDragOverLogoInfo(null);
  }, []);

  const handleLogoDrop = useCallback((e, creditId) => {
      e.preventDefault();
      if (!draggedLogoInfo || !dragOverLogoInfo || draggedLogoInfo.logoId === dragOverLogoInfo.logoId || draggedLogoInfo.creditId !== creditId) {
          handleLogoDragEnd();
          return;
      }

      setCredits(prevCredits =>
          prevCredits.map(c => {
              if (c.id === creditId) {
                  const newLogos = [...c.logos];
                  const dragIndex = newLogos.findIndex(l => l.id === draggedLogoInfo.logoId);
                  const dropIndex = newLogos.findIndex(l => l.id === dragOverLogoInfo.logoId);

                  if (dragIndex === -1 || dropIndex === -1) {
                      return c;
                  }
                  
                  const [draggedItem] = newLogos.splice(dragIndex, 1);
                  newLogos.splice(dropIndex, 0, draggedItem);

                  return { ...c, logos: newLogos };
              }
              return c;
          })
      );
      handleLogoDragEnd();
  }, [draggedLogoInfo, dragOverLogoInfo, handleLogoDragEnd]);

  const handleAudioUpload = (event) => { const file = event.target.files[0]; if (file && file.type.startsWith('audio/')) { const url = URL.createObjectURL(file); const audio = document.createElement('audio'); audio.src = url; audio.addEventListener('loadedmetadata', () => { setAudioInfo({ url: url, name: file.name, duration: audio.duration }); }); audio.onerror = () => { showMessageBox("Error", "Could not load audio metadata.", "error", true); }; setAudioInfo({ url: null, name: file.name, duration: 0 }); } else { showMessageBox("Error", "Please select a valid audio file.", "error", true); } event.target.value = ''; };
  const syncScrollToMusic = () => { if (audioInfo.duration > 0) { setSettings(s => ({...s, speedMode: 'auto', targetDuration: audioInfo.duration })); showMessageBox("Success", `Scroll duration set to ${audioInfo.duration.toFixed(2)} seconds.`, "success", true); } };
  const handleSearchChange = (e) => setSearchTerm(e.target.value);
  const filteredCredits = credits.filter(credit => { if (!searchTerm) return true; const term = searchTerm.toLowerCase(); const role = credit.role || ''; const name = credit.name || ''; if (selectedDisplayLanguage !== 'all' && credit.language !== selectedDisplayLanguage) return false; return role.toLowerCase().includes(term) || name.toLowerCase().includes(term); });
  const filteredCardCredits = cardCredits.filter(credit => { if (!searchTerm) return true; const term = searchTerm.toLowerCase(); const layers = credit.textLayers || []; return layers.some(layer => (layer.content || '').toLowerCase().includes(term)); });
  
  const createMinimalCreditsData = (credits) => {
    return credits.map(credit => {
        const minimal = {
            id: credit.id,
            type: credit.type || 'single',
            name: String(credit.name || ''),
            role: String(credit.role || ''),
            creditDisplayType: credit.creditDisplayType || 'scroll'
        };

        // Only send an explicit duration when this credit actually has a per-item
        // override. Omitting it here (instead of defaulting to 5) lets the render
        // backend's `card.duration || settings.cardDuration || 5` fallback pick up
        // the global Card Duration setting, instead of that setting being silently
        // discarded at export time.
        if (credit.duration !== undefined && credit.duration !== null && credit.duration !== '') {
            minimal.duration = Number(credit.duration);
        }

        // [FIX] Bottom Spacing (credit.marginBottom) used to be sent ONLY from
        // inside the `if (credit.override)` block below, so it reached the
        // renderer only when some Style-tab control had happened to flip that
        // flag. But Bottom Spacing is a Layout-tab field: its control calls
        // plain handleUpdate('marginBottom', value), which never sets
        // `override` (see the isOverridden comment in InspectorPanel). So for
        // the normal case — open a credit, raise its Bottom Spacing, export —
        // the value was dropped from the payload entirely, the renderer fell
        // back to settings.blockSpacing for every credit, and the render came
        // back evenly spaced while the preview (which reads credit.marginBottom
        // straight off live state) showed the gaps correctly.
        //
        // Sent unconditionally now, exactly like gutterWidth/columnGap/
        // roleSpacing. `!== undefined` rather than a truthiness check so a
        // deliberate 0 — "pull this credit tight against the next one" —
        // survives instead of being read as "unset" and replaced by the global.
        if (credit.marginBottom !== undefined && credit.marginBottom !== null && credit.marginBottom !== '') {
            minimal.marginBottom = Number(credit.marginBottom);
        }

        if (credit.type === 'main-title' && credit.textLayers) {
            minimal.textLayers = credit.textLayers.map(layer => ({
                id: layer.id,
                content: String(layer.content || ''),
                fontSize: Number(layer.fontSize) || 48,
                fontWeight: String(layer.fontWeight || 'normal'),
                fontStyle: String(layer.fontStyle || 'normal'),
                color: String(layer.color || '#ffffff'),
                fontFamily: String(layer.fontFamily || 'poppins'),
                // [FIX] The layer's Caps setting (upper/lowercase) was never sent,
                // so export ignored it while the preview applied it.
                ...(layer.transform ? { transform: String(layer.transform) } : {})
            }));
            minimal.cardPositionX = Number(credit.cardPositionX) || 50;
            minimal.cardPositionY = Number(credit.cardPositionY) || 50;
            minimal.cardTextAlign = String(credit.cardTextAlign || 'center');
            minimal.cardVerticalSpacing = Number(credit.cardVerticalSpacing) || 0.5;
            // Per-card Name Gap. Only sent when Custom — omitted = Natural. Uses a
            // null check, not ||, so a deliberate 0 survives.
            if (credit.cardNameGap !== undefined && credit.cardNameGap !== null && credit.cardNameGap !== ''
                && Number.isFinite(Number(credit.cardNameGap))) {
                minimal.cardNameGap = Number(credit.cardNameGap);
            }
        }

        if (credit.type === 'multiple') {
            minimal.multipleNamesColumnCount = Number(credit.multipleNamesColumnCount) || 1;
            if (credit.multipleMode === 'song') {
                minimal.multipleMode = 'song';
                minimal.songSwapBold = !!credit.songSwapBold;
                if (credit.songEntrySpacing !== undefined) minimal.songEntrySpacing = Number(credit.songEntrySpacing);
            }
            // [FIX] Was `Number(credit.columnGap) || 2` — a legitimate 0 is
            // falsy, so `|| 2` silently replaced a user-set zero gap with 2
            // on export while preview (reading the live value directly)
            // correctly showed 0. Matches the roleSpacing line below, which
            // already used the safe pattern.
            minimal.columnGap = credit.columnGap !== undefined ? Number(credit.columnGap) : 2;
            minimal.blockWidth = Number(credit.blockWidth) || 100;
            // roleSpacing (legacy) only when actually set — omitted now means
            // "match other headers" (see resolveMultiHeaderSpacing in CreditPreview).
            if (credit.roleSpacing !== undefined && credit.roleSpacing !== null) minimal.roleSpacing = Number(credit.roleSpacing);
            if (credit.headerSpacing !== undefined && credit.headerSpacing !== null && credit.headerSpacing !== '') minimal.headerSpacing = Number(credit.headerSpacing);
        }

        if (credit.type === 'dual') {
            // [FIX] credit.gutterWidth (the per-credit "Column Gap" override
            // on a Two Column credit) was never included in the export
            // payload at all — only the global settings.gutterWidth was
            // sent. Every dual-type credit's export ignored its own
            // override and used the global gap instead, regardless of what
            // was set here. Preview worked because it reads credit.gutterWidth
            // directly from live state; export never saw it. `!== undefined`
            // (not `credit.gutterWidth &&`) so an explicit 0 is preserved.
            if (credit.gutterWidth !== undefined) minimal.gutterWidth = Number(credit.gutterWidth);
        }

        if (credit.type === 'logo') {
            minimal.layout = String(credit.layout || 'single');
            minimal.logoUrl = String(credit.logoUrl || '');
            minimal.logoSize = String(credit.logoSize || 'medium');
            minimal.logoOpacity = Number(credit.logoOpacity) || 100;
            minimal.logoAlignment = String(credit.logoAlignment || 'center');
            minimal.logoScale = Number(credit.logoScale) || 100;
            
            if (credit.layout === 'grid' && credit.logos) {
                minimal.gridColumns = Number(credit.gridColumns) || 3;
                minimal.gridGap = Number(credit.gridGap) || 1;
                minimal.logos = credit.logos.map(logo => ({
                    id: logo.id,
                    logoUrl: String(logo.logoUrl || ''),
                    size: String(logo.size || 'medium'),
                    opacity: Number(logo.opacity) || 100,
                    offsetX: Number(logo.offsetX) || 0,
                    offsetY: Number(logo.offsetY) || 0
                }));
            }

            if (credit.layout === 'rows' && Array.isArray(credit.rows)) {
                minimal.gridGap = Number(credit.gridGap) || 1;
                minimal.blockWidth = Number(credit.blockWidth) || 90;
                minimal.rows = credit.rows.map(row => ({
                    id: row.id,
                    gap: row.gap !== undefined && row.gap !== null ? Number(row.gap) : undefined,
                    logos: (row.logos || []).map(logo => ({
                        id: logo.id,
                        logoUrl: String(logo.logoUrl || ''),
                        size: String(logo.size || 'medium')
                    }))
                }));
            }
        }

        if (credit.override) {
            minimal.override = true;
            if (credit.fontSize) minimal.fontSize = Number(credit.fontSize);
            if (credit.fontFamily) minimal.fontFamily = String(credit.fontFamily);
            if (credit.fontWeight) minimal.fontWeight = String(credit.fontWeight);
            if (credit.fontStyle) minimal.fontStyle = String(credit.fontStyle);
            if (credit.lineHeight) minimal.lineHeight = Number(credit.lineHeight);
            if (credit.letterSpacing) minimal.letterSpacing = Number(credit.letterSpacing);
            // marginBottom is handled unconditionally above — it is a Layout
            // field, not a style-override one. Don't re-add it here.
            if (credit.roleColor) minimal.roleColor = String(credit.roleColor);
            if (credit.nameColor) minimal.nameColor = String(credit.nameColor);
            if (credit.blockNameColor) minimal.blockNameColor = String(credit.blockNameColor);
            if (credit.roleFontSize) minimal.roleFontSize = Number(credit.roleFontSize);
            if (credit.nameFontSize) minimal.nameFontSize = Number(credit.nameFontSize);
            if (credit.roleFontWeight) minimal.roleFontWeight = String(credit.roleFontWeight);
            if (credit.nameFontWeight) minimal.nameFontWeight = String(credit.nameFontWeight);
            if (credit.dualAlignment) minimal.dualAlignment = String(credit.dualAlignment);
            // [FIX] Previewed per-credit overrides that never reached the export.
            if (credit.textAlign) minimal.textAlign = String(credit.textAlign);
            if (credit.roleTransform) minimal.roleTransform = String(credit.roleTransform);
            if (credit.nameTransform) minimal.nameTransform = String(credit.nameTransform);
        }

        return minimal;
    });
  };

  const createMinimalSettings = (settings) => {
      return {
          fontSize: Number(settings.fontSize) || 17,
          fontFamily: String(settings.fontFamily || 'poppins'),
          scrollFontFamily: String(settings.scrollFontFamily || settings.fontFamily || 'poppins'),
          backgroundColor: String(settings.backgroundColor || '#000000'),
          textColor: String(settings.textColor || '#ffffff'),
          textAlign: String(settings.textAlign || 'center'),
          blockNameColor: String(settings.blockNameColor || '#ffffff'),
          roleColor: String(settings.roleColor || '#ffffff'),
          nameColor: String(settings.nameColor || '#ffffff'),
          roleFontSize: settings.roleFontSize !== undefined ? Number(settings.roleFontSize) : undefined,
          nameFontSize: settings.nameFontSize !== undefined ? Number(settings.nameFontSize) : undefined,
          roleFontWeight: settings.roleFontWeight !== undefined ? String(settings.roleFontWeight) : undefined,
          nameFontWeight: settings.nameFontWeight !== undefined ? String(settings.nameFontWeight) : undefined,
          headerFontSize: settings.headerFontSize !== undefined ? Number(settings.headerFontSize) : undefined,
          headerFontWeight: settings.headerFontWeight !== undefined ? String(settings.headerFontWeight) : undefined,
          dualAlignment: String(settings.dualAlignment || 'classic'),
          speed: Number(settings.speed) || 150,
          gutterWidth: Number(settings.gutterWidth ?? 3),
          blockSpacing: Number(settings.blockSpacing) || 2,
          speedMode: String(settings.speedMode || 'auto'),
          targetDuration: Number(settings.targetDuration) || 120,
          scrollDirection: String(settings.scrollDirection || 'down'),
          letterboxRatio: String(settings.letterboxRatio || 'none'),
          blockNameCaps: String(settings.blockNameCaps || 'normal'),
          roleCaps: String(settings.roleCaps || 'normal'),
          nameCaps: String(settings.nameCaps || 'normal'),
          lineHeight: Number(settings.lineHeight) || 1.5,
          letterSpacing: Number(settings.letterSpacing) || 0,
          defaultFontWeight: String(settings.defaultFontWeight || 'normal'),
          defaultFontStyle: String(settings.defaultFontStyle || 'normal'),
          customFonts: settings.customFonts || [],
          
          cardsEnabled: Boolean(settings.cardsEnabled),
          cardSequence: String(settings.cardSequence || 'before'),
          cardFontFamily: String(settings.cardFontFamily || 'poppins'),
          cardFontSize: Number(settings.cardFontSize) || 48,
          cardFontStyle: String(settings.cardFontStyle || 'normal'),
          cardDuration: Number(settings.cardDuration) || 5,
          cardIntermissionDuration: Number(settings.cardIntermissionDuration) || 2,
          cardAnimationType: String(settings.cardAnimationType || 'fade'),
          cardAnimationDuration: Number(settings.cardAnimationDuration) || 1,
          fadeOutDuration: Number(settings.fadeOutDuration) || 1,
          hasAlphaChannel: Boolean(settings.hasAlphaChannel)
      };
  };

const exportVideo = async (exportOptions) => {
    if (OFFLINE_MODE || !userId || !functions) {
        showMessageBox("Error", "Cannot export: Not connected or project not selected.", "error");
        return;
    }

    if (projectData?.needsLicense) {
        setShowExportModal(false);
        showMessageBox(
            "License Required",
            "This is a duplicated project and hasn't been assigned a license yet, so it can't render. Assign a license now to unlock exports — all your edits will carry over.",
            "warning",
            false,
            () => { setPurchaseIntent(projectData?.tier || 'indie'); localStorage.setItem('renewProjectId', currentProjectId); setCurrentPage('checkout'); }
        );
        return;
    }
    
    if (analytics) {
        logEvent(analytics, 'export_started', {
            resolution: exportOptions.resolution,
            quality: exportOptions.quality,
            codec: exportOptions.codecChoice,
            project_tier: projectData?.tier
        });
    }
    
    setShowExportModal(false);

// Dynamic Time Estimate Logic (Updated Ratios)
    // Render time estimate — calibrated from actual Modal execution times (Jul 24 2026):
    //   4K cinematic ProRes scroll: 22 min / 210s content = 6.3×
    //   4K cinematic H.264  scroll: 14 min / 210s          = 4×
    //   4K standard  H.264  scroll: 11 min / 210s          = 3.1×
    //   4K ProRes 4444 cards (28 cards, 3:12): 8 min       = ~2.5× card duration
    const calculateEstimatedRenderTime = (durationInSeconds, quality, resolution, codecChoice, exportType) => {
        const qualityLower = quality?.toLowerCase() || 'standard';
        const resW = parseInt((resolution || '1920x1080').split('x')[0]) || 1920;
        const isProRes = (codecChoice || '').includes('prores');
        const is4K = resW >= 3840;

        // Scroll ratio: seconds of render per second of content
        let scrollRatio = 1;
        if (is4K) {
            if (qualityLower === 'cinematic') scrollRatio = isProRes ? 7 : 4.5;
            else if (qualityLower === 'standard') scrollRatio = isProRes ? 5 : 3.5;
            else scrollRatio = 2;
        } else {
            if (qualityLower === 'cinematic') scrollRatio = isProRes ? 3.5 : 2.5;
            else if (qualityLower === 'standard') scrollRatio = isProRes ? 2.5 : 1.5;
            else scrollRatio = 0.75;
        }

        // Cards: per-card render is fast (parallel), estimate from total card duration
        const totalCardSecs = (cardCredits || []).reduce(
            (sum, c) => sum + (c.duration || settings.cardDuration || 5), 0
        );
        const cardRatio = is4K ? 2.5 : 1.5;

        const scrollSecs  = exportType !== 'cards' ? durationInSeconds * scrollRatio : 0;
        const cardSecs    = exportType !== 'scroll' ? totalCardSecs * cardRatio : 0;
        const totalEstimatedSeconds = scrollSecs + cardSecs;

        const hours   = Math.floor(totalEstimatedSeconds / 3600);
        const minutes = Math.ceil((totalEstimatedSeconds % 3600) / 60);
        if (hours > 0) return `${hours}h ${minutes}m`;
        return `${minutes} mins`;
    };

    const renderEstimate = calculateEstimatedRenderTime(
        settings.targetDuration,
        exportOptions.quality,
        exportOptions.resolution,
        exportOptions.codecChoice,
        exportOptions.exportType
    );

    const exportNoticeMessage = (
        <div className="text-left text-sm space-y-6 max-w-lg mx-auto p-2">
            <header>
                <p className="text-lg font-medium text-white mb-2">
                    Your export for <span className="text-yellow-brand text-glow">"{projectName}"</span> is in the pipeline.
                </p>
                <p className="text-zinc-400 leading-relaxed">
                    Our proprietary optimization engine yields exceptional clarity at <span className="text-zinc-200">Standard H.264</span> for rapid, high-quality delivery. However, for the purist seeking <span className="text-zinc-200 italic">flawless fidelity</span>, our Cinematic ProRes pipeline meticulously refines every pixel—a standard where true excellence requires patience.
                </p>
            </header>
            
            <div className="bg-zinc-900/80 border border-zinc-800 p-5 rounded-xl flex items-center justify-between shadow-2xl">
                <div>
                    <p className="text-zinc-500 text-[10px] uppercase tracking-[0.2em] mb-1">Estimated Delivery</p>
                    <p className="text-3xl font-black text-white tracking-tight">
                        {renderEstimate}
                    </p>
                </div>
                <div className="text-right">
                    <p className="text-zinc-500 text-[10px] uppercase tracking-[0.2em] mb-1">Processing Tier</p>
                    <p className={`text-xs font-bold px-3 py-1 rounded-full border ${
                        exportOptions.quality === 'cinematic' 
                        ? 'border-purple-500/50 text-purple-400 bg-purple-500/10' 
                        : 'border-green-500/50 text-green-400 bg-green-500/10'
                    }`}>
                        {exportOptions.quality?.toUpperCase()}
                    </p>
                </div>
            </div>

            <footer className="space-y-3">
                <p className="text-[11px] text-zinc-500 leading-relaxed italic">
                    * This timeline accounts for our <span className="text-zinc-400">proprietary speed calculations</span> and <span className="text-zinc-400">motion formula</span>.
                </p>
                <div className="pt-4 border-t border-zinc-800/50">
                    <p className="text-zinc-400">
                        Monitor live frame-by-frame progress in the 
                        <span className="text-yellow-brand font-semibold cursor-pointer hover:text-yellow-300 transition-colors"> Render Queue</span>.
                    </p>
                </div>
            </footer>
        </div>
    );

    showMessageBox("Export Submitted", exportNoticeMessage, "info", false);
    
    try {
        console.log("Creating minimal data structures...");
        const combinedSettings = {
            ...settings,
            hasAlphaChannel: Boolean(exportOptions.hasAlphaChannel)
        };
        const minimalSettings = createMinimalSettings(combinedSettings);
        
        const minimalCredits = createMinimalCreditsData(credits);
        const minimalCardCredits = createMinimalCreditsData(cardCredits);
        
        console.log("Minimal credits sample:", minimalCredits.slice(0, 2));
        console.log("Minimal cardCredits sample:", minimalCardCredits.slice(0, 2));
        console.log("Minimal settings:", minimalSettings);
        
        const cleanExportOptions = {
            quality: String(exportOptions.quality || 'standard'),
            codecChoice: String(exportOptions.codecChoice || 'h264-mp4'),
            hasAlphaChannel: Boolean(exportOptions.hasAlphaChannel),
            exportType: String(exportOptions.exportType || 'full'),
            resolution: String(exportOptions.resolution || '1920x1080'),
            fps: String(exportOptions.fps || '23.98')
        };
        
        const exportVideoFunction = httpsCallable(functions, 'exportVideo');
        
        const payload = {
            projectId: String(currentProjectId),
            projectName: String(projectName || 'Untitled Project'),
            userId: String(userId),
            credits: minimalCredits,
            settings: minimalSettings,
            cardCredits: minimalCardCredits,
            ...cleanExportOptions
        };
        
        const testSerialization = JSON.stringify(payload);
        console.log("Payload serialization test passed, size:", testSerialization.length);
        
        console.log("Sending export request with safe payload:", {
            projectId: payload.projectId,
            projectName: payload.projectName,
            userId: payload.userId,
            creditsCount: payload.credits.length,
            cardCreditsCount: payload.cardCredits.length,
            exportOptions: cleanExportOptions
        });
        
        const result = await exportVideoFunction(payload);
        console.log("Export function result:", result);
        
    } catch (error) {
        console.error('Export error details:', error);
        if (error.code === 'functions/permission-denied' && error.message.includes('Export limit')) {
            setShowReplenishModal(true); // Open the new modal
        } else {
            showMessageBox("Export Failed", `Could not start the export: ${error.message}`, "error");
        }
    } finally {
        setExportIsProcessing(false);
    }
};

  const exportNLE = async (exportType) => {
  if (exportType === 'fcpxml') {
    // Your existing FCPX code
    showMessageBox("Generating FCPX File...", "Creating your Final Cut Pro project...", "info", true);
    
    try {
      if (credits.length === 0 && cardCredits.length === 0) {
        showMessageBox("No Content", "Please add some credits before exporting.", "warning");
        return;
      }
      
      const fileContent = generateFCPXML(credits, cardCredits, settings, projectName || 'Credits');
      const cleanProjectName = (projectName || 'Credits').replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_').substring(0, 50);
      const fileName = `${cleanProjectName}_Credits.fcpxml`;
      
      const blob = new Blob([fileContent], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      showMessageBox(
        "FCPX Export Complete", 
        "Your Final Cut Pro project has been downloaded successfully! The XML uses a minimal structure that should import without issues.",
        "success"
      );
      
    } catch (error) {
      console.error('FCPX Export error:', error);
      showMessageBox("Export Failed", `Could not generate FCPX file: ${error.message}`, "error");
    }
    
    return;
    
  } else if (exportType === 'premiere') {
    showMessageBox("Generating Premiere File...", "Creating your Adobe Premiere Pro project...", "info", true);
    
    try {
      if (credits.length === 0 && cardCredits.length === 0) {
        showMessageBox("No Content", "Please add some credits before exporting.", "warning");
        return;
      }
      
      const fileContent = generateSimplePremiereXML(credits, cardCredits, settings, projectName || 'Credits');
      const cleanProjectName = (projectName || 'Credits').replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_').substring(0, 50);
      const fileName = `${cleanProjectName}_Credits.xml`;
      
      const blob = new Blob([fileContent], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      showMessageBox(
        "Premiere Export Complete", 
        "Your Adobe Premiere Pro project has been downloaded! This includes actual title clips with your credits text already embedded. Import the XML file into Premiere Pro to see your credits on the timeline.",
        "success"
      );
      
    } catch (error) {
      console.error('Premiere Export error:', error);
      showMessageBox("Export Failed", `Could not generate Premiere file: ${error.message}`, "error");
    }
    
    return;
    
  } else if (exportType === 'resolve') {
    showMessageBox("Generating DaVinci Resolve File...", "Creating your DaVinci Resolve project...", "info", true);
    
    try {
      if (credits.length === 0 && cardCredits.length === 0) {
        showMessageBox("No Content", "Please add some credits before exporting.", "warning");
        return;
      }
      
      const fileContent = generateResolveXML(credits, cardCredits, settings, projectName || 'Credits');
      const cleanProjectName = (projectName || 'Credits').replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_').substring(0, 50);
      const fileName = `${cleanProjectName}_Credits.xml`;
      
      const blob = new Blob([fileContent], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      showMessageBox(
        "DaVinci Resolve Export Complete", 
        "Your DaVinci Resolve project has been downloaded! This includes text generators with your credits already embedded. Import the XML file into DaVinci Resolve to see your credits timeline.",
        "success"
      );
      
    } catch (error) {
      console.error('DaVinci Resolve Export error:', error);
      showMessageBox("Export Failed", `Could not generate DaVinci Resolve file: ${error.message}`, "error");
    }
    
    return;
  }
  
  showMessageBox("Coming Soon", `${exportType.toUpperCase()} export will be available soon!`, "info");
};

function escapeXML(str) {
  if (!str) return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');
}

const generateResolveXML = (credits, cardCredits, settings, projectName) => {
  const fps = parseFloat(settings.fps) || 24;
  const timebase = fps; 
  
  let totalDurationFrames = 0;
  const sequence = settings.cardSequence || 'before';
  const clips = [];
  
  if ((sequence === 'before' || sequence === 'cards-only') && cardCredits.length > 0) {
    cardCredits.forEach((card, index) => {
      const durationSeconds = card.duration || settings.cardDuration || 5;
      const durationFrames = Math.round(durationSeconds * fps);
      
      const mainText = card.textLayers?.[0]?.content || `Card ${index + 1}`;
      const subtitle = card.textLayers?.[1]?.content || '';
      const combinedText = subtitle ? `${mainText}\n${subtitle}` : mainText;
      
      clips.push({
        type: 'title',
        name: `Title Card ${index + 1}`,
        startFrame: totalDurationFrames,
        durationFrames: durationFrames,
        startSeconds: totalDurationFrames / fps,
        durationSeconds: durationSeconds,
        text: combinedText,
        fontSize: card.textLayers?.[0]?.fontSize || 48,
        alignment: card.cardTextAlign || 'center',
        positionX: (card.cardPositionX || 50) / 100,
        positionY: (card.cardPositionY || 50) / 100,
        isCard: true
      });
      
      totalDurationFrames += durationFrames;
    });
  }
  
  if ((sequence === 'before' || sequence === 'after' || sequence === 'scroll-only') && credits.length > 0) {
    const scrollDurationSeconds = settings.speedMode === 'auto' 
      ? (settings.targetDuration || 120) 
      : Math.max(60, credits.length * 2);
    const scrollDurationFrames = Math.round(scrollDurationSeconds * fps);
    
    const creditsText = credits.map(credit => {
      if (credit.type === 'title') return credit.name || '';
      if (credit.type === 'dual') return `${credit.role || ''}\n${credit.name || ''}`;
      if (credit.type === 'single') return credit.name || '';
      if (credit.type === 'multiple') {
        const names = credit.name ? credit.name.split('\n').join('\n') : '';
        return `${credit.role || ''}\n${names}`;
      }
      return '';
    }).filter(text => text.trim()).join('\n\n');
    
    clips.push({
      type: 'title',
      name: 'Credits Scroll',
      startFrame: totalDurationFrames,
      durationFrames: scrollDurationFrames,
      startSeconds: totalDurationFrames / fps,
      durationSeconds: scrollDurationSeconds,
      text: creditsText,
      fontSize: settings.fontSize || 24,
      alignment: 'center',
      positionX: 0.5,
      positionY: 0.5,
      isCard: false
    });
    
    totalDurationFrames += scrollDurationFrames;
  }
  
  if (sequence === 'after' && cardCredits.length > 0) {
    cardCredits.forEach((card, index) => {
      const durationSeconds = card.duration || settings.cardDuration || 5;
      const durationFrames = Math.round(durationSeconds * fps);
      
      const mainText = card.textLayers?.[0]?.content || `Card ${index + 1}`;
      const subtitle = card.textLayers?.[1]?.content || '';
      const combinedText = subtitle ? `${mainText}\n${subtitle}` : mainText;
      
      clips.push({
        type: 'title',
        name: `Title Card ${index + 1}`,
        startFrame: totalDurationFrames,
        durationFrames: durationFrames,
        startSeconds: totalDurationFrames / fps,
        durationSeconds: durationSeconds,
        text: combinedText,
        fontSize: card.textLayers?.[0]?.fontSize || 48,
        alignment: card.cardTextAlign || 'center',
        positionX: (card.cardPositionX || 50) / 100,
        positionY: (card.cardPositionY || 50) / 100,
        isCard: true
      });
      
      totalDurationFrames += durationFrames;
    });
  }
  
  if (totalDurationFrames === 0) {
    totalDurationFrames = Math.round(90 * fps);
  }
  
  const totalDurationSeconds = totalDurationFrames / fps;
  
  const resolveXML = `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE fcpxml>
<fcpxml version="1.8">
  <resources>
    <format id="r1" name="FFVideoFormat1080p${Math.round(fps)}" frameDuration="1/${Math.round(fps)}s" width="1920" height="1080" colorSpace="1-1-1 (Rec. 709)"/>
    <effect id="r2" name="Text" uid=".../Text"/>
${generateResolveTextEffects(clips)}
  </resources>
  
  <library location="">
    <event name="${escapeXML(projectName || 'Credits')} Event">
      <project name="${escapeXML(projectName || 'Credits')}">
        <sequence format="r1" duration="${totalDurationSeconds}s" tcStart="0s" tcFormat="NDF" audioLayout="stereo" audioRate="48k">
          <spine>
${generateResolveSpineContent(clips, fps)}
          </spine>
        </sequence>
      </project>
    </event>
  </library>
</fcpxml>`;

  return resolveXML;
};

const generateResolveTextEffects = (clips) => {
  return clips.map((clip, index) => {
    return `    <effect id="r${index + 3}" name="Text Generator" uid=".../Text Generator"/>`;
  }).join('\n');
};

const generateResolveSpineContent = (clips, fps) => {
  return clips.map((clip, index) => {
    const alignmentValue = clip.alignment === 'left' ? 'left' : clip.alignment === 'right' ? 'right' : 'center';
    
    const startFrames = Math.round(clip.startSeconds * fps);
    const startTimecode = `${Math.floor(startFrames / fps / 3600)}:${Math.floor((startFrames / fps % 3600) / 60).toString().padStart(2, '0')}:${Math.floor(startFrames / fps % 60).toString().padStart(2, '0')}:${(startFrames % fps).toString().padStart(2, '0')}`;
    
    return `            <title name="${escapeXML(clip.name)}" lane="1" offset="${clip.startSeconds}s" duration="${clip.durationSeconds}s" ref="r${index + 3}" start="${startTimecode}">
              <text>
                <text-style ref="ts${index + 1}">${escapeXML(clip.text)}</text-style>
              </text>
              <text-style-def id="ts${index + 1}">
                <text-style font="Arial" fontSize="${clip.fontSize}" fontFace="Regular" fontColor="1 1 1 1" alignment="${alignmentValue}"/>
              </text-style-def>
              <param name="Position" key="9999/999166631/999166633/1/100/101" value="${(clip.positionX - 0.5) * 1920} ${(0.5 - clip.positionY) * 1080}"/>
            </title>`;
  }).join('\n');
};

// ============================================================================
// FCPXML EXPORT  (v2 — styled runs)
// ----------------------------------------------------------------------------
// v1 fixed the black-bar bug: the old exporter dumped every credit into a
// <note> element (inspector metadata, renders nothing) and put a single empty
// <gap> on the spine.
//
// v2 fixes the *formatting*. v1 emitted one text-style for the whole roll, so
// headers, roles and names all came in at the same size, and role/name ran
// together on one line. FCPXML's <text> element accepts MANY <text-style> runs,
// each pointing at its own <text-style-def>, so we now emit a run per credit
// part carrying that part's real font, size, weight, colour and case from the
// project settings (with per-credit overrides applied on top).
//
// What Final Cut CAN match: font, size, face/weight, italic, colour, case,
// line spacing, alignment, and per-line style changes.
// What it CANNOT match: our gutter-based 'classic' dual layout. Apple's
// Scrolling generator lays out one text block; it has no two-column engine.
// See FCPX_DUAL_LAYOUT below for the trade-off.
// ============================================================================

// fps -> [numerator, denominator] for FCPXML rational time. Never emit decimal
// seconds: FCPX wants frame-aligned rationals or it rounds clips into each other.
const FCPX_TIMEBASE = {
  23.98: [1001, 24000],
  23.976: [1001, 24000],
  24: [1, 24],
  25: [1, 25],
  29.97: [1001, 30000],
  30: [1, 30],
  48: [1, 48],
  50: [1, 50],
  59.94: [1001, 60000],
  60: [1, 60]
};

const FCPX_FORMAT_SUFFIX = {
  23.98: '2398', 23.976: '2398', 24: '24', 25: '25',
  29.97: '2997', 30: '30', 48: '48', 50: '50', 59.94: '5994', 60: '60'
};

// Our font keys are slugs ('eb-garamond'); FCPX wants the installed face name.
const FCPX_FONT_NAMES = {
  'arial': 'Arial',
  'courier-new': 'Courier New',
  'eb-garamond': 'EB Garamond',
  'futura': 'Futura',
  'garamond': 'Garamond',
  'georgia': 'Georgia',
  'helvetica': 'Helvetica',
  'helvetica-rounded': 'Helvetica Rounded',
  'optima': 'Optima',
  'palatino': 'Palatino',
  'poppins': 'Poppins'
};

// Parameter keys for Apple's Scrolling (credit roll) generator. These were read
// straight off a Final Cut round-trip of our own export — FCP rewrote the file
// and told us its parameter paths. Do not guess these; re-derive them the same
// way if the generator ever changes.
const FCPX_SCROLL_PARAM_KEYS = {
  position: '9999/10006/10070/1/100/101',
  anchorPoint: '9999/10006/10070/1/100/107',
  leftMargin: '9999/10006/10070/2/323',
  rightMargin: '9999/10006/10070/2/324',
  topMargin: '9999/10006/10070/2/325',
  bottomMargin: '9999/10006/10070/2/326'
};

// Position and Anchor Point MUST be stated explicitly on every column layer.
// When they are left out, Final Cut derives its own value per title from the
// content — a round-trip showed the Roles layer landing on "-10.9381 4.25272"
// while Names landed on "-10.9381 34.43", a ~30-unit vertical offset that made
// each name sit off the baseline of its role. Pinning all three layers to the
// same origin is what keeps the columns on a shared baseline.
const FCPX_SCROLL_POSITION = '0 0';
const FCPX_SCROLL_ANCHOR = '847 -538';

// The generator's default text box on a 1920x1080 frame, same source.
const FCPX_SCROLL_MARGIN_L = 90;
const FCPX_SCROLL_MARGIN_R = 1626;
const FCPX_SCROLL_MARGIN_T = -2;
const FCPX_SCROLL_MARGIN_B = -1082;

// Width of each gutter column, in frame pixels, measured inward from the
// gutter. 0 = use the full half (best case: with right/left alignment the text
// hugs the gutter and long role names never wrap). Set a value like 560 only if
// your Final Cut build ignores per-run alignment and centres the columns — a
// narrower box pulls the centred text back toward the gutter.
const FCPX_SCROLL_COLUMN_WIDTH = 0;

// Forces a scroll layout regardless of settings.dualAlignment:
//   'auto'     follow the project ('classic' -> two columns, else stacked)
//   'columns'  always two columns
//   'stacked'  always role-above-name
const FCPX_DUAL_LAYOUT = 'auto';

// Scroll sizes here are authored in preview units (default 17), not 1080p
// points — a 17pt credit roll would be unreadable in Final Cut. Card sizes are
// already authored in points (default 48) and pass through untouched.
// If the renderer's scroll scaling changes, change this one number.
const FCPX_SCROLL_PT_SCALE = 3;

const fcpxFontName = (key) => FCPX_FONT_NAMES[key] || (key ? String(key) : 'Helvetica');

const fcpxIsBold = (weight) => {
  if (weight === 'bold') return true;
  const n = parseInt(weight, 10);
  return Number.isFinite(n) && n >= 700;
};

// '#RRGGBB' -> 'r g b a' in 0–1 floats, the only colour form FCPX takes.
const fcpxColor = (hex, alpha = 1) => {
  const m = /^#?([0-9a-f]{6})$/i.exec((hex || '').trim());
  if (!m) return `1 1 1 ${alpha}`;
  const n = parseInt(m[1], 16);
  return `${(((n >> 16) & 255) / 255).toFixed(4)} ${(((n >> 8) & 255) / 255).toFixed(4)} ${((n & 255) / 255).toFixed(4)} ${alpha}`;
};

const fcpxAlignment = (a) =>
  a === 'left' ? 'left' : a === 'right' ? 'right' : a === 'justified' ? 'justified' : 'center';

// Mirrors the CASE_OPTIONS transform applied in the preview/renderer.
const fcpxApplyCase = (text, mode) => {
  const t = String(text == null ? '' : text);
  if (mode === 'uppercase') return t.toUpperCase();
  if (mode === 'lowercase') return t.toLowerCase();
  if (mode === 'capitalize') return t.replace(/\b\w/g, c => c.toUpperCase());
  return t;
};

// Collects <text-style> runs and de-duplicates their <text-style-def>s, so a
// 300-credit roll emits ~4 style defs instead of 300 identical ones.
const createFcpxStyleTable = (counter) => {
  const defs = [];
  const seen = new Map();
  return {
    // style: { font, fontSize, bold, italic, color, alignment, lineSpacing, tabStops }
    idFor(style) {
      const key = JSON.stringify(style);
      if (seen.has(key)) return seen.get(key);
      // FCPXML declares text-style-def/@id as DTD type ID, so it must be unique
      // across the ENTIRE document, not just within its own title. The counter
      // is shared by every style table in one export.
      counter.n += 1;
      const id = `ts${counter.n}`;
      seen.set(key, id);
      defs.push({ id, style });
      return id;
    },
    render(indent) {
      return defs.map(({ id, style }) => {
        const attrs = [
          `font="${escapeXML(style.font)}"`,
          `fontSize="${style.fontSize}"`,
          `fontFace="${style.bold ? 'Bold' : 'Regular'}"`,
          `fontColor="${style.color}"`,
          `bold="${style.bold ? 1 : 0}"`,
          `italic="${style.italic ? 1 : 0}"`,
          `alignment="${style.alignment}"`
        ];
        if (style.lineSpacing != null) attrs.push(`lineSpacing="${style.lineSpacing}"`);
        if (style.tabStops) attrs.push(`tabStops="${escapeXML(style.tabStops)}"`);
        return `${indent}<text-style-def id="${id}">\n${indent}  <text-style ${attrs.join(' ')}/>\n${indent}</text-style-def>`;
      }).join('\n');
    }
  };
};

const generateFCPXML = (credits, cardCredits, settings, projectName) => {
  const fps = parseFloat(settings.fps) || 24;
  const [tbNum, tbDen] = FCPX_TIMEBASE[fps] || FCPX_TIMEBASE[24];
  const frameDuration = `${tbNum}/${tbDen}s`;
  const formatName = `FFVideoFormat1080p${FCPX_FORMAT_SUFFIX[fps] || '24'}`;

  const T = (frames) => `${Math.round(frames) * tbNum}/${tbDen}s`;
  const F = (seconds) => Math.max(1, Math.round((Number(seconds) || 0) * (tbDen / tbNum)));

  const safeName = projectName || 'Credits';
  const sequenceMode = settings.cardSequence || 'before';
  const cardsEnabled = settings.cardsEnabled !== false;
  const showCards = cardsEnabled && sequenceMode !== 'scroll-only' && (cardCredits || []).length > 0;
  const showScroll = sequenceMode !== 'cards-only' && (credits || []).length > 0;

  const scrollFontKey = settings.scrollFontFamily || settings.fontFamily;
  const cardFontKey = settings.cardFontFamily || settings.fontFamily;
  const baseAlign = fcpxAlignment(settings.textAlign);
  const scrollScale = FCPX_SCROLL_PT_SCALE;
  const pt = (v, fallback) => Math.max(8, Math.round((Number(v) || fallback) * scrollScale));

  // gutterWidth is authored in the same preview units as font size.
  const twoColumn = FCPX_DUAL_LAYOUT === 'columns'
    || (FCPX_DUAL_LAYOUT === 'auto' && (settings.dualAlignment || 'classic') === 'classic');

  const blocks = [];
  const styleIds = { n: 0 }; // document-wide, see createFcpxStyleTable

  // ---- Title cards: one Basic Title each, one styled run per text layer -----
  const pushCards = () => {
    const intermission = Number(settings.cardIntermissionDuration) || 0;
    const cards = cardCredits || [];

    cards.forEach((card, i) => {
      const styles = createFcpxStyleTable(styleIds);
      const layers = (card.textLayers || []).filter(l => (l.content || '').trim());
      const frames = F(card.duration || settings.cardDuration || 5);

      if (layers.length === 0) {
        blocks.push({ kind: 'gap', frames, name: `Card ${i + 1} (empty)` });
      } else {
        const runs = layers.map((layer, li) => {
          const id = styles.idFor({
            font: fcpxFontName(layer.fontFamily || cardFontKey),
            fontSize: Math.max(8, Math.round(Number(layer.fontSize) || Number(settings.cardFontSize) || 48)),
            bold: fcpxIsBold(layer.fontWeight || settings.defaultFontWeight),
            italic: (layer.fontStyle || settings.cardFontStyle) === 'italic',
            color: fcpxColor(layer.color || settings.nameColor || '#ffffff'),
            alignment: baseAlign,
            lineSpacing: null,
            tabStops: null
          });
          const text = (layer.content || '') + (li < layers.length - 1 ? '\n' : '');
          return { id, text };
        });

        blocks.push({
          kind: 'title',
          effect: 'r2',
          frames,
          name: (layers[0].content || `Card ${i + 1}`).slice(0, 60),
          runs,
          styles
        });
      }

      if (intermission > 0 && i < cards.length - 1) {
        blocks.push({ kind: 'gap', frames: F(intermission), name: 'Intermission' });
      }
    });
  };

  // ---- Scroll ---------------------------------------------------------------
  // Built from a LINE PLAN: one entry per rendered line of the roll. Each line
  // belongs to exactly one layer — the two gutter columns, or the full-width
  // centre. Every layer emits a line for every index (a space where it has no
  // content) at the SAME fontSize, so all layers are identical in height and
  // scroll in perfect lockstep.
  const pushScroll = () => {
    const lineSpacing = Number(settings.blockSpacing) || null;
    const bodySize = pt(settings.nameFontSize || settings.fontSize, 17);

    const mkStyle = (over) => ({
      font: fcpxFontName(over.fontFamily || scrollFontKey),
      fontSize: over.fontSize,
      bold: !!over.bold,
      italic: settings.defaultFontStyle === 'italic',
      color: fcpxColor(over.color || '#ffffff'),
      alignment: over.align || baseAlign,
      lineSpacing,
      tabStops: null
    });

    // In two-column mode the roles column is right-aligned and the names column
    // left-aligned, so both hug the gutter instead of floating in the middle of
    // their own half — that is what makes it read as a classic credit roll.
    // Final Cut derives its per-paragraph Alignment parameter from this
    // attribute on import (verified: every style we sent as 'center' came back
    // as '1 (Center)'), so this is the only lever we have on line alignment.
    const roleAlign = twoColumn ? 'right' : baseAlign;
    const nameAlign = twoColumn ? 'left' : baseAlign;

    // align is passed explicitly: nameStyle serves BOTH the left-aligned Names
    // column and centred single / group-member lines on the centre layer, so it
    // must never assume the column alignment.
    const roleStyle = (c, size, align) => mkStyle({
      fontFamily: c.fontFamily,
      fontSize: size,
      align,
      bold: fcpxIsBold(c.roleFontWeight || settings.defaultFontWeight),
      color: c.roleColor || settings.roleColor
    });
    const nameStyle = (c, size, align) => mkStyle({
      fontFamily: c.fontFamily,
      fontSize: size,
      align,
      bold: fcpxIsBold(c.nameFontWeight || settings.defaultFontWeight),
      color: c.nameColor || settings.nameColor
    });
    const headStyle = (c, size) => mkStyle({
      align: baseAlign,
      fontFamily: c.fontFamily,
      fontSize: size,
      bold: fcpxIsBold(settings.headerFontWeight || '700'),
      color: c.nameColor || settings.blockNameColor
    });

    // --- line plan ---
    const lines = [];
    const logoNames = [];
    const list = credits || [];

    list.forEach((credit, i) => {
      switch (credit.type) {
        case 'logo':
          logoNames.push(credit.name || 'Logo');
          return;

        case 'dual': {
          const rSize = pt(credit.roleFontSize || settings.roleFontSize || settings.fontSize, 17);
          const nSize = pt(credit.nameFontSize || settings.nameFontSize || settings.fontSize, 17);
          // Both columns must share a line height or they drift apart over a
          // 3-minute roll, so the taller of the pair wins for this line.
          const size = Math.max(rSize, nSize);
          if (twoColumn) {
            lines.push({
              layer: 'split', size,
              role: fcpxApplyCase(credit.role, settings.roleCaps), roleStyle: roleStyle(credit, size, roleAlign),
              name: fcpxApplyCase(credit.name, settings.nameCaps), nameStyle: nameStyle(credit, size, nameAlign)
            });
          } else {
            lines.push({ layer: 'full', size: rSize, text: fcpxApplyCase(credit.role, settings.roleCaps), style: roleStyle(credit, rSize, baseAlign) });
            lines.push({ layer: 'full', size: nSize, text: fcpxApplyCase(credit.name, settings.nameCaps), style: nameStyle(credit, nSize, baseAlign) });
          }
          break;
        }

        case 'multiple': {
          const hSize = pt(credit.fontSize || settings.headerFontSize, 24);
          lines.push({ layer: 'full', size: hSize, text: fcpxApplyCase(credit.role, settings.blockNameCaps), style: headStyle(credit, hSize) });
          const nSize = pt(credit.nameFontSize || settings.nameFontSize || settings.fontSize, 17);
          String(credit.name || '').split('\n').map(n => n.trim()).filter(Boolean).forEach(n => {
            lines.push({ layer: 'full', size: nSize, text: fcpxApplyCase(n, settings.nameCaps), style: nameStyle(credit, nSize, baseAlign) });
          });
          break;
        }

        case 'title': {
          const hSize = pt(credit.fontSize || settings.headerFontSize, 24);
          lines.push({ layer: 'full', size: hSize, text: fcpxApplyCase(credit.name, settings.blockNameCaps), style: headStyle(credit, hSize) });
          break;
        }

        case 'single':
        default: {
          const nSize = pt(credit.nameFontSize || settings.nameFontSize || settings.fontSize, 17);
          lines.push({ layer: 'full', size: nSize, text: fcpxApplyCase(credit.name, settings.blockNameCaps), style: nameStyle(credit, nSize, baseAlign) });
          break;
        }
      }

      if (i < list.length - 1) lines.push({ layer: 'spacer', size: bodySize });
    });

    if (lines.length === 0) {
      logoNames.forEach(n => blocks.push({ kind: 'gap', frames: F(2), name: `LOGO — ${n}` }));
      return;
    }

    const scrollSeconds = settings.speedMode === 'auto'
      ? (Number(settings.targetDuration) || 120)
      : Math.max(30, lines.length * 1.2);
    const frames = F(scrollSeconds);

    // A layer renders one line per plan entry: its own text where it owns the
    // line, a space elsewhere — held at the line's size to preserve height.
    const buildLayer = (owns, textOf, styleOf, align) => {
      const styles = createFcpxStyleTable(styleIds);
      const runs = [];
      lines.forEach((ln, i) => {
        const tail = i < lines.length - 1 ? '\n' : '';
        const mine = owns(ln);
        const style = mine
          ? styleOf(ln)
          : mkStyle({ fontSize: ln.size, color: settings.nameColor, align });
        runs.push({ id: styles.idFor(style), text: (mine ? textOf(ln) : ' ') + tail });
      });
      return { runs, styles };
    };

    if (twoColumn) {
      // Three synced Scrolling titles. Column margins are carved out of the
      // generator's own default text box (Left 90 / Right 1626 on a 1920 frame,
      // read off Final Cut's own export), split around centre by the gutter.
      const gutterPx = Math.min(400, Math.max(24, Math.round((Number(settings.gutterWidth) || 3) * scrollScale * 8)));
      const centre = (FCPX_SCROLL_MARGIN_L + FCPX_SCROLL_MARGIN_R) / 2;
      const rolesR = Math.round(centre - gutterPx / 2);
      const namesL = Math.round(centre + gutterPx / 2);
      const rolesL = FCPX_SCROLL_COLUMN_WIDTH > 0
        ? Math.max(FCPX_SCROLL_MARGIN_L, rolesR - FCPX_SCROLL_COLUMN_WIDTH)
        : FCPX_SCROLL_MARGIN_L;
      const namesR = FCPX_SCROLL_COLUMN_WIDTH > 0
        ? Math.min(FCPX_SCROLL_MARGIN_R, namesL + FCPX_SCROLL_COLUMN_WIDTH)
        : FCPX_SCROLL_MARGIN_R;

      const centreLayer = buildLayer(l => l.layer === 'full', l => l.text, l => l.style, baseAlign);
      const roleLayer = buildLayer(l => l.layer === 'split', l => l.role, l => l.roleStyle, roleAlign);
      const nameLayer = buildLayer(l => l.layer === 'split', l => l.name, l => l.nameStyle, nameAlign);

      blocks.push({
        kind: 'title',
        effect: 'r3',
        frames,
        name: `${safeName} — Credit Roll`,
        margins: [FCPX_SCROLL_MARGIN_L, FCPX_SCROLL_MARGIN_R],
        runs: centreLayer.runs,
        styles: centreLayer.styles,
        children: [
          { lane: 1, name: 'Roles', effect: 'r3', frames, margins: [rolesL, rolesR], runs: roleLayer.runs, styles: roleLayer.styles },
          { lane: 2, name: 'Names', effect: 'r3', frames, margins: [namesL, namesR], runs: nameLayer.runs, styles: nameLayer.styles }
        ]
      });
    } else {
      const single = buildLayer(() => true, l => (l.layer === 'spacer' ? ' ' : l.text), l => l.style || mkStyle({ fontSize: l.size, color: settings.nameColor }), baseAlign);
      blocks.push({
        kind: 'title',
        effect: 'r3',
        frames,
        name: `${safeName} — Credit Roll`,
        margins: [FCPX_SCROLL_MARGIN_L, FCPX_SCROLL_MARGIN_R],
        runs: single.runs,
        styles: single.styles
      });
    }

    // Logo credits can't travel inside an XML with no media references, so each
    // becomes a named placeholder the editor can drop artwork onto.
    logoNames.forEach(n => blocks.push({ kind: 'gap', frames: F(2), name: `LOGO — ${n}` }));
  };

  if (sequenceMode === 'after') {
    if (showScroll) pushScroll();
    if (showCards) pushCards();
  } else {
    if (showCards) pushCards();
    if (showScroll) pushScroll();
  }

  // An empty project would otherwise export as the black bar we're fixing.
  if (blocks.length === 0) {
    const styles = createFcpxStyleTable(styleIds);
    const id = styles.idFor({
      font: fcpxFontName(cardFontKey),
      fontSize: Number(settings.cardFontSize) || 48,
      bold: false,
      italic: false,
      color: fcpxColor(settings.nameColor || '#ffffff'),
      alignment: baseAlign,
      lineSpacing: null,
      tabStops: null
    });
    blocks.push({ kind: 'title', effect: 'r2', frames: F(5), name: safeName, runs: [{ id, text: safeName }], styles });
  }

  // ---- Lay the blocks end to end on the spine -------------------------------
  // DTD child order for <title> is (param*, text*, text-style-def*, ..., anchor
  // items*), so margins come first and nested column layers come last.
  const renderTitle = (t, indent, offset, duration) => {
    const pad = ' '.repeat(indent);
    const params = t.margins ? [
      `${pad}  <param name="Position" key="${FCPX_SCROLL_PARAM_KEYS.position}" value="${FCPX_SCROLL_POSITION}"/>`,
      `${pad}  <param name="Anchor Point" key="${FCPX_SCROLL_PARAM_KEYS.anchorPoint}" value="${FCPX_SCROLL_ANCHOR}"/>`,
      `${pad}  <param name="Left Margin" key="${FCPX_SCROLL_PARAM_KEYS.leftMargin}" value="${t.margins[0]}"/>`,
      `${pad}  <param name="Right Margin" key="${FCPX_SCROLL_PARAM_KEYS.rightMargin}" value="${t.margins[1]}"/>`,
      `${pad}  <param name="Top Margin" key="${FCPX_SCROLL_PARAM_KEYS.topMargin}" value="${FCPX_SCROLL_MARGIN_T}"/>`,
      `${pad}  <param name="Bottom Margin" key="${FCPX_SCROLL_PARAM_KEYS.bottomMargin}" value="${FCPX_SCROLL_MARGIN_B}"/>`
    ].join('\n') + '\n' : '';

    const runs = t.runs
      .map(r => `${pad}    <text-style ref="${r.id}">${escapeXML(r.text)}</text-style>`)
      .join('\n');

    const kids = (t.children || [])
      .map(c => renderTitle(c, indent + 2, '0s', duration))
      .join('\n');

    const lane = t.lane ? ` lane="${t.lane}"` : '';
    return `${pad}<title ref="${t.effect}"${lane} name="${escapeXML(t.name)}" offset="${offset}" duration="${duration}" start="0s">
${params}${pad}  <text>
${runs}
${pad}  </text>
${t.styles.render(pad + '  ')}${kids ? '\n' + kids : ''}
${pad}</title>`;
  };

  let cursor = 0;
  const spine = blocks.map(b => {
    const offset = T(cursor);
    const duration = T(b.frames);
    cursor += b.frames;

    if (b.kind === 'gap') {
      return `            <gap name="${escapeXML(b.name)}" offset="${offset}" duration="${duration}" start="0s"/>`;
    }
    return renderTitle(b, 12, offset, duration);
  }).join('\n');

  return `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE fcpxml>
<fcpxml version="1.9">
  <resources>
    <format id="r1" name="${formatName}" frameDuration="${frameDuration}" width="1920" height="1080" colorSpace="1-1-1 (Rec. 709)"/>
    <effect id="r2" name="Basic Title" uid=".../Titles.localized/Bumper:Opener.localized/Basic Title.localized/Basic Title.moti"/>
    <effect id="r3" name="Scrolling" uid=".../Titles.localized/Credits.localized/Scrolling.localized/Scrolling.moti"/>
  </resources>

  <library>
    <event name="${escapeXML(safeName)} Event">
      <project name="${escapeXML(safeName)}">
        <sequence format="r1" duration="${T(cursor)}" tcStart="0s" tcFormat="NDF" audioLayout="stereo" audioRate="48k">
          <spine>
${spine}
          </spine>
        </sequence>
      </project>
    </event>
  </library>
</fcpxml>`;
};

const generatePremiereXML = (credits, cardCredits, settings, projectName) => {
  const fps = parseFloat(settings.fps) || 24;
  const timebase = Math.round(fps);
  
  let totalDurationFrames = 0;
  const sequence = settings.cardSequence || 'before';
  const clips = [];
  
  if ((sequence === 'before' || sequence === 'cards-only') && cardCredits.length > 0) {
    cardCredits.forEach((card, index) => {
      const durationSeconds = card.duration || settings.cardDuration || 5;
      const durationFrames = Math.round(durationSeconds * fps);
      
      const mainText = card.textLayers?.[0]?.content || `Card ${index + 1}`;
      const subtitle = card.textLayers?.[1]?.content || '';
      const combinedText = subtitle ? `${mainText}\n${subtitle}` : mainText;
      
      clips.push({
        type: 'title',
        name: `Title Card ${index + 1}`,
        startFrame: totalDurationFrames,
        durationFrames: durationFrames,
        text: combinedText,
        fontSize: card.textLayers?.[0]?.fontSize || 48,
        alignment: card.cardTextAlign || 'center',
        isCard: true
      });
      
      totalDurationFrames += durationFrames;
    });
  }
  
  if ((sequence === 'before' || sequence === 'after' || sequence === 'scroll-only') && credits.length > 0) {
    const scrollDurationSeconds = settings.speedMode === 'auto' 
      ? (settings.targetDuration || 120) 
      : Math.max(60, credits.length * 2);
    const scrollDurationFrames = Math.round(scrollDurationSeconds * fps);
    
    const creditsText = credits.map(credit => {
      if (credit.type === 'title') return credit.name || '';
      if (credit.type === 'dual') return `${credit.role || ''}\n${credit.name || ''}`;
      if (credit.type === 'single') return credit.name || '';
      if (credit.type === 'multiple') {
        const names = credit.name ? credit.name.split('\n').join('\n') : '';
        return `${credit.role || ''}\n${names}`;
      }
      return '';
    }).filter(text => text.trim()).join('\n\n');
    
    clips.push({
      type: 'title',
      name: 'Credits Scroll',
      startFrame: totalDurationFrames,
      durationFrames: scrollDurationFrames,
      text: creditsText,
      fontSize: settings.fontSize || 24,
      alignment: 'center',
      isCard: false
    });
    
    totalDurationFrames += scrollDurationFrames;
  }
  
  if (sequence === 'after' && cardCredits.length > 0) {
    cardCredits.forEach((card, index) => {
      const durationSeconds = card.duration || settings.cardDuration || 5;
      const durationFrames = Math.round(durationSeconds * fps);
      
      const mainText = card.textLayers?.[0]?.content || `Card ${index + 1}`;
      const subtitle = card.textLayers?.[1]?.content || '';
      const combinedText = subtitle ? `${mainText}\n${subtitle}` : mainText;
      
      clips.push({
        type: 'title',
        name: `Title Card ${index + 1}`,
        startFrame: totalDurationFrames,
        durationFrames: durationFrames,
        text: combinedText,
        fontSize: card.textLayers?.[0]?.fontSize || 48,
        alignment: card.cardTextAlign || 'center',
        isCard: true
      });
      
      totalDurationFrames += durationFrames;
    });
  }
  
  if (totalDurationFrames === 0) {
    totalDurationFrames = Math.round(90 * fps);
  }
  
  const videoTracks = generatePremiereVideoTracks(clips, timebase);
  
  const premiereXML = `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE xmeml>
<xmeml version="4">
  <project>
    <name>${escapeXML(projectName || 'Credits')}</name>
    <children>
      <bin>
        <name>Sequences</name>
        <children>
          <sequence id="sequence-1">
            <name>${escapeXML(projectName || 'Credits')} Sequence</name>
            <duration>${totalDurationFrames}</duration>
            <rate>
              <timebase>${timebase}</timebase>
              <ntsc>FALSE</ntsc>
            </rate>
            <timecode>
              <rate>
                <timebase>${timebase}</timebase>
                <ntsc>FALSE</ntsc>
              </rate>
              <string>01:00:00:00</string>
              <frame>0</frame>
              <source>source</source>
              <displayformat>NDF</displayformat>
            </timecode>
            <in>0</in>
            <out>${totalDurationFrames}</out>
            <media>
              <video>
                <format>
                  <samplecharacteristics>
                    <rate>
                      <timebase>${timebase}</timebase>
                      <ntsc>FALSE</ntsc>
                    </rate>
                    <width>1920</width>
                    <height>1080</height>
                    <anamorphic>FALSE</anamorphic>
                    <pixelaspectratio>square</pixelaspectratio>
                    <fielddominance>none</fielddominance>
                    <colordepth>8</colordepth>
                  </samplecharacteristics>
                </format>
${videoTracks}
              </video>
            </media>
          </sequence>
        </children>
      </bin>
    </children>
  </project>
</xmeml>`;

  return premiereXML;
};

const generatePremiereVideoTracks = (clips, timebase) => {
  let trackXML = `                <track>
                  <clipitem id="clipitem-1">
                    <name>Credits Sequence</name>
                    <duration>${clips.reduce((total, clip) => total + clip.durationFrames, 0)}</duration>
                    <rate>
                      <timebase>${timebase}</timebase>
                      <ntsc>FALSE</ntsc>
                    </rate>
                    <start>0</start>
                    <end>${clips.reduce((total, clip) => total + clip.durationFrames, 0)}</end>
                    <in>0</in>
                    <out>${clips.reduce((total, clip) => total + clip.durationFrames, 0)}</out>
                  </clipitem>
                </track>`;

  clips.forEach((clip, index) => {
    const alignmentValue = clip.alignment === 'left' ? '0' : clip.alignment === 'right' ? '2' : '1';
    
    trackXML += `
                <track>
                  <clipitem id="clipitem-${index + 2}">
                    <name>${escapeXML(clip.name)}</name>
                    <duration>${clip.durationFrames}</duration>
                    <rate>
                      <timebase>${timebase}</timebase>
                      <ntsc>FALSE</ntsc>
                    </rate>
                    <start>${clip.startFrame}</start>
                    <end>${clip.startFrame + clip.durationFrames}</end>
                    <in>0</in>
                    <out>${clip.durationFrames}</out>
                    <file id="file-${index + 1}">
                      <name>${escapeXML(clip.name)}</name>
                      <pathurl>file://localhost/Title</pathurl>
                      <rate>
                        <timebase>${timebase}</timebase>
                        <ntsc>FALSE</ntsc>
                      </rate>
                      <duration>${clip.durationFrames}</duration>
                      <media>
                        <video>
                          <samplecharacteristics>
                            <rate>
                              <timebase>${timebase}</timebase>
                              <ntsc>FALSE</ntsc>
                            </rate>
                            <width>1920</width>
                            <height>1080</height>
                            <anamorphic>FALSE</anamorphic>
                            <pixelaspectratio>square</pixelaspectratio>
                            <fielddominance>none</fielddominance>
                            <colordepth>8</colordepth>
                          </samplecharacteristics>
                        </video>
                      </media>
                    </file>
                    <sourcetrack>
                      <mediatype>video</mediatype>
                    </sourcetrack>
                    <fielddominance>none</fielddominance>
                    <effect>
                      <name>Basic Title</name>
                      <effectid>Basic Title</effectid>
                      <effectcategory>Title</effectcategory>
                      <effecttype>title</effecttype>
                      <mediatype>video</mediatype>
                      <parameter>
                        <parameterid>str</parameterid>
                        <name>Source Text</name>
                        <value>${escapeXML(clip.text)}</value>
                      </parameter>
                      <parameter>
                        <parameterid>font</parameterid>
                        <name>Font</name>
                        <value>Arial</value>
                      </parameter>
                      <parameter>
                        <parameterid>size</parameterid>
                        <name>Size</name>
                        <value>${clip.fontSize}</value>
                      </parameter>
                      <parameter>
                        <parameterid>alignment</parameterid>
                        <name>Alignment</name>
                        <value>${alignmentValue}</value>
                      </parameter>
                      <parameter>
                        <parameterid>color</parameterid>
                        <name>Color</name>
                        <value>255 255 255</value>
                      </parameter>
                    </effect>
                  </clipitem>
                </track>`;
  });

  return trackXML;
};

const generateSimplePremiereXML = (credits, cardCredits, settings, projectName) => {
  const fps = Math.round(parseFloat(settings.fps) || 24);
  const durationFrames = Math.round(90 * fps);
  
  const allCreditsText = [
    ...cardCredits.map(card => {
      const layers = card.textLayers || [];
      return layers.map(layer => layer.content || '').join('\n');
    }),
    ...credits.map(credit => {
      if (credit.type === 'title') return credit.name || '';
      if (credit.type === 'dual') return `${credit.role || ''}\n${credit.name || ''}`;
      if (credit.type === 'single') return credit.name || '';
      if (credit.type === 'multiple') {
        const names = credit.name ? credit.name.split('\n').join('\n') : '';
        return `${credit.role || ''}\n${names}`;
      }
      return '';
    })
  ].filter(text => text.trim()).join('\n\n');

  return `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE xmeml>
<xmeml version="4">
  <project>
    <name>${escapeXML(projectName || 'Credits')}</name>
    <children>
      <bin>
        <name>Sequences</name>
        <children>
          <sequence id="sequence-1">
            <name>${escapeXML(projectName || 'Credits')} Sequence</name>
            <duration>${durationFrames}</duration>
            <rate>
              <timebase>${fps}</timebase>
              <ntsc>FALSE</ntsc>
            </rate>
            <timecode>
              <rate>
                <timebase>${fps}</timebase>
                <ntsc>FALSE</ntsc>
              </rate>
              <string>01:00:00:00</string>
              <frame>0</frame>
              <source>source</source>
              <displayformat>NDF</displayformat>
            </timecode>
            <in>0</in>
            <out>${durationFrames}</out>
            <media>
              <video>
                <format>
                  <samplecharacteristics>
                    <rate>
                      <timebase>${fps}</timebase>
                      <ntsc>FALSE</ntsc>
                    </rate>
                    <width>1920</width>
                    <height>1080</height>
                    <anamorphic>FALSE</anamorphic>
                    <pixelaspectratio>square</pixelaspectratio>
                    <fielddominance>none</fielddominance>
                    <colordepth>8</colordepth>
                  </samplecharacteristics>
                </format>
                <track>
                  <clipitem id="clipitem-1">
                    <name>Credits Title</name>
                    <duration>${durationFrames}</duration>
                    <rate>
                      <timebase>${fps}</timebase>
                      <ntsc>FALSE</ntsc>
                    </rate>
                    <start>0</start>
                    <end>${durationFrames}</end>
                    <in>0</in>
                    <out>${durationFrames}</out>
                    <file id="file-1">
                      <name>Credits Title</name>
                      <pathurl>file://localhost/Title</pathurl>
                      <rate>
                        <timebase>${fps}</timebase>
                        <ntsc>FALSE</ntsc>
                      </rate>
                      <duration>${durationFrames}</duration>
                    </file>
                    <effect>
                      <name>Legacy Title</name>
                      <effectid>Legacy Title</effectid>
                      <effectcategory>Title</effectcategory>
                      <effecttype>title</effecttype>
                      <mediatype>video</mediatype>
                      <parameter>
                        <parameterid>str</parameterid>
                        <name>Source Text</name>
                        <value>${escapeXML(allCreditsText)}</value>
                      </parameter>
                      <parameter>
                        <parameterid>font</parameterid>
                        <name>Font</name>
                        <value>Arial</value>
                      </parameter>
                      <parameter>
                        <parameterid>size</parameterid>
                        <name>Size</name>
                        <value>24</value>
                      </parameter>
                      <parameter>
                        <parameterid>alignment</parameterid>
                        <name>Alignment</name>
                        <value>1</value>
                      </parameter>
                      <parameter>
                        <parameterid>color</parameterid>
                        <name>Color</name>
                        <value>255 255 255</value>
                      </parameter>
                    </effect>
                  </clipitem>
                </track>
              </video>
            </media>
          </sequence>
        </children>
      </bin>
    </children>
  </project>
</xmeml>`;
};


const renderPreviewContent = () => {
    const commonPreviewProps = { 
        settings, 
        onCreditClick: handlePreviewClick, 
        isEditable: !isPlaying, 
        onUpdateCredit: updateCredit, 
        onEditingStateChange: setIsEditingContent,
        handleLogoDragStart, 
        activeLogoId, 
        onSelectLogo, 
        handleLogoDragOver, 
        handleLogoDrop, 
        handleLogoDragEnd,
        draggedLogoInfo, 
        dragOverLogoInfo,
        activeCreditId,
        // On-screen frame width: CreditPreview zooms its export-proportioned
        // layout stage to this, so the preview matches the render (WYSIWYG).
        previewPaneWidth: contentWidth
    };

    // --- START: RESTRUCTURED LOGIC ---
    let activeCard = null;
    const hasCards = cardCredits && cardCredits.length > 0;

    // PRIORITY 1: Handle a playing or paused card sequence. Always show the current card.
    if (playbackState === 'cards' && currentCardIndex > -1 && currentCardIndex < cardCredits.length) {
        activeCard = cardCredits[currentCardIndex];
    } 
    // PRIORITY 2: Handle the idle/stopped state. Show the selected card if one is active.
    // `|| null` matters: if activeCardId is stale (points at a card that was just deleted,
    // e.g. by a Batch Add replace), .find() returns undefined, which used to leave activeCard
    // as `undefined` and — because this was an `else if` — permanently skip Priority 3 below.
    // The preview would then fall through to the scroll view no matter what, showing nothing
    // useful. Normalizing to null and checking below lets a stale id fall through instead.
    else if (playbackState === 'idle' && activeCardId) {
        activeCard = cardCredits.find(c => c.id === activeCardId) || null;
    }
    // PRIORITY 3: Handle the idle state with no selection (or a selection that didn't
    // resolve to anything real). Default to the first card if appropriate.
    if (!activeCard && playbackState === 'idle' && !activeCreditId && hasCards && settings.cardSequence !== 'scroll-only') {
        activeCard = cardCredits[0];
    }

    // If we determined a card should be rendered, show the card view.
    if (activeCard) {
        const cardStyle = {
            position: 'absolute',
            top: `${activeCard.cardPositionY || 50}%`,
            left: `${activeCard.cardPositionX || 50}%`,
            transform: 'translate(-50%, -50%)',
            animationDuration: `${settings.cardAnimationDuration || 1}s`,
            transition: 'top 0.3s ease-out, left 0.3s ease-out, opacity 0.2s ease-in-out',
        };

        return (
            <div 
                className="absolute inset-0 p-8 flex items-center justify-center"
                style={{
                    transition: 'opacity 0.2s ease-in-out',
                    opacity: 1
                }}
            >
                <div 
                    // Apply animation class ONLY when actively playing. When paused, no class is applied, freezing it.
                    className={playbackState === 'cards' && isPlaying ? currentCardAnimationClass : ''} 
                    style={{
                        ...cardStyle,
                        opacity: (playbackState === 'cards' && isPlaying && !currentCardAnimationClass) ? 0 : 1,
                    }}
                    key={activeCard.id}
                >
                    <RenderErrorBoundary label="The card preview hit an error.">
                        <CreditPreview {...commonPreviewProps} credits={[]} cardCredits={[activeCard]} />
                    </RenderErrorBoundary>
                </div>
            </div>
        );
    }
    // --- END: RESTRUCTURED LOGIC ---

    // If no card is to be rendered, show the scroll view.
    return (
        <div 
            className="w-full h-full"
            style={{
                transition: 'opacity 0.2s ease-in-out',
                opacity: 1 // Opacity is handled by the parent container now
            }}
        >
            <div ref={scrollRef} className={`credit-scroll-container flex-1 flex flex-col items-center overflow-y-auto relative w-full h-full ${isPlaying ? 'animating-scroll' : ''}`}>
                <div style={{ height: '100vh', width: '100%', flexShrink: 0 }}></div>
                <div className="w-full flex flex-col items-center">
<RenderErrorBoundary label="The credits preview hit an error.">
<CreditPreview {...commonPreviewProps} credits={credits} cardCredits={[]} removeLogoFromGrid={removeLogoFromGrid} />
</RenderErrorBoundary>
                </div>
                <div style={{ height: '100vh', width: '100%', flexShrink: 0 }}></div>
            </div>
        </div>
    );
};

  const getExportButtonColor = () => { switch (exportNoticeStatus) { case 'processing': return 'bg-yellow-600 text-white hover:bg-yellow-700'; case 'ready': return 'bg-green-600 text-white hover:bg-green-700'; case 'failed': return 'bg-red-600 text-white hover:bg-red-700'; default: return 'bg-zinc-800 text-gray-300 hover:bg-zinc-700'; } };
  
    const FONT_FAMILY_OPTIONS = Object.keys({
        'arial': { name: 'Arial' }, 'courier-new': { name: 'Courier New' }, 'eb-garamond': { name: 'EB Garamond' },
        'futura': { name: 'Futura' }, 'garamond': { name: 'Garamond' }, 'georgia': { name: 'Georgia' },
        'helvetica': { name: 'Helvetica' }, 'helvetica-rounded': { name: 'Helvetica Rounded' }, 'optima': { name: 'Optima' },
        'palatino': { name: 'Palatino' }, 'poppins': { name: 'Poppins' }
    }).map(key => ({
        value: key, label: ({
        'arial': { name: 'Arial' }, 'courier-new': { name: 'Courier New' }, 'eb-garamond': { name: 'EB Garamond' },
        'futura': { name: 'Futura' }, 'garamond': { name: 'Garamond' }, 'georgia': { name: 'Georgia' },
        'helvetica': { name: 'Helvetica' }, 'helvetica-rounded': { name: 'Helvetica Rounded' }, 'optima': { name: 'Optima' },
        'palatino': { name: 'Palatino' }, 'poppins': { name: 'Poppins' }
    })[key].name
    })).concat((settings.customFonts || []).map(f => ({ value: f.name, label: f.name })));

const getExportNoticeButtonStyling = () => {
        switch (exportNoticeStatus) {
            case 'processing': return 'bg-yellow-900/50 border-yellow-700/50 text-yellow-200 hover:bg-yellow-900/70';
            case 'ready': return 'bg-green-900/50 border-green-700/50 text-green-200 hover:bg-green-900/70';
            case 'failed': return 'bg-red-900/50 border-red-700/50 text-red-200 hover:bg-red-900/70';
            default: return 'bg-blue-900/50 border-blue-700/50 text-blue-200 hover:bg-blue-900/70';
        }
    };

  return (
    <div className="flex h-screen bg-black font-poppins text-gray-100 overflow-hidden relative">     
        {/* Branded loading overlay — sits on top while the project doc loads, but the
            real tree underneath still mounts immediately and normally. It must NOT be
            an early `return` here: several effects in this component (e.g. the preview
            pane's ResizeObserver a few hundred lines up, which only runs once on mount)
            grab DOM refs the first time this tree renders. Swapping the whole tree out
            for a loader on that first mount — which is exactly what an early return did —
            meant those refs were never attached to anything, so things silently stayed
            broken (0×0 preview pane) even once the real data showed up. An overlay keeps
            mount timing identical to before; it just visually sits above everything else
            until projectData is ready.
        */}
        {!OFFLINE_MODE && !projectData && (
            <div className="absolute inset-0 z-[70]">
                <BrandedLoader message="Loading your project…" />
            </div>
        )}
        {/* READ-ONLY MODE BANNER */}
        {isReadOnly && (
            <div className="absolute top-0 left-0 right-0 z-50 bg-red-900/90 border-b-2 border-red-500 px-4 py-2 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Eye size={20} className="text-red-200" />
                    <span className="text-red-100 font-semibold">Read-Only Mode</span>
                    <span className="text-red-200 text-sm">— Project expired. View only.</span>
                </div>
                <button 
                    onClick={() => setCurrentPage('projects')}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-1 rounded-md text-sm font-semibold"
                >
                    Purchase New Project
                </button>
            </div>
        )}



     <style>{`
        body { font-family: 'Poppins', sans-serif; }
        .credit-scroll-container { -webkit-overflow-scrolling: touch; will-change: scroll-position; scroll-behavior: smooth; }
        .credit-scroll-container.animating-scroll { scroll-behavior: auto !important; }
        .credit-item { transform: translateZ(0); backface-visibility: hidden; }
        .editable-text-box {
            border: 1px dashed transparent;
            padding: 2px;
            margin: -3px; /* Balances both the border and padding */
            border-radius: 4px;
        }
       .editable-text-box:hover { 
            background-color: rgba(255, 255, 255, 0.05); 
            border-color: rgba(255, 255, 255, 0.2); 
        }
        .editable-text-box:focus { 
            background-color: rgba(255, 255, 255, 0.1); 
            border-color: rgba(255, 255, 255, 0.4); 
            outline: none; 
            box-shadow: 0 0 0 2px rgba(168, 85, 247, 0.5); 
        }
        @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
        @keyframes fade-out { from { opacity: 1; } to { opacity: 0; } }

        @keyframes slide-in-from-right { 
            from { transform: translate(calc(-50% + 50vw), -50%); opacity: 0; } 
            to { transform: translate(-50%, -50%); opacity: 1; } 
        }
        @keyframes slide-out-to-left {
            from { transform: translate(-50%, -50%); opacity: 1; }
            to { transform: translate(calc(-50% - 50vw), -50%); opacity: 0; }
        }

        @keyframes slide-up-in {
            from { transform: translate(-50%, -40%); opacity: 0; }
            to { transform: translate(-50%, -50%); opacity: 1; }
        }
        @keyframes slide-up-out {
            from { transform: translate(-50%, -50%); opacity: 1; }
            to { transform: translate(-50%, -60%); opacity: 0; }
        }
        
        @keyframes scale-in { 
            from { transform: translate(-50%, -50%) scale(0.8); opacity: 0; } 
            to { transform: translate(-50%, -50%) scale(1); opacity: 1; } 
        }
        @keyframes scale-out {
            from { transform: translate(-50%, -50%) scale(1); opacity: 1; }
            to { transform: translate(-50%, -50%) scale(1.2); opacity: 0; }
        }
        
        .animate-base { animation-fill-mode: forwards; animation-timing-function: ease-in-out; }
        .animate-fade-in { animation-name: fade-in; }
        .animate-fade-out { animation-name: fade-out; }
        .animate-slide-in { animation-name: slide-in-from-right; }
        .animate-slide-out { animation-name: slide-out-to-left; }
        .animate-slide-up { animation-name: slide-up-in; }
        .animate-slide-up-out { animation-name: slide-up-out; }
        .animate-scale-in { animation-name: scale-in; }
        .animate-scale-out { animation-name: scale-out; }

        ::-webkit-scrollbar { width: 8px; }
        ::-webkit-scrollbar-track { background: #27272a; border-radius: 10px; }
        ::-webkit-scrollbar-thumb { background: #52525b; border-radius: 10px; }
        ::-webkit-scrollbar-thumb:hover { background: #6b7280; }

    `}</style>
         <div className="flex h-full w-full">
            <div 
                onMouseEnter={() => scrollRef.current && scrollRef.current.blur()}
                className={`flex-shrink-0 bg-zinc-950 h-full flex flex-col transition-all duration-300 ease-in-out border-r border-zinc-900 overflow-hidden ${leftPanelVisible ? 'w-[22rem]' : 'w-0'}`}
            >
                {/* Clean, un-cluttered project metadata panel */}
                <div className="flex flex-col gap-2 p-3 pb-4 border-b border-zinc-900 bg-zinc-950">
                    <div className="px-1 py-1">
                        <input
                            type="text"
                            value={projectName}
                            onChange={(e) => setProjectName(e.target.value)}
                            onBlur={(e) => handleRenameProject(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.target.blur();
                                }
                            }}
                            className="text-sm font-semibold text-zinc-100 bg-transparent border-0 rounded w-full p-0 focus:ring-0 whitespace-nowrap overflow-hidden text-ellipsis placeholder:text-zinc-700"
                            placeholder="Untitled Project"
                            aria-label="Project Name"
                            disabled={isReadOnly}
                        />
                    </div>

                    {/* Minimal status/upgrade drawer container */}
                    {projectData?.isTrial && !isReadOnly && (
                        <UpgradeTrialBanner onUpgrade={handleTrialUpgrade} isUpgrading={isUpgrading} />
                    )}

                    {/* Consistent workspace system buttons */}
                    <div className="space-y-1">
                        <button 
                            onClick={() => setCurrentPage('projects')} 
                            className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800/60 rounded text-[11px] font-bold text-zinc-400 hover:text-white uppercase tracking-wider transition-colors"
                        >
                            <ChevronLeft size={12}/> Back to Projects
                        </button>
                        <button 
                            onClick={() => setShowExportNoticesModal(true)} 
                            className={`w-full flex items-center justify-center gap-2 px-3 py-2 bg-zinc-900 hover:bg-zinc-800 border rounded text-[11px] font-bold uppercase tracking-wider transition-colors ${getExportNoticeButtonStyling()}`}
                        >
                            <CloudDownload size={12}/> Render Queue
                        </button>

                        {/* The updated sleek Pro banner sits natively right here into the button stack */}
                        {projectData?.tier === 'indie' && (
                            <UpgradeToProBanner onUpgrade={handleUpgrade} isUpgrading={isUpgrading} />
                        )}
                    </div>
                </div>
                
                <div className="flex-grow overflow-y-auto p-4" ref={creditListRef}>
                    <div className="space-y-4">
                        <EnhancedCollapsibleSection title="Credits" icon={List} priority="high" defaultOpen={true} count={credits.length + cardCredits.length}>
                           <div className="relative mt-2">
                               <input type="text" placeholder="Search credits..." value={searchTerm} onChange={handleSearchChange} className="w-full p-2.5 pl-9 rounded-md border border-zinc-600 bg-zinc-800 text-gray-200 focus:ring-2 focus:ring-purple-500 text-sm" />
                               <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                           </div>
                        </EnhancedCollapsibleSection>
                        
                        <EnhancedCollapsibleSection title="Title Cards" icon={Film} priority="high" defaultOpen={true} count={filteredCardCredits.length}>
                           <CardCreditsDisplay addCredit={addCredit} cardCredits={filteredCardCredits} activeCardId={activeCardId} selectedCreditIds={selectedCreditIds} removeCredit={removeCredit} setActiveCardId={setActiveCardId} setActiveCreditId={setActiveCreditId} handleCreditSelect={handleCreditSelect} handleCardDragStart={handleCardDragStart} handleCardDragOver={handleCardDragOver} handleCardDrop={handleCardDrop} draggedCardItem={draggedCardItem} dragOverCardItem={dragOverCardItem} />
                        </EnhancedCollapsibleSection>
                        
                        <EnhancedCollapsibleSection title="Scrolling Credits" icon={List} priority="normal" defaultOpen={true} count={filteredCredits.length}>
                            <ScrollingCreditsDisplay addCredit={addCredit} credits={filteredCredits} cardCredits={cardCredits} activeCreditId={activeCreditId} selectedCreditIds={selectedCreditIds} handleCreditSelect={handleCreditSelect} removeCredit={removeCredit} updateCardCredit={updateCardCredit} setActiveCardId={setActiveCardId} setActiveCreditId={setActiveCreditId} handleCreditDragStart={handleCreditDragStart} handleCreditDragOver={handleCreditDragOver} handleCreditDrop={handleCreditDrop} draggedCreditId={draggedCreditId} dragOverCreditId={dragOverCreditId} settings={settings} showAddCreditDropdown={showAddCreditDropdown} setShowAddCreditDropdown={setShowAddCreditDropdown} addCreditDropdownRef={addCreditDropdownRef} setShowBatchAddModal={setShowBatchAddModal} setShowImportModal={setShowImportModal} isProjectEmpty={credits.length === 0 && cardCredits.length === 0} askCopilot={askCopilot} />
                        </EnhancedCollapsibleSection>

                        <EnhancedCollapsibleSection title="Asset Library" icon={Images} priority="normal" defaultOpen={false} count={libraryAssets.length}>
                            <div className="space-y-3 mt-2">
                                <p className="text-[11px] text-zinc-500 leading-relaxed">
                                    Logos and fonts you've uploaded before, ready to reuse in any project — no re-upload needed.
                                    {inspectorActiveCredit?.type === 'logo' && (
                                        <span className="text-purple-400"> Click a logo below to apply it to the selected credit.</span>
                                    )}
                                </p>

                                <label className="flex items-center gap-2 text-[11px] text-zinc-400 cursor-pointer select-none">
                                    <input
                                        type="checkbox"
                                        checked={autoSaveToLibrary}
                                        onChange={(e) => setAutoSaveToLibrary(e.target.checked)}
                                        className="rounded border-zinc-600 bg-zinc-800"
                                    />
                                    Auto-save new uploads to library
                                </label>

                                <button
                                    onClick={scanProjectAssetsIntoLibrary}
                                    disabled={isScanningAssets}
                                    className="w-full flex items-center justify-center gap-1.5 py-1.5 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded text-[11px] font-medium text-zinc-300 transition-colors disabled:opacity-50"
                                    title="Pull this project's existing logos & fonts into your library"
                                >
                                    {isScanningAssets ? <Loader2 size={12} className="animate-spin" /> : <CloudUpload size={12} />}
                                    {isScanningAssets ? 'Scanning…' : "Add this project's assets"}
                                </button>

                                {pendingLibraryUploads.size > 0 && (
                                    <p className="text-[10px] text-zinc-600 flex items-center gap-1.5"><Loader2 size={11} className="animate-spin" /> Saving to library…</p>
                                )}

                                {libraryAssets.filter(a => a.type === 'logo').length > 0 && (
                                    <div>
                                        <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-600 mb-1.5">Logos</p>
                                        <div className="grid grid-cols-3 gap-2">
                                            {libraryAssets.filter(a => a.type === 'logo').map(asset => (
                                                <div key={asset.id} className="relative group">
                                                    <button
                                                        onClick={() => applyLibraryLogo(asset)}
                                                        title={`Use "${asset.name}"`}
                                                        className="w-full aspect-square bg-zinc-800 border border-zinc-700 hover:border-purple-500 rounded-md p-1.5 flex items-center justify-center transition-colors"
                                                    >
                                                        <img src={asset.url} alt={asset.name} className="max-w-full max-h-full object-contain pointer-events-none" />
                                                    </button>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); deleteFromLibrary(asset); }}
                                                        title="Remove from library"
                                                        className="absolute -top-1.5 -right-1.5 bg-zinc-900 border border-zinc-700 rounded-full p-0.5 text-zinc-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                                                    >
                                                        <X size={10} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {libraryAssets.filter(a => a.type === 'font').length > 0 && (
                                    <div>
                                        <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-600 mb-1.5 mt-3">Fonts</p>
                                        <div className="space-y-1">
                                            {libraryAssets.filter(a => a.type === 'font').map(asset => (
                                                <div key={asset.id} className="flex items-center justify-between gap-2 bg-zinc-800/60 border border-zinc-700/60 rounded-md px-2.5 py-1.5 group">
                                                    <button onClick={() => applyLibraryFont(asset)} className="flex-1 text-left text-xs text-zinc-300 hover:text-white truncate">
                                                        {asset.name}
                                                    </button>
                                                    <button onClick={() => deleteFromLibrary(asset)} title="Remove from library" className="text-zinc-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                                                        <X size={12} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {libraryAssets.length === 0 && (
                                    <p className="text-[11px] text-zinc-600 text-center py-3">Nothing here yet — upload a logo or font and it'll show up for reuse next time.</p>
                                )}
                            </div>
                        </EnhancedCollapsibleSection>
                    </div>
                </div>
            </div>
                               
            <div className="flex-1 flex flex-col bg-black relative overflow-hidden items-center justify-center">
                <button onClick={() => setLeftPanelVisible(!leftPanelVisible)} className="absolute top-4 left-4 z-20 p-3 bg-zinc-800 bg-opacity-75 rounded-full shadow-lg text-gray-300 hover:bg-zinc-700">
                    {leftPanelVisible ? <ChevronsLeft size={20}/> : <ChevronsRight size={20}/>}
                </button>
                <button onClick={() => setRightPanelVisible(!rightPanelVisible)} className="absolute top-4 right-4 z-20 p-3 bg-zinc-800 bg-opacity-75 rounded-full shadow-lg text-gray-300 hover:bg-zinc-700">
                    {rightPanelVisible ? <ChevronsRight size={20}/> : <ChevronsLeft size={20}/>}
                </button>

                <div onMouseEnter={() => scrollRef.current && scrollRef.current.blur()}>
                    <TopMenuBar
                        ref={topMenuRef}
                        togglePlayPause={togglePlayPause}
                        isPlaying={isPlaying}
                        rewindAnimation={rewindAnimation}
                        addCredit={addCredit}
                        setShowBatchAddModal={setShowBatchAddModal}
                        setShowImportModal={setShowImportModal}
                        settings={settings}
                        setSettings={setSettings}
                        creditsCount={credits.length}
                        onExportClick={handleExportButtonClick}
                        showSafeGuides={showSafeGuides}
                        setShowSafeGuides={setShowSafeGuides}
                        showMessageBox={showMessageBox}
                        fontFamilyOptions={FONT_FAMILY_OPTIONS}
                        handleDefaultFamilyChange={handleDefaultFamilyChange}
                        handleSuggestDuration={handleSuggestDuration}
                        setShowSettingsModal={setShowSettingsModal}
                        setShowFindReplaceModal={setShowFindReplaceModal}
                        exportNoticeStatus={exportNoticeStatus}
                        showAssistant={showAssistant}
                        setShowAssistant={setShowAssistant}
                    />
                </div>
                <div ref={previewRef} className="w-full h-full flex items-center justify-center overflow-hidden relative">
                  <div ref={contentAreaRef} className="relative flex-shrink-0 flex flex-col items-center justify-center overflow-hidden transition-opacity duration-200" style={{ backgroundColor: settings.backgroundColor, width: `${contentWidth}px`, height: `${contentHeight}px`, opacity: previewOpacity }}>
                    {audioInfo.url && <audio ref={audioRef} src={audioInfo.url} />}
                    {renderPreviewContent()}

                    {projectData?.isTrial && (
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20">
                                <span 
                                    className="text-5xl md:text-7xl font-bold" 
                                    style={{
                                        color: 'rgba(255, 255, 255, 0.08)',
                                        transform: 'rotate(-15deg)',
                                        textShadow: '0 0 10px rgba(0,0,0,0.5)'
                                    }}
                                >
                                    BLACKBOXcr Trial
                                </span>
                            </div>
                        )}

                    {showSafeGuides && (
                        <div className="absolute inset-0 pointer-events-none z-10">
                            <div className="absolute top-1/2 left-0 w-full h-px bg-red-500 opacity-50"></div>
                            <div className="absolute left-1/2 top-0 h-full w-px bg-red-500 opacity-50"></div>
                            <div className="absolute border-2 border-dashed border-blue-400 opacity-70" style={{ top: '5%', left: '5%', width: '90%', height: '90%' }}></div>
                            <div className="absolute border-2 border-dashed border-yellow-400 opacity-70" style={{ top: '10%', left: '10%', width: '80%', height: '80%' }}></div>
                        </div>
                    )}
                  </div>
                </div>
            </div>
            <div 
                onMouseEnter={() => scrollRef.current && scrollRef.current.blur()}
                className={`flex-shrink-0 bg-zinc-900 h-full overflow-hidden transition-all duration-300 ease-in-out border-l border-zinc-800 ${rightPanelVisible ? 'w-[24rem]' : 'w-0'}`}
            >
                <div className="w-[24rem] h-full">
                    <InspectorPanel activeCredit={inspectorActiveCredit} activeTab={inspectorTab} setActiveTab={setInspectorTab} handleSuggestDuration={handleSuggestDuration} addSpacerToGroup={addSpacerToGroup} activeLogoId={activeLogoId} setActiveLogoId={setActiveLogoId} handleLogoDragOver={handleLogoDragOver} handleLogoDrop={handleLogoDrop} handleLogoDragEnd={handleLogoDragEnd} draggedLogoInfo={draggedLogoInfo} dragOverLogoInfo={dragOverLogoInfo}updateCredit={updateCredit} removeCredit={removeCredit} settings={settings} setSettings={setSettings} customFonts={settings.customFonts || []} addLogoToGroup={addLogoToGroup} handleGroupedLogoUpload={handleLogoUpload} removeLogoFromGrid={removeLogoFromGrid} addTextItemToGroup={addTextItemToGroup} removeTextItemFromGroup={removeTextItemFromGroup} batchLogoUpload={batchLogoUpload} handleLogoUpload={handleLogoUpload} handleGridLogoUpload={handleGridLogoUpload} handleFontUpload={handleFontUpload} copySettings={copySettings} pasteSettings={pasteSettings} copiedSettings={copiedSettings} addTextLayer={addTextLayer} updateTextLayer={updateTextLayer} removeTextLayer={removeTextLayer} reorderTextLayers={reorderTextLayers} copyCardStyle={copyCardStyle} pasteCardStyle={pasteCardStyle} copiedCardStyle={copiedCardStyle} selectedCreditIds={selectedCreditIds} bulkDeleteSelectedCredits={bulkDeleteSelectedCredits} handleLogoDragStart={handleLogoDragStart} handleDefaultFamilyChange={handleDefaultFamilyChange} openLogoBoard={openLogoBoard} openSongBoard={openSongBoard} resetAllCardDurations={resetAllCardDurations} />
                </div>
            </div>
         </div>

         <ReplenishModal 
                isOpen={showReplenishModal}
                onClose={() => setShowReplenishModal(false)}
                onChoose={handlePurchase}
                isProcessing={isProcessingPurchase}
            />

            <FindAndReplaceModal
            isOpen={showFindReplaceModal}
            onClose={() => setShowFindReplaceModal(false)}
            credits={credits}
            cardCredits={cardCredits}
            onGoTo={goToFindMatch}
            onReplaceOne={handleReplaceOneMatch}
            onReplaceAll={handleReplaceAll}
            focusSignal={findFocusSignal}
        />

        {/* AI Assistant Panel */}
<AssistantPanel 
    isOpen={showAssistant} 
    onClose={() => setShowAssistant(false)} 
    seedPrompt={assistantSeedPrompt}
    onSeedConsumed={() => setAssistantSeedPrompt(null)}
    currentContext={{
    page: 'editor',
    settings: settings,
    creditCount: credits.length,
    hasCardCredits: cardCredits.length > 0,
    projectTier: projectData?.tier || null,
    // Send a small slice of the first credit to show the AI the data structure
    exampleCredit: credits[0] 
}}
    onNavigateAction={(action) => {
        switch (action) {
            case 'projects': setCurrentPage('projects'); break;
            case 'import': setShowImportModal(true); break;
            case 'batch-add': setShowBatchAddModal(true); break;
            case 'export': handleExportButtonClick(); break;
            case 'settings': setShowSettingsModal(true); break;
            case 'find-replace': setShowFindReplaceModal(true); break;
            case 'render-queue': setShowExportNoticesModal(true); break;
            case 'add-credit': topMenuRef.current?.openMenu('add'); break;
            case 'typography': topMenuRef.current?.openMenu('typography'); break;
            case 'sequence': topMenuRef.current?.openMenu('sequence'); break;
            case 'timing': topMenuRef.current?.openMenu('timing'); break;
            case 'shortcuts': showMessageBox("Keyboard Shortcuts", "Click the Info icon (ⓘ) in the toolbar to view the full keyboard shortcut reference.", "info"); break;
            default: break;
        }
        setShowAssistant(false);
    }}
/>
        
        <SettingsModal
            isOpen={showSettingsModal}
            onClose={() => setShowSettingsModal(false)}
            audioInfo={audioInfo}
            handleAudioUpload={handleAudioUpload}
            syncScrollToMusic={syncScrollToMusic}
            saveProjectFile={saveProjectFile}
            saveAsTemplate={saveAsTemplate}
            setShowTemplatesModal={setShowTemplatesModal}
            selectedDisplayLanguage={selectedDisplayLanguage}
            setSelectedDisplayLanguage={setSelectedDisplayLanguage}
        />
        {showBatchAddModal && (() => {
            // Only existing section headers and grouped credits make sense as insertion
            // landmarks — not every individual dual credit, which would be an unusable
            // list on a 100+ row project.
            const batchInsertionTargets = credits
                .map((credit, idx) => ({ credit, idx }))
                .filter(({ credit }) => credit.type === 'single' || credit.type === 'title' || credit.type === 'multiple')
                .map(({ credit, idx }) => ({
                    id: credit.id,
                    idx,
                    type: credit.type,
                    label: credit.type === 'multiple'
                        ? (credit.role || (credit.multipleMode === 'song' ? 'Song Credits' : credit.name) || 'Untitled Group')
                        : (credit.name || 'Untitled Header'),
                }));
            const selectedTarget = batchInsertionTargets.find(t => String(t.id) === String(batchInsertTargetId));

            // --- Derived state for the redesigned Batch Add surface -------------
            const isCards = batchAddType === 'main-title';
            const rowTypes = getBatchRowTypes(batchPreviewRows);
            const countType = (t) => rowTypes.filter(x => x === t).length;
            const pairedCount = countType('dual');
            const sectionCount = countType('single') + countType('title');
            const groupCount = countType('group-header');
            const filledRows = batchPreviewRows.filter(r => (r.role || '').trim() || (r.name || '').trim());
            const filledCards = batchCardPreviewCards.filter(c => c.layers.some(l => (l.content || '').trim()));
            const commitCount = isCards ? filledCards.length : filledRows.length;
            const lineCount = batchAddText.trim() ? batchAddText.trim().split('\n').filter(l => l.trim()).length : 0;

            const loadBatchSample = () => setBatchAddText(isCards ? BATCH_SAMPLE_CARDS : BATCH_SAMPLE_SCROLL);
            const appendBatchText = (txt) => setBatchAddText(prev => (prev && prev.trim()) ? `${prev.replace(/\s+$/, '')}\n${txt}` : txt);
            const pasteFromClipboard = async () => {
                // Convenience only — if the browser denies clipboard read, the user
                // can still just focus the textarea and hit Cmd/Ctrl+V as normal.
                try {
                    const txt = await navigator.clipboard.readText();
                    if (txt && txt.trim()) appendBatchText(txt);
                } catch (err) { /* no clipboard permission — silently ignore */ }
            };
            // Dropping a .txt/.csv/.tsv straight onto the paste pane means a user
            // never has to open a spreadsheet just to copy out of it.
            const handleBatchFileDrop = (e) => {
                e.preventDefault();
                const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
                if (!file) return;
                const reader = new FileReader();
                reader.onload = (ev) => {
                    const raw = String((ev.target && ev.target.result) || '');
                    // A dropped CSV becomes Role<TAB>Name by splitting on the FIRST
                    // comma only — names like "Smith, John" survive intact, and any
                    // line that already has a tab is left completely alone.
                    const txt = raw.split(/\r?\n/).map(line => {
                        if (!line.trim() || line.includes('\t')) return line;
                        const i = line.indexOf(',');
                        if (i === -1) return line;
                        return `${line.slice(0, i).trim()}\t${line.slice(i + 1).replace(/^\s*"|"\s*$/g, '').trim()}`;
                    }).join('\n');
                    if (txt.trim()) appendBatchText(txt);
                };
                reader.readAsText(file);
            };

            // Hands the Copilot a question scoped to whichever mode is active,
            // instead of just opening an empty chat panel next to a confused user.
            const askCopilotAboutBatch = () => askCopilot(isCards
                ? 'I want to use Batch Add to create title cards. How should I format the list I paste, and what does each line become?'
                : 'How should I format my credit list for Batch Add? Explain how sections, multi-name groups like Stunts, and role/name pairs should be laid out.');

            const statPill = (value, label, cls) => (
                <span className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${cls}`}>
                    <span className="tabular-nums">{value}</span> {label}
                </span>
            );

            return (
            <div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4 backdrop-blur-md sm:p-6"
                onKeyDown={(e) => { if (e.key === 'Escape') setShowBatchAddModal(false); }}
            >
                <div className={`relative flex h-[92vh] w-full max-w-[1500px] flex-col overflow-hidden rounded-2xl border border-white/10 bg-zinc-950 text-zinc-200 shadow-[0_50px_140px_-30px_rgba(0,0,0,0.95)] transition-all duration-300 ${showAssistant ? 'xl:mr-[26rem]' : ''}`}>
                    {/* Ambient studio glow — purely decorative */}
                    <div className="pointer-events-none absolute -top-32 left-1/2 h-64 w-[42rem] -translate-x-1/2 rounded-full bg-purple-600/20 blur-[90px]" />
                    <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-purple-400/50 to-transparent" />

                    {/* ---------- HEADER ---------- */}
                    <div className="relative z-10 flex items-start justify-between gap-6 px-8 pb-5 pt-7">
                        <div className="flex items-start gap-4">
                            <div className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-purple-500/30 bg-purple-500/10 text-purple-300">
                                <Rows4 size={20} />
                            </div>
                            <div>
                                <div className="flex flex-wrap items-center gap-2.5">
                                    <h3 className="text-[22px] font-semibold tracking-tight text-white">Paste Your Credit List</h3>
                                    <span className="rounded-full border border-purple-500/30 bg-purple-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-purple-300">Fastest way in</span>
                                </div>
                                <p className="mt-1.5 max-w-2xl text-[13px] leading-relaxed text-zinc-400">
                                    Straight from a spreadsheet, script, or notes — or drop a <span className="text-zinc-300">.txt</span> / <span className="text-zinc-300">.csv</span> on the left. Section headers, grouped names, and role/name pairs are detected for you. Nothing enters your project until you commit.
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => setShowBatchAddModal(false)}
                            title="Close (Esc)"
                            className="rounded-lg p-2 text-zinc-500 transition-colors hover:bg-white/5 hover:text-zinc-200"
                        >
                            <X size={18} />
                        </button>
                    </div>

                    {/* ---------- MODE SWITCH ---------- */}
                    <div className="relative z-10 px-8 pb-5">
                        <div className="inline-flex gap-1 rounded-xl border border-white/10 bg-black/40 p-1">
                            {[
                                { v: 'dual', icon: List, label: 'Scrolling Credits', hint: 'Smart auto-detect' },
                                { v: 'main-title', icon: Film, label: 'Title Cards', hint: 'One card per line' },
                            ].map(opt => {
                                const Icon = opt.icon;
                                const on = batchAddType === opt.v;
                                return (
                                    <button
                                        key={opt.v}
                                        onClick={() => setBatchAddType(opt.v)}
                                        className={`flex items-center gap-2.5 rounded-lg px-4 py-2.5 text-left transition-all ${on ? 'bg-gradient-to-b from-purple-600 to-purple-700 text-white shadow-lg shadow-purple-950/50' : 'text-zinc-400 hover:bg-white/5 hover:text-zinc-200'}`}
                                    >
                                        <Icon size={16} className={on ? 'text-white' : 'text-zinc-500'} />
                                        <span className="leading-tight">
                                            <span className="block text-[13px] font-semibold">{opt.label}</span>
                                            <span className={`block text-[10px] uppercase tracking-wider ${on ? 'text-purple-200/80' : 'text-zinc-600'}`}>{opt.hint}</span>
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* ---------- BODY ---------- */}
                    <div className="relative z-10 grid min-h-0 flex-1 border-t border-white/5 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">

                        {/* LEFT — paste pane */}
                        <div className="flex min-h-0 flex-col border-white/5 p-6 lg:border-r">
                            <div className="mb-3 flex items-center justify-between gap-3">
                                <span className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.15em] text-zinc-500">
                                    <ClipboardPaste size={13} /> Your List
                                </span>
                                <div className="flex items-center gap-1">
                                    <button onClick={loadBatchSample} className="flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-semibold text-purple-300 transition-colors hover:bg-purple-500/10 hover:text-purple-200">
                                        <Sparkles size={12} /> Try a sample
                                    </button>
                                    <button onClick={pasteFromClipboard} className="flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-semibold text-zinc-400 transition-colors hover:bg-white/5 hover:text-zinc-200">
                                        <ClipboardCheck size={12} /> Paste
                                    </button>
                                    {batchAddText.trim() && (
                                        <button onClick={() => setBatchAddText('')} className="rounded-md px-2 py-1 text-[11px] font-semibold text-zinc-500 transition-colors hover:bg-white/5 hover:text-red-300">
                                            Clear
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="relative flex min-h-0 flex-1" onDrop={handleBatchFileDrop} onDragOver={(e) => e.preventDefault()}>
                                <textarea
                                    value={batchAddText}
                                    onChange={(e) => setBatchAddText(e.target.value)}
                                    placeholder={"Directed by\tAVA GREEN\nWritten by\tELIJAH CARTER\n\nCAST\nMARCUS\tJASON MITCHELL\n\nStunts\n\tRAY OKONKWO\n\tMIA HALVORSEN"}
                                    spellCheck={false}
                                    className="w-full flex-1 resize-none rounded-xl border border-white/10 bg-black/60 p-4 font-mono text-[13px] leading-relaxed text-zinc-100 outline-none transition placeholder:text-zinc-700 focus:border-purple-500/60 focus:bg-black/80 focus:ring-1 focus:ring-purple-500/40"
                                />
                            </div>

                            <div className="mt-2.5 flex items-center justify-between text-[11px] text-zinc-600">
                                <span className="tabular-nums">{lineCount} line{lineCount === 1 ? '' : 's'}</span>
                                <span>A Tab — or 2+ spaces — splits Role from Name</span>
                            </div>

                            {/* Inline format guidance: the thing that was missing before */}
                            <div className="mt-3 grid grid-cols-1 gap-px overflow-hidden rounded-xl border border-white/5 bg-white/[0.03] sm:grid-cols-3">
                                {[
                                    { k: 'Role ⇥ Name', v: 'Paired credit' },
                                    { k: 'Name only', v: 'Section divider' },
                                    { k: 'Role, then names', v: 'Multi-name group' },
                                ].map(h => (
                                    <div key={h.k} className="bg-zinc-950/60 px-3 py-2.5">
                                        <p className="font-mono text-[11px] text-zinc-300">{h.k}</p>
                                        <p className="mt-0.5 text-[10px] uppercase tracking-wider text-zinc-600">{h.v}</p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* RIGHT — live, editable preview */}
                        <div className="flex min-h-0 flex-col bg-black/40 p-6">
                            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-zinc-500">Preview</span>
                                    {!isCards && batchPreviewRows.length > 0 && (
                                        <>
                                            {pairedCount > 0 && statPill(pairedCount, 'paired', 'bg-blue-500/15 text-blue-300')}
                                            {sectionCount > 0 && statPill(sectionCount, 'sections', 'bg-purple-500/15 text-purple-300')}
                                            {groupCount > 0 && statPill(groupCount, 'groups', 'bg-amber-500/15 text-amber-300')}
                                        </>
                                    )}
                                    {isCards && batchCardPreviewCards.length > 0 && statPill(batchCardPreviewCards.length, batchCardPreviewCards.length === 1 ? 'card' : 'cards', 'bg-yellow-500/15 text-yellow-300')}
                                </div>
                                <div className="flex items-center gap-1">
                                    <button
                                        onClick={() => setReverseBatchOrder(v => !v)}
                                        disabled={batchAddText.trim() === ''}
                                        title="Flip the order — useful if your list is written for end credits but you're using it at the front (or vice versa)"
                                        className={`flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-semibold transition-colors disabled:cursor-not-allowed disabled:text-zinc-700 disabled:hover:bg-transparent ${reverseBatchOrder ? 'bg-purple-500/15 text-purple-300 hover:bg-purple-500/20' : 'text-zinc-400 hover:bg-white/5 hover:text-zinc-200'}`}
                                    >
                                        <ArrowRightLeft size={12} className="rotate-90" /> Reverse Order
                                    </button>
                                    {!isCards ? (
                                        <>
                                            <button onClick={swapBatchColumns} disabled={batchPreviewRows.length === 0} className="flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-semibold text-zinc-400 transition-colors hover:bg-white/5 hover:text-zinc-200 disabled:cursor-not-allowed disabled:text-zinc-700 disabled:hover:bg-transparent">
                                                <ArrowRightLeft size={12} /> Swap Role / Name
                                            </button>
                                            <button onClick={addBlankBatchPreviewRow} className="flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-semibold text-purple-300 transition-colors hover:bg-purple-500/10 hover:text-purple-200">
                                                <ListPlus size={12} /> Add Row
                                            </button>
                                        </>
                                    ) : (
                                        <button onClick={addBlankBatchPreviewCard} className="flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-semibold text-purple-300 transition-colors hover:bg-purple-500/10 hover:text-purple-200">
                                            <ListPlus size={12} /> Add Card
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Scrolling-credit rows */}
                            {!isCards && (
                                <div className="custom-scrollbar min-h-0 flex-1 overflow-y-auto rounded-xl border border-white/10 bg-zinc-950/80">
                                    {batchPreviewRows.length === 0 ? (
                                        <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
                                            <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-white/5 bg-white/[0.03] text-zinc-600">
                                                <Wand2 size={20} />
                                            </div>
                                            <p className="max-w-xs text-[13px] leading-relaxed text-zinc-500">
                                                Paste on the left and every row lands here — typed, editable, and yours to correct before a single credit is created.
                                            </p>
                                            <button onClick={loadBatchSample} className="flex items-center gap-1.5 rounded-lg border border-purple-500/30 bg-purple-500/10 px-3 py-1.5 text-[11px] font-semibold text-purple-300 transition-colors hover:bg-purple-500/20">
                                                <Sparkles size={12} /> Load a sample list
                                            </button>
                                            <button onClick={askCopilotAboutBatch} className="mt-1 text-[11px] font-medium text-zinc-500 underline decoration-zinc-700 underline-offset-4 transition-colors hover:text-purple-300 hover:decoration-purple-500/60">
                                                Not sure how to format yours? Ask the Copilot
                                            </button>
                                        </div>
                                    ) : (
                                        <table className="w-full">
                                            <thead>
                                                <tr className="sticky top-0 z-10 border-b border-white/10 bg-zinc-900/95 backdrop-blur">
                                                    <th className="w-1/3 p-2 pl-3 text-left text-[10px] font-bold uppercase tracking-wider text-zinc-500">Role</th>
                                                    <th className="w-1/3 p-2 text-left text-[10px] font-bold uppercase tracking-wider text-zinc-500">Name</th>
                                                    <th className="p-2 text-left text-[10px] font-bold uppercase tracking-wider text-zinc-500">Type</th>
                                                    <th className="w-14 p-2"></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {batchPreviewRows.map((row, idx) => {
                                                    const effectiveType = rowTypes[idx];
                                                    const meta = BATCH_ROW_BADGE_META[effectiveType] || BATCH_ROW_BADGE_META.empty;
                                                    const isMember = effectiveType === 'group-member';
                                                    return (
                                                        <tr key={row.id} className="group border-b border-white/5 transition-colors last:border-b-0 hover:bg-white/[0.03]">
                                                            <td className="w-1/3 p-1.5 pl-3">
                                                                <input
                                                                    value={row.role}
                                                                    onChange={(e) => updateBatchPreviewRow(row.id, 'role', e.target.value)}
                                                                    placeholder="Role"
                                                                    className={`w-full rounded-md border border-transparent bg-transparent p-1.5 text-[13px] text-zinc-200 outline-none transition placeholder:text-zinc-700 hover:border-white/10 focus:border-purple-500/60 focus:bg-black/60 ${isMember ? 'pl-5' : ''}`}
                                                                />
                                                            </td>
                                                            <td className="w-1/3 p-1.5">
                                                                <input
                                                                    value={row.name}
                                                                    onChange={(e) => updateBatchPreviewRow(row.id, 'name', e.target.value)}
                                                                    placeholder="Name"
                                                                    className="w-full rounded-md border border-transparent bg-transparent p-1.5 text-[13px] text-zinc-200 outline-none transition placeholder:text-zinc-700 hover:border-white/10 focus:border-purple-500/60 focus:bg-black/60"
                                                                />
                                                            </td>
                                                            <td className="whitespace-nowrap p-1.5">
                                                                <select
                                                                    value={row.manualType || 'auto'}
                                                                    onChange={(e) => updateBatchPreviewRow(row.id, 'manualType', e.target.value === 'auto' ? null : e.target.value)}
                                                                    title="Auto-detected — change it if this row is wrong"
                                                                    className={`cursor-pointer rounded-md border-0 py-1 pl-2 pr-1 text-[10px] font-bold uppercase tracking-wide outline-none ${meta.className}`}
                                                                >
                                                                    <option value="auto">Auto: {meta.label}</option>
                                                                    <option value="dual">Role + Name</option>
                                                                    <option value="single">Section Divider</option>
                                                                    <option value="title">Header</option>
                                                                    <option value="group-header">Multiname Header</option>
                                                                    <option value="group-member">Grouped Below</option>
                                                                </select>
                                                            </td>
                                                            <td className="w-14 whitespace-nowrap p-1.5 text-right">
                                                                <button onClick={() => insertBlankBatchPreviewRowAfter(row.id)} title="Insert row below" className="rounded p-1 text-zinc-600 opacity-0 transition hover:bg-white/5 hover:text-purple-300 group-hover:opacity-100">
                                                                    <Plus size={13} />
                                                                </button>
                                                                <button onClick={() => removeBatchPreviewRow(row.id)} title="Remove row" className="rounded p-1 text-zinc-600 opacity-0 transition hover:bg-white/5 hover:text-red-400 group-hover:opacity-100">
                                                                    <X size={13} />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            )}

                            {/* Title-card stack */}
                            {isCards && (
                                <div className="custom-scrollbar min-h-0 flex-1 space-y-3 overflow-y-auto rounded-xl border border-white/10 bg-zinc-950/80 p-3">
                                    {batchCardPreviewCards.length === 0 ? (
                                        <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
                                            <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-white/5 bg-white/[0.03] text-zinc-600">
                                                <Film size={20} />
                                            </div>
                                            <p className="max-w-xs text-[13px] leading-relaxed text-zinc-500">
                                                Each line becomes one title card, stacked here the way it will actually render.
                                            </p>
                                            <button onClick={loadBatchSample} className="flex items-center gap-1.5 rounded-lg border border-purple-500/30 bg-purple-500/10 px-3 py-1.5 text-[11px] font-semibold text-purple-300 transition-colors hover:bg-purple-500/20">
                                                <Sparkles size={12} /> Load a sample list
                                            </button>
                                            <button onClick={askCopilotAboutBatch} className="mt-1 text-[11px] font-medium text-zinc-500 underline decoration-zinc-700 underline-offset-4 transition-colors hover:text-purple-300 hover:decoration-purple-500/60">
                                                Not sure how to format yours? Ask the Copilot
                                            </button>
                                        </div>
                                    ) : (
                                        batchCardPreviewCards.map((card, cIdx) => (
                                            <div key={card.id} className="group rounded-xl border border-white/10 bg-gradient-to-b from-zinc-900/80 to-zinc-950 p-3 transition-colors hover:border-white/20">
                                                <div className="flex flex-col items-center gap-1.5 py-3">
                                                    {card.layers.map((layer, lIdx) => {
                                                        const style = CARD_LAYER_STYLE_DEFAULTS[layer.style] || CARD_LAYER_STYLE_DEFAULTS.main;
                                                        return (
                                                            <div key={layer.id} className="flex w-full max-w-sm items-center gap-1.5">
                                                                <div className="flex flex-col">
                                                                    <button onClick={() => moveBatchPreviewCardLayer(card.id, layer.id, -1)} disabled={lIdx === 0} title="Move up" className="leading-none text-zinc-700 transition hover:text-purple-300 disabled:opacity-20 disabled:hover:text-zinc-700">
                                                                        <ArrowUp size={11} />
                                                                    </button>
                                                                    <button onClick={() => moveBatchPreviewCardLayer(card.id, layer.id, 1)} disabled={lIdx === card.layers.length - 1} title="Move down" className="leading-none text-zinc-700 transition hover:text-purple-300 disabled:opacity-20 disabled:hover:text-zinc-700">
                                                                        <ArrowDown size={11} />
                                                                    </button>
                                                                </div>
                                                                <input
                                                                    value={layer.content}
                                                                    onChange={(e) => updateBatchPreviewCardLayer(card.id, layer.id, e.target.value)}
                                                                    placeholder={layer.style === 'main' ? 'Name' : 'Role'}
                                                                    style={{ fontWeight: style.fontWeight, fontStyle: style.fontStyle, color: style.color, fontSize: layer.style === 'main' ? '16px' : '11px' }}
                                                                    className="flex-1 rounded-md border border-transparent bg-transparent px-2 py-1 text-center outline-none transition placeholder:text-zinc-700 hover:border-white/10 focus:border-purple-500/60 focus:bg-black/60"
                                                                />
                                                                <button onClick={() => removeLayerFromBatchPreviewCard(card.id, layer.id)} title="Remove layer" className="rounded p-0.5 text-zinc-700 opacity-0 transition hover:text-red-400 group-hover:opacity-100">
                                                                    <X size={12} />
                                                                </button>
                                                            </div>
                                                        );
                                                    })}
                                                    <div className="mt-1 flex items-center gap-3">
                                                        <button onClick={() => addLayerToBatchPreviewCard(card.id, 'subtitle')} className="text-[10px] font-semibold text-purple-300/70 transition-colors hover:text-purple-200">+ Role Line</button>
                                                        <button onClick={() => addLayerToBatchPreviewCard(card.id, 'main')} className="text-[10px] font-semibold text-purple-300/70 transition-colors hover:text-purple-200">+ Name Line</button>
                                                    </div>
                                                </div>
                                                <div className="mt-1 flex items-center justify-between border-t border-white/5 pt-2">
                                                    <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-700">Card {cIdx + 1}</span>
                                                    <div className="flex items-center gap-3">
                                                        <button onClick={() => insertBlankBatchPreviewCardAfter(card.id)} className="text-[10px] font-semibold text-zinc-600 opacity-0 transition hover:text-purple-300 group-hover:opacity-100">+ Insert Below</button>
                                                        <button onClick={() => removeBatchPreviewCard(card.id)} className="text-[10px] font-semibold text-zinc-600 opacity-0 transition hover:text-red-400 group-hover:opacity-100">Remove</button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            )}

                            {/* Badge legend — makes the auto-detection legible at a glance */}
                            {!isCards && batchPreviewRows.length > 0 && (
                                <div className="mt-3 flex flex-wrap items-center gap-1.5">
                                    {Object.keys(BATCH_ROW_BADGE_META).filter(k => k !== 'empty').map(k => (
                                        <span key={k} className={`rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${BATCH_ROW_BADGE_META[k].className}`}>
                                            {BATCH_ROW_BADGE_META[k].label}
                                        </span>
                                    ))}
                                    <span className="ml-1 text-[10px] text-zinc-600">Wrong on a row? Change it in that row's Type dropdown.</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ---------- FOOTER ---------- */}
                    <div className="relative z-10 flex flex-col gap-4 border-t border-white/10 bg-black/60 px-8 py-5 lg:flex-row lg:items-center lg:justify-between">
                        <div className="flex flex-wrap items-center gap-2.5">
                            {!isCards && credits.length > 0 ? (
                                <>
                                    <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-zinc-500">Insert</span>
                                    <select
                                        value={batchInsertTargetId}
                                        onChange={(e) => { setBatchInsertTargetId(e.target.value); setBatchInsertMode('new-section'); }}
                                        className={`max-w-xs cursor-pointer rounded-lg border px-3 py-2 text-[12px] outline-none transition ${batchInsertTargetId === 'replace' ? 'border-red-500/50 bg-red-950/40 text-red-200' : 'border-white/10 bg-zinc-900 text-zinc-200 focus:border-purple-500/60'}`}
                                    >
                                        <option value="end">At the end of the list</option>
                                        {batchInsertionTargets.map(t => (
                                            <option key={t.id} value={t.id}>After "{t.label}"</option>
                                        ))}
                                        <option value="replace">— Replace all current credits —</option>
                                    </select>
                                    {selectedTarget && selectedTarget.type === 'multiple' && (
                                        <div className="flex items-center gap-1 rounded-lg border border-white/10 bg-black/40 p-1">
                                            {[
                                                { m: 'new-section', label: 'New section' },
                                                { m: 'merge', label: `Add into "${selectedTarget.label}"` },
                                            ].map(opt => (
                                                <button
                                                    key={opt.m}
                                                    onClick={() => setBatchInsertMode(opt.m)}
                                                    className={`rounded-md px-2.5 py-1.5 text-[11px] font-semibold transition-all ${batchInsertMode === opt.m ? 'bg-purple-600 text-white' : 'text-zinc-400 hover:bg-white/5 hover:text-zinc-200'}`}
                                                >
                                                    {opt.label}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    {batchInsertTargetId === 'replace' && (
                                        <span className="text-[11px] font-medium text-red-300">This will erase your {credits.length} existing credit{credits.length === 1 ? '' : 's'}.</span>
                                    )}
                                </>
                            ) : isCards && cardCredits.length > 0 ? (
                                <>
                                    <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-zinc-500">Insert</span>
                                    <div className="flex items-center gap-1 rounded-lg border border-white/10 bg-black/40 p-1">
                                        {[
                                            { v: 'end', label: 'Add to existing' },
                                            { v: 'replace', label: 'Replace all' },
                                        ].map(opt => (
                                            <button
                                                key={opt.v}
                                                onClick={() => setBatchInsertTargetId(opt.v)}
                                                className={`rounded-md px-2.5 py-1.5 text-[11px] font-semibold transition-all ${batchInsertTargetId === opt.v ? (opt.v === 'replace' ? 'bg-red-600 text-white' : 'bg-purple-600 text-white') : 'text-zinc-400 hover:bg-white/5 hover:text-zinc-200'}`}
                                            >
                                                {opt.label}
                                            </button>
                                        ))}
                                    </div>
                                    {batchInsertTargetId === 'replace' && (
                                        <span className="text-[11px] font-medium text-red-300">This will erase your {cardCredits.length} existing card{cardCredits.length === 1 ? '' : 's'}.</span>
                                    )}
                                </>
                            ) : (
                                <span className="text-[11px] text-zinc-600">
                                    {isCards ? 'New cards are appended to your Title Cards list.' : 'Credits are added to the end of your list.'}
                                </span>
                            )}
                            <button
                                onClick={askCopilotAboutBatch}
                                title="Ask the Copilot about this list"
                                className="flex items-center gap-1.5 rounded-lg border border-white/10 px-2.5 py-1.5 text-[11px] font-semibold text-zinc-400 transition-colors hover:border-purple-500/40 hover:bg-purple-500/10 hover:text-purple-300"
                            >
                                <Sparkles size={12} /> Ask the Copilot
                            </button>
                        </div>

                        <div className="flex items-center gap-3">
                            {!isCards && commitCount > 0 && (
                                <span className="hidden text-[12px] text-zinc-500 xl:inline">
                                    {pairedCount} paired · {sectionCount} section{sectionCount === 1 ? '' : 's'} · {groupCount} group{groupCount === 1 ? '' : 's'}
                                </span>
                            )}
                            <button
                                onClick={() => setShowBatchAddModal(false)}
                                className="rounded-xl border border-white/10 px-5 py-3 text-sm font-medium text-zinc-300 transition-colors hover:bg-white/5 hover:text-white"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleBatchAddCommit}
                                disabled={commitCount === 0}
                                className={`inline-flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold text-white shadow-lg transition-all disabled:cursor-not-allowed disabled:bg-none disabled:bg-zinc-900 disabled:text-zinc-600 disabled:shadow-none ${
                                    batchInsertTargetId === 'replace'
                                        ? 'bg-gradient-to-b from-red-500 to-red-700 shadow-red-950/50 enabled:hover:from-red-400 enabled:hover:to-red-600'
                                        : 'bg-gradient-to-b from-purple-500 to-purple-700 shadow-purple-950/50 enabled:hover:from-purple-400 enabled:hover:to-purple-600'
                                }`}
                            >
                                {batchInsertTargetId === 'replace' ? <RotateCcw size={16} /> : <Zap size={16} />}
                                {batchInsertTargetId === 'replace'
                                    ? `Replace with ${commitCount} ${isCards ? 'Card' : 'Credit'}${commitCount === 1 ? '' : 's'}`
                                    : `Add ${commitCount} ${isCards ? 'Card' : 'Credit'}${commitCount === 1 ? '' : 's'}`}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            );
        })()}

        {showLogoBoard && (() => {
            const logoBoardCredit = credits.find(c => c.id === logoBoardCreditId);
            return (
            <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4">
                <div className="bg-zinc-900 rounded-xl shadow-2xl border border-zinc-700 w-full max-w-3xl max-h-[90vh] flex flex-col">
                    {/* Header */}
                    <div className="p-5 border-b border-zinc-800 flex items-center justify-between flex-shrink-0">
                        <div>
                            <h3 className="text-xl font-semibold text-purple-300 flex items-center gap-2"><Grid3x3 size={22}/> Logo Board</h3>
                            <p className="text-xs text-zinc-500 mt-1">Drag images in anywhere, or drag logo cards to rearrange them. Up to 5 per row. Nothing changes in your credits until you commit.</p>
                        </div>
                        <button onClick={closeLogoBoard} className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-md transition-colors"><X size={20}/></button>
                    </div>

                    {/* Rows workspace — also the whole-board file drop zone */}
                    <div
                        className={`flex-grow overflow-y-auto p-5 space-y-4 custom-scrollbar transition-colors ${isBoardFileDragActive ? 'bg-purple-950/20' : ''}`}
                        onDragOver={(e) => { if (e.dataTransfer.types.includes('Files')) { e.preventDefault(); setIsBoardFileDragActive(true); } }}
                        onDragLeave={(e) => { if (e.currentTarget === e.target) setIsBoardFileDragActive(false); }}
                        onDrop={(e) => {
                            e.preventDefault();
                            setIsBoardFileDragActive(false);
                            if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                                addFilesToLogoBoard(e.dataTransfer.files);
                            }
                        }}
                    >
                        {logoBoardRows.length === 0 && (
                            <p className="text-sm text-zinc-500 text-center py-8">No rows yet. Drag images in to get started, or add a row below.</p>
                        )}
                        {logoBoardRows.map((row, rowIndex) => (
                            <div
                                key={row.id}
                                className="bg-zinc-950 border border-zinc-800 rounded-lg p-3"
                                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                                        addFilesToLogoBoardRow(row.id, e.dataTransfer.files);
                                        setIsBoardFileDragActive(false);
                                        return;
                                    }
                                    if (draggedLogoBoardItem) {
                                        moveLogoBoardLogoTo(draggedLogoBoardItem.rowId, draggedLogoBoardItem.logoId, row.id, row.logos.length);
                                        setDraggedLogoBoardItem(null);
                                    }
                                }}
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs font-semibold text-zinc-400 uppercase tracking-wide">Row {rowIndex + 1} · {row.logos.length}/5</span>
                                    <div className="flex items-center gap-3">
                                        <div className="flex items-center gap-1 bg-zinc-800 rounded-md px-1.5 py-1" title="Space between the logos in this row">
                                            <span className="text-[10px] text-zinc-500 uppercase tracking-wide mr-1">Gap</span>
                                            <button onClick={() => setLogoBoardRowGap(row.id, Math.max(0, (row.gap ?? logoBoardCredit?.gridGap ?? 1) - 0.25))} className="w-5 h-5 flex items-center justify-center text-zinc-300 hover:text-white hover:bg-zinc-700 rounded">−</button>
                                            <input
                                                type="number"
                                                step="0.25"
                                                min="0"
                                                max="60"
                                                value={(row.gap ?? logoBoardCredit?.gridGap ?? 1).toFixed(2)}
                                                onChange={(e) => { const v = parseFloat(e.target.value); if (!isNaN(v)) setLogoBoardRowGap(row.id, Math.max(0, Math.min(60, v))); }}
                                                className="w-12 text-[10px] text-zinc-300 text-center tabular-nums bg-transparent border-b border-transparent hover:border-zinc-600 focus:border-purple-500 outline-none"
                                                title="Type an exact value"
                                            />
                                            <button onClick={() => setLogoBoardRowGap(row.id, Math.min(60, (row.gap ?? logoBoardCredit?.gridGap ?? 1) + 0.25))} className="w-5 h-5 flex items-center justify-center text-zinc-300 hover:text-white hover:bg-zinc-700 rounded">+</button>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <button onClick={() => moveLogoBoardRow(row.id, -1)} disabled={rowIndex === 0} className="p-1.5 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded disabled:opacity-30 disabled:cursor-not-allowed" title="Move row up"><ArrowUp size={14}/></button>
                                            <button onClick={() => moveLogoBoardRow(row.id, 1)} disabled={rowIndex === logoBoardRows.length - 1} className="p-1.5 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded disabled:opacity-30 disabled:cursor-not-allowed" title="Move row down"><ArrowDown size={14}/></button>
                                            <button onClick={() => removeLogoBoardRow(row.id)} className="p-1.5 text-red-400 hover:text-red-300 hover:bg-zinc-800 rounded" title="Delete row"><Trash2 size={14}/></button>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex flex-wrap gap-3">
                                    {row.logos.map((logo, logoIndex) => (
                                        <div
                                            key={logo.id}
                                            draggable
                                            onDragStart={(e) => { e.stopPropagation(); setDraggedLogoBoardItem({ rowId: row.id, logoId: logo.id }); }}
                                            onDragEnd={() => setDraggedLogoBoardItem(null)}
                                            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                            onDrop={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                                                    // Dropping a file directly onto an existing logo replaces it.
                                                    const file = e.dataTransfer.files[0];
                                                    if (isLikelyImageFile(file)) uploadLogoBoardFileToSlot(row.id, logo.id, file);
                                                    setIsBoardFileDragActive(false);
                                                    return;
                                                }
                                                if (draggedLogoBoardItem) {
                                                    const targetIndex = row.logos.findIndex(l => l.id === logo.id);
                                                    moveLogoBoardLogoTo(draggedLogoBoardItem.rowId, draggedLogoBoardItem.logoId, row.id, targetIndex);
                                                    setDraggedLogoBoardItem(null);
                                                }
                                            }}
                                            className={`relative w-28 bg-zinc-800 border border-zinc-700 rounded-md p-2 group cursor-grab active:cursor-grabbing ${draggedLogoBoardItem?.logoId === logo.id ? 'opacity-30' : ''}`}
                                        >
                                            <div className="w-full h-16 bg-zinc-900 rounded flex items-center justify-center overflow-hidden mb-1.5">
                                                {logo.logoUrl ? (
                                                    <img src={logo.logoUrl} alt="" className="max-w-full max-h-full object-contain pointer-events-none" />
                                                ) : (
                                                    <div className="w-full h-full flex flex-col items-center justify-center gap-1">
                                                        <label className="flex items-center justify-center cursor-pointer text-zinc-600 hover:text-zinc-400">
                                                            <ImagePlus size={18} />
                                                            <input type="file" accept="image/*" onChange={(e) => uploadLogoBoardImage(row.id, logo.id, e)} className="hidden" />
                                                        </label>
                                                        <button onClick={() => setLibraryPickerTarget({ rowId: row.id, logoId: logo.id })} className="text-zinc-600 hover:text-purple-400" title="Choose from library">
                                                            <Images size={14} />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                            {logo.logoUrl && (
                                                <div className="flex items-center justify-center gap-2 text-[10px] mb-1">
                                                    <label className="text-purple-300 hover:text-purple-200 cursor-pointer">
                                                        Replace
                                                        <input type="file" accept="image/*" onChange={(e) => uploadLogoBoardImage(row.id, logo.id, e)} className="hidden" />
                                                    </label>
                                                    <span className="text-zinc-700">·</span>
                                                    <button onClick={() => setLibraryPickerTarget({ rowId: row.id, logoId: logo.id })} className="text-purple-300 hover:text-purple-200">
                                                        Library
                                                    </button>
                                                </div>
                                            )}
                                            <select value={logo.size || 'medium'} onChange={(e) => setLogoBoardLogoSize(row.id, logo.id, e.target.value)} className="w-full text-[10px] p-1 rounded bg-zinc-700 text-zinc-200 border border-zinc-600 mb-1">
                                                <option value="xs">X-Small</option>
                                                <option value="small">Small</option>
                                                <option value="medium">Medium</option>
                                                <option value="large">Large</option>
                                                <option value="xl">X-Large</option>
                                            </select>
                                            <div className="flex items-center justify-between">
                                                <button onClick={() => moveLogoBoardLogo(row.id, logo.id, -1)} disabled={logoIndex === 0} className="p-1 text-zinc-500 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed" title="Move left"><ArrowLeft size={12}/></button>
                                                <button onClick={() => removeLogoBoardLogo(row.id, logo.id)} className="p-1 text-red-400 hover:text-red-300" title="Remove"><X size={12}/></button>
                                                <button onClick={() => moveLogoBoardLogo(row.id, logo.id, 1)} disabled={logoIndex === row.logos.length - 1} className="p-1 text-zinc-500 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed" title="Move right"><ArrowRight size={12}/></button>
                                            </div>
                                        </div>
                                    ))}
                                    {row.logos.length < 5 && (
                                        <button onClick={() => addLogoBoardSlotFromLibrary(row.id)} className="w-28 h-full min-h-[104px] border-2 border-dashed border-zinc-700 hover:border-purple-500 rounded-md flex flex-col items-center justify-center text-zinc-600 hover:text-purple-400 transition-colors">
                                            <Plus size={20} />
                                            <span className="text-[10px] mt-1">Add Logo</span>
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                        <button onClick={addLogoBoardRow} className="w-full flex items-center justify-center p-3 border-2 border-dashed border-zinc-700 hover:border-purple-500 text-zinc-400 hover:text-purple-400 rounded-lg transition-colors">
                            <Plus size={16} className="mr-2"/> Add Row
                        </button>
                    </div>

                    {/* Footer */}
                    <div className="p-4 border-t border-zinc-800 flex justify-end gap-3 flex-shrink-0">
                        <button onClick={closeLogoBoard} className="px-5 py-2 bg-zinc-700 text-gray-200 rounded-lg hover:bg-zinc-600 transition-colors">Cancel</button>
                        <button onClick={commitLogoBoard} className="px-5 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium">Commit to Credit</button>
                    </div>
                </div>

                {libraryPickerTarget && (
                    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[60] p-4" onClick={() => setLibraryPickerTarget(null)}>
                        <div className="bg-zinc-900 rounded-xl shadow-2xl border border-zinc-700 w-full max-w-lg max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                            <div className="p-4 border-b border-zinc-800 flex items-center justify-between flex-shrink-0">
                                <h3 className="text-base font-semibold text-purple-300 flex items-center gap-2"><Images size={18}/> Choose a Logo</h3>
                                <button onClick={() => setLibraryPickerTarget(null)} className="p-1.5 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-md"><X size={18}/></button>
                            </div>
                            <div className="p-4 flex-grow overflow-y-auto custom-scrollbar space-y-4">
                                <label className="flex items-center justify-center gap-2 py-2.5 border-2 border-dashed border-zinc-700 hover:border-purple-500 rounded-lg cursor-pointer text-zinc-400 hover:text-purple-400 transition-colors text-sm">
                                    <Upload size={16} />
                                    Upload from device
                                    <input
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={(e) => {
                                            const file = e.target.files[0];
                                            if (file && libraryPickerTarget) {
                                                uploadLogoBoardFileToSlot(libraryPickerTarget.rowId, libraryPickerTarget.logoId, file);
                                                setLibraryPickerTarget(null);
                                            }
                                        }}
                                    />
                                </label>

                                {libraryAssets.filter(a => a.type === 'logo').length > 0 ? (
                                    <div>
                                        <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-600 mb-2">From Your Library</p>
                                        <div className="grid grid-cols-4 gap-2">
                                            {libraryAssets.filter(a => a.type === 'logo').map(asset => (
                                                <button
                                                    key={asset.id}
                                                    onClick={() => applyLibraryLogoToBoardSlot(asset)}
                                                    title={asset.name}
                                                    className="aspect-square bg-zinc-800 border border-zinc-700 hover:border-purple-500 rounded-md p-1.5 flex items-center justify-center transition-colors"
                                                >
                                                    <img src={asset.url} alt={asset.name} className="max-w-full max-h-full object-contain pointer-events-none" />
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <p className="text-xs text-zinc-600 text-center py-2">Your library is empty — uploaded logos will show up here for reuse next time.</p>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
            );
        })()}
        {showSongBoard && (
            <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4">
                <div className="bg-zinc-900 rounded-xl shadow-2xl border border-zinc-700 w-full max-w-2xl max-h-[90vh] flex flex-col">
                    {/* Header */}
                    <div className="p-5 border-b border-zinc-800 flex items-center justify-between flex-shrink-0">
                        <div>
                            <h3 className="text-xl font-semibold text-purple-300 flex items-center gap-2"><Music size={22}/> Song Board</h3>
                            <p className="text-xs text-zinc-500 mt-1">Add a Song Title and its writing/performing/publishing info for each song — press Enter in the info box for extra lines on complicated credits. Drag cards to reorder. Up to 3 songs per block, so it always lays out as one clean row — add another Song Credits block for more. Nothing changes in your credits until you commit.</p>
                        </div>
                        <button onClick={closeSongBoard} className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-md transition-colors"><X size={20}/></button>
                    </div>

                    {/* Song cards workspace */}
                    <div className="flex-grow overflow-y-auto p-5 space-y-3 custom-scrollbar">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-600 text-right">{songBoardEntries.length} of {SONG_BOARD_MAX_ENTRIES} songs</p>
                        {songBoardEntries.length === 0 && (
                            <p className="text-sm text-zinc-500 text-center py-8">No songs yet. Add one below.</p>
                        )}
                        {songBoardEntries.map((entry, index) => (
                            <div
                                key={entry.id}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    if (draggedSongBoardEntryId && draggedSongBoardEntryId !== entry.id) {
                                        moveSongBoardEntryTo(draggedSongBoardEntryId, index);
                                    }
                                    setDraggedSongBoardEntryId(null);
                                }}
                                className={`flex items-start gap-3 bg-zinc-950 border border-zinc-800 rounded-lg p-3 transition-opacity ${draggedSongBoardEntryId === entry.id ? 'opacity-30' : ''}`}
                            >
                                <div
                                    draggable
                                    onDragStart={(e) => { e.stopPropagation(); setDraggedSongBoardEntryId(entry.id); }}
                                    onDragEnd={() => setDraggedSongBoardEntryId(null)}
                                    className="pt-2.5 text-zinc-600 cursor-grab active:cursor-grabbing"
                                    title="Drag to reorder"
                                ><GripVertical size={16}/></div>
                                <div className="flex-grow space-y-2">
                                    <input
                                        type="text"
                                        value={entry.title}
                                        onChange={(e) => updateSongBoardEntryField(entry.id, 'title', e.target.value)}
                                        placeholder="Song Title"
                                        className="w-full p-2 rounded-md border border-zinc-700 bg-zinc-800 text-gray-100 text-sm font-semibold focus:ring-2 focus:ring-purple-500 outline-none"
                                    />
                                    <textarea
                                        value={entry.info}
                                        onChange={(e) => updateSongBoardEntryField(entry.id, 'info', e.target.value)}
                                        placeholder={"Written and Performed by...\nAdd more lines for Published by, Courtesy of, etc."}
                                        rows={Math.min(6, Math.max(1, (entry.info.match(/\n/g) || []).length + 1))}
                                        className="w-full p-2 rounded-md border border-zinc-700 bg-zinc-800 text-gray-300 text-sm focus:ring-2 focus:ring-purple-500 outline-none resize-y leading-snug"
                                    />
                                </div>
                                <button onClick={() => removeSongBoardEntry(entry.id)} className="p-1.5 text-red-400 hover:text-red-300 hover:bg-zinc-800 rounded mt-1" title="Remove"><Trash2 size={14}/></button>
                            </div>
                        ))}
                        {songBoardEntries.length < SONG_BOARD_MAX_ENTRIES ? (
                            <button onClick={addSongBoardEntry} className="w-full flex items-center justify-center p-3 border-2 border-dashed border-zinc-700 hover:border-purple-500 text-zinc-400 hover:text-purple-400 rounded-lg transition-colors">
                                <Plus size={16} className="mr-2"/> Add Song
                            </button>
                        ) : (
                            <p className="text-[11px] text-zinc-600 text-center py-2">This block is full. Commit this one, then add another Song Credits block for more songs.</p>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="p-4 border-t border-zinc-800 flex justify-end gap-3 flex-shrink-0">
                        <button onClick={closeSongBoard} className="px-5 py-2 bg-zinc-700 text-gray-200 rounded-lg hover:bg-zinc-600 transition-colors">Cancel</button>
                        <button onClick={commitSongBoard} className="px-5 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium">Commit to Credit</button>
                    </div>
                </div>
            </div>
        )}
        {showImportModal && ( <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"> <div className="bg-zinc-800 p-8 rounded-xl shadow-xl max-w-lg w-full text-gray-200 border border-zinc-700"> <h3 className="text-2xl font-semibold mb-6 text-purple-400 flex items-center"><Upload size={24} className="mr-2"/> Import Options</h3> <div className="space-y-4"> <div> <label className="block mb-2 text-gray-300">Import from CSV:</label> <input type="file" accept=".csv" onChange={handleFileUpload} className="block w-full text-sm text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-950 file:text-blue-300 hover:file:bg-blue-900 cursor-pointer"/> </div> <div className="text-center text-gray-500">OR</div><div> <label className="block mb-2 text-gray-300">Batch Upload Logos (Creates Individual Credits):</label> <input type="file" accept="image/*" multiple onChange={(e) => { const files = Array.from(e.target.files); if (!files.length) return; const newCredits = []; files.forEach(file => { if (file.type.startsWith('image/')) { const reader = new FileReader(); reader.onload = (re) => { newCredits.push({ id: Date.now() + Math.random(), name: file.name.split('.').slice(0, -1).join('.'), type: 'logo', logoUrl: re.target.result }); if (newCredits.length === files.length) { setCredits(prev => [...prev, ...newCredits]); showMessageBox('Success', `${files.length} logos imported as new credits.`, 'success', true); setShowImportModal(false); } }; reader.readAsDataURL(file); } }); e.target.value = ''; }} className="block w-full text-sm text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-950 file:text-blue-300 hover:file:bg-blue-900 cursor-pointer"/> </div> </div> <button onClick={() => setShowImportModal(false)} className="mt-8 w-full bg-zinc-700 text-gray-200 p-3 rounded-lg hover:bg-zinc-600 transition-colors duration-200 font-poppins font-light shadow-md">Close</button> </div> </div> )}
        {showImportOptionsModal && ( <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"> <div className="bg-zinc-800 p-8 rounded-xl shadow-xl max-w-lg w-full text-gray-200 border border-zinc-700"> <h3 className="text-2xl font-semibold mb-6 text-purple-400 flex items-center"><Upload size={24} className="mr-2"/> CSV Import Mode</h3> <p className="text-gray-300 mb-6">You've selected a CSV file. How would you like to import it?</p> <div className="space-y-4"> <div> <input type="checkbox" id="convertImportedToCards" checked={convertImportedToCards} onChange={(e) => setConvertImportedToCards(e.target.checked)} className="mr-2 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"/> <label htmlFor="convertImportedToCards" className="text-sm font-medium text-gray-300">Convert all imported credits to Title Cards</label> </div> <button onClick={() => importFromCSV(pendingCsvFile, 'add')} className="w-full bg-blue-700 text-white p-3 rounded-lg hover:bg-blue-800 transition-colors duration-200 font-poppins font-light shadow-md">Add to Existing Credits</button> <div className="pt-2 border-t border-zinc-700"> <p className="text-xs text-zinc-400 mb-2 uppercase tracking-wide font-semibold">Replace</p> <div className="space-y-2"> <button onClick={() => importFromCSV(pendingCsvFile, 'replace-scroll')} className="w-full bg-red-900/60 text-white p-3 rounded-lg hover:bg-red-800 transition-colors duration-200 font-poppins font-light shadow-md text-left"> Replace Scrolling Credits Only <span className="block text-xs text-red-200/80 font-normal">Your title cards are left untouched</span> </button> <button onClick={() => importFromCSV(pendingCsvFile, 'replace-cards')} className="w-full bg-red-900/60 text-white p-3 rounded-lg hover:bg-red-800 transition-colors duration-200 font-poppins font-light shadow-md text-left"> Replace Title Cards Only <span className="block text-xs text-red-200/80 font-normal">Your scrolling credits are left untouched</span> </button> <button onClick={() => importFromCSV(pendingCsvFile, 'replace-all')} className="w-full bg-red-700 text-white p-3 rounded-lg hover:bg-red-800 transition-colors duration-200 font-poppins font-light shadow-md text-left"> Replace Everything <span className="block text-xs text-red-200/80 font-normal">Wipes both scrolling credits and title cards</span> </button> </div> </div> </div> <button onClick={() => { setShowImportOptionsModal(false); setPendingCsvFile(null); setShowImportModal(false); }} className="mt-8 w-full bg-zinc-700 text-gray-200 p-3 rounded-lg hover:bg-zinc-600 transition-colors duration-200 font-poppins font-light shadow-md">Cancel</button> </div> </div> )}
        {showPasteOptionsModal && ( <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"> <div className="bg-zinc-800 p-8 rounded-xl shadow-xl max-w-md w-full text-gray-200 border border-zinc-700"> <h3 className="text-2xl font-semibold mb-2 text-purple-400">Paste Attributes</h3> <p className="text-zinc-400 mb-6">Apply copied style to {selectedCreditIds.size} selected card(s).</p> <div className="space-y-4 text-left"> <label className="flex items-center p-3 bg-zinc-900 rounded-lg hover:bg-zinc-700 cursor-pointer"> <input type="checkbox" checked={pasteOptions.transform} onChange={(e) => setPasteOptions(p => ({...p, transform: e.target.checked}))} className="h-5 w-5 rounded border-zinc-600 bg-zinc-700 text-purple-600 focus:ring-purple-500" /> <span className="ml-3 font-medium text-zinc-200">Position</span> </label> <label className="flex items-center p-3 bg-zinc-900 rounded-lg hover:bg-zinc-700 cursor-pointer"> <input type="checkbox" checked={pasteOptions.duration} onChange={(e) => setPasteOptions(p => ({...p, duration: e.target.checked}))} className="h-5 w-5 rounded border-zinc-600 bg-zinc-700 text-purple-600 focus:ring-purple-500" /> <span className="ml-3 font-medium text-zinc-200">Duration</span> </label> <label className="flex items-center p-3 bg-zinc-900 rounded-lg hover:bg-zinc-700 cursor-pointer"> <input type="checkbox" checked={pasteOptions.layout} onChange={(e) => setPasteOptions(p => ({...p, layout: e.target.checked}))} className="h-5 w-5 rounded border-zinc-600 bg-zinc-700 text-purple-600 focus:ring-purple-500" /> <span className="ml-3 font-medium text-zinc-200">Alignment & Spacing</span> </label> <label className="flex items-center p-3 bg-zinc-900 rounded-lg hover:bg-zinc-700 cursor-pointer"> <input type="checkbox" checked={pasteOptions.layerStyles} onChange={(e) => setPasteOptions(p => ({...p, layerStyles: e.target.checked}))} className="h-5 w-5 rounded border-zinc-600 bg-zinc-700 text-purple-600 focus:ring-purple-500" /> <span className="ml-3 font-medium text-zinc-200">Text Layer Styles & Order</span> </label> </div> <div className="flex justify-end space-x-4 mt-8"> <button onClick={() => setShowPasteOptionsModal(false)} className="bg-zinc-700 text-gray-200 px-5 py-2 rounded-lg hover:bg-zinc-600">Cancel</button> <button onClick={executePasteStyle} className="bg-purple-600 text-white px-5 py-2 rounded-lg hover:bg-purple-700">Apply</button> </div> </div> </div> )}
        <ExportModal 
            isOpen={showExportModal} 
            onClose={() => setShowExportModal(false)}
            onExport={exportVideo}
            onNLEExport={exportNLE} 
            projectName={projectName}
            isExporting={exportIsProcessing}
            projectTier={projectData?.tier}
        />
        {showDurationReminderModal && (
            <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
                <div className="bg-zinc-800 p-8 rounded-xl shadow-xl max-w-md w-full text-gray-200 border border-zinc-700">
                    <div className="flex items-start gap-3 mb-2">
                        <Timer size={24} className="text-amber-400 shrink-0 mt-0.5" />
                        <h3 className="text-xl font-semibold text-white">Set your duration first?</h3>
                    </div>
                    <p className="text-sm text-zinc-400 leading-relaxed mb-5">
                        This project is still using the default {DEFAULT_TARGET_DURATION_SECONDS}-second duration.
                        If that's not what you want, set it in the Timing menu before exporting — each export uses
                        a render credit, and you don't want to spend one on the wrong length.
                    </p>
                    <label className="flex items-center gap-2 mb-6 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={hideDurationReminder}
                            onChange={(e) => {
                                setHideDurationReminder(e.target.checked);
                                localStorage.setItem('hideDurationReminder', String(e.target.checked));
                            }}
                            className="h-4 w-4 rounded border-zinc-600 bg-zinc-700 text-purple-600 focus:ring-purple-500"
                        />
                        <span className="text-sm text-zinc-400">Don't show this again</span>
                    </label>
                    <div className="flex justify-end gap-3">
                        <button
                            onClick={() => setShowDurationReminderModal(false)}
                            className="px-5 py-2 text-sm text-zinc-400 hover:text-white transition-colors font-medium"
                        >
                            Back to Timing menu
                        </button>
                        <button
                            onClick={() => { setShowDurationReminderModal(false); setShowExportModal(true); }}
                            className="px-5 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold rounded-lg transition-colors"
                        >
                            Export anyway
                        </button>
                    </div>
                </div>
            </div>
        )}
        {showTemplatesModal && (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 font-poppins">
        <div className="bg-zinc-900 rounded-xl shadow-2xl max-w-lg w-full text-gray-100 border border-zinc-700 relative">
            <div className="p-6 border-b border-zinc-800 flex items-center justify-between">
                <h3 className="text-2xl font-thin text-purple-400 flex items-center gap-2"><FolderOpen size={24} className="mr-2"/> Load Template</h3>
                <button onClick={() => setShowTemplatesModal(false)} className="p-2 rounded-full hover:bg-zinc-700 text-gray-400">
                    <X size={20} />
                </button>
            </div>
            <div className='max-h-96 overflow-y-auto p-6'>
                <CollapsibleSection title="Included Templates" icon={Award} defaultOpen={true}>
                    <div className="space-y-4 pt-4">
                        <button onClick={() => loadTemplate({ name: 'TV Opening', ...defaultTemplates['tv-opening']})} className="w-full bg-zinc-800 border border-zinc-700 text-blue-400 p-3 rounded-lg hover:bg-zinc-700 hover:text-blue-300 transition-colors duration-200 font-poppins font-light shadow-md flex items-center justify-center gap-2"><Tv size={18}/> TV Show Opening</button>
                        <button onClick={() => loadTemplate({ name: 'Indie Film', ...defaultTemplates['indie-film']})} className="w-full bg-zinc-800 border border-zinc-700 text-blue-400 p-3 rounded-lg hover:bg-zinc-700 hover:text-blue-300 transition-colors duration-200 font-poppins font-light shadow-md flex items-center justify-center gap-2"><Camera size={18}/> Indie Film</button>
                        <button onClick={() => loadTemplate({ name: 'Studio Film', ...defaultTemplates['studio-film']})} className="w-full bg-zinc-800 border border-zinc-700 text-blue-400 p-3 rounded-lg hover:bg-zinc-700 hover:text-blue-300 transition-colors duration-200 font-poppins font-light shadow-md flex items-center justify-center gap-2"><Film size={18}/> Studio Film</button>
                    </div>
                </CollapsibleSection>
                <CollapsibleSection title="Your Templates" icon={Save} defaultOpen={true} count={userTemplates.length}>
                    <div className="space-y-4 pt-4">
                        {userTemplates.length > 0 ? (
                            userTemplates.map(template => (
                                <button key={template.id} onClick={() => loadTemplate(template)} className="w-full bg-zinc-800 border border-zinc-700 text-green-400 p-3 rounded-lg hover:bg-zinc-700 hover:text-green-300 transition-colors duration-200 font-poppins font-light shadow-md flex items-center justify-center gap-2">
                                    {template.name}
                                </button>
                            ))
                        ) : (
                            <p className="text-zinc-500 text-sm italic text-center">You haven't saved any templates yet.</p>
                        )}
                    </div>
                </CollapsibleSection>
            </div>
            <div className="p-6 border-t border-zinc-800 flex justify-end">
                <button onClick={() => setShowTemplatesModal(false)} className="bg-zinc-700 text-gray-200 px-5 py-2 rounded-lg hover:bg-zinc-600">Close</button>
            </div>
        </div>
    </div>
)}
<ExportNoticesModal isOpen={showExportNoticesModal} onClose={() => setShowExportNoticesModal(false)} db={db} userId={userId} showMessageBox={showMessageBox} currentProjectId={currentProjectId} functions={functions} />
        <FloatingToolbar
            selectedCreditIds={selectedCreditIds}
            credits={credits}
            cardCredits={cardCredits}
            onDelete={bulkDeleteSelectedCredits}
            copiedCardStyle={copiedCardStyle}
            onConvertToCards={() => {
                const scrollCreditsToConvert = credits.filter(c => selectedCreditIds.has(c.id));
                
                // Map over them to create an array of all new cards at once
                const newCards = scrollCreditsToConvert.map((credit, index) => {
                    return {
                        id: Date.now() + Math.random() + index, // Generate a truly unique ID per item
                        originalId: credit.id,
                        type: 'main-title',
                        creditDisplayType: 'card',
                        // duration intentionally omitted: falls back to settings.cardDuration (global).
                        cardPositionX: 50,
                        cardPositionY: 50,
                        cardTextAlign: 'center',
                        textLayers: [
                            { id: Math.random(), content: credit.role || "Role", fontSize: 28, fontWeight: 'normal', fontStyle: 'italic', color: '#CCCCCC', fontFamily: settings.cardFontFamily || 'poppins' },
                            { id: Math.random(), content: credit.name || "Name", fontSize: 46, fontWeight: 'bold', fontStyle: 'normal', color: '#FFFFFF', fontFamily: settings.cardFontFamily || 'poppins' }
                        ]
                    };
                });

                // Batch add all new cards at once to the bottom of the list
                setCardCredits(prev => [...prev, ...newCards]);
                
                showMessageBox("Success", `Converted ${scrollCreditsToConvert.length} credits to title cards!`, "success", true);
            }}
           onCopyStyle={() => {
                if (inspectorActiveCredit?.creditDisplayType === 'card') {
                    copyCardStyle();
                } else {
                    copySettings();
                }
            }}
            onPasteStyle={() => {
        // <--- UPDATE THIS LOGIC
        if (copiedCardStyle) {
            pasteCardStyle();
        } else {
            pasteSettings();
        }
    }}
            onGroupTogether={() => {
                const selectedScrollCredits = credits.filter(c => selectedCreditIds.has(c.id));
                const otherScrollCredits = credits.filter(c => !selectedCreditIds.has(c.id));
                const firstSelectedIndex = credits.findIndex(c => selectedCreditIds.has(c.id));
                if (firstSelectedIndex !== -1) {
                    const newCredits = [...otherScrollCredits];
                    newCredits.splice(firstSelectedIndex, 0, ...selectedScrollCredits);
                    setCredits(newCredits);
                    showMessageBox("Success", "Credits grouped together!", "success", true);
                }
            }}
            onMoveToTop={() => {
                const selectedScrollCredits = credits.filter(c => selectedCreditIds.has(c.id));
                const selectedCards = cardCredits.filter(c => selectedCreditIds.has(c.id));
                const otherScrollCredits = credits.filter(c => !selectedCreditIds.has(c.id));
                const otherCards = cardCredits.filter(c => !selectedCreditIds.has(c.id));
                
                setCredits([...selectedScrollCredits, ...otherScrollCredits]);
                setCardCredits([...selectedCards, ...otherCards]);
                showMessageBox("Success", "Moved to top!", "success", true);
            }}
            onMoveToBottom={() => {
                const selectedScrollCredits = credits.filter(c => selectedCreditIds.has(c.id));
                const selectedCards = cardCredits.filter(c => selectedCreditIds.has(c.id));
                const otherScrollCredits = credits.filter(c => !selectedCreditIds.has(c.id));
                const otherCards = cardCredits.filter(c => !selectedCreditIds.has(c.id));
                
                setCredits([...otherScrollCredits, ...selectedScrollCredits]);
                setCardCredits([...otherCards, ...selectedCards]);
                showMessageBox("Success", "Moved to bottom!", "success", true);
            }}
            copiedSettings={copiedSettings}
            position={{ bottom: leftPanelVisible ? '2rem' : '5rem' }}
        />
    </div>
  );
};

     
// --- Main Application Wrapper ---
  const App = () => {
  const { currentPage, setCurrentPage, setPurchaseIntent, messageBox, closeMessageBox, confirmAction, userId, isAuthReady, reviewToken } = useContext(AppContext);
  
  const handleNavigate = (page) => {
  setCurrentPage(page);
  window.scrollTo(0, 0);
};

 if (isAuthReady && !userId && currentPage === 'projects') {
    // If auth is ready, there's no user, and they are trying to access projects, redirect them.
    setCurrentPage('login');
  }
  
  // NEW: Context-aware navigation handlers
  const handleTutorialExit = () => {
    // If logged in, go back to their projects. If not, go to the landing page.
    if (userId) {
      setCurrentPage('projects');
    } else {
      setCurrentPage('landing');
    }
  };

  const handleTutorialComplete = () => {
    // If logged in, send them to their projects.
    // If not, they are a warm lead - send them to sign up with a purchase intent!
    if (userId) {
      setCurrentPage('projects');
    } else {
      setPurchaseIntent('indie'); // Pre-select the most popular plan
      setCurrentPage('signup');
    }
  };

   return (
    <>
      {currentPage === 'landing' && <LandingPage onNavigate={handleNavigate} setPurchaseIntent={setPurchaseIntent} />}
      {currentPage === 'features' && <FeaturesPage onNavigate={handleNavigate} setPurchaseIntent={setPurchaseIntent} />}
      {currentPage === 'login' && <LoginPage onNavigate={handleNavigate} />}
      {currentPage === 'signup' && <SignUpPage />}
      {currentPage === 'checkout' && <CheckoutPage />}
      {currentPage === 'projects' && <ProjectsPage />}
      {currentPage === 'editor' && <EditorPage />}
      {currentPage === 'admin' && <AdminPage />}
      {currentPage === 'terms' && <TermsOfService onNavigate={handleNavigate} />}
      {currentPage === 'privacy' && <PrivacyPolicy onNavigate={handleNavigate} />}
      {currentPage === 'tutorial' && <TutorialPage onExit={handleTutorialExit} onComplete={handleTutorialComplete} />}
      {currentPage === 'affiliate' && <AffiliateDashboard />}
      {currentPage === 'affiliate-admin' && <AffiliateAdminPage />}
      {currentPage === 'review' && <ReviewPage token={reviewToken} />}

   {messageBox.visible && (
  <div className="fixed inset-0 flex items-center justify-center z-[100] px-4">
    {/* Backdrop */}
    <div 
      className="absolute inset-0 bg-black/85 backdrop-blur-md"
      onClick={!messageBox.onConfirm ? closeMessageBox : undefined}
    ></div>

    {/* Modal Card */}
    <div className="relative z-10 max-w-md w-full">
      {/* Thin top accent line based on type */}
      <div className={`h-px w-full mb-0 rounded-t-xl ${
        messageBox.type === 'error' ? 'bg-red-500/60' : 
        messageBox.type === 'success' ? 'bg-emerald-500/60' : 
        messageBox.type === 'warning' ? 'bg-amber-500/60' :
        'bg-zinc-500/40'
      }`} />
      
      <div className="bg-zinc-950 border border-zinc-800/80 rounded-xl shadow-2xl shadow-black/60 p-7 flex flex-col gap-5">
        
        {/* Header */}
        <div className="flex items-start gap-4">
          {/* Status dot */}
          <div className={`w-1.5 h-1.5 rounded-full mt-2 shrink-0 ${
            messageBox.type === 'error' ? 'bg-red-400' : 
            messageBox.type === 'success' ? 'bg-emerald-400' : 
            messageBox.type === 'warning' ? 'bg-amber-400' :
            'bg-zinc-500'
          }`} />

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <span className={`text-[9px] font-bold tracking-[0.25em] uppercase ${
                messageBox.type === 'error' ? 'text-red-500' : 
                messageBox.type === 'success' ? 'text-emerald-500' : 
                messageBox.type === 'warning' ? 'text-amber-500' :
                'text-zinc-500'
              }`}>
                {messageBox.type === 'error' ? 'Error' : 
                 messageBox.type === 'success' ? 'Success' : 
                 messageBox.type === 'warning' ? 'Warning' : 'Notice'}
              </span>
            </div>
            <h3 className="text-base font-semibold text-white tracking-tight leading-snug mb-2">
              {messageBox.title}
            </h3>
            <div className="text-sm text-zinc-400 leading-relaxed">
              {messageBox.message}
            </div>
          </div>
        </div>

                {/* Download Link */}
                {messageBox.linkUrl && (
                <a 
                    href={messageBox.linkUrl} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    download
                    className="flex items-center justify-center gap-2 w-full py-2.5 bg-zinc-900 hover:bg-zinc-800 text-zinc-300 hover:text-white border border-zinc-800 rounded-lg transition-all text-sm font-medium tracking-wide"
                >
                    <CloudDownload size={15} /> 
                    {messageBox.linkText || 'Download File'}
                </a>
                )}

                {/* Actions */}
                <div className="flex justify-end gap-2.5 pt-1 border-t border-zinc-800/50">
                {messageBox.onConfirm ? (
                    <>
                    <button 
                        onClick={closeMessageBox} 
                        className="px-5 py-2 text-sm text-zinc-400 hover:text-white transition-colors font-medium"
                    >
                        Cancel
                    </button>
                    <button 
                        onClick={confirmAction} 
                        className="px-5 py-2 bg-white hover:bg-zinc-100 text-black text-sm font-semibold rounded-lg transition-colors"
                    >
                        Confirm
                    </button>
                    </>
                ) : (
                    <button 
                    onClick={closeMessageBox} 
                    className="px-5 py-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-zinc-300 hover:text-white text-sm font-medium rounded-lg transition-all tracking-wide"
                    >
                    Dismiss
                    </button>
                )}
                </div>

            </div>
            </div>
        </div>
        )}
        </>
        );
        };                                                                                                                                                                                                                                                                                                                                                   

// Main component wrapped with AppProvider
const RootApp = () => (
<AppProvider>
<App />
</AppProvider>
);

export default RootApp;